import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { buildArena, REBOOT_VAN_X, REBOOT_VAN_Z, VEND_X, VEND_Z, CRAFT_BENCH_X, CRAFT_BENCH_Z, SLURP_BARREL_X, SLURP_BARREL_Z, SHIELD_KEG_X, SHIELD_KEG_Z } from './WorldBuilder.js';
import { PlayerController } from './PlayerController.js';
import { WeaponSystem, weaponKindShortLabel, type WeaponKind } from './WeaponSystem.js';
import {
  applyPoiAmbientGradeTint,
  applyStormLightningFlash,
  applyStormSkyWarmth,
  createNightSkyElements,
  createStylizedSky,
  updateNightSkyElements,
  updateStylizedSky,
  type NightSkyElements,
} from './stylizedSky.js';
import { findPoiZoneAt, FPS_POI_ZONES, getPoiZoneColors, getPoiAmbientGradeTint, getPoiTintBlendAt } from './FpsPoiLandmarks.js';
import { createPostProcessingPipeline, getTimeOfDayBloomStrength, type PostProcessingPipeline } from './fortniteGradePass.js';
import { getRarityCssColor, windUniform } from './materials.js';
import { FpsCharacterAnimator, type CharAnimState } from './FpsCharacterAnimator.js';
import { FpsStorm } from './FpsStorm.js';
import { FpsStormLightning } from './FpsStormLightning.js';
import { FpsStormCircle } from './FpsStormCircle.js';
import { FpsAudio } from './FpsAudio.js';
import { FpsSlideVfx } from './FpsSlideVfx.js';
import { FpsKnockBubble } from './FpsKnockBubble.js';
import { FpsSeasonFx } from './FpsSeasonFx.js';
import { FpsAmbientLife } from './FpsAmbientLife.js';
import { FpsBuildTurboVfx } from './FpsBuildTurboVfx.js';
import { FpsMinimap, type FpsMinimapMarker } from './FpsMinimap.js';
import { POI_LOOT_ZONES } from './FpsDropHeatmap.js';
import { createFpsGraphicsState, FpsSettingsMenu, type FpsGraphicsState } from './FpsSettingsMenu.js';
import {
  type FpsCrosshairSettings,
  crosshairColorCss,
  crosshairGlowCss,
  loadCrosshairSettings,
} from './FpsCrosshairSettings.js';
import { FpsBuildPlacer, type BuildPieceKind } from './FpsBuildPlacer.js';
import {
  isVictoryRoyale,
  tryPlaceBuildPiece,
  addMaterials,
  applyCombatDamage,
  hitStopFromDamage,
  shieldBreakIntensity,
  lateCirclePressureMult,
  type PlacedBuildRecord,
} from './matchRules.js';
import { FpsLoot, CHEST_LOOT_STAGGER_SEC, chestLootRarityFlashHex, type ChestConsumable, type ChestLootPayload } from './FpsLoot.js';
import {
  formatRarityWeaponName,
  RARITY_LABELS,
  rollRarityBiased,
  rollSupplyDropWeaponRarity,
  upgradeRarity,
  type WeaponRarity,
} from './FpsWeaponRarity.js';
import { FpsBattleBus } from './FpsBattleBus.js';
import { buildGliderMesh } from './FpsGliderModel.js';
import { FpsHotbar } from './FpsHotbar.js';
import { getStormPhaseInfo } from './FpsStormPhase.js';
import { FpsXpBar } from './FpsXpBar.js';
import { FpsHarvest } from './FpsHarvest.js';
import { FpsWorldPingStack } from './FpsMinimapPing.js';
import { FpsWorldPingMarkers, type FpsPingType } from './FpsWorldPingMarkers.js';
import { FpsDailyChallenges } from './FpsChallenges.js';
import { FpsWeeklyBoard, WEEKLY_WIN_MILESTONE } from './FpsWeeklyBoard.js';
import { FpsKillTracer } from './FpsKillTracer.js';
import { FpsImpactSparks } from './FpsImpactSparks.js';
import { FpsParticleSystem } from './FpsParticleSystem.js';
import { FpsPerfOverlay } from './FpsPerfOverlay.js';
import { FpsChestBurst } from './FpsChestBurst.js';
import { FpsFootstepDust } from './FpsFootstepDust.js';
import { FpsBuildDustVfx } from './FpsBuildDustVfx.js';
import { FpsBulletCasingVfx } from './FpsBulletCasingVfx.js';
import { FpsShieldCrackVfx } from './FpsShieldCrackVfx.js';
import { FpsWaterSplash } from './FpsWaterSplash.js';
import { FpsShieldHud } from './FpsShieldHud.js';
import { FpsStaminaHud } from './FpsStaminaHud.js';
import { FpsFloorPickupField } from './FpsFloorPickupField.js';
import { FpsLootRarity } from './FpsLootRarity.js';
import {
  burstChest,
  createPickupCard,
  createRarityStinger,
  spawnRareLootArrivalPulse,
  spawnPickupFlyChip,
  type DomEffectHandle,
  type LootEffectHandle,
  type PickupCard,
  type RarityStinger,
} from './FpsLootPresentation.js';
import { FpsWeaponPickups, type WeaponPickupResult } from './FpsWeaponPickups.js';
import {
  getModAttachFeedLine,
  getModDisplayName,
  getModHudLetters,
  type WeaponMod,
} from './FpsWeaponMods.js';
import { FpsJumpPadField } from './FpsJumpPadField.js';
import { randomElimName, randomFeedVerb, randomBotElimFlavor } from './FpsBotNames.js';
import { FpsSupplyDrop } from './FpsSupplyDrop.js';
import { FpsSupplyDropPlane } from './FpsSupplyDropPlane.js';
import { FpsLlama } from './FpsLlama.js';
import { FpsMatchStats } from './FpsMatchStats.js';
import { FpsBots, type BotShotEvent } from './FpsBots.js';
import { FpsBotAI2 } from './FpsBotAI2.js';
import { FpsTeammate, REVIVE_RADIUS, CARRY_RADIUS, KNOCK_BLEEDOUT, REBOOT_VAN_QUEUE_HOLD } from './FpsTeammate.js';
import { FpsVehicles } from './FpsVehicles.js';
import { FpsZiplines } from './FpsZiplines.js';
import { FpsDamageNumbers } from './FpsDamageNumbers.js';
import { FpsHitFeedback, type FpsHitFeedbackKind } from './FpsHitFeedback.js';
import { FpsGrenades } from './FpsGrenades.js';
import { FpsAirstrike } from './FpsAirstrike.js';
import { FpsTouchControls } from './FpsTouchControls.js';
import { FpsHaptics } from './FpsHaptics.js';
import { FpsCareerStats, type ArenaRank } from './FpsCareerStats.js';
import { FpsBattlePass, BP_TIER_COUNT, BP_XP_PER_TIER } from './FpsBattlePass.js';
import { FpsTutorial } from './FpsTutorial.js';
import { FpsSquadCallouts } from './FpsSquadCallouts.js';
import { FpsCraftBench, CRAFT_RECIPES, MECH_PART_SPAWN_POSITIONS, MONSTER_PART_SPAWN_POSITIONS } from './FpsCraftBench.js';
import { FpsAudioVisualizer } from './FpsAudioVisualizer.js';
import { FpsNetwork } from './FpsNetwork.js';
import { FpsLODCuller } from './FpsLODCuller.js';
import { FpsNpcQuest, NPC_QUEST_X, NPC_QUEST_Z, QUEST_MAT_REWARD, QUEST_SHIELD_REWARD } from './FpsNpcQuest.js';
import {
  FpsNpcQuestHarvest,
  HARVEST_QUEST_MAT_REWARD,
  HARVEST_QUEST_HP_REWARD,
} from './FpsNpcQuestHarvest.js';
import { FpsStormSurge } from './FpsStormSurge.js';
import { FpsMatchDirector, type MatchDirectorSuggestion, FINAL_CIRCLE_BOT_CAP } from './FpsMatchDirector.js';
import { FpsChugSplash } from './FpsChugSplash.js';
import { FpsSniperGlint } from './FpsSniperGlint.js';
import { FpsShockwave } from './FpsShockwave.js';
import {
  FpsNpcQuestVisit,
  NPC_VISIT_X,
  NPC_VISIT_Z,
  VISIT_QUEST_MAT_REWARD,
  VISIT_QUEST_SHIELD_REWARD,
} from './FpsNpcQuestVisit.js';
import { FpsFishing, FISH_HP_HEAL } from './FpsFishing.js';
import {
  getActiveLtm,
  getDailyLtm,
  getLtmById,
  getLtmOverride,
  setLtmOverride,
  type FpsLtmId,
  type FpsLtmInfo,
} from './FpsLtm.js';
import { FpsLtmPicker } from './FpsLtmPicker.js';
import { FpsLobbyIsland, type LobbyPortal } from './FpsLobbyIsland.js';
import { FpsLiveEvent } from './FpsLiveEvent.js';
import { FpsAudioMix, type MusicStem } from './FpsAudioMix.js';
import { FpsMaterials } from './FpsMaterials.js';
import { FpsBuildEditMode } from './FpsBuildEditMode.js';
import { FpsPivotPlaza } from './FpsPivotPlaza.js';
import { FpsViewmodelRig } from './FpsViewmodelRig.js';
import { FpsVictoryRoyale, type KillFeedEntry } from './FpsVictoryRoyale.js';
import { FpsMythicWeapons, type MythicKind, type MythicUpdateResult } from './FpsMythicWeapons.js';
import { FpsHealItems, type HealPickupResult } from './FpsHealItems.js';
import { FpsMobilityProps, type MobilityTriggerResult } from './FpsMobilityProps.js';
import {
  FpsNpcVendors,
  FpsGoldBars,
  GOLD_PER_ELIM,
  type VendorInteractResult,
  type VendGoldItem,
} from './FpsNpcVendors.js';
import { FpsBossFights, type BossHitResult, type KeycardDrop } from './FpsBossFights.js';
import { FpsVaults, type VaultLootItem, type VaultUpdateResult } from './FpsVaults.js';
import { FpsSkinLocker, type SkinLoadout } from './FpsSkinLocker.js';
import { FpsEmoteWheel, type EmoteTriggerResult } from './FpsEmoteWheel.js';

const FIXED = 1 / 60;
const STORM_INIT_R = 94;
const STORM_MIN_R = 16;
const STORM_SHRINK = 2.2;
const EYE_HEIGHT = 1.62;
const SLURP_X = -14;
const SLURP_Z = 22;
const SLURP_R = 5.2;
const SLURP_BARREL_R = 2.4;
const SHIELD_KEG_R = 2.4;
const REBOOT_X = REBOOT_VAN_X;
const REBOOT_Z = REBOOT_VAN_Z;
const REBOOT_RADIUS = 8;
const TEAMMATE_REVIVE_HOLD = 4;
const TEAMMATE_CARRY_HOLD = 1.5;
const BLEEDOUT_TIME = 45;
const STORM_KNOCKED_MULT = 3.1;
const SUPPLY_DROP_AFTER_S = 20;
const LLAMA_SPAWN_AFTER_S = 45;
const MATCH_END_CONFETTI_S = 3;
const BLOOM_STRENGTH_NORMAL = 0.28;
const BLOOM_STRENGTH_PERF = 0.18;
const BASE_FOV = 76;
const ADS_FOV = 50;
const GLIDER_FOV_BUMP = 4;
const GLIDER_FOV_BUMP_DURATION = 0.3;
const ENDGAME_FOV_PULSE = 1.6;
const ENDGAME_FOV_PULSE_DURATION = 0.45;
const MANTLE_ASSIST_MIN_RISE = 0.55;
const MANTLE_ASSIST_MAX_RISE = 1.45;
const FOG_DAY = new THREE.Color(0xa9d9ff);
const FOG_SUNRISE = new THREE.Color(0xffc0a3);
const FOG_SUNSET = new THREE.Color(0x8d5a91);
const FOG_NIGHT = new THREE.Color(0x28385c);
const FOG_STORM = new THREE.Color(0x9b4dca);
const SUN_DAY = new THREE.Color(0xfffaf2);
const SUNRISE_LIGHT = new THREE.Color(0xffd2ad);
const SUNSET_LIGHT = new THREE.Color(0xff8b45);
const GRADE_SUNSET = new THREE.Color(1.08, 0.96, 0.84);
const GRADE_NIGHT = new THREE.Color(0.86, 0.93, 1.12);
const GRADE_STORM = new THREE.Color(1.03, 0.88, 1.12);

const OUTFIT_HUE_KEY = 'stormsurge_outfit_hue';
const OUTFIT_SWATCH_HUES = [0.02, 0.12, 0.28, 0.52, 0.68, 0.82];
const KILL_CAM_DURATION = 1.1;
const KILL_CAM_SLOW = 0.35;
const SHIELD_REGEN_DELAY = 6;
const SHIELD_REGEN_RATE = 8;
const DEATH_SPECTATE_DURATION = 6;
const DEATH_REPLAY_DURATION = 3;

type VendOption = {
  label: string;
  cost: number;
  hint: string;
};

const VEND_OPTIONS: VendOption[] = [
  { label: 'Shield', cost: 50, hint: '+45 shield' },
  { label: 'AR Ammo', cost: 30, hint: 'refill slot 0' },
  { label: 'Med', cost: 40, hint: '+25 HP' },
  { label: 'Weapon', cost: 75, hint: 'random uncommon+' },
];

const VEND_WEAPON_KINDS: WeaponKind[] = ['ar', 'smg', 'shotgun', 'pistol', 'sniper'];

const WEAPON_SHORT_LABELS: Record<WeaponKind, string> = {
  ar: 'AR',
  smg: 'SMG',
  shotgun: 'SG',
  pistol: 'PI',
  sniper: 'SR',
};

type GameMode = 'solo' | 'duos' | 'trios';

const GAME_MODE_CYCLE: GameMode[] = ['solo', 'duos', 'trios'];

const GAME_MODE_LOBBY: Record<
  GameMode,
  { title: string; toggle: string; players: number }
> = {
  solo: { title: 'Battle Royale · Solo', toggle: 'Mode: Solo', players: 11 },
  duos: { title: 'Battle Royale · Duos', toggle: 'Mode: Duos', players: 12 },
  trios: { title: 'Battle Royale · Trios', toggle: 'Mode: Trios', players: 13 },
};

const RANKED_STORAGE_KEY = 'stormsurge-ranked-v1';
const LOADOUT_STORAGE_KEY = 'stormsurge-loadout-v1';

type LoadoutKind = 'balanced' | 'aggro' | 'sniper';

const LOADOUT_CYCLE: LoadoutKind[] = ['balanced', 'aggro', 'sniper'];

const LOADOUT_SLOT0: Record<LoadoutKind, 'ar' | 'smg' | 'pistol'> = {
  balanced: 'ar',
  aggro: 'smg',
  sniper: 'ar',
};

const LOADOUT_SLOT0_ACCEPTS: Record<LoadoutKind, Array<'ar' | 'smg' | 'pistol'>> = {
  balanced: ['ar'],
  aggro: ['smg', 'pistol'],
  sniper: ['ar'],
};

const LOADOUT_SLOT1_WEAPON: Record<LoadoutKind, 'shotgun' | 'sniper'> = {
  balanced: 'shotgun',
  aggro: 'shotgun',
  sniper: 'sniper',
};

type LtmLobbySelection = 'auto' | FpsLtmId;

const LTM_LOBBY_CYCLE: LtmLobbySelection[] = [
  'auto',
  'blitz',
  'zero_build',
  'explosives',
  'super_siphon',
  'creative',
];

const EMOTE_OPTIONS = [
  { label: 'Dance', toast: '💃 Dance Moves!' },
  { label: 'Wave', toast: '👋 Wave' },
  { label: 'Flex', toast: '💪 Flex' },
  { label: 'Laugh', toast: '😂 Laugh It Up' },
] as const;

const CHAT_WHEEL_OPTIONS = [
  'Need meds',
  'Enemy here',
  'Storm moving',
  'Thanks',
] as const;

const PING_WHEEL_OPTIONS: { type: FpsPingType; label: string }[] = [
  { type: 'danger', label: 'Danger' },
  { type: 'loot', label: 'Loot' },
  { type: 'go', label: 'Go' },
  { type: 'enemy', label: 'Enemy' },
];

type WorldFxParticle = {
  mesh: THREE.Mesh;
  vel: THREE.Vector3;
  life: number;
  maxLife: number;
  billboard?: boolean;
  baseOpacity?: number;
};

export class FPSGame {
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private postFx: PostProcessingPipeline | null = null;
  private world = new CANNON.World({ gravity: new CANNON.Vec3(0, -26, 0) });
  private clock = new THREE.Clock();
  private player!: PlayerController;
  private weapon!: WeaponSystem;
  private built!: ReturnType<typeof buildArena>;
  private getTerrainHeight: (x: number, z: number) => number = () => 0;
  private pivotPlaza!: FpsPivotPlaza;
  private floorMat = new CANNON.Material('floor');
  private objectMat = new CANNON.Material('object');
  private playerMat = new CANNON.Material('player');
  private debrisChunks: Array<{ mesh: THREE.Mesh; vel: THREE.Vector3; life: number }> = [];
  private treeFallTickers: Array<(dt: number) => void> = [];
  private ui: HTMLDivElement;
  private crosshair: HTMLDivElement;
  private crosshairLines: HTMLDivElement[] = [];
  private crosshairDot!: HTMLDivElement;
  private crosshairRing!: HTMLDivElement;
  private crosshairPulse = 0;
  private running = true;
  private disposed = false;
  private pmrem?: THREE.PMREMGenerator;
  private sky!: ReturnType<typeof createStylizedSky>;
  private nightSky!: NightSkyElements;
  private sunLight!: THREE.DirectionalLight;
  private playerShadowBlob!: THREE.Mesh;
  private readonly playerShadowMat = new THREE.MeshBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.36,
    depthWrite: false,
  });
  private storm!: FpsStorm;
  private stormLightning!: FpsStormLightning;
  private stormCircle!: FpsStormCircle;
  private nextZoneRing!: THREE.Mesh;
  private nextZoneRingKey = -1;
  private audio = new FpsAudio();
  private minimap!: FpsMinimap;
  private settings!: FpsSettingsMenu;
  private craftBench!: FpsCraftBench;
  private npcQuest!: FpsNpcQuest;
  private npcQuestHarvest!: FpsNpcQuestHarvest;
  private npcQuestVisit!: FpsNpcQuestVisit;
  private vendors!: FpsNpcVendors;
  private bossFights!: FpsBossFights;
  private vaults!: FpsVaults;
  private heldKeycards = new Set<string>();
  private stormSurge = new FpsStormSurge();
  private matchDirector = new FpsMatchDirector();
  private matchDirectorPressureTimer = 0;
  private squadCallouts!: FpsSquadCallouts;
  private craftPrepaidPiece: BuildPieceKind | null = null;
  private tutorial!: FpsTutorial;
  private tutorialDismissedSession = false;
  private tutorialPointerLockPending = false;
  private stormSirenCyclePhase = 0;
  private stormSirenPlayed = false;
  private stormLateUrgencyPhase = 0;
  private stormMovingAnnounced = false;
  private stormLatePhaseAnnounced = false;
  private stormOutsideAnnounceAt = -Infinity;
  private wasInStorm = false;
  private safeZoneGuidancePulseUntil = 0;
  private buildPlacer!: FpsBuildPlacer;
  private loot!: FpsLoot;
  private battleBus!: FpsBattleBus;
  private hotbar!: FpsHotbar;
  private graphicsState: FpsGraphicsState = createFpsGraphicsState();
  private crosshairSettings: FpsCrosshairSettings = loadCrosshairSettings();
  private lobbyActive = true;
  private introActive = false;
  private lobbyOverlay!: HTMLDivElement;
  private lobbyStatusEl!: HTMLDivElement;
  private lobbyDotsEl!: HTMLSpanElement;
  private lobbyCountEl!: HTMLDivElement;
  private lobbyStartBtn!: HTMLButtonElement;
  private lobbyStartBtnHovered = false;
  private lobbyStartBtnPressed = false;
  private lobbyBpTierEl!: HTMLDivElement;
  private lobbyBpFillEl!: HTMLDivElement;
  private lobbyBpPreviewEl!: HTMLDivElement;
  private lobbyBpGalleryEl!: HTMLDivElement;
  private lobbyRankBadgeEl!: HTMLDivElement;
  private lobbyArenaFillEl!: HTMLDivElement;
  private lobbyArenaTextEl!: HTMLDivElement;
  private lobbyReady = false;
  private lobbyMatchT = 0;
  private gameMode: GameMode = 'solo';
  private rankedMode = false;
  private lobbyModeTitleEl!: HTMLDivElement;
  private lobbyModeToggleEl!: HTMLButtonElement;
  private lobbyRankedToggleEl!: HTMLButtonElement;
  private lobbyRankedBadgeEl!: HTMLDivElement;
  private lobbyLtmBannerEl!: HTMLDivElement;
  private lobbyLtmToggleEl!: HTMLButtonElement;
  private hudRankedChip!: HTMLDivElement;
  private hudLtmChip!: HTMLDivElement;
  private dailyLtm!: FpsLtmInfo;
  private loadoutKind: LoadoutKind = 'balanced';
  private buildEnabled = true;
  private siphonAmount = 25;
  private teammates: FpsTeammate[] = [];
  private reviveTarget: FpsTeammate | null = null;
  private teammateReviveProgress = 0;
  private carriedTeammate: FpsTeammate | null = null;
  private teammateCarryProgress = 0;
  private teammateRebootTarget: FpsTeammate | null = null;
  private teammateRebootProgress = 0;
  private elimCounts = new Map<string, number>();
  private killLeaderName: string | null = null;
  private killLeaderHud!: HTMLDivElement;
  private playerCrownHud!: HTMLDivElement;
  private stormTickAcc = 0;
  private stormAmbientOn = false;
  private gliderTrailAcc = 0;
  private readonly worldFx: WorldFxParticle[] = [];
  private readonly fxGeo = new THREE.SphereGeometry(0.07, 5, 5);
  private readonly fxGlidePlaneGeo = new THREE.PlaneGeometry(0.14, 0.14);
  private readonly fxGlideMat = new THREE.MeshBasicMaterial({
    color: 0x44eeff,
    transparent: true,
    opacity: 0.72,
    depthWrite: false,
  });
  private readonly fxDustMat = new THREE.MeshBasicMaterial({
    color: 0xb8a88a,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
  });
  private readonly fxGoldMat = new THREE.MeshBasicMaterial({
    color: 0xffd700,
    transparent: true,
    opacity: 0.92,
    depthWrite: false,
  });
  private readonly fxScratch = new THREE.Vector3();
  private readonly replayQuat = new THREE.Quaternion();
  private health = 100;
  private slurpBarrelUsed = false;
  private shieldKegUsed = false;
  private mats = 0;
  private winTimer = 0;
  private matchEnd: null | 'won' | 'lost' = null;
  private hudHealth!: HTMLDivElement;
  private hudStorm!: HTMLDivElement;
  private hudStormSurge!: HTMLDivElement;
  private hudMats!: HTMLDivElement;
  private hudGold!: HTMLDivElement;
  private interactHint!: HTMLDivElement;
  private endScreen!: HTMLDivElement;
  private elimFeed!: HTMLDivElement;
  private audioUnlocked = false;
  private xpBar!: FpsXpBar;
  private harvest!: FpsHarvest;
  private pingStack!: FpsWorldPingStack;
  private worldPingMarkers!: FpsWorldPingMarkers;
  private dailyChallenges!: FpsDailyChallenges;
  private hudPhase!: HTMLDivElement;
  private hudTacticalSprint!: HTMLDivElement;
  private hudTactical!: HTMLDivElement;
  private deathOverlay!: HTMLDivElement;
  private emoteToast!: HTMLDivElement;
  private emoteToastTimer = 0;
  private hudHintStrip!: HTMLDivElement;
  private emoteWheel!: HTMLDivElement;
  private emoteWheelSegments: HTMLDivElement[] = [];
  private emoteWheelOpen = false;
  private emoteWheelPick = -1;
  private emoteVDownT = 0;
  private emoteVDownMouse = { x: 0, y: 0 };
  private emoteMouseMoved = false;
  private chatWheel!: HTMLDivElement;
  private chatWheelSegments: HTMLDivElement[] = [];
  private chatWheelOpen = false;
  private chatWheelPick = -1;
  private chatHDownT = 0;
  private chatHDownMouse = { x: 0, y: 0 };
  private chatMouseMoved = false;
  private pingWheel!: HTMLDivElement;
  private pingWheelSegments: HTMLDivElement[] = [];
  private pingWheelOpen = false;
  private pingWheelPick = -1;
  private pingGDownT = 0;
  private pingGDownMouse = { x: 0, y: 0 };
  private pingMouseMoved = false;
  private fishing = new FpsFishing();
  private hudFish!: HTMLDivElement;
  private lastMouse = { x: 0, y: 0 };
  private killCamActive = false;
  private killCamTimer = 0;
  private killCamTarget = new THREE.Vector3();
  private killCamElimName = '';
  private killCamWeapon = '';
  private killCamHeadshot = false;
  private killCamOverlay!: HTMLDivElement;
  private killTracer = new FpsKillTracer();
  private impactSparks!: FpsImpactSparks;
  private particles!: FpsParticleSystem;
  private chestBurst!: FpsChestBurst;
  private lootRarity!: FpsLootRarity;
  private footstepDust!: FpsFootstepDust;
  private buildDustVfx!: FpsBuildDustVfx;
  private bulletCasingVfx!: FpsBulletCasingVfx;
  private shieldCrackVfx!: FpsShieldCrackVfx;
  private static readonly MUZZLE_FLASH_DECAY_S = 0.08;
  private readonly shotMuzzleLights: { light: THREE.PointLight; life: number; peak: number }[] = [];
  private readonly muzzleFlashScratch = new THREE.Vector3();
  private shieldCrackFlash!: HTMLDivElement;
  private shieldCrackFlashOpacity = 0;
  private waterSplash!: FpsWaterSplash;
  private lootToast!: HTMLDivElement;
  private lootToastTimer = 0;
  private pickupCard!: PickupCard;
  private rarityStinger!: RarityStinger;
  private pickupCardTimer = 0;
  private pickupCardDelayTimer = 0;
  private priorityHudQuietTimer = 0;
  private priorityFeedQuietTimer = 0;
  private sniperScope!: HTMLDivElement;
  private sniperScopeReticle!: HTMLDivElement;
  private bandageCd = 0;
  private knocked = false;
  private bleedoutTimer = 0;
  private shieldHud!: FpsShieldHud;
  private staminaHud!: FpsStaminaHud;
  private floorPickups!: FpsFloorPickupField;
  private weaponPickups!: FpsWeaponPickups;
  private poiLabelEl!: HTMLDivElement;
  private poiLabelAge = -1;
  private poiCurrentZone: string | null = null;
  private busJumpChip!: HTMLDivElement;
  private busJumpCount = 99;
  private readonly poiZoneCooldowns = new Map<string, number>();
  private jumpPads!: FpsJumpPadField;
  private redeployLeft = 1;
  private hudReboot!: HTMLDivElement;
  private hudVend!: HTMLDivElement;
  private hudCraftBench!: HTMLDivElement;
  private hudQuestChip!: HTMLDivElement;
  private hudEditHint!: HTMLDivElement;
  private hudBuildHp!: HTMLDivElement;
  private vendOptionIndex = 0;
  private hudAmmo!: HTMLDivElement;
  private weaponInspectOverlay!: HTMLDivElement;
  private weaponInspectOpen = false;
  private hudGlide!: HTMLDivElement;
  private hudDropCue!: HTMLDivElement;
  private hudMantleHint!: HTMLDivElement;
  private hudSwim!: HTMLDivElement;
  private supplyDrop: FpsSupplyDrop | null = null;
  private supplyDropPlane: FpsSupplyDropPlane | null = null;
  private supplySpawned = false;
  private supplyDropLandedAnnounced = false;
  private supplyDropCraterSpawned = false;
  private supplyDropSmokeAcc = 0;
  private llama: FpsLlama | null = null;
  private llamaSpawned = false;
  private bossObjectiveAnnounced = false;
  private vaultObjectiveAnnounced = false;
  private readonly objectiveAnnouncementCooldowns = new Map<string, number>();
  private confettiCanvas!: HTMLCanvasElement;
  private confettiCtx!: CanvasRenderingContext2D | null;
  private confettiTimer = 0;
  private readonly confettiParticles: {
    x: number;
    y: number;
    vx: number;
    vy: number;
    color: string;
    size: number;
    rot: number;
    rotV: number;
    life: number;
  }[] = [];
  private defeatVignetteEl!: HTMLDivElement;
  private defeatVignetteActive = false;
  private adsVignetteEl!: HTMLDivElement;
  private adsVignetteAlpha = 0;
  private lootRarityFlashEl!: HTMLDivElement;
  private lootRarityFlashTimer = 0;
  private bots!: FpsBots;
  private botAI2!: FpsBotAI2;
  private hitMarker!: HTMLDivElement;
  private hitMarkerOpacity = 0;
  private hitMarkerHeadshot = false;
  private killBanner!: HTMLDivElement;
  private killBannerTimer = 0;
  private victoryRoyale!: FpsVictoryRoyale;
  private aliveCounter!: HTMLDivElement;
  private playerEliminations = 0;
  private elimStreak = 0;
  private bestElimStreak = 0;
  private rebootCardCount = 0;
  private botAirstrikeCd = 28;
  private botSupplyContestT = 0;
  private vehicles!: FpsVehicles;
  private ziplines!: FpsZiplines;
  private mythics!: FpsMythicWeapons;
  private healItems!: FpsHealItems;
  private mobilityProps!: FpsMobilityProps;
  private skinLocker!: FpsSkinLocker;
  private emoteWheel12!: FpsEmoteWheel;
  // Batch E — Phases 14–19
  private ltmPicker!: FpsLtmPicker;
  private replaySystem: import('./FpsReplaySystem.js').FpsReplaySystem | null = null;
  private replaySystemLoad: Promise<void> | null = null;
  private lobbyIsland!: FpsLobbyIsland;
  private liveEvent!: FpsLiveEvent;
  private audioMix!: FpsAudioMix;
  private hudVehicle!: HTMLDivElement;
  private vehicleEnterHint!: HTMLDivElement;
  private ziplineHint!: HTMLDivElement;
  /** Storm grace period before shrinking starts. */
  private stormGrace = 14;
  /** Third-person camera toggle (Y). */
  private thirdPerson = false;
  private cameraThirdPersonDist = 3.5;
  private cameraThirdPersonShoulder = 0.55;
  private thirdPersonLeanZ = 0;
  private mantleAssistCooldown = 0;
  private mantleHintCooldown = 0;
  private mantleHintTimer = 0;
  private dayNightTime = 0.25;
  private lightingNightAmount = 0;
  private lightingSunsetAmount = 0;
  private stormMoodAmount = 0;
  private stormMoodPulse = 0;
  private readonly moodFogColor = new THREE.Color();
  private readonly moodGradeColor = new THREE.Color();
  private playerCharacter!: THREE.Group;
  private playerTorsoMat!: THREE.MeshStandardMaterial;
  private readonly playerBaseTorsoEmissive = new THREE.Color();
  private playerCombatRimT = 0;
  private gliderMesh!: THREE.Group;
  private playerAnimator!: FpsCharacterAnimator;
  private lobbyPedestal!: THREE.Group;
  private lobbyPedestalRingMat!: THREE.MeshStandardMaterial;
  private lobbyShowcaseRimLight!: THREE.PointLight;
  private lobbyShowcasePulseT = 0;
  private lobbyShowcaseYaw = 0;
  private lobbyDragging = false;
  private lobbyDragPrevX = 0;
  private selectedOutfitHue = 0.55;
  private victoryOrbitAngle = 0;
  private playerOutfitMats: {
    body: THREE.MeshStandardMaterial;
    shirt: THREE.MeshStandardMaterial;
    legs: THREE.MeshStandardMaterial;
  } | null = null;
  private viewModelGun!: THREE.Group;
  private viewModelBodyMat!: THREE.MeshStandardMaterial;
  private viewModelAccentMat!: THREE.MeshStandardMaterial;
  private viewModelBobPhase = 0;
  private viewModelAdsBlend = 0;
  private viewModelVariants: Partial<Record<WeaponKind, THREE.Group>> = {};
  private viewModelMags: Partial<Record<WeaponKind, THREE.Group>> = {};
  private readonly viewModelGunRest = { x: 0.22, y: -0.18, z: -0.38, rotX: 0 };
  private readonly viewModelGunBaseRotY = Math.PI * 0.02;
  private prevReloadRatio = 0;
  private weaponSwapT = 0;
  private weaponSwapFromKind: WeaponKind | null = null;
  private weaponSwapEquipCuePlayed = false;
  private pickaxeSwingT = 0;
  private pickaxeSwingDur = 0.26;
  private pickaxeMode = false;
  private pickaxeModeSticky = false;
  private pickaxeKeyHeld = false;
  private harvestStreak = 0;
  private harvestStreakIdle = 0;
  private harvestImpactCd = 0;
  private readonly HARVEST_STREAK_BONUS = 5;
  private readonly HARVEST_STREAK_HITS = 5;
  private readonly HARVEST_STREAK_RESET = 2;
  private viewModelPickaxe!: THREE.Group;
  private viewmodelRig!: FpsViewmodelRig;
  /** Bots alive at match start — used so “eliminate all” still wins after corpses are pruned. */
  private initialBotCount = 0;
  private damageDirIndicator!: HTMLDivElement;
  private lastDamageDir: { x: number; z: number; t: number } | null = null;
  private lastDamageTime = 0;
  private lastAttackerName: string | null = null;
  private lastElimSource: 'combat' | 'storm' | 'self' = 'combat';
  private deathRecapVisible = false;
  private spectateActive = false;
  private spectateTimer = 0;
  private spectateBotId: number | null = null;
  private pendingMatchEnd: 'won' | 'lost' | null = null;
  private spectateOverlay!: HTMLDivElement;
  private cameraDetached = false;
  private matchElapsed = 0;
  private statChests = 0;
  private statSupply = 0;
  private statMeters = 0;
  private matchStats = new FpsMatchStats();
  private statMaxPhase = 1;
  private footstepAcc = 0;
  private footstepDustAcc = 0;
  private waterSplashAcc = 0;
  private skydiveWindAcc = 0;
  private prevGlider = false;
  private earlyObjectiveHintShown = false;
  private prevMovementCueSprint = false;
  private prevMovementCueSlide = false;
  private prevMovementCueVault = false;
  private readonly movementCueLastAt = new Map<string, number>();
  private prevPlayerVelY = 0;
  private prevZiplineRiding = false;
  private gliderFovBumpT = 0;
  private endgameFovPulseT = 0;
  private prevSwimming = false;
  private adsHeld = false;
  private grenadeKeyHeld = false;
  private wasAdsSettled = false;
  private reloadMidpointCuePlayed = false;
  private damageFlash!: HTMLDivElement;
  private damageFlashOpacity = 0;
  private hitFeedback!: FpsHitFeedback;
  private suppressionCueCd = 0;
  private speedLinesOverlay!: HTMLDivElement;
  private speedLinesOpacity = 0;
  private speedLinesStyle!: HTMLStyleElement;
  private lastStormPhaseNotified = 1;
  private miniShieldCount = 0;
  private bandageInventoryCount = 0;
  private medKitCount = 0;
  private consumableUseCd = 0;
  private healFeedbackCd = 0;
  private stormPhaseBanner!: HTMLDivElement;
  private stormPhaseBannerTimer = 0;
  private stormPhaseBannerStyle!: HTMLStyleElement;
  private hudAnimStyle!: HTMLStyleElement;
  private hudRoot!: HTMLDivElement;
  private placementBanner!: HTMLDivElement;
  private placementBannerTimer = 0;
  private placementTop10Shown = false;
  private placementTop5Shown = false;
  private placementTop3Shown = false;
  private finalDuelShown = false;
  private lastOpponentCueShown = false;
  private finalCircleAnnounced = false;
  private finalCircleBotCapApplied = false;
  private matchDirectorHotZoneAnnounced = false;
  private buildMouseHeld = false;
  private gunMouseHeld = false;
  private buildPlaceCd = 0;
  private hudBuildCost!: HTMLDivElement;
  private buildBlockedFeedAt = 0;
  private enemyBuildLowHpFeedAt = 0;
  private readonly BUILD_PLACE_CD = 0.34;
  private readonly BUILD_TURBO_CD_BASE = 0.085;
  private buildTurboCd = 0.085;
  private fpsMaterials!: FpsMaterials;
  private buildEditMode!: FpsBuildEditMode;
  private compassBar!: HTMLDivElement;
  private compassTrack!: HTMLDivElement;
  private compassMarkerEls: HTMLDivElement[] = [];
  private compassSupplyDropEl!: HTMLDivElement;
  private compassDropMarkerEl!: HTMLDivElement;
  private compassSafeZoneEl!: HTMLDivElement;
  private compassPoiLabelEl!: HTMLDivElement;
  private dropMarker: { x: number; z: number } | null = null;
  private dropMarkerVisibleUntilLand = false;
  private fpsSampleAcc = 0;
  private fpsSampleFrames = 0;
  private fpsSampleTimer = 0;
  private fpsLowStreak = 0;
  private perfAutoEnabled = false;
  private hudStormGrace!: HTMLDivElement;
  private hudStormGraceRing!: HTMLDivElement;
  private stormGraceFade = 0;
  private damageNumbers!: FpsDamageNumbers;
  private grenades!: FpsGrenades;
  private airstrike!: FpsAirstrike;
  private chugSplash!: FpsChugSplash;
  private shockwave!: FpsShockwave;
  private sniperGlint!: FpsSniperGlint;
  private supplyContestHud!: HTMLDivElement;
  private teammateKnockPinged: boolean[] = [];
  private slideVfx!: FpsSlideVfx;
  private knockBubble!: FpsKnockBubble;
  private seasonFx!: FpsSeasonFx;
  private ambientLife!: FpsAmbientLife;
  private readonly ambientDecorProps: THREE.Object3D[] = [];
  private ambientDecorAcc = 0;
  private buildTurboVfx!: FpsBuildTurboVfx;
  private audioVisualizer!: FpsAudioVisualizer;
  private network!: FpsNetwork;
  private culler = new FpsLODCuller();
  private readonly scheduledTimeouts = new Set<number>();
  private readonly lootEffectHandles = new Set<LootEffectHandle>();
  private readonly domEffectHandles = new Set<DomEffectHandle>();
  private perfOverlay!: FpsPerfOverlay;
  private onPerfKeyDown = (ev: KeyboardEvent): void => {
    if (ev.code !== 'F9' || ev.repeat) return;
    this.perfOverlay.toggle();
    ev.preventDefault();
  };
  private readonly onContextMenu = (ev: MouseEvent): void => ev.preventDefault();
  private readonly onLobbyPointerDown = (ev: PointerEvent): void => {
    if (!this.lobbyActive) return;
    if (ev.target instanceof HTMLButtonElement || ev.target instanceof HTMLInputElement || ev.target instanceof HTMLSelectElement) {
      return;
    }
    this.lobbyDragging = true;
    this.lobbyDragPrevX = ev.clientX;
  };
  private readonly onLobbyPointerMove = (ev: PointerEvent): void => {
    if (!this.lobbyActive || !this.lobbyDragging) return;
    const deltaX = ev.clientX - this.lobbyDragPrevX;
    this.lobbyDragPrevX = ev.clientX;
    this.lobbyShowcaseYaw -= deltaX * 0.0075;
  };
  private readonly onLobbyPointerEnd = (): void => {
    this.lobbyDragging = false;
  };
  private stormWarnBorder!: HTMLDivElement;
  private stormWarnFlash = 0;
  private tacticalCountEls!: {
    grenades: HTMLSpanElement;
    stink: HTMLSpanElement;
    mini: HTMLSpanElement;
    bandage: HTMLSpanElement;
    med: HTMLSpanElement;
    airstrike: HTMLSpanElement;
    chug: HTMLSpanElement;
    reboot: HTMLSpanElement;
    shock: HTMLSpanElement;
  };
  private hudKnocked!: HTMLDivElement;
  private hudSquad!: HTMLDivElement;
  private hudRevive!: HTMLDivElement;
  private hudCarry!: HTMLDivElement;
  private hudAutoRun!: HTMLDivElement;
  private touchControls!: FpsTouchControls;
  /** When set, tick uses this fixed dt (for deterministic test stepping). */
  private forcedDt: number | null = null;
  private hitStopTimer = 0;
  private readonly skydivers: {
    x: number;
    z: number;
    targetX: number;
    targetZ: number;
    speed: number;
  }[] = [];

  constructor(private mount: HTMLElement) {
    this.rankedMode = this.loadRankedMode();
    this.world.allowSleep = true;

    const w = mount.clientWidth || window.innerWidth;
    const h = mount.clientHeight || window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(BASE_FOV, w / h, 0.05, 320);
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
    });
    this.renderer.setPixelRatio(this.getRenderPixelRatio(w, h));
    this.renderer.setSize(w, h);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // Keep midtones readable outdoors; avoid near-white wash under headless/high bloom
    this.renderer.toneMappingExposure = 1.08;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(this.renderer.domElement);
    this.perfOverlay = new FpsPerfOverlay(document.body);
    window.addEventListener('keydown', this.onPerfKeyDown);

    // Softer horizon haze — farther near plane so terrain/POIs stay saturated and readable
    this.scene.fog = new THREE.Fog(0x7eb6e0, 110, 320);

    this.pmrem = new THREE.PMREMGenerator(this.renderer);
    this.pmrem.compileEquirectangularShader();
    const envScene = new RoomEnvironment();
    const rt = this.pmrem.fromScene(envScene, 0.04);
    this.scene.environment = rt.texture;
    envScene.dispose();

    const hemi = new THREE.HemisphereLight(0xc8e8ff, 0x3a4a38, 0.72);
    this.scene.add(hemi);
    this.sunLight = new THREE.DirectionalLight(0xfff0dc, 1.45);
    this.sunLight.position.set(-52, 78, 42);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.set(4096, 4096);
    this.sunLight.shadow.camera.near = 2;
    this.sunLight.shadow.camera.far = 240;
    this.sunLight.shadow.camera.left = -95;
    this.sunLight.shadow.camera.right = 95;
    this.sunLight.shadow.camera.top = 95;
    this.sunLight.shadow.camera.bottom = -95;
    this.sunLight.shadow.radius = 2.6;
    this.sunLight.shadow.normalBias = 0.024;
    this.sunLight.shadow.bias = -0.00018;
    this.scene.add(this.sunLight);

    const rim = new THREE.DirectionalLight(0xb8d4ff, 0.72);
    rim.position.set(55, 28, -60);
    this.scene.add(rim);
    // Warm bounce fill for richer midtones on characters/structures
    const bounce = new THREE.DirectionalLight(0xffd9a8, 0.28);
    bounce.position.set(12, 18, 40);
    this.scene.add(bounce);

    this.sky = createStylizedSky(this.scene, this.sunLight.position);
    this.nightSky = createNightSkyElements(this.scene);

    const fog = this.scene.fog as THREE.Fog;
    this.storm = new FpsStorm(this.scene, {
      fog,
      initialRadius: STORM_INIT_R,
      shrinkPerSecond: STORM_SHRINK,
      minRadius: STORM_MIN_R,
      damagePerSecond: 11,
      center: new THREE.Vector3(0, 0, 0),
    });
    this.stormLightning = new FpsStormLightning(this.scene, this.audio);
    this.stormCircle = new FpsStormCircle(this.scene, document.body);

    this.nextZoneRing = new THREE.Mesh(
      new THREE.RingGeometry(0.9, 1, 96),
      new THREE.MeshBasicMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.22,
        side: THREE.DoubleSide,
        depthWrite: false,
      }),
    );
    this.nextZoneRing.rotation.x = -Math.PI / 2;
    this.nextZoneRing.position.y = 0.06;
    this.nextZoneRing.visible = false;
    this.nextZoneRing.renderOrder = 0;
    this.scene.add(this.nextZoneRing);

    this.world.defaultContactMaterial.friction = 0.44;
    this.world.defaultContactMaterial.restitution = 0.04;
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.playerMat, this.floorMat, { friction: 0.075, restitution: 0 }),
    );
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.playerMat, this.objectMat, { friction: 0.38, restitution: 0.05 }),
    );
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.objectMat, this.objectMat, { friction: 0.38, restitution: 0.14 }),
    );
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.objectMat, this.floorMat, { friction: 0.58, restitution: 0.08 }),
    );

    this.built = buildArena(this.scene, this.world, this.floorMat, this.objectMat);
    this.getTerrainHeight = this.built.getTerrainHeight ?? (() => 0);

    this.pivotPlaza = new FpsPivotPlaza(
      this.scene, this.world, this.floorMat, this.objectMat, -55, -55,
    );
    this.built.sceneRoot.add(this.pivotPlaza.group);

    // Register large numbers of decos into the culler
    if (this.built.decoGroup) {
      for (const child of this.built.decoGroup.children) {
        this.culler.add(child, typeof child.userData.cullDistance === 'number' ? child.userData.cullDistance : 120);
      }
    }
    this.culler.collectDecorative(this.built.sceneRoot, 120);
    this.collectAmbientDecorProps();

    this.npcQuest = new FpsNpcQuest(
      this.built.sceneRoot,
      document.body,
      () => this.pushElimFeed('Quest accepted: Eliminate 2 opponents', 'loot'),
      () => this.completeNpcQuest(),
    );
    this.npcQuestHarvest = new FpsNpcQuestHarvest(
      this.built.sceneRoot,
      document.body,
      () => this.pushElimFeed('Quest accepted: Harvest 120 materials', 'loot'),
      () => this.completeNpcQuestHarvest(),
    );
    this.npcQuestVisit = new FpsNpcQuestVisit(
      this.built.sceneRoot,
      document.body,
      () => this.pushElimFeed('Quest accepted: Visit 2 POIs', 'loot'),
      () => this.completeNpcQuestVisit(),
    );

    this.player = new PlayerController(
      this.world,
      this.camera,
      new THREE.Vector3(0, 0, 26),
      this.playerMat,
    );
    this.player.setFootstepRaycastRoot(this.built.sceneRoot);
    this.scene.add(this.player.yawObject);
    this.player.camera.layers.enable(0);
    this.player.camera.layers.enable(1);

    this.weapon = new WeaponSystem(this.camera, this.scene, [this.built.sceneRoot], 52);
    this.weapon.attachMuzzle(this.player.camera);
    this.slideVfx = new FpsSlideVfx(this.scene);
    this.impactSparks = new FpsImpactSparks(this.scene);
    this.particles = new FpsParticleSystem(this.scene, 3200);
    this.weapon.setParticleSystem(this.particles);
    this.chestBurst = new FpsChestBurst(this.scene);
    this.lootRarity = new FpsLootRarity(this.scene, document.body);
    this.footstepDust = new FpsFootstepDust(this.scene);
    this.buildDustVfx = new FpsBuildDustVfx(this.scene);
    this.bulletCasingVfx = new FpsBulletCasingVfx(this.scene);
    this.shieldCrackVfx = new FpsShieldCrackVfx(this.scene);

    this.shieldCrackFlash = document.createElement('div');
    this.shieldCrackFlash.style.cssText =
      'position:fixed;inset:0;z-index:34;pointer-events:none;background:radial-gradient(ellipse at center, transparent 32%, rgba(0,180,255,0.66) 66%, rgba(126,249,255,0.28)), radial-gradient(circle at center, rgba(255,255,255,0.18), transparent 26%), rgba(46,230,255,0.2);opacity:0';
    document.body.appendChild(this.shieldCrackFlash);
    this.waterSplash = new FpsWaterSplash(this.scene);
    this.knockBubble = new FpsKnockBubble(this.scene);
    this.seasonFx = new FpsSeasonFx(this.scene);
    this.ambientLife = new FpsAmbientLife(this.scene);
    this.buildTurboVfx = new FpsBuildTurboVfx(this.scene);
    this.audioVisualizer = new FpsAudioVisualizer(document.body);
    this.network = new FpsNetwork(this.scene, this.player);
    this.network.connect();
    this.buildViewModel();
    this.viewmodelRig = new FpsViewmodelRig(this.camera);
    this.camera.add(this.viewmodelRig.group);

    this.bots = new FpsBots(this.scene, this.built.sceneRoot, [
      { x: -32, z: 22 },
      { x: 36, z: 18 },
      { x: -20, z: -26 },
      { x: 5, z: -36 },
      { x: -26, z: -10 },
      { x: 18, z: 26 },
      { x: -10, z: 30 },
      { x: 30, z: -10 },
      { x: -38, z: 0 },
      { x: 0, z: 35 },
    ], {
      onBotElim: (killer, victim, elimPos, weaponKind) => {
        this.recordElim(killer, victim);
        const line = killer ? randomBotElimFlavor(victim, killer) : randomBotElimFlavor(victim);
        this.pushElimFeed(line, 'elim');
        this.pushKillFeedEntry(
          killer || 'Storm',
          victim,
          weaponKind ? this.weaponDisplayName(weaponKind) : 'Rifle',
          elimPos,
          undefined,
          weaponKind ? weaponKindShortLabel(weaponKind) : 'AR',
        );
      },
      onBotChatter: (botName, quip) => {
        this.pushElimFeed(`${botName}: ${quip}`);
      },
      onBotSquadCallout: (botName, quip) => {
        this.pushSquadCallout(botName, quip);
      },
      onBuildDamage: (object, amount) => {
        if (!this.buildPlacer) return;
        const buildHit = this.buildPlacer.damageBuildFromHit(object, amount);
        if (buildHit.hit) {
          this.audio.playHit();
          if (buildHit.destroyed) {
            this.audio.playBuildBreak();
          }
        } else {
          const destroyed = this.bots.damageBotWall(object, amount);
          if (destroyed) {
            this.audio.playBuildBreak();
          } else {
            this.audio.playHit();
            this.pushEnemyBuildLowHpHint(object);
          }
        }
      },
      hitChanceBonus: this.rankedMode ? 0.06 : 0,
    }, this.world, this.floorMat);
    this.initialBotCount = this.bots.aliveCount();

    this.botAI2 = new FpsBotAI2(this.bots, this.scene, {
      difficulty: this.rankedMode ? 'elite' : 'standard',
    });
    this.botAI2.setDifficultyTier(this.graphicsState.botDifficulty);
    this.applyBotDifficulty();
    this.botAI2.initialize(this.bots.aliveCount());

    this.vehicles = new FpsVehicles(
      this.scene,
      this.built.sceneRoot,
      this.world,
      this.floorMat,
      [
        { x: 18, z: -22, kind: 'quad', hue: 0.05 },
        { x: -28, z: 8, kind: 'truck', hue: 0.6 },
        { x: 28, z: 30, kind: 'quad', hue: 0.4 },
      ],
    );

    this.ziplines = new FpsZiplines(this.built.sceneRoot, [
      { ax: 2, az: -38, bx: -35, bz: 22, height: 32 },
      { ax: 40, az: 15, bx: 28, bz: -22, height: 30 },
      { ax: -22, az: -28, bx: 32, bz: -32, height: 28 },
    ]);

    this.mythics = new FpsMythicWeapons(this.scene, document.body);
    this.healItems = new FpsHealItems(this.scene, document.body);
    this.mobilityProps = new FpsMobilityProps(this.scene);
    // Phase 13 — 12-slot emote wheel (hold B)
    this.emoteWheel12 = new FpsEmoteWheel(document.body);

    // Phase 14 — LTM Picker (full-screen mode selector)
    this.ltmPicker = new FpsLtmPicker(document.body, (id) => {
      setLtmOverride(id);
      this.applyLobbyLtmUi();
      this.ltmPicker.hide();
    });

    // Phase 15 — Replay System (lazy-loaded)
    void this.ensureReplaySystem();

    // Phase 16 — Lobby Island
    this.lobbyIsland = new FpsLobbyIsland(this.scene);

    // Phase 18 — Live Event
    this.liveEvent = new FpsLiveEvent(document.body);

    // Phase 19 — Audio Mix
    this.audioMix = new FpsAudioMix();

    this.weapon.setExtraHitTargetsProvider(() => this.bots.getHitMeshes());
    this.weapon.setShotHitCallback(({ point, object, damage, dir }) => {
      const spawnImpactSparks = (): void => {
        const normal = dir.clone().negate().normalize();
        this.impactSparks.spawn(point.x, point.y, point.z, normal);
        this.particles.emit({
          count: 8,
          position: point.clone(),
          velocityRange: {
            min: new THREE.Vector3(-3, 0, -3),
            max: new THREE.Vector3(3, 4, 3),
          },
          colorA: new THREE.Color(1, 0.85, 0.3),
          colorB: new THREE.Color(0.4, 0.2, 0),
          lifetime: 0.35,
          gravity: 6,
          sizeStart: 0.06,
          sizeEnd: 0.0,
          critical: true,
        });
      };
      if (this.bots.isBotBuildObject(object)) {
        const destroyed = this.bots.damageBotBuildFromHit(object, damage);
        this.matchStats.recordHit();
        this.matchStats.recordDamage(damage);
        this.dailyChallenges.recordDamage(damage);
        this.audio.triggerCombatFocus(0.55);
        this.audio.playHit();
        spawnImpactSparks();
        if (destroyed) {
          this.audio.playBuildBreak();
          this.pushBotFeed('Bot wall destroyed');
          for (let i = 0; i < 4; i++) {
            this.scheduleTimeout(spawnImpactSparks, i * 40);
          }
        } else {
          this.pushEnemyBuildLowHpHint(object);
        }
        return;
      }
      const buildHit = this.buildPlacer.damageBuildFromHit(object, damage);
      if (buildHit.hit) {
        this.matchStats.recordHit();
        this.matchStats.recordDamage(damage);
        this.dailyChallenges.recordDamage(damage);
        this.audio.triggerCombatFocus(0.55);
        this.audio.playHit();
        spawnImpactSparks();
        if (buildHit.destroyed) {
          this.audio.playBuildBreak();
          // Spawn extra sparks for destruction
          for (let i = 0; i < 4; i++) {
            this.scheduleTimeout(spawnImpactSparks, i * 40);
          }
        }
        return;
      }
      const res = this.bots.damageBotByHit(object, point, damage);
      if (!res) {
        // Check if we hit a destructible world object
        if (object instanceof THREE.Mesh && object.userData['destructible']) {
          this.damageWorldBuilding(object, damage, point);
        }
        spawnImpactSparks();
        return;
      }
      this.botAI2.onBotDamagedByName(res.name);
      this.matchStats.recordHit();
      this.matchStats.recordDamage(res.damage);
      this.dailyChallenges.recordDamage(res.damage);
      const feedbackKind = this.resolveHitFeedbackKind(res);
      this.showHitMarker(res.headshot, res.headshot ? 1 : feedbackKind === 'crit' ? 0.92 : 0.85);
      this.hitFeedback.showHit(feedbackKind);
      this.hitFeedback.showDamage(
        res.damage,
        feedbackKind === 'elimination' ? 'body' : feedbackKind,
      );
      this.applyHitConfirmImpulse(feedbackKind);
      // Phase 32X-3: headshot audio + focus amp
      this.audio.triggerCombatFocus(res.headshot ? 1.35 : res.killed ? 1.15 : 0.9);
      this.audio.playHitTier(feedbackKind === 'elimination' ? 'headshot' : feedbackKind);
      this.damageNumbers.spawn(point, res.damage, this.camera, res.headshot, feedbackKind === 'shield');

      // Phase 31X-16 / 32X: hitstop scales with damage (shipped pure rule)
      const hitStopDur = hitStopFromDamage(res.damage, {
        headshot: res.headshot,
        killed: res.killed,
      });
      if (hitStopDur > 0) {
        this.triggerHitStop(hitStopDur * (res.headshot ? 1.15 : 1));
      }

      if (res.killed) {
        this.onPlayerElimination({ headshot: res.headshot, victim: res.name });
        if (this.xpBar.addXp(res.headshot ? 60 : 45)) {
          this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
        }
        this.reportPlayerElim(res.name, res.pos, res.headshot ? ' · HEADSHOT' : undefined);
        this.tryTriggerKillCam(res.pos, {
          name: res.name,
          weapon: this.weapon.getDisplayName(),
          headshot: res.headshot,
        });
        this.trySpawnElimWeaponDrop(res.pos);
        this.mats += 30;
        this.hudMats.textContent = `Mats ${this.mats}`;
      } else if (res.knocked) {
        this.pushElimFeed(`Knocked ${res.name}`, 'knock');
        this.showHitMarker(false, 0.65);
      } else if (res.headshot) {
        this.pushElimFeed(`Headshot ${res.name} (-${Math.round(res.damage)})`, 'knock');
      }
    });

    void this.initPostProcessing(w, h);

    this.battleBus = new FpsBattleBus(this.scene);
    this.buildPlacer = new FpsBuildPlacer(
      this.scene,
      this.world,
      this.floorMat,
      this.built.sceneRoot,
      this.camera,
    );
    this.buildEditMode = new FpsBuildEditMode(this.scene, this.buildPlacer);
    this.grenades = new FpsGrenades(this.built.sceneRoot, this.world);
    this.airstrike = new FpsAirstrike(this.built.sceneRoot);
    this.chugSplash = new FpsChugSplash(this.built.sceneRoot, this.world);
    this.shockwave = new FpsShockwave(this.built.sceneRoot, this.world);
    this.sniperGlint = new FpsSniperGlint(this.scene);
    this.loot = new FpsLoot(this.scene, this.world, this.built.sceneRoot, [
      { x: -28, z: 15 },
      { x: 22, z: -22 },
      { x: -38, z: -38 },
      { x: 35, z: 28 },
      { x: 5, z: 8 },
      { x: 0, z: -8 },
      { x: -32, z: 20 },
      // Pivot Plaza chests
      { x: -55 + -9 - 3, z: -55 + -9 - 3 },
      { x: -55 + -9 + 3, z: -55 + -9 + 3 },
      { x: -55 + 10,     z: -55 + -8 + 2 },
      { x: -55 + -10 - 4, z: -55 + 10 },
      { x: -55 + 11,     z: -55 + 11 },
    ]);

    this.hudRoot = document.createElement('div');
    this.hudRoot.id = 'fps-hud-root';
    this.hudRoot.style.cssText = [
      'position:fixed',
      'inset:0',
      'padding-top:env(safe-area-inset-top,0px)',
      'padding-right:env(safe-area-inset-right,0px)',
      'padding-bottom:env(safe-area-inset-bottom,0px)',
      'padding-left:env(safe-area-inset-left,0px)',
      'pointer-events:none',
      'z-index:20',
      'transform-origin:center center',
      'font-family:var(--fps-font,system-ui,sans-serif)',
    ].join(';');
    document.body.appendChild(this.hudRoot);

    this.minimap = new FpsMinimap(this.hudRoot, {
      worldHalfExtent: 50,
      pois: POI_LOOT_ZONES.map((z) => ({ name: z.name, x: z.x, z: z.z })),
    });
    this.minimap.setOnMapClick((x, z) => this.setDropMarker(x, z));

    this.settings = new FpsSettingsMenu(
      document.body,
      () => this.graphicsState,
      (p) => this.applyGraphics(p),
      () => this.crosshairSettings,
      (p) => this.applyCrosshair(p),
    );
    this.squadCallouts = new FpsSquadCallouts((speaker, message) => this.pushSquadCallout(speaker, message));
    this.craftBench = new FpsCraftBench(
      document.body,
      () => this.mats,
      (id) => this.tryCraftRecipe(id),
      this.scene,
      () => ({
        slot0Kind: this.hotbar.getSlot0Weapon(),
        slot0Rarity: this.hotbar.getSlotRarity(0),
        shotgunRarity: this.hotbar.getSlotRarity(1),
        sniperRarity: this.hotbar.getSlotRarity(2),
      }),
    );

    this.vendors = new FpsNpcVendors(
      this.scene,
      document.body,
      () => this.mats,
      (n: number) => { this.mats = Math.max(0, this.mats - n); },
      {
        onBountyPing: (x, z) => {
          this.pingStack.pushPing(x, z, 4200);
          this.worldPingMarkers.spawnAt(new THREE.Vector3(x, 1.4, z), {
            type: 'danger',
            label: 'BOUNTY',
          });
        },
      },
    );
    this.bossFights = new FpsBossFights(this.scene, document.body);
    this.vaults = new FpsVaults(this.scene, document.body);
    this.vaults.setOnLootCollect((item: VaultLootItem) => {
      this.pushElimFeed(`Vault loot: Legendary ${item.weaponKind}!`, 'loot');
    });
    this.tutorial = new FpsTutorial(document.body, () => {
      this.tutorialDismissedSession = true;
    });
    this.audio.setMasterVolume(this.graphicsState.masterVolume);
    this.applyGraphics(this.graphicsState);

    this.xpBar = new FpsXpBar(this.hudRoot);
    this.harvest = new FpsHarvest(
      this.camera,
      [this.built.sceneRoot],
      () => this.built.syncables,
      this.world,
      (origin, dir, comboHit) => this.bots.damageBotInMelee(origin, dir, comboHit),
    );
    this.pingStack = new FpsWorldPingStack(document.body, 50);
    this.worldPingMarkers = new FpsWorldPingMarkers(this.scene, document.body);
    this.dailyChallenges = new FpsDailyChallenges(
      this.hudRoot,
      (ev) => {
        if (this.xpBar.addXp(ev.xp)) {
          this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
        }
        this.showPickupToast(`Challenge complete · +${ev.xp} XP`, '#7dffb0');
        this.pushElimFeed(`Daily: ${ev.title}`, 'loot');
      },
      (ev) => {
        this.grantBattlePassXp(ev.xp);
        this.showPickupToast(`Weekly complete · +${ev.xp} BP XP`, '#b8a0ff');
        this.pushElimFeed(`Weekly: ${ev.title}`, 'loot');
      },
    );

    this.hotbar = new FpsHotbar(this.hudRoot);
    this.hotbar.setSelected(0);
    this.hotbar.applyWeaponRarityBorders();
    this.weapon.setWeaponKind('ar');
    this.syncViewModelWeapon();

    this.shieldHud = new FpsShieldHud(this.hudRoot);
    this.shieldHud.setHealth(this.health);
    this.staminaHud = new FpsStaminaHud(this.hudRoot);
    this.weaponPickups = new FpsWeaponPickups(this.scene, this.built.sceneRoot);

    this.poiLabelEl = document.createElement('div');
    this.poiLabelEl.style.cssText =
      'position:fixed;top:11%;left:50%;transform:translate(-50%,0);z-index:42;padding:14px 34px;border-radius:14px;' +
      'font:900 clamp(30px,5.8vw,54px) "Trebuchet MS","Segoe UI",system-ui,sans-serif;letter-spacing:0.12em;' +
      'color:#fff;text-shadow:0 4px 20px rgba(0,0,0,0.9);pointer-events:none;display:none;white-space:nowrap';
    this.hudRoot.appendChild(this.poiLabelEl);

    this.busJumpChip = document.createElement('div');
    this.busJumpChip.style.cssText =
      'position:fixed;top:18px;left:50%;transform:translateX(-50%);z-index:44;display:none;pointer-events:none;' +
      'padding:8px 18px;border-radius:999px;background:rgba(8,14,28,0.82);border:2px solid rgba(120,190,255,0.65);' +
      'font:800 clamp(13px,2.2vw,16px) "Trebuchet MS","Segoe UI",system-ui,sans-serif;letter-spacing:0.06em;' +
      'color:#e8f4ff;text-shadow:0 2px 10px rgba(0,0,0,0.75),0 0 16px rgba(100,180,255,0.35);white-space:nowrap';
    this.hudRoot.appendChild(this.busJumpChip);

    this.stormPhaseBannerStyle = document.createElement('style');
    this.stormPhaseBannerStyle.textContent = `@keyframes stormPhaseBannerIn {
  0% { opacity: 0; transform: translate(-50%, -50%) scale(0.55); }
  12% { opacity: 1; transform: translate(-50%, -50%) scale(1.08); }
  22% { transform: translate(-50%, -50%) scale(1); }
  78% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  100% { opacity: 0; transform: translate(-50%, -50%) scale(0.92); }
}
@media (prefers-reduced-motion: reduce) {
  .fps-storm-phase-banner,.fps-placement-banner,.fps-kill-leader-hud { animation: none !important; }
}
body[data-fps-reduced-motion="true"] .fps-storm-phase-banner,
body[data-fps-reduced-motion="true"] .fps-placement-banner,
body[data-fps-reduced-motion="true"] .fps-kill-leader-hud { animation:none!important; transition:none!important;
}`;
    document.head.appendChild(this.stormPhaseBannerStyle);

    this.hudAnimStyle = document.createElement('style');
    this.hudAnimStyle.textContent = `@keyframes fps-elim-slide-in {
      from { opacity: 0; transform: translate3d(18px,-2px,0) scale(0.98); }
      to { opacity: 1; transform: translate3d(0,0,0) scale(1); }
    }
    @keyframes fps-toast-card-in {
      from { opacity: 0; filter:brightness(1.16); }
      to { opacity: 1; filter:brightness(1); }
    }
    .fps-hud-priority-muted { opacity:.28!important;filter:saturate(.62)!important; }
    .fps-elim-feed {
      top:clamp(88px,14vh,126px)!important;right:clamp(8px,2vw,18px)!important;width:clamp(192px,25vw,292px)!important;
      gap:clamp(4px,0.8vw,7px)!important;
    }
    .fps-elim-feed-entry {
      position:relative;overflow:hidden;animation:fps-elim-slide-in 0.28s ease-out;
      backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);
      text-shadow:0 1px 4px rgba(0,0,0,0.82),0 0 10px rgba(0,0,0,0.42);
      border-radius:var(--fps-radius-sm,10px);
      font-family:var(--fps-font,system-ui,sans-serif);
    }
    .fps-elim-feed-entry::after {
      content:"";position:absolute;inset:0;pointer-events:none;
      background:linear-gradient(110deg,rgba(255,255,255,0.16),rgba(255,255,255,0.03) 36%,transparent 62%);
      opacity:0.45;
    }
    .fps-elim-feed-entry[data-kind="elim"] { text-transform:uppercase; }
    .fps-hud-toast-card {
      max-width:min(72vw,560px);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);
      background:linear-gradient(180deg,rgba(20,28,48,0.78),rgba(7,10,20,0.86))!important;
      border:1px solid rgba(255,255,255,0.18)!important;border-left:4px solid var(--fps-toast-rail,#2ee6ff)!important;
      box-shadow:0 10px 34px rgba(0,0,0,0.42),0 0 20px var(--fps-toast-glow,rgba(46,230,255,0.2)),inset 0 1px 0 rgba(255,255,255,0.1)!important;
      animation:fps-toast-card-in 0.22s ease-out;text-shadow:0 2px 8px rgba(0,0,0,0.78),0 0 10px rgba(0,0,0,0.35);
    }
    .fps-key-hint-strip {
      position:fixed;right:clamp(10px,2.2vw,22px);bottom:clamp(10px,2.4vh,20px);z-index:24;display:flex;align-items:center;gap:clamp(5px,0.9vw,9px);
      max-width:min(78vw,760px);padding:7px 10px;border-radius:999px;pointer-events:none;
      background:linear-gradient(180deg,rgba(12,18,34,0.76),rgba(6,10,20,0.86));border:1px solid rgba(126,249,255,0.24);
      box-shadow:0 8px 24px rgba(0,0,0,0.36),inset 0 1px 0 rgba(255,255,255,0.08);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);
      color:#e8f4ff;font:800 clamp(9px,1.2vw,11px)/1.1 "Trebuchet MS","Segoe UI",system-ui,sans-serif;letter-spacing:0.06em;text-shadow:0 1px 4px rgba(0,0,0,0.85);
      transition:opacity 0.22s ease,filter 0.22s ease;
    }
    .fps-key-hint-strip span { display:inline-flex;align-items:center;gap:4px;white-space:nowrap; }
    .fps-key-hint-strip kbd { min-width:1.45em;padding:2px 5px;border-radius:5px;background:rgba(255,255,255,0.12);border:1px solid rgba(255,255,255,0.22);color:#fff;font:900 10px/1 system-ui,sans-serif;box-shadow:inset 0 -1px 0 rgba(0,0,0,0.35); }
    .fps-health-hud,.fps-ltm-chip,.fps-quest-chip,.fps-mats-hud,.fps-alive-counter,.fps-compass-bar,.fps-storm-grace,.fps-hud-auto-run,.fps-tactical-sprint,.fps-ammo-hud,.fps-tactical-row,.fps-squad-hud { box-sizing:border-box; }
    #fps-hud-root { transform-origin:center center; }
    .fps-health-hud,.fps-ltm-chip,.fps-quest-chip,.fps-mats-hud,.fps-daily-challenges-panel { transform-origin:top left; }
    .fps-alive-counter,.fps-tactical-sprint,.fps-hud-auto-run,.fps-elim-feed { transform-origin:top right; }
    .fps-ammo-hud { transform-origin:bottom right; }
    .fps-tactical-row,.fps-squad-hud,.fps-vehicle-hud { transform-origin:bottom left; }
    .fps-compass-bar,.fps-storm-grace,.fps-storm-hud,.fps-phase-hud,.fps-key-hint-strip { transform-origin:center; }
    @media (max-width: 680px) {
      .fps-elim-feed { top:clamp(76px,13vh,96px)!important;width:clamp(168px,38vw,214px)!important; }
      .fps-elim-feed-entry { font-size:11px!important;padding:6px 9px!important; }
      .fps-elim-feed-entry-normal { display:none!important; }
      .fps-key-hint-strip { left:50%;right:auto;bottom:8px;transform:translateX(-50%);max-width:94vw;padding:6px 8px; }
      .fps-key-hint-optional { display:none!important; }
    }
    @media (max-width: 720px), (max-height: 520px) {
      .fps-health-hud { top:calc(env(safe-area-inset-top,0px) + 8px)!important;left:calc(env(safe-area-inset-left,0px) + 8px)!important;min-width:132px!important;padding:7px 9px!important;font-size:11px!important; }
      .fps-ltm-chip { top:calc(env(safe-area-inset-top,0px) + 40px)!important;left:calc(env(safe-area-inset-left,0px) + 8px)!important;padding:3px 7px!important;font-size:9px!important;max-width:136px!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important; }
      .fps-quest-chip { top:calc(env(safe-area-inset-top,0px) + 66px)!important;left:calc(env(safe-area-inset-left,0px) + 8px)!important;padding:3px 7px!important;font-size:9px!important;max-width:136px!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important; }
      .fps-mats-hud { top:calc(env(safe-area-inset-top,0px) + 8px)!important;left:calc(env(safe-area-inset-left,0px) + 148px)!important;padding:6px 8px!important;font-size:10px!important; }
      .fps-daily-challenges-panel { top:calc(env(safe-area-inset-top,0px) + 94px)!important;left:calc(env(safe-area-inset-left,0px) + 8px)!important;width:min(192px,44vw)!important;transform-origin:top left!important; }
      .fps-alive-counter { top:calc(env(safe-area-inset-top,0px) + 124px)!important;right:calc(env(safe-area-inset-right,0px) + 8px)!important;padding:5px 8px!important;font-size:10px!important;max-width:112px!important;text-align:center!important; }
      .fps-tactical-sprint,.fps-hud-auto-run { top:calc(env(safe-area-inset-top,0px) + 152px)!important;right:calc(env(safe-area-inset-right,0px) + 8px)!important;padding:3px 7px!important;font-size:9px!important; }
      .fps-compass-bar { top:calc(env(safe-area-inset-top,0px) + 6px)!important;width:clamp(184px,44vw,260px)!important;height:28px!important; }
      .fps-kill-leader-hud { top:calc(env(safe-area-inset-top,0px) + 38px)!important;padding:4px 10px!important;font-size:10px!important;max-width:42vw!important;overflow:hidden!important;text-overflow:ellipsis!important; }
      .fps-player-crown { top:calc(env(safe-area-inset-top,0px) + 62px)!important;font-size:18px!important; }
      .fps-ammo-hud { right:calc(env(safe-area-inset-right,0px) + 8px)!important;bottom:calc(env(safe-area-inset-bottom,0px) + 58px)!important;padding:5px 8px!important;font-size:11px!important; }
      .fps-tactical-row { left:calc(env(safe-area-inset-left,0px) + 8px)!important;bottom:calc(env(safe-area-inset-bottom,0px) + 8px)!important;transform:scale(.82)!important;transform-origin:bottom left!important; }
      .fps-squad-hud,.fps-vehicle-hud { left:calc(env(safe-area-inset-left,0px) + 8px)!important;bottom:calc(env(safe-area-inset-bottom,0px) + 62px)!important; }
      .fps-center-bottom-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 86px)!important;max-width:calc(100vw - env(safe-area-inset-left,0px) - env(safe-area-inset-right,0px) - 20px)!important;text-align:center!important;font-size:11px!important;padding:6px 10px!important; }
      .fps-high-bottom-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 118px)!important;max-width:calc(100vw - env(safe-area-inset-left,0px) - env(safe-area-inset-right,0px) - 24px)!important;text-align:center!important;font-size:11px!important; }
      .fps-storm-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 56px)!important;padding:5px 10px!important;font-size:10px!important;max-width:52vw!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important; }
      .fps-phase-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 82px)!important;padding:3px 8px!important;font-size:9px!important; }
      .fps-hud-toast-card { max-width:min(86vw,420px)!important;font-size:12px!important;padding:9px 14px!important; }
      .fps-lobby-overlay { padding:calc(env(safe-area-inset-top,0px) + 34px) calc(env(safe-area-inset-right,0px) + 12px) calc(env(safe-area-inset-bottom,0px) + 92px) calc(env(safe-area-inset-left,0px) + 12px)!important;justify-content:flex-start!important;overflow:auto!important;background:linear-gradient(180deg,rgba(4,8,20,0.9) 0%,rgba(14,8,34,0.92) 52%,rgba(4,7,16,0.96) 100%)!important;backdrop-filter:blur(8px) saturate(1.08)!important;-webkit-backdrop-filter:blur(8px) saturate(1.08)!important; }
    }
    @media (max-width: 520px), (max-height: 520px) {
      .fps-daily-challenges-panel { width:160px!important;transform:scale(.82)!important; }
      .fps-daily-challenges-panel[data-collapsed="true"] { width:132px!important; }
      .fps-elim-feed { top:calc(env(safe-area-inset-top,0px) + 178px)!important;right:calc(env(safe-area-inset-right,0px) + 6px)!important;width:150px!important; }
      .fps-elim-feed-entry { font-size:10px!important;padding:5px 7px!important; }
      .fps-compass-bar { width:176px!important;height:24px!important; }
      .fps-mats-hud { display:none!important; }
      .fps-tactical-row { display:none!important; }
      .fps-storm-hud,.fps-phase-hud { display:none!important; }
      .fps-center-bottom-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 54px)!important; }
      .fps-high-bottom-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 82px)!important; }
    }
    @media (max-height: 420px) {
      .fps-daily-challenges-panel,.fps-elim-feed,.fps-key-hint-strip,.fps-ltm-chip,.fps-quest-chip,.fps-tactical-sprint,.fps-hud-auto-run { display:none!important; }
      .fps-alive-counter { top:calc(env(safe-area-inset-top,0px) + 106px)!important; }
      .fps-ammo-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 42px)!important; }
      .fps-center-bottom-hud,.fps-high-bottom-hud { bottom:calc(env(safe-area-inset-bottom,0px) + 44px)!important; }
    }
    @media (max-width: 440px), (max-height: 420px) {
      .fps-key-hint-strip { display:none!important; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fps-elim-feed-entry,.fps-hud-toast-card,.fps-kill-banner-pop,.fps-end-stats-panel,.fps-stat-value { animation:none!important; }
      .fps-key-hint-strip,.fps-elim-feed-entry,.fps-hud-toast-card { transition:none!important; }
    }
    body[data-fps-reduced-motion="true"] .fps-elim-feed-entry,
    body[data-fps-reduced-motion="true"] .fps-hud-toast-card,
    body[data-fps-reduced-motion="true"] .fps-kill-banner-pop,
    body[data-fps-reduced-motion="true"] .fps-end-stats-panel,
    body[data-fps-reduced-motion="true"] .fps-stat-value { animation:none!important; transition:none!important; }
    @keyframes fps-kill-banner-pop {
      0% { opacity: 0; transform: translate(-50%, -50%) scale(0.62); }
      55% { transform: translate(-50%, -50%) scale(1.14); }
      100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
    }
    .fps-kill-banner-pop { animation: fps-kill-banner-pop 0.42s cubic-bezier(0.22, 1.15, 0.36, 1); }
    @keyframes fps-stat-pop {
      from { opacity: 0; transform: translateY(8px) scale(0.92); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes fps-end-panel-in {
      from { opacity: 0; transform: translateY(12px); }
      to { opacity: 1; transform: translateY(0); }
    }
    .fps-end-stats-panel { animation: fps-end-panel-in 0.5s ease-out; }
    .fps-stat-value { animation: fps-stat-pop 0.55s ease-out both; }`;
    document.head.appendChild(this.hudAnimStyle);

    this.stormPhaseBanner = document.createElement('div');
    this.stormPhaseBanner.className = 'fps-storm-phase-banner';
    this.stormPhaseBanner.style.cssText =
      'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:165;pointer-events:none;display:none;' +
      'padding:18px 36px;border-radius:12px;background:rgba(120,40,180,0.22);border:3px solid rgba(180,100,255,0.65);' +
      'font:900 clamp(28px,5.5vw,52px) "Trebuchet MS","Segoe UI",system-ui,sans-serif;letter-spacing:0.08em;' +
      'color:#fff;text-shadow:0 4px 20px rgba(0,0,0,0.9),0 0 32px rgba(180,80,255,0.5);white-space:nowrap';
    this.hudRoot.appendChild(this.stormPhaseBanner);

    this.placementBanner = document.createElement('div');
    this.placementBanner.className = 'fps-placement-banner';
    this.placementBanner.style.cssText =
      'position:fixed;top:38%;left:50%;transform:translate(-50%,-50%);z-index:166;pointer-events:none;display:none;' +
      'padding:16px 40px;border-radius:14px;background:rgba(255,180,40,0.18);border:3px solid rgba(255,220,80,0.75);' +
      'font:900 clamp(32px,6vw,58px) "Trebuchet MS","Segoe UI",system-ui,sans-serif;letter-spacing:0.1em;' +
      'color:#fff;text-shadow:0 4px 22px rgba(0,0,0,0.92),0 0 36px rgba(255,200,60,0.55);white-space:nowrap';
    this.hudRoot.appendChild(this.placementBanner);

    this.floorPickups = new FpsFloorPickupField(this.scene, this.built.sceneRoot, [
      { x: -12, z: 8, kind: 'shield', amount: 25 },
      { x: 14, z: -10, kind: 'mat', amount: 20 },
      { x: -22, z: -12, kind: 'ammo', amount: 15 },
      { x: 8, z: 22, kind: 'shield', amount: 15 },
      { x: -5, z: -24, kind: 'mat', amount: 30 },
      { x: 28, z: 6, kind: 'ammo', amount: 20 },
      { x: -30, z: 20, kind: 'shield', amount: 20 },
      { x: 20, z: 28, kind: 'mat', amount: 18 },
      { x: -8, z: -18, kind: 'ammo', amount: 15 },
      { x: 32, z: -28, kind: 'shield', amount: 30 },
      { x: -36, z: -8, kind: 'mat', amount: 22 },
      { x: 4, z: 4, kind: 'shield', amount: 18 },
      { x: -18, z: 18, kind: 'forage', amount: 20 },
      { x: 11, z: -28, kind: 'forage', amount: 15 },
      { x: 25, z: -5, kind: 'rift', amount: 1 },
      { x: -33, z: 5, kind: 'rift', amount: 1 },
      { x: -40, z: -22, kind: 'ammo', amount: 15 },
      { x: 38, z: 18, kind: 'ammo', amount: 15 },
      { x: -15, z: 35, kind: 'ammo', amount: 15 },
      { x: 6, z: -35, kind: 'ammo', amount: 15 },
      { x: 35, z: -8, kind: 'ammo', amount: 15 },
      { x: -28, z: -30, kind: 'ammo', amount: 15 },
      { x: 18, z: 12, kind: 'ammo', amount: 15 },
      { x: -6, z: 30, kind: 'ammo', amount: 15 },
      { x: 30, z: 32, kind: 'ammo', amount: 15 },
      { x: -35, z: 12, kind: 'ammo', amount: 15 },
    ]);
    this.jumpPads = new FpsJumpPadField([
      { x: -18, z: 12 },
      { x: 22, z: -18, boostY: 13 },
      { x: 0, z: 30, radius: 1.6 },
    ]);

    this.hudHealth = document.createElement('div');
    this.hudHealth.className = 'fps-health-hud';
    this.hudHealth.style.cssText = 'display:none;';
    this.hudRoot.appendChild(this.hudHealth);

    this.hudRankedChip = document.createElement('div');
    this.hudRankedChip.style.cssText =
      'position:fixed;top:14px;left:50%;transform:translateX(-50%);z-index:36;display:none;padding:3px 10px;border-radius:6px;' +
      'background:rgba(180,24,24,0.88);border:1px solid rgba(255,96,96,0.75);color:#ffe8e8;font:800 10px system-ui,sans-serif;letter-spacing:0.14em;pointer-events:none;';
    this.hudRankedChip.textContent = 'RANKED';
    this.hudRoot.appendChild(this.hudRankedChip);

    this.hudLtmChip = document.createElement('div');
    this.hudLtmChip.className = 'fps-ltm-chip';
    this.hudLtmChip.style.cssText =
      'position:fixed;top:42px;left:14px;z-index:36;display:none;padding:4px 10px;border-radius:6px;' +
      'border:1px solid rgba(255,255,255,0.35);color:#fff;font:800 10px system-ui,sans-serif;letter-spacing:0.1em;pointer-events:none;';
    this.hudRoot.appendChild(this.hudLtmChip);

    this.hudStorm = document.createElement('div');
    this.hudStorm.className = 'fps-storm-hud';
    this.hudStorm.style.cssText =
      'position:fixed;bottom:86px;left:50%;transform:translateX(-50%);z-index:54;padding:6px 14px;border-radius:10px;' +
      'background:rgba(80,20,120,0.48);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);' +
      'border:1px solid rgba(200,120,255,0.35);box-shadow:0 4px 20px rgba(80,20,120,0.35),inset 0 1px 0 rgba(255,255,255,0.06);' +
      'color:#f0e0ff;font:700 12px system-ui,sans-serif;pointer-events:none;';
    this.hudStorm.textContent = 'STORM — safe zone shrinking';
    this.hudRoot.appendChild(this.hudStorm);

    this.hudStormSurge = document.createElement('div');
    this.hudStormSurge.className = 'fps-storm-surge-hud';
    this.hudStormSurge.style.cssText =
      'position:fixed;top:108px;left:50%;transform:translateX(-50%);z-index:55;display:none;padding:7px 16px;border-radius:10px;' +
      'background:rgba(72,12,28,0.82);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);' +
      'border:1px solid rgba(255,90,120,0.65);box-shadow:0 6px 24px rgba(180,20,60,0.35);' +
      'color:#ffb8c8;font:800 11px system-ui,sans-serif;letter-spacing:0.06em;pointer-events:none;' +
      'animation:fpsStormSurgePulse 1.4s ease-in-out infinite';
    this.hudStormSurge.textContent = 'STORM SURGE — excess damage';
    this.hudRoot.appendChild(this.hudStormSurge);

    this.hudPhase = document.createElement('div');
    this.hudPhase.className = 'fps-phase-hud';
    this.hudPhase.style.cssText =
      'position:fixed;bottom:118px;left:50%;transform:translateX(-50%);z-index:53;padding:4px 12px;border-radius:8px;' +
      'background:rgba(20,10,40,0.48);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);' +
      'border:1px solid rgba(200,120,255,0.22);box-shadow:0 4px 16px rgba(0,0,0,0.22);' +
      'color:#e8d4ff;font:700 11px system-ui,sans-serif;pointer-events:none;';
    this.hudPhase.textContent = 'Phase 1';
    this.hudRoot.appendChild(this.hudPhase);

    this.hudTacticalSprint = document.createElement('div');
    this.hudTacticalSprint.className = 'fps-tactical-sprint';
    this.hudTacticalSprint.style.cssText =
      'position:fixed;top:52px;right:188px;z-index:36;padding:4px 8px;border-radius:6px;background:rgba(255,120,40,0.35);border:1px solid rgba(255,180,80,0.6);color:#ffe8cc;font:800 10px system-ui,sans-serif;pointer-events:none;opacity:0;transition:opacity 0.2s';
    this.hudTacticalSprint.textContent = 'TACTICAL SPRINT';
    this.hudRoot.appendChild(this.hudTacticalSprint);

    this.deathOverlay = document.createElement('div');
    this.deathOverlay.style.cssText =
      'position:fixed;inset:0;z-index:150;pointer-events:none;background:#200010;opacity:0;transition:opacity 0.08s';
    document.body.appendChild(this.deathOverlay);

    this.damageFlash = document.createElement('div');
    this.damageFlash.style.cssText =
      'position:fixed;inset:0;z-index:34;pointer-events:none;background:radial-gradient(ellipse at center, transparent 40%, rgba(160,0,40,0.48) 74%, rgba(255,42,72,0.72) 100%);opacity:0;box-shadow:inset 0 0 0 2px rgba(255,255,255,0.12)';
    document.body.appendChild(this.damageFlash);

    this.stormWarnBorder = document.createElement('div');
    this.stormWarnBorder.style.cssText =
      'position:fixed;inset:0;z-index:35;pointer-events:none;opacity:0;box-shadow:inset 0 0 0 4px rgba(255,190,55,0.92),inset 0 0 0 7px rgba(30,0,40,0.5),inset 0 0 56px rgba(255,100,20,0.34);transition:opacity 0.12s';
    document.body.appendChild(this.stormWarnBorder);

    this.speedLinesStyle = document.createElement('style');
    this.speedLinesStyle.textContent = `
      @keyframes fps-speed-lines {
        from { background-position: 0% 50%; }
        to { background-position: 100% 50%; }
      }
    `;
    document.head.appendChild(this.speedLinesStyle);

    this.speedLinesOverlay = document.createElement('div');
    this.speedLinesOverlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:33',
      'pointer-events:none',
      'opacity:0',
      'background:repeating-linear-gradient(102deg,transparent 0px,transparent 36px,rgba(255,255,255,0.07) 36px,rgba(255,255,255,0.07) 38px,transparent 38px,transparent 72px)',
      'background-size:220% 220%',
      'mix-blend-mode:screen',
      'animation:fps-speed-lines 0.55s linear infinite',
      'animation-play-state:paused',
    ].join(';');
    document.body.appendChild(this.speedLinesOverlay);

    this.emoteToast = document.createElement('div');
    this.emoteToast.className = 'fps-hud-toast-card';
    this.emoteToast.style.cssText =
      'position:fixed;bottom:200px;left:50%;transform:translateX(-50%);z-index:90;padding:12px 24px;border-radius:12px;color:#fff;font:800 14px system-ui,sans-serif;pointer-events:none;display:none';
    this.emoteToast.style.setProperty('--fps-toast-rail', '#c77dff');
    this.emoteToast.style.setProperty('--fps-toast-glow', 'rgba(199,125,255,0.22)');
    this.hudRoot.appendChild(this.emoteToast);

    this.hudHintStrip = document.createElement('div');
    this.hudHintStrip.className = 'fps-key-hint-strip';
    this.hudHintStrip.setAttribute('role', 'note');
    this.hudHintStrip.setAttribute('aria-label', 'Keyboard controls: Shift sprint, C slide, 4 build, M material, R repair, Space glider, Tab pickaxe');
    this.hudHintStrip.innerHTML =
      '<span><kbd>Shift</kbd>Sprint</span>' +
      '<span><kbd>C</kbd>Slide</span>' +
      '<span><kbd>4</kbd>Build</span>' +
      '<span><kbd>M</kbd>Material</span>' +
      '<span><kbd>R</kbd>Repair</span>' +
      '<span class="fps-key-hint-optional"><kbd>Space</kbd>Glider</span>' +
      '<span class="fps-key-hint-optional"><kbd>Tab</kbd>Pickaxe</span>';
    this.hudRoot.appendChild(this.hudHintStrip);

    this.emoteWheel = document.createElement('div');
    this.emoteWheel.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:118px',
      'transform:translate(-50%,0)',
      'z-index:95',
      'width:220px',
      'height:220px',
      'border-radius:50%',
      'pointer-events:none',
      'display:none',
      'background:conic-gradient(from -45deg,#5a189a 0deg 90deg,#7b2cbf 90deg 180deg,#9d4edd 180deg 270deg,#c77dff 270deg 360deg)',
      'border:3px solid #e0aaff',
      'box-shadow:0 0 32px rgba(157,78,221,0.55),inset 0 0 24px rgba(0,0,0,0.35)',
    ].join(';');
    const emoteWheelInner = document.createElement('div');
    emoteWheelInner.style.cssText =
      'position:absolute;inset:28px;border-radius:50%;background:radial-gradient(circle,rgba(26,8,48,0.92) 0%,rgba(10,4,20,0.96) 100%);border:2px solid rgba(224,170,255,0.35);display:flex;align-items:center;justify-content:center;color:#f3e8ff;font:900 11px system-ui,sans-serif;letter-spacing:0.14em';
    emoteWheelInner.textContent = 'EMOTE';
    this.emoteWheel.appendChild(emoteWheelInner);
    const emoteOffsets = [
      { x: '50%', y: '8%', tx: '-50%', ty: '0' },
      { x: '92%', y: '50%', tx: '-100%', ty: '-50%' },
      { x: '50%', y: '92%', tx: '-50%', ty: '-100%' },
      { x: '8%', y: '50%', tx: '0', ty: '-50%' },
    ];
    for (let i = 0; i < EMOTE_OPTIONS.length; i++) {
      const seg = document.createElement('div');
      const em = EMOTE_OPTIONS[i]!;
      const off = emoteOffsets[i]!;
      seg.style.cssText = [
        'position:absolute',
        `left:${off.x}`,
        `top:${off.y}`,
        'transform:translate(' + off.tx + ',' + off.ty + ')',
        'padding:6px 10px',
        'border-radius:8px',
        'background:rgba(0,0,0,0.45)',
        'border:1px solid rgba(224,170,255,0.35)',
        'color:#fff',
        'font:800 10px system-ui,sans-serif',
        'letter-spacing:0.06em',
        'text-align:center',
        'white-space:nowrap',
        'transition:transform 0.08s,background 0.08s,border-color 0.08s',
      ].join(';');
      seg.innerHTML = `<div style="font-size:16px;line-height:1">${['💃', '👋', '💪', '😂'][i]}</div><div>${em.label.toUpperCase()}</div><div style="font:700 9px system-ui;color:#d8b4fe;margin-top:2px">${i + 1}</div>`;
      this.emoteWheel.appendChild(seg);
      this.emoteWheelSegments.push(seg);
    }
    document.body.appendChild(this.emoteWheel);

    this.chatWheel = document.createElement('div');
    this.chatWheel.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:118px',
      'transform:translate(-50%,0)',
      'z-index:95',
      'width:220px',
      'height:220px',
      'border-radius:50%',
      'pointer-events:none',
      'display:none',
      'background:conic-gradient(from -45deg,#0c4a6e 0deg 90deg,#0369a1 90deg 180deg,#0284c7 180deg 270deg,#38bdf8 270deg 360deg)',
      'border:3px solid #7dd3fc',
      'box-shadow:0 0 32px rgba(56,189,248,0.45),inset 0 0 24px rgba(0,0,0,0.35)',
    ].join(';');
    const chatWheelInner = document.createElement('div');
    chatWheelInner.style.cssText =
      'position:absolute;inset:28px;border-radius:50%;background:radial-gradient(circle,rgba(8,28,48,0.92) 0%,rgba(4,12,24,0.96) 100%);border:2px solid rgba(125,211,252,0.35);display:flex;align-items:center;justify-content:center;color:#e0f2fe;font:900 11px system-ui,sans-serif;letter-spacing:0.14em';
    chatWheelInner.textContent = 'CHAT';
    this.chatWheel.appendChild(chatWheelInner);
    const chatOffsets = [
      { x: '50%', y: '8%', tx: '-50%', ty: '0' },
      { x: '92%', y: '50%', tx: '-100%', ty: '-50%' },
      { x: '50%', y: '92%', tx: '-50%', ty: '-100%' },
      { x: '8%', y: '50%', tx: '0', ty: '-50%' },
    ];
    for (let i = 0; i < CHAT_WHEEL_OPTIONS.length; i++) {
      const seg = document.createElement('div');
      const label = CHAT_WHEEL_OPTIONS[i]!;
      const off = chatOffsets[i]!;
      seg.style.cssText = [
        'position:absolute',
        `left:${off.x}`,
        `top:${off.y}`,
        'transform:translate(' + off.tx + ',' + off.ty + ')',
        'padding:6px 10px',
        'border-radius:8px',
        'background:rgba(0,0,0,0.45)',
        'border:1px solid rgba(125,211,252,0.35)',
        'color:#fff',
        'font:800 9px system-ui,sans-serif',
        'letter-spacing:0.04em',
        'text-align:center',
        'white-space:nowrap',
        'max-width:72px',
        'transition:transform 0.08s,background 0.08s,border-color 0.08s',
      ].join(';');
      seg.textContent = label;
      this.chatWheel.appendChild(seg);
      this.chatWheelSegments.push(seg);
    }
    document.body.appendChild(this.chatWheel);

    this.pingWheel = document.createElement('div');
    this.pingWheel.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:118px',
      'transform:translate(-50%,0)',
      'z-index:95',
      'width:220px',
      'height:220px',
      'border-radius:50%',
      'pointer-events:none',
      'display:none',
      'background:conic-gradient(from -45deg,#7f1d1d 0deg 90deg,#ca8a04 90deg 180deg,#0369a1 180deg 270deg,#be185d 270deg 360deg)',
      'border:3px solid #fda4af',
      'box-shadow:0 0 32px rgba(251,113,133,0.45),inset 0 0 24px rgba(0,0,0,0.35)',
    ].join(';');
    const pingWheelInner = document.createElement('div');
    pingWheelInner.style.cssText =
      'position:absolute;inset:28px;border-radius:50%;background:radial-gradient(circle,rgba(28,8,16,0.92) 0%,rgba(12,4,10,0.96) 100%);border:2px solid rgba(253,164,175,0.35);display:flex;align-items:center;justify-content:center;color:#ffe4e6;font:900 11px system-ui,sans-serif;letter-spacing:0.14em';
    pingWheelInner.textContent = 'PING';
    this.pingWheel.appendChild(pingWheelInner);
    const pingOffsets = [
      { x: '50%', y: '8%', tx: '-50%', ty: '0' },
      { x: '92%', y: '50%', tx: '-100%', ty: '-50%' },
      { x: '50%', y: '92%', tx: '-50%', ty: '-100%' },
      { x: '8%', y: '50%', tx: '0', ty: '-50%' },
    ];
    for (let i = 0; i < PING_WHEEL_OPTIONS.length; i++) {
      const seg = document.createElement('div');
      const opt = PING_WHEEL_OPTIONS[i]!;
      const off = pingOffsets[i]!;
      seg.style.cssText = [
        'position:absolute',
        `left:${off.x}`,
        `top:${off.y}`,
        'transform:translate(' + off.tx + ',' + off.ty + ')',
        'padding:6px 10px',
        'border-radius:8px',
        'background:rgba(0,0,0,0.45)',
        'border:1px solid rgba(253,164,175,0.35)',
        'color:#fff',
        'font:800 10px system-ui,sans-serif',
        'letter-spacing:0.06em',
        'text-align:center',
        'white-space:nowrap',
        'transition:transform 0.08s,background 0.08s,border-color 0.08s',
      ].join(';');
      seg.innerHTML = `<div style="font-size:14px;line-height:1">${['⚠', '★', '➜', '◎'][i]}</div><div>${opt.label.toUpperCase()}</div>`;
      this.pingWheel.appendChild(seg);
      this.pingWheelSegments.push(seg);
    }
    document.body.appendChild(this.pingWheel);

    this.killCamOverlay = document.createElement('div');
    this.killCamOverlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:165',
      'pointer-events:none',
      'display:none',
      'align-items:flex-start',
      'justify-content:center',
      'padding-top:14%',
      'background:radial-gradient(ellipse at center,transparent 40%,rgba(40,0,60,0.35) 100%)',
    ].join(';');
    this.killCamOverlay.innerHTML =
      '<div style="font:900 clamp(28px,6vw,44px) system-ui;letter-spacing:0.22em;color:#fff;' +
      'text-shadow:0 0 24px rgba(199,125,255,0.85),0 2px 8px rgba(0,0,0,0.8);' +
      'border-bottom:3px solid #c77dff;padding-bottom:8px">ELIMINATION</div>';
    document.body.appendChild(this.killCamOverlay);

    this.spectateOverlay = document.createElement('div');
    this.spectateOverlay.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:164',
      'pointer-events:none',
      'display:none',
      'align-items:flex-start',
      'justify-content:center',
      'padding-top:12%',
      'background:radial-gradient(ellipse at center,transparent 55%,rgba(8,16,32,0.42) 100%)',
    ].join(';');
    this.spectateOverlay.innerHTML =
      '<div id="spectate-recap" style="display:none;margin-bottom:18px;padding:16px 22px;border-radius:12px;' +
      'background:rgba(8,14,28,0.88);border:2px solid rgba(255,180,80,0.45);text-align:center;min-width:min(88vw,320px)">' +
      '<div id="spectate-recap-elim" style="font:900 clamp(16px,3.2vw,22px) system-ui;color:#ffe8cc;letter-spacing:0.06em;text-shadow:0 2px 8px rgba(0,0,0,0.65)"></div>' +
      '<div id="spectate-recap-placement" style="margin-top:8px;font:800 clamp(13px,2.4vw,16px) system-ui;color:#9edcff"></div>' +
      '<div id="spectate-recap-damage" style="margin-top:6px;font:700 12px system-ui;color:#8899aa;letter-spacing:0.04em"></div></div>' +
      '<div id="spectate-label" style="font:800 clamp(14px,2.8vw,18px) system-ui;letter-spacing:0.18em;color:#9edcff;' +
      'text-shadow:0 0 16px rgba(46,180,255,0.55),0 2px 6px rgba(0,0,0,0.75);' +
      'padding:10px 18px;border-radius:6px;border:1px solid rgba(80,180,255,0.35);' +
      'background:rgba(6,14,28,0.72)">SPECTATING</div>' +
      '<div id="spectate-hint" style="display:none;margin-top:14px;font:700 11px system-ui;color:rgba(200,220,255,0.72);' +
      'letter-spacing:0.08em">Space — Next player · ← → cycle</div>';
    document.body.appendChild(this.spectateOverlay);

    this.hudMats = document.createElement('div');
    this.hudMats.className = 'fps-mats-hud';
    this.hudMats.style.cssText =
      'position:fixed;top:14px;left:220px;z-index:36;padding:8px 12px;border-radius:10px;background:rgba(10,14,24,0.85);border:1px solid rgba(255,200,80,0.35);color:#ffe8aa;font:600 12px system-ui,sans-serif;pointer-events:none;';
    this.hudMats.textContent = 'Mats 0';
    this.hudRoot.appendChild(this.hudMats);

    this.hudGold = document.createElement('div');
    this.hudGold.className = 'fps-gold-hud';
    this.hudGold.style.cssText =
      'position:fixed;top:14px;left:330px;z-index:36;padding:8px 12px;border-radius:10px;background:rgba(10,14,24,0.85);border:1px solid rgba(255,213,74,0.45);color:#ffd54a;font:600 12px system-ui,sans-serif;pointer-events:none;';
    this.syncGoldHud();
    this.hudRoot.appendChild(this.hudGold);

    this.fpsMaterials = new FpsMaterials(document.body);

    this.hudReboot = document.createElement('div');
    this.hudReboot.className = 'fps-high-bottom-hud';
    this.hudReboot.style.cssText =
      'position:fixed;bottom:150px;left:50%;transform:translateX(-50%);z-index:155;padding:10px 20px;border-radius:10px;background:rgba(20,40,80,0.94);border:2px solid #6cf;box-shadow:0 10px 30px rgba(0,0,0,0.45);font:800 13px system-ui,sans-serif;color:#dff;pointer-events:none;display:none';
    this.hudReboot.textContent = 'Press R — Reboot Van';
    this.hudRoot.appendChild(this.hudReboot);

    this.hudVend = document.createElement('div');
    this.hudVend.className = 'fps-center-bottom-hud';
    this.hudVend.style.cssText =
      'position:fixed;left:50%;bottom:148px;transform:translateX(-50%);z-index:72;padding:6px 12px;border-radius:8px;background:rgba(40,20,60,0.86);color:#ecb3ff;font:600 11px system-ui,sans-serif;pointer-events:none;display:none;box-shadow:0 8px 24px rgba(0,0,0,0.34)';
    this.hudVend.textContent = '[Q] Vending — 50 mats → shield';
    this.hudRoot.appendChild(this.hudVend);

    this.hudCraftBench = document.createElement('div');
    this.hudCraftBench.className = 'fps-center-bottom-hud';
    this.hudCraftBench.style.cssText =
      'position:fixed;left:50%;bottom:148px;transform:translateX(-50%);z-index:72;padding:6px 12px;border-radius:8px;background:rgba(16,32,22,0.9);border:1px solid rgba(68,255,136,0.55);color:#9bff9b;font:600 11px system-ui,sans-serif;pointer-events:none;display:none;box-shadow:0 8px 24px rgba(0,0,0,0.34)';
    this.hudCraftBench.textContent = '[E] Crafting Bench';
    this.hudRoot.appendChild(this.hudCraftBench);

    this.hudQuestChip = document.createElement('div');
    this.hudQuestChip.className = 'fps-quest-chip';
    this.hudQuestChip.style.cssText =
      'position:fixed;top:78px;left:14px;z-index:36;display:none;padding:4px 10px;border-radius:6px;' +
      'background:rgba(28,24,10,0.88);border:1px solid rgba(255,220,80,0.55);color:#ffe8a0;' +
      'font:800 10px system-ui,sans-serif;letter-spacing:0.06em;pointer-events:none';
    this.hudRoot.appendChild(this.hudQuestChip);

    this.hudEditHint = document.createElement('div');
    this.hudEditHint.className = 'fps-center-bottom-hud';
    this.hudEditHint.style.cssText =
      'position:fixed;left:50%;bottom:118px;transform:translateX(-50%);z-index:72;padding:6px 14px;border-radius:8px;background:rgba(8,18,40,0.9);border:1px solid rgba(108,156,255,0.65);color:#cfe4ff;font:700 12px system-ui,sans-serif;pointer-events:none;display:none;box-shadow:0 8px 24px rgba(0,0,0,0.34)';
    this.hudEditHint.textContent = '[R] Edit piece';
    this.hudRoot.appendChild(this.hudEditHint);

    this.hudBuildHp = document.createElement('div');
    this.hudBuildHp.className = 'fps-high-bottom-hud';
    this.hudBuildHp.style.cssText =
      'position:fixed;left:50%;bottom:148px;transform:translateX(-50%);z-index:72;padding:5px 12px;border-radius:8px;background:rgba(10,14,24,0.92);border:1px solid rgba(255,160,80,0.65);color:#ffd8b0;font:800 12px ui-monospace,monospace;pointer-events:none;display:none;box-shadow:0 8px 24px rgba(0,0,0,0.34)';
    this.hudRoot.appendChild(this.hudBuildHp);

    this.hudAmmo = document.createElement('div');
    this.hudAmmo.className = 'fps-ammo-hud';
    this.hudAmmo.style.cssText =
      'position:fixed;right:14px;bottom:86px;z-index:37;padding:6px 12px;border-radius:8px;background:rgba(10,14,24,0.88);border:1px solid rgba(120,200,255,0.4);color:#cfe8ff;font:800 13px ui-monospace,monospace;pointer-events:none';
    this.hudAmmo.textContent = '30/30';
    this.hudRoot.appendChild(this.hudAmmo);

    this.weaponInspectOverlay = document.createElement('div');
    this.weaponInspectOverlay.style.cssText = [
      'position:fixed',
      'right:18px',
      'top:50%',
      'transform:translateY(-50%)',
      'z-index:38',
      'width:248px',
      'padding:14px 16px',
      'border-radius:10px',
      'background:rgba(12,18,32,0.72)',
      'border:1px solid rgba(140,180,255,0.45)',
      'box-shadow:0 8px 28px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
      'color:#e8f0ff',
      'font:600 13px system-ui,sans-serif',
      'pointer-events:none',
      'display:none',
      'backdrop-filter:blur(6px)',
    ].join(';');
    document.body.appendChild(this.weaponInspectOverlay);

    this.hudGlide = document.createElement('div');
    this.hudGlide.className = 'fps-high-bottom-hud';
    this.hudGlide.style.cssText =
      'position:fixed;left:50%;bottom:200px;transform:translateX(-50%);z-index:38;padding:6px 14px;border-radius:8px;background:rgba(30,80,140,0.82);border:1px solid #8cf;color:#dff;font:800 11px system-ui,sans-serif;pointer-events:none;display:none';
    this.hudGlide.textContent = 'GLIDER';
    this.hudRoot.appendChild(this.hudGlide);

    this.hudDropCue = document.createElement('div');
    this.hudDropCue.className = 'fps-high-bottom-hud';
    this.hudDropCue.setAttribute('role', 'status');
    this.hudDropCue.style.cssText = [
      'position:fixed',
      'left:50%',
      'bottom:236px',
      'transform:translateX(-50%)',
      'z-index:39',
      'min-width:220px',
      'padding:10px 18px',
      'border-radius:14px',
      'background:linear-gradient(135deg,rgba(6,16,36,0.92),rgba(16,64,116,0.9))',
      'border:2px solid rgba(136,220,255,0.84)',
      'box-shadow:0 14px 34px rgba(0,0,0,0.46),0 0 22px rgba(68,238,255,0.28),inset 0 1px 0 rgba(255,255,255,0.12)',
      'color:#e8fbff',
      'font:900 12px system-ui,sans-serif',
      'letter-spacing:0.06em',
      'line-height:1.3',
      'text-align:center',
      'text-transform:uppercase',
      'pointer-events:none',
      'display:none',
    ].join(';');
    this.hudRoot.appendChild(this.hudDropCue);

    this.hudMantleHint = document.createElement('div');
    this.hudMantleHint.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:58%',
      'transform:translate(-50%,-50%) scale(0.96)',
      'z-index:40',
      'padding:6px 12px',
      'border-radius:8px',
      'background:rgba(12,18,32,0.72)',
      'border:1px solid rgba(170,220,255,0.55)',
      'box-shadow:0 8px 22px rgba(0,0,0,0.26)',
      'color:#f2fbff',
      'font:900 11px system-ui,sans-serif',
      'letter-spacing:0.12em',
      'text-transform:uppercase',
      'pointer-events:none',
      'opacity:0',
      'transition:opacity 0.12s,transform 0.12s',
    ].join(';');
    this.hudMantleHint.textContent = 'Mantle';
    this.hudRoot.appendChild(this.hudMantleHint);

    this.hudSwim = document.createElement('div');
    this.hudSwim.className = 'fps-high-bottom-hud';
    this.hudSwim.style.cssText =
      'position:fixed;left:50%;bottom:200px;transform:translateX(-50%);z-index:38;padding:6px 14px;border-radius:8px;background:rgba(12,72,120,0.88);border:1px solid #4ad4ff;color:#dff;font:800 11px system-ui,sans-serif;pointer-events:none;display:none';
    this.hudSwim.textContent = 'SWIMMING';
    this.hudRoot.appendChild(this.hudSwim);

    this.hudTactical = document.createElement('div');
    this.hudTactical.className = 'fps-tactical-row';
    this.hudTactical.style.cssText = [
      'position:fixed',
      'left:14px',
      'bottom:14px',
      'z-index:37',
      'display:flex',
      'gap:6px',
      'pointer-events:none',
    ].join(';');
    const mkTacticalChip = (
      key: string,
      label: string,
      border: string,
    ): HTMLSpanElement => {
      const wrap = document.createElement('div');
      wrap.style.cssText = [
        'position:relative',
        'width:52px',
        'height:44px',
        'border-radius:10px',
        'background:rgba(10,14,24,0.92)',
        `border:2px solid ${border}`,
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'font:700 9px system-ui,sans-serif',
        'color:rgba(255,255,255,0.82)',
        'box-shadow:inset 0 1px 0 rgba(255,255,255,0.06)',
      ].join(';');
      const keyEl = document.createElement('span');
      keyEl.textContent = key;
      keyEl.style.cssText =
        'position:absolute;top:3px;left:5px;font-size:9px;opacity:0.45;color:#8ab';
      const labEl = document.createElement('span');
      labEl.textContent = label;
      const countEl = document.createElement('span');
      countEl.textContent = '0';
      countEl.style.cssText =
        'position:absolute;bottom:3px;right:5px;font-size:11px;font-weight:800;color:#fff;text-shadow:0 1px 3px #000';
      wrap.appendChild(keyEl);
      wrap.appendChild(labEl);
      wrap.appendChild(countEl);
      this.hudTactical.appendChild(wrap);
      return countEl;
    };
    this.tacticalCountEls = {
      grenades: mkTacticalChip('T', 'Nade', 'rgba(255,140,60,0.55)'),
      stink: mkTacticalChip('8', 'Stink', 'rgba(80,220,80,0.55)'),
      mini: mkTacticalChip('6', 'Mini', 'rgba(52,152,219,0.85)'),
      bandage: mkTacticalChip('7', 'Band', 'rgba(88,214,141,0.85)'),
      med: mkTacticalChip('5', 'Med', 'rgba(175,122,197,0.85)'),
      airstrike: mkTacticalChip('9', 'Air', 'rgba(220,60,60,0.85)'),
      chug: mkTacticalChip('0', 'Splash', 'rgba(175,122,197,0.85)'),
      reboot: mkTacticalChip('R', 'Card', 'rgba(255,220,80,0.85)'),
      shock: mkTacticalChip('-', 'Wave', 'rgba(68,136,255,0.85)'),
    };
    this.hudRoot.appendChild(this.hudTactical);

    this.supplyContestHud = document.createElement('div');
    this.supplyContestHud.style.cssText =
      'position:fixed;top:188px;right:14px;z-index:36;padding:6px 12px;border-radius:8px;' +
      'background:rgba(120,20,20,0.82);border:1px solid rgba(255,80,80,0.65);color:#ffb0b0;' +
      'font:800 11px system-ui;letter-spacing:0.06em;pointer-events:none;display:none';
    this.supplyContestHud.textContent = 'BOT CONTESTING DROP';
    this.hudRoot.appendChild(this.supplyContestHud);

    this.hudAutoRun = document.createElement('div');
    this.hudAutoRun.className = 'fps-hud-auto-run';
    this.hudAutoRun.style.cssText =
      'position:fixed;top:52px;right:14px;z-index:36;padding:4px 10px;border-radius:6px;background:rgba(40,120,200,0.35);border:1px solid rgba(100,200,255,0.6);color:#dff;font:800 10px system-ui,sans-serif;pointer-events:none;opacity:0;transition:opacity 0.2s';
    this.hudAutoRun.textContent = 'AUTO RUN';
    this.hudRoot.appendChild(this.hudAutoRun);

    this.hudKnocked = document.createElement('div');
    this.hudKnocked.style.cssText =
      'position:fixed;inset:0;z-index:145;pointer-events:none;display:none;align-items:center;justify-content:center;flex-direction:column;' +
      'background:radial-gradient(ellipse at center, transparent 35%, rgba(120,0,20,0.55) 100%)';
    this.hudKnocked.innerHTML =
      '<div style="font:900 clamp(42px,10vw,72px) system-ui;letter-spacing:0.14em;color:#ff3a3a;' +
      'text-shadow:0 0 28px rgba(255,40,40,0.65),0 2px 8px rgba(0,0,0,0.8)">KNOCKED</div>' +
      '<div id="knocked-timer" style="margin-top:12px;font:800 18px system-ui;color:#ffb0b0;letter-spacing:0.08em">45s</div>' +
      '<div style="margin-top:10px;font:700 13px system-ui;color:#ffcccc;opacity:0.88">Crawl to Reboot Van · [R] within 8m</div>';
    this.hudRoot.appendChild(this.hudKnocked);

    this.interactHint = document.createElement('div');
    this.interactHint.className = 'fps-center-bottom-hud';
    this.interactHint.style.cssText =
      'position:fixed;left:50%;bottom:120px;transform:translateX(-50%);z-index:72;padding:8px 16px;border-radius:8px;background:rgba(0,0,0,0.72);color:#fff;font:700 13px system-ui,sans-serif;pointer-events:none;display:none;border:1px solid rgba(255,255,255,0.18);box-shadow:0 8px 24px rgba(0,0,0,0.36);';
    this.interactHint.textContent = '[E] Open chest';
    this.hudRoot.appendChild(this.interactHint);

    this.hudSquad = document.createElement('div');
    this.hudSquad.className = 'fps-squad-hud';
    this.hudSquad.style.cssText =
      'position:fixed;left:14px;bottom:88px;z-index:37;display:flex;flex-direction:column;gap:6px;pointer-events:none';
    this.hudRoot.appendChild(this.hudSquad);

    this.hudRevive = document.createElement('div');
    this.hudRevive.className = 'fps-high-bottom-hud';
    this.hudRevive.style.cssText =
      'position:fixed;left:50%;bottom:168px;transform:translateX(-50%);z-index:38;min-width:220px;padding:10px 16px;border-radius:10px;background:rgba(12,28,18,0.92);border:2px solid rgba(54,227,107,0.55);pointer-events:none;display:none;text-align:center';
    this.hudRevive.innerHTML =
      '<div id="revive-hint" style="font:800 12px system-ui;color:#9bff6a;letter-spacing:0.04em">HOLD E — Revive</div>' +
      '<div style="margin-top:8px;height:5px;border-radius:3px;background:rgba(0,0,0,0.55);overflow:hidden">' +
      '<div id="revive-progress-fill" style="height:100%;width:0%;background:linear-gradient(90deg,#36e36b,#c8ff72);transition:width 0.05s linear"></div></div>';
    this.hudRoot.appendChild(this.hudRevive);

    this.hudCarry = document.createElement('div');
    this.hudCarry.className = 'fps-high-bottom-hud';
    this.hudCarry.style.cssText =
      'position:fixed;left:50%;bottom:132px;transform:translateX(-50%);z-index:38;min-width:220px;padding:10px 16px;border-radius:10px;background:rgba(28,18,12,0.92);border:2px solid rgba(255,154,60,0.55);pointer-events:none;display:none;text-align:center';
    this.hudCarry.innerHTML =
      '<div id="carry-hint" style="font:800 12px system-ui;color:#ffb86a;letter-spacing:0.04em">HOLD F — Carry</div>' +
      '<div style="margin-top:8px;height:5px;border-radius:3px;background:rgba(0,0,0,0.55);overflow:hidden">' +
      '<div id="carry-progress-fill" style="height:100%;width:0%;background:linear-gradient(90deg,#ff9a3c,#ffd36b);transition:width 0.05s linear"></div></div>';
    this.hudRoot.appendChild(this.hudCarry);

    this.hudFish = document.createElement('div');
    this.hudFish.className = 'fps-high-bottom-hud';
    this.hudFish.style.cssText =
      'position:fixed;left:50%;bottom:204px;transform:translateX(-50%);z-index:38;min-width:220px;padding:10px 16px;border-radius:10px;background:rgba(8,24,40,0.92);border:2px solid rgba(56,189,248,0.55);pointer-events:none;display:none;text-align:center';
    this.hudFish.innerHTML =
      '<div id="fish-hint" style="font:800 12px system-ui;color:#7dd3fc;letter-spacing:0.04em">[Hold E] Fish</div>' +
      '<div style="margin-top:8px;height:5px;border-radius:3px;background:rgba(0,0,0,0.55);overflow:hidden">' +
      '<div id="fish-progress-fill" style="height:100%;width:0%;background:linear-gradient(90deg,#0284c7,#7dd3fc);transition:width 0.05s linear"></div></div>';
    this.hudRoot.appendChild(this.hudFish);

    this.elimFeed = document.createElement('div');
    this.elimFeed.className = 'fps-elim-feed';
    this.elimFeed.style.cssText =
      'position:fixed;top:120px;right:14px;z-index:72;width:240px;display:flex;flex-direction:column;gap:6px;pointer-events:none;';
    this.hudRoot.appendChild(this.elimFeed);

    this.endScreen = document.createElement('div');
    this.endScreen.style.cssText =
      'position:fixed;inset:0;z-index:200;display:none;align-items:center;justify-content:center;flex-direction:column;' +
      'background:radial-gradient(ellipse 80% 60% at 50% 40%,rgba(30,20,60,0.92) 0%,rgba(5,8,16,0.94) 100%);' +
      'color:#fff;font:800 28px system-ui,sans-serif;text-align:center;pointer-events:auto;';
    document.body.appendChild(this.endScreen);

    this.victoryRoyale = new FpsVictoryRoyale(document.body, this.camera);

    this.confettiCanvas = document.createElement('canvas');
    this.confettiCanvas.style.cssText =
      'position:fixed;inset:0;z-index:210;pointer-events:none;display:none;width:100%;height:100%';
    document.body.appendChild(this.confettiCanvas);
    this.confettiCtx = this.confettiCanvas.getContext('2d');

    this.defeatVignetteEl = document.createElement('div');
    this.defeatVignetteEl.style.cssText =
      'position:fixed;inset:0;z-index:195;pointer-events:none;display:none;' +
      'background:radial-gradient(ellipse at center, transparent 38%, rgba(90,0,18,0.62) 100%);opacity:0';
    document.body.appendChild(this.defeatVignetteEl);

    this.adsVignetteEl = document.createElement('div');
    this.adsVignetteEl.style.cssText =
      'position:fixed;inset:0;z-index:28;pointer-events:none;opacity:0;' +
      'background:radial-gradient(ellipse at center, transparent 50%, rgba(0,0,0,0.58) 100%);';
    this.hudRoot.appendChild(this.adsVignetteEl);

    this.lootRarityFlashEl = document.createElement('div');
    this.lootRarityFlashEl.style.cssText =
      'position:fixed;inset:0;z-index:34;pointer-events:none;opacity:0;' +
      'background:radial-gradient(ellipse at center, rgba(255,255,255,0.08) 0%, transparent 62%);' +
      'mix-blend-mode:screen;';
    this.hudRoot.appendChild(this.lootRarityFlashEl);

    this.hitMarker = document.createElement('div');
    this.hitMarker.style.cssText =
      'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);width:38px;height:38px;z-index:32;pointer-events:none;opacity:0;' +
      'background:' +
      'linear-gradient(#fff,#fff) center/2px 12px no-repeat,' +
      'linear-gradient(#fff,#fff) center/12px 2px no-repeat;' +
      'filter:drop-shadow(0 0 4px #f33);';
    this.hudRoot.appendChild(this.hitMarker);

    this.killBanner = document.createElement('div');
    this.killBanner.style.cssText =
      'position:fixed;left:50%;top:18%;transform:translate(-50%,-50%);z-index:160;padding:10px 22px;border-radius:8px;background:rgba(255,40,40,0.18);border:2px solid #ff6060;color:#ffe;font:900 18px system-ui,sans-serif;text-shadow:0 1px 6px #800;pointer-events:none;display:none;letter-spacing:1px';
    this.hudRoot.appendChild(this.killBanner);

    this.aliveCounter = document.createElement('div');
    this.aliveCounter.className = 'fps-alive-counter';
    this.aliveCounter.style.cssText =
      'position:fixed;top:14px;right:200px;z-index:36;padding:8px 12px;border-radius:10px;background:rgba(10,14,24,0.85);border:1px solid rgba(255,120,120,0.4);color:#ffd4d4;font:700 12px system-ui,sans-serif;pointer-events:none;';
    this.aliveCounter.textContent = 'Players · 11';
    this.hudRoot.appendChild(this.aliveCounter);

    this.killLeaderHud = document.createElement('div');
    this.killLeaderHud.className = 'fps-kill-leader-hud';
    this.killLeaderHud.style.cssText =
      'position:fixed;top:44px;left:50%;transform:translateX(-50%);z-index:39;padding:6px 16px;border-radius:8px;' +
      'background:rgba(18,12,4,0.88);border:1px solid rgba(255,200,60,0.65);color:#ffd54a;font:800 12px system-ui,sans-serif;' +
      'pointer-events:none;display:none;letter-spacing:0.06em;text-shadow:0 1px 4px rgba(0,0,0,0.65);white-space:nowrap';
    this.hudRoot.appendChild(this.killLeaderHud);

    this.playerCrownHud = document.createElement('div');
    this.playerCrownHud.className = 'fps-player-crown';
    this.playerCrownHud.style.cssText =
      'position:fixed;top:72px;left:50%;transform:translateX(-50%);z-index:39;font-size:22px;pointer-events:none;display:none;' +
      'filter:drop-shadow(0 0 6px rgba(255,200,60,0.85))';
    this.playerCrownHud.textContent = '👑';
    this.playerCrownHud.title = 'Kill Leader';
    this.hudRoot.appendChild(this.playerCrownHud);

    this.hudVehicle = document.createElement('div');
    this.hudVehicle.className = 'fps-vehicle-hud';
    this.hudVehicle.style.cssText =
      'position:fixed;left:14px;bottom:86px;z-index:37;padding:8px 14px;border-radius:10px;background:rgba(10,14,24,0.92);border:2px solid #2ee6ff;color:#dff;font:800 14px ui-monospace,monospace;display:none;pointer-events:none;line-height:1.35';
    this.hudVehicle.textContent = '0 km/h';
    this.hudRoot.appendChild(this.hudVehicle);

    this.hudBuildCost = document.createElement('div');
    this.hudBuildCost.style.cssText =
      'position:fixed;left:50%;top:calc(50% + 28px);transform:translateX(-50%);z-index:72;padding:5px 12px;border-radius:8px;background:rgba(8,12,22,0.9);border:1px solid rgba(108,156,255,0.65);color:#cfe4ff;font:700 12px system-ui,sans-serif;pointer-events:none;display:none;white-space:nowrap;text-shadow:0 1px 3px rgba(0,0,0,0.65);box-shadow:0 8px 24px rgba(0,0,0,0.34)';
    this.hudRoot.appendChild(this.hudBuildCost);

    this.vehicleEnterHint = document.createElement('div');
    this.vehicleEnterHint.className = 'fps-center-bottom-hud';
    this.ziplineHint = document.createElement('div');
    this.ziplineHint.className = 'fps-zipline-hint';
    this.ziplineHint.style.cssText =
      'position:fixed;bottom:28%;left:50%;transform:translateX(-50%);z-index:38;display:none;' +
      'padding:8px 14px;border-radius:8px;background:rgba(8,14,28,0.82);border:1px solid rgba(80,200,255,0.45);' +
      'color:#d8f4ff;font:700 12px system-ui,sans-serif;pointer-events:none;letter-spacing:0.04em;';
    this.hudRoot.appendChild(this.ziplineHint);

    this.vehicleEnterHint.style.cssText =
      'position:fixed;left:50%;bottom:90px;transform:translateX(-50%);z-index:36;padding:8px 18px;border-radius:8px;background:rgba(0,0,0,0.65);color:#fff;font:700 13px system-ui,sans-serif;pointer-events:none;display:none;border:1px solid #2ee6ff';
    this.vehicleEnterHint.textContent = '[F] Enter vehicle';
    this.hudRoot.appendChild(this.vehicleEnterHint);

    this.damageDirIndicator = document.createElement('div');
    this.damageDirIndicator.style.cssText =
      'position:fixed;left:50%;top:50%;width:300px;height:300px;transform:translate(-50%,-50%);pointer-events:none;z-index:31;opacity:0;transition:opacity 0.15s';
    this.damageDirIndicator.innerHTML =
      '<svg viewBox="-50 -50 100 100" style="width:100%;height:100%;overflow:visible"><g id="ddi-g"><path d="M-12 -42 L0 -52 L12 -42 Z" fill="rgba(255,48,61,0.95)" stroke="#fff" stroke-width="0.8"/><path d="M-12 -42 L0 -52 L12 -42 Z" fill="none" stroke="#050008" stroke-width="2.6" opacity="0.95"/></g></svg>';
    document.body.appendChild(this.damageDirIndicator);

    this.compassBar = document.createElement('div');
    this.compassBar.className = 'fps-compass-bar';
    this.compassBar.style.cssText =
      'position:fixed;top:8px;left:50%;transform:translateX(-50%);z-index:38;width:320px;height:32px;' +
      'border-radius:10px;background:linear-gradient(180deg,rgba(14,20,34,0.78),rgba(8,12,22,0.88));' +
      'backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);' +
      'border:1px solid rgba(46,230,255,0.28);overflow:hidden;pointer-events:none;' +
      'box-shadow:0 4px 18px rgba(0,0,0,0.42),inset 0 1px 0 rgba(255,255,255,0.08),0 0 16px rgba(46,230,255,0.1)';
    this.compassBar.innerHTML =
      '<div style="position:absolute;inset:0;background:repeating-linear-gradient(90deg,rgba(255,255,255,0.04) 0 1px,transparent 1px 28px);opacity:0.35;pointer-events:none"></div>' +
      '<div style="position:absolute;left:50%;top:0;transform:translateX(-50%);width:2px;height:100%;background:linear-gradient(180deg,rgba(46,230,255,0.15),rgba(46,230,255,0.95),rgba(46,230,255,0.15));z-index:2;box-shadow:0 0 8px rgba(46,230,255,0.65)"></div>' +
      '<div style="position:absolute;left:50%;top:0;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:7px solid #2ee6ff;z-index:3;filter:drop-shadow(0 0 4px rgba(46,230,255,0.8))"></div>';
    this.compassTrack = document.createElement('div');
    this.compassTrack.style.cssText =
      'position:absolute;top:0;left:0;height:100%;display:flex;align-items:center;will-change:transform;z-index:1';
    this.compassBar.appendChild(this.compassTrack);
    const compassLabels = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    for (let rep = 0; rep < 3; rep++) {
      for (let i = 0; i < compassLabels.length; i++) {
        const el = document.createElement('div');
        el.textContent = compassLabels[i]!;
        el.style.cssText =
          'flex:0 0 56px;width:56px;text-align:center;font:700 11px system-ui,sans-serif;color:rgba(232,244,255,0.52);' +
          'letter-spacing:0.6px;transition:color 0.08s,transform 0.08s,font-size 0.08s,text-shadow 0.08s';
        this.compassTrack.appendChild(el);
        this.compassMarkerEls.push(el);
      }
    }
    this.compassSupplyDropEl = document.createElement('div');
    this.compassSupplyDropEl.style.cssText =
      'position:absolute;top:50%;transform:translate(-50%,-50%);z-index:4;display:none;' +
      'font:900 13px system-ui,sans-serif;color:#c8a0ff;text-shadow:0 0 8px rgba(160,112,255,0.95),0 0 2px #000;' +
      'pointer-events:none;line-height:1';
    this.compassSupplyDropEl.textContent = '▼';
    this.compassBar.appendChild(this.compassSupplyDropEl);
    this.compassDropMarkerEl = document.createElement('div');
    this.compassDropMarkerEl.style.cssText =
      'position:absolute;top:2px;transform:translateX(-50%);font:900 13px system-ui;color:#2ee6ff;' +
      'text-shadow:0 0 8px rgba(46,230,255,0.85);pointer-events:none;display:none';
    this.compassDropMarkerEl.textContent = '✕';
    this.compassBar.appendChild(this.compassDropMarkerEl);
    this.compassSafeZoneEl = document.createElement('div');
    this.compassSafeZoneEl.style.cssText =
      'position:absolute;top:3px;transform:translateX(-50%);font:900 10px system-ui;color:#bffaff;' +
      'text-shadow:0 0 8px rgba(46,230,255,0.9),0 1px 2px #001;pointer-events:none;display:none;' +
      'padding:1px 5px;border-radius:999px;background:rgba(18,64,88,0.46);border:1px solid rgba(132,238,255,0.42);letter-spacing:0.08em';
    this.compassSafeZoneEl.textContent = 'SAFE';
    this.compassBar.appendChild(this.compassSafeZoneEl);
    this.compassPoiLabelEl = document.createElement('div');
    this.compassPoiLabelEl.style.cssText =
      'position:absolute;bottom:2px;left:50%;transform:translateX(-50%);z-index:5;' +
      'padding:1px 8px;border-radius:6px;background:rgba(8,12,20,0.55);' +
      'border:1px solid rgba(255,181,74,0.35);color:#ffd89a;font:800 9px system-ui,sans-serif;' +
      'letter-spacing:0.4px;text-transform:uppercase;pointer-events:none;opacity:0;' +
      'transition:opacity 0.15s;text-shadow:0 0 6px rgba(255,181,74,0.45);white-space:nowrap';
    this.compassBar.appendChild(this.compassPoiLabelEl);
    this.hudRoot.appendChild(this.compassBar);

    this.hudStormGrace = document.createElement('div');
    this.hudStormGrace.className = 'fps-storm-grace';
    this.hudStormGrace.style.cssText =
      'position:fixed;top:46px;left:50%;transform:translateX(-50%);z-index:38;padding:5px 14px;border-radius:10px;' +
      'background:rgba(70,18,110,0.48);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);' +
      'border:1px solid rgba(200,120,255,0.4);box-shadow:0 4px 20px rgba(70,18,110,0.32),inset 0 1px 0 rgba(255,255,255,0.06);' +
      'color:#f0e0ff;font:800 12px system-ui,sans-serif;pointer-events:none;opacity:0;transition:opacity 0.35s';
    this.hudStormGrace.textContent = 'Storm closes in 14s';
    this.hudRoot.appendChild(this.hudStormGrace);

    this.hudStormGraceRing = document.createElement('div');
    this.hudStormGraceRing.style.cssText =
      'position:fixed;top:42px;left:50%;transform:translateX(-50%);z-index:37;width:52px;height:52px;' +
      'border-radius:50%;border:2px solid rgba(180,100,255,0.45);pointer-events:none;opacity:0;' +
      'box-sizing:border-box;transition:opacity 0.35s';
    this.hudRoot.appendChild(this.hudStormGraceRing);

    this.damageNumbers = new FpsDamageNumbers(this.hudRoot);
    this.hitFeedback = new FpsHitFeedback(this.hudRoot);
    this.applyGraphics({
      reducedMotion: this.graphicsState.reducedMotion,
      hitFeedbackIntensity: this.graphicsState.hitFeedbackIntensity,
      hudScale: this.graphicsState.hudScale,
    });

    this.selectedOutfitHue = this.loadOutfitHue();
    this.playerCharacter = this.buildPlayerCharacter();
    this.playerAnimator = new FpsCharacterAnimator(this.playerCharacter);
    this.applyOutfitHueToCharacter(this.selectedOutfitHue);
    this.gliderMesh = buildGliderMesh(this.selectedOutfitHue);
    this.gliderMesh.visible = false;
    this.gliderMesh.position.set(0, 1.2, 0);
    this.playerCharacter.add(this.gliderMesh);
    this.playerCharacter.visible = false;
    this.scene.add(this.playerCharacter);

    this.playerShadowBlob = new THREE.Mesh(new THREE.CircleGeometry(0.54, 28), this.playerShadowMat);
    this.playerShadowBlob.rotation.x = -Math.PI / 2;
    this.playerShadowBlob.renderOrder = -2;
    this.playerShadowBlob.visible = false;
    this.built.sceneRoot.add(this.playerShadowBlob);

    const pedestalGroup = new THREE.Group();
    pedestalGroup.position.set(0, 0.17, 0);

    const baseMesh = new THREE.Mesh(
      new THREE.CylinderGeometry(0.92, 1.05, 0.3, 32),
      new THREE.MeshStandardMaterial({
        color: 0x1d273a,
        roughness: 0.22,
        metalness: 0.88,
        emissive: 0x0c121e,
        emissiveIntensity: 0.2,
      }),
    );
    baseMesh.castShadow = true;
    baseMesh.receiveShadow = true;
    pedestalGroup.add(baseMesh);

    // Glowing neon rim ring
    this.lobbyPedestalRingMat = new THREE.MeshStandardMaterial({
      color: 0x00f3ff,
      emissive: 0x00f3ff,
      emissiveIntensity: 1.6,
    });
    const ringMesh = new THREE.Mesh(
      new THREE.CylinderGeometry(0.94, 0.94, 0.05, 32),
      this.lobbyPedestalRingMat,
    );
    ringMesh.position.y = 0.13;
    pedestalGroup.add(ringMesh);

    // Top metal detail plate
    const topPlate = new THREE.Mesh(
      new THREE.CylinderGeometry(0.88, 0.88, 0.02, 32),
      new THREE.MeshStandardMaterial({
        color: 0x4a5a70,
        roughness: 0.3,
        metalness: 0.75,
      }),
    );
    topPlate.position.y = 0.151;
    topPlate.receiveShadow = true;
    pedestalGroup.add(topPlate);

    this.lobbyPedestal = pedestalGroup;
    this.lobbyPedestal.visible = false;
    this.scene.add(this.lobbyPedestal);

    this.lobbyShowcaseRimLight = new THREE.PointLight(0x66f5ff, 0, 4.2);
    this.lobbyShowcaseRimLight.position.set(-1.35, 1.75, 1.65);
    this.lobbyShowcaseRimLight.visible = false;
    this.scene.add(this.lobbyShowcaseRimLight);

    this.player.setIntroFrozen(true);
    this.player.pitchObject.remove(this.camera);
    this.scene.add(this.camera);

    this.lobbyOverlay = document.createElement('div');
    this.lobbyOverlay.className = 'fps-lobby-overlay';
    this.lobbyOverlay.style.cssText =
      'position:fixed;inset:0;z-index:180;display:flex;flex-direction:column;align-items:center;justify-content:center;' +
      'background:linear-gradient(180deg,rgba(8,12,28,0.55) 0%,rgba(18,8,42,0.72) 45%,rgba(6,10,22,0.88) 100%);' +
      'font-family:"Trebuchet MS","Segoe UI",system-ui,sans-serif;color:#fff;pointer-events:auto;';
    this.lobbyOverlay.innerHTML =
      '<div style="position:absolute;top:28px;left:50%;transform:translateX(-50%);font:900 13px system-ui;letter-spacing:0.28em;' +
      'background:linear-gradient(90deg,#7ef9ff,#9b59ff);-webkit-background-clip:text;-webkit-text-fill-color:transparent">STORM SURGE</div>' +
      '<div style="margin-bottom:28px;padding:18px 28px;border-radius:14px;background:rgba(12,18,36,0.82);' +
      'border:2px solid rgba(120,180,255,0.45);box-shadow:0 8px 32px rgba(0,0,0,0.45);text-align:center;min-width:min(92vw,380px)">' +
      '<div id="lobby-mode-title" style="font:800 22px system-ui;letter-spacing:0.04em;margin-bottom:8px">Battle Royale · Solo</div>' +
      '<button id="lobby-mode-toggle" type="button" style="margin-bottom:10px;padding:6px 14px;border-radius:8px;background:rgba(12,22,38,0.85);border:1px solid rgba(120,180,255,0.55);color:#b8d4ff;font:700 11px system-ui;letter-spacing:0.08em;cursor:pointer">Mode: Solo</button>' +
      '<button id="lobby-ranked-toggle" type="button" style="margin-bottom:10px;padding:6px 14px;border-radius:8px;background:rgba(12,22,38,0.85);border:1px solid rgba(255,96,96,0.45);color:#ffb8b8;font:700 11px system-ui;letter-spacing:0.08em;cursor:pointer">Ranked: OFF</button>' +
      '<div id="lobby-ranked-badge" style="display:none;margin-bottom:8px"></div>' +
      '<button id="lobby-ltm-toggle" type="button" style="margin-bottom:10px;padding:6px 14px;border-radius:8px;background:rgba(12,22,38,0.85);border:1px solid rgba(120,180,255,0.55);color:#b8d4ff;font:700 11px system-ui;letter-spacing:0.08em;cursor:pointer">LTM: Auto</button>' +
      '<div id="lobby-ltm-banner" style="margin-bottom:10px;padding:8px 16px;border-radius:999px;font:700 11px system-ui;letter-spacing:0.06em;line-height:1.45;max-width:min(88vw,340px)"></div>' +
      '<div id="lobby-rank-badge" style="margin-bottom:8px"></div>' +
      '<div id="lobby-arena-progress" style="margin-bottom:10px;width:100%;max-width:280px;margin-left:auto;margin-right:auto">' +
      '<div style="height:8px;border-radius:4px;background:rgba(255,255,255,0.1);overflow:hidden;margin-bottom:5px">' +
      '<div id="lobby-arena-fill" style="height:100%;width:0%;background:linear-gradient(90deg,#cd7f32,#ffd54a);transition:width 0.25s ease-out"></div>' +
      '</div>' +
      '<div id="lobby-arena-text" style="font:700 10px system-ui;color:#9ec4ff;letter-spacing:0.06em"></div>' +
      '</div>' +
      '<div id="lobby-status" style="font:700 14px system-ui;color:#9ec4ff;letter-spacing:0.08em;margin-bottom:6px">Searching<span id="lobby-dots"></span></div>' +
      '<div id="lobby-count" style="font:700 15px system-ui;color:#b8d4ff;letter-spacing:0.06em">100 players</div>' +
      '<div style="margin-top:10px;font:600 11px system-ui;color:#8899bb;text-transform:uppercase;letter-spacing:0.12em">Drop in · loot · build · survive</div>' +
      '<div id="bp-panel" style="margin-top:14px;padding:12px 14px;border-radius:10px;background:rgba(0,0,0,0.28);border:1px solid rgba(255,200,60,0.35);text-align:left">' +
      '<div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px">' +
      '<div style="font:800 11px system-ui;color:#ffd54a;letter-spacing:0.12em;text-transform:uppercase">Season 1 Battle Pass</div>' +
      '<div id="bp-tier" style="font:800 12px system-ui;color:#fff">Tier 1</div>' +
      '</div>' +
      '<div style="height:10px;border-radius:5px;background:rgba(255,255,255,0.1);overflow:hidden">' +
      '<div id="bp-fill" style="height:100%;width:0%;background:linear-gradient(90deg,#ffd54a,#ff9a00);transition:width 0.25s ease-out"></div>' +
      '</div>' +
      '<div id="bp-preview" style="margin-top:8px;font:600 11px system-ui;color:#c8d8ee;line-height:1.4"></div>' +
      '<div id="bp-gallery"></div>' +
      '</div>' +
      '<div id="lobby-daily-wrap"></div>' +
      '<div id="lobby-weekly-wrap"></div>' +
      '</div>' +
      '<div id="outfit-picker" style="margin-bottom:12px;display:flex;flex-direction:column;align-items:center;gap:8px">' +
      '<div style="font:700 11px system-ui;color:#9ec4ff;letter-spacing:0.14em;text-transform:uppercase">Outfit Color</div>' +
      '<div id="outfit-swatches" style="display:flex;gap:10px"></div>' +
      '</div>' +
      '<div id="loadout-picker" style="margin-bottom:18px;display:flex;flex-direction:column;align-items:center;gap:8px">' +
      '<div style="font:700 11px system-ui;color:#9ec4ff;letter-spacing:0.14em;text-transform:uppercase">Loadout</div>' +
      '<div id="loadout-buttons" style="display:flex;gap:8px"></div>' +
      '</div>' +
      '<button id="start-btn" type="button" disabled style="cursor:not-allowed;opacity:0.58;border:none;border-radius:12px;padding:22px 72px;' +
      'font:900 clamp(28px,6vw,42px) system-ui;letter-spacing:0.14em;color:#1a1200;' +
      'background:linear-gradient(180deg,#ffe566 0%,#ffc800 45%,#ff9a00 100%);' +
      'text-shadow:0 1px 0 rgba(255,255,255,0.35);' +
      'box-shadow:0 4px 0 #9b5c00,0 12px 34px rgba(255,180,0,0.28);transition:transform 0.12s ease,filter 0.12s ease,box-shadow 0.12s ease">' +
      'PLAY</button>' +
      '<div style="position:absolute;bottom:24px;left:50%;transform:translateX(-50%);width:min(96vw,920px);' +
      'padding:12px 18px;border-radius:10px;background:rgba(0,0,0,0.48);border:1px solid rgba(255,255,255,0.12);' +
      'text-align:center;font:600 12px system-ui;color:#c8d8ee;line-height:1.7;letter-spacing:0.02em">' +
      'WASD move · Shift sprint · C crouch · Space jump/glide · LMB fire · RMB aim · 1–5 hotbar · Esc settings' +
      '</div>';
    document.body.appendChild(this.lobbyOverlay);
    this.lobbyStatusEl = this.lobbyOverlay.querySelector('#lobby-status') as HTMLDivElement;
    this.lobbyDotsEl = this.lobbyOverlay.querySelector('#lobby-dots') as HTMLSpanElement;
    this.lobbyCountEl = this.lobbyOverlay.querySelector('#lobby-count') as HTMLDivElement;
    this.lobbyStartBtn = this.lobbyOverlay.querySelector('#start-btn') as HTMLButtonElement;
    this.lobbyBpTierEl = this.lobbyOverlay.querySelector('#bp-tier') as HTMLDivElement;
    this.lobbyBpFillEl = this.lobbyOverlay.querySelector('#bp-fill') as HTMLDivElement;
    this.lobbyBpPreviewEl = this.lobbyOverlay.querySelector('#bp-preview') as HTMLDivElement;
    this.lobbyBpGalleryEl = this.lobbyOverlay.querySelector('#bp-gallery') as HTMLDivElement;
    this.lobbyRankBadgeEl = this.lobbyOverlay.querySelector('#lobby-rank-badge') as HTMLDivElement;
    this.lobbyArenaFillEl = this.lobbyOverlay.querySelector('#lobby-arena-fill') as HTMLDivElement;
    this.lobbyArenaTextEl = this.lobbyOverlay.querySelector('#lobby-arena-text') as HTMLDivElement;
    this.lobbyModeTitleEl = this.lobbyOverlay.querySelector('#lobby-mode-title') as HTMLDivElement;
    this.lobbyModeToggleEl = this.lobbyOverlay.querySelector('#lobby-mode-toggle') as HTMLButtonElement;
    this.lobbyRankedToggleEl = this.lobbyOverlay.querySelector('#lobby-ranked-toggle') as HTMLButtonElement;
    this.lobbyRankedBadgeEl = this.lobbyOverlay.querySelector('#lobby-ranked-badge') as HTMLDivElement;
    this.lobbyLtmBannerEl = this.lobbyOverlay.querySelector('#lobby-ltm-banner') as HTMLDivElement;
    this.lobbyLtmToggleEl = this.lobbyOverlay.querySelector('#lobby-ltm-toggle') as HTMLButtonElement;
    this.markLobbyReady();
    this.lobbyStartBtn.addEventListener('click', () => this.startMatchFromLobby());
    this.loadoutKind = this.loadLoadoutKind();
    this.lobbyModeToggleEl.addEventListener('click', () => this.toggleLobbyGameMode());
    this.lobbyRankedToggleEl?.addEventListener('click', () => this.toggleRankedMode());
    this.lobbyLtmToggleEl.addEventListener('click', () => this.toggleLobbyLtm());
    this.applyLobbyGameModeUi();
    this.applyRankedLobbyUi();
    this.applyLobbyLtmUi();
    this.refreshLobbyBattlePassUi();
    this.buildLobbyOutfitSwatches();
    this.buildLobbyLoadoutPicker();
    this.applyLoadoutPreference(this.loadoutKind, false);
    const startBtn = this.lobbyStartBtn;
    this.lobbyOverlay.addEventListener('mousedown', this.unlockLobbyAudioOnce);
    startBtn?.addEventListener('mouseenter', () => {
      this.lobbyStartBtnHovered = true;
      this.applyLobbyStartButtonVisuals();
    });
    startBtn?.addEventListener('mouseleave', () => {
      this.lobbyStartBtnHovered = false;
      this.lobbyStartBtnPressed = false;
      this.applyLobbyStartButtonVisuals();
    });
    startBtn?.addEventListener('mousedown', () => {
      this.lobbyStartBtnPressed = true;
      this.applyLobbyStartButtonVisuals();
    });
    startBtn?.addEventListener('mouseup', () => {
      this.lobbyStartBtnPressed = false;
      this.applyLobbyStartButtonVisuals();
    });
    this.markLobbyReady();

    // Phase 12 — Skin Locker (opens via lobby button)
    this.skinLocker = new FpsSkinLocker(document.body);
    this.skinLocker.onLoadoutChange(() => {
      this.selectedOutfitHue = this.skinLocker.getSkinHue();
      this.applyOutfitHueToCharacter(this.selectedOutfitHue);
    });
    // Add a "Locker" button to the lobby overlay
    const lockerBtn = document.createElement('button');
    lockerBtn.type = 'button';
    lockerBtn.textContent = 'LOCKER';
    lockerBtn.style.cssText = 'position:absolute;top:28px;right:24px;padding:8px 18px;border-radius:8px;' +
      'background:rgba(12,22,38,0.85);border:1px solid rgba(120,180,255,0.55);color:#b8d4ff;' +
      'font:700 11px system-ui;letter-spacing:0.10em;cursor:pointer;z-index:10';
    lockerBtn.addEventListener('click', () => this.skinLocker.show());
    this.lobbyOverlay.appendChild(lockerBtn);
    // Phase 14 — Modes picker button in lobby
    const modesBtn = document.createElement('button');
    modesBtn.type = 'button';
    modesBtn.textContent = 'MODES';
    modesBtn.style.cssText = 'position:absolute;top:28px;right:116px;padding:8px 18px;border-radius:8px;' +
      'background:rgba(12,22,38,0.85);border:1px solid rgba(120,180,255,0.55);color:#b8d4ff;' +
      'font:700 11px system-ui;letter-spacing:0.10em;cursor:pointer;z-index:10';
    modesBtn.addEventListener('click', () => this.ltmPicker.show());
    this.lobbyOverlay.appendChild(modesBtn);

    const careerBtn = document.createElement('button');
    careerBtn.type = 'button';
    careerBtn.textContent = 'CAREER';
    careerBtn.style.cssText = 'position:absolute;top:28px;left:24px;padding:8px 18px;border-radius:8px;' +
      'background:rgba(12,22,38,0.85);border:1px solid rgba(255,213,74,0.55);color:#ffd54a;' +
      'font:700 11px system-ui;letter-spacing:0.10em;cursor:pointer;z-index:10';
    careerBtn.addEventListener('click', () => FpsCareerStats.showPanel());
    this.lobbyOverlay.appendChild(careerBtn);

    const inviteBtn = document.createElement('button');
    inviteBtn.type = 'button';
    inviteBtn.textContent = 'COPY INVITE';
    inviteBtn.style.cssText = 'position:absolute;top:28px;left:108px;padding:8px 14px;border-radius:8px;' +
      'background:rgba(12,22,38,0.85);border:1px solid rgba(120,255,180,0.55);color:#9bffca;' +
      'font:700 10px system-ui;letter-spacing:0.08em;cursor:pointer;z-index:10';
    inviteBtn.addEventListener('click', () => this.copyInviteLink(inviteBtn));
    this.lobbyOverlay.appendChild(inviteBtn);

    // Phase 18 — attach live-event countdown to lobby overlay
    this.liveEvent.attachCountdownTo(this.lobbyOverlay);
    this.processLobbyDailyLogin();
    this.scheduleTimeout(() => this.markLobbyReady(), 4200);

    this.ui = document.createElement('div');
    this.ui.style.cssText =
      'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;z-index:20;font:600 15px system-ui,sans-serif;color:#fff;text-shadow:0 1px 4px #000;text-align:center;padding:24px;opacity:0;';
    this.ui.innerHTML =
      '<div><span style="opacity:0.95">Click or Enter to deploy early</span></div>';
    mount.appendChild(this.ui);

    this.crosshair = document.createElement('div');
    this.crosshair.style.cssText =
      'position:fixed;left:50%;top:50%;width:0;height:0;transform:translate(-50%,-50%);transform-origin:center center;pointer-events:none;z-index:19;opacity:0.92;';
    this.crosshairDot = document.createElement('div');
    this.crosshairDot.style.cssText =
      'position:absolute;left:50%;top:50%;width:3px;height:3px;margin:-1.5px 0 0 -1.5px;border-radius:50%;' +
      'background:rgba(255,255,255,0.96);box-shadow:0 0 4px rgba(255,255,255,0.45),0 0 1px rgba(0,0,0,0.6);';
    this.crosshair.appendChild(this.crosshairDot);
    this.crosshairRing = document.createElement('div');
    this.crosshairRing.style.cssText =
      'position:absolute;left:50%;top:50%;width:18px;height:18px;margin:-9px 0 0 -9px;border-radius:50%;' +
      'border:1.5px solid rgba(255,255,255,0.92);box-shadow:0 0 4px rgba(255,255,255,0.35);display:none;';
    this.crosshair.appendChild(this.crosshairRing);
    const lineDirs: Array<'top' | 'bottom' | 'left' | 'right'> = ['top', 'bottom', 'left', 'right'];
    for (const dir of lineDirs) {
      const line = document.createElement('div');
      line.dataset.dir = dir;
      line.style.cssText =
        'position:absolute;background:rgba(255,255,255,0.96);box-shadow:0 0 4px rgba(255,255,255,0.35),0 0 1px rgba(0,0,0,0.55);border-radius:0.5px;';
      this.crosshair.appendChild(line);
      this.crosshairLines.push(line);
    }
    mount.appendChild(this.crosshair);

    this.sniperScope = document.createElement('div');
    this.sniperScope.style.cssText = [
      'position:fixed',
      'inset:0',
      'z-index:18',
      'pointer-events:none',
      'display:none',
      'background:radial-gradient(circle at center, transparent 0 16%, rgba(0,0,0,0.12) 18%, rgba(0,0,0,0.72) 48%, rgba(0,0,0,0.94) 72%)',
      'box-shadow:inset 0 0 80px rgba(0,0,0,0.85)',
    ].join(';');
    const scopeRing = document.createElement('div');
    scopeRing.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'width:min(72vmin,520px)',
      'height:min(72vmin,520px)',
      'transform:translate(-50%,-50%)',
      'border-radius:50%',
      'border:3px solid rgba(20,20,24,0.92)',
      'box-shadow:0 0 0 9999px rgba(0,0,0,0.55), inset 0 0 24px rgba(0,0,0,0.45)',
    ].join(';');
    const scopeCross = document.createElement('div');
    scopeCross.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'width:18px',
      'height:18px',
      'transform:translate(-50%,-50%)',
      'background:radial-gradient(circle, rgba(255,80,80,0.95) 0 2px, transparent 2.5px)',
      'filter:drop-shadow(0 0 2px rgba(0,0,0,0.6))',
    ].join(';');
    this.sniperScopeReticle = scopeCross;
    this.sniperScope.appendChild(scopeRing);
    this.sniperScope.appendChild(scopeCross);
    mount.appendChild(this.sniperScope);

    this.lootToast = document.createElement('div');
    this.lootToast.className = 'fps-hud-toast-card';
    this.lootToast.style.cssText = [
      'position:fixed',
      'left:50%',
      'top:38%',
      'transform:translate(-50%,-50%)',
      'z-index:91',
      'padding:14px 22px',
      'border-radius:12px',
      'font:800 15px system-ui,sans-serif',
      'letter-spacing:0.02em',
      'pointer-events:none',
      'display:none',
      'text-align:center',
    ].join(';');
    this.lootToast.style.setProperty('--fps-toast-rail', '#ffd54a');
    this.lootToast.style.setProperty('--fps-toast-glow', 'rgba(255,213,74,0.24)');
    document.body.appendChild(this.lootToast);
    this.pickupCard = createPickupCard(document.body);
    this.rarityStinger = createRarityStinger(document.body);

    this.touchControls = new FpsTouchControls(mount, {
      onMoveKey: (code, down) => this.player.injectKey(code, down),
      onLook: (dx, dy) => this.player.injectLook(dx, dy),
      onFire: (down) => {
        if (down) this.onMouseDown({ button: 0, preventDefault: () => {} } as MouseEvent);
        else this.onMouseUp({ button: 0 } as MouseEvent);
      },
      onReload: () => {
        this.weapon.tryReload();
      },
      onJump: () => this.player.injectJumpPulse(),
      onBuild: () => {
        this.hotbar.setSelected(3);
        this.syncHotbarFromSlot();
      },
    });
    if (!localStorage.getItem('stormsurge_fps_touch_aim_assist') && this.touchControls.isTouchDevice()) {
      this.applyGraphics({ touchAimAssist: true });
    }

    this.renderer.domElement.addEventListener('mousedown', this.onMouseDown);
    this.renderer.domElement.addEventListener('mouseup', this.onMouseUp);
    this.renderer.domElement.addEventListener('wheel', this.onWheel, { passive: true });
    this.renderer.domElement.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('mouseup', this.onWindowMouseUp);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('keydown', this.onGlobalKeyDown);
    window.addEventListener('keyup', this.onGlobalKeyUp);
    window.addEventListener('mousemove', this.onWindowMouseMove);
    window.addEventListener('pointerdown', this.onLobbyPointerDown);
    window.addEventListener('pointermove', this.onLobbyPointerMove);
    window.addEventListener('pointerup', this.onLobbyPointerEnd);
    window.addEventListener('pointercancel', this.onLobbyPointerEnd);
    document.addEventListener('pointerlockchange', this.onPointerLockChange);

    this.renderer.setAnimationLoop(this.tick);
  }

  private scheduleTimeout(fn: () => void, ms: number): number {
    const id = window.setTimeout(() => {
      this.scheduledTimeouts.delete(id);
      if (this.running) fn();
    }, ms);
    this.scheduledTimeouts.add(id);
    return id;
  }

  private clearScheduledTimeout(id: number): void {
    window.clearTimeout(id);
    this.scheduledTimeouts.delete(id);
  }

  private trackLootEffect(effect: LootEffectHandle | null): void {
    if (!effect) return;
    this.lootEffectHandles.add(effect);
    this.scheduleTimeout(() => {
      effect.disposeEffect?.();
      this.lootEffectHandles.delete(effect);
    }, 1200);
  }

  private trackDomEffect(effect: DomEffectHandle): void {
    this.domEffectHandles.add(effect);
    this.scheduleTimeout(() => this.domEffectHandles.delete(effect), 900);
  }

  private collectAmbientDecorProps(): void {
    this.ambientDecorProps.length = 0;
    this.built.sceneRoot.traverse((child) => {
      if (child.userData.ambientPulse || child.userData.ambientSpin) {
        this.ambientDecorProps.push(child);
      }
    });
  }

  private updateAmbientDecor(dt: number): void {
    this.ambientDecorAcc += dt;
    if (this.ambientDecorAcc < 0.1) return;
    const step = this.ambientDecorAcc;
    this.ambientDecorAcc = 0;
    const t = performance.now() * 0.001;

    for (const prop of this.ambientDecorProps) {
      const spin = prop.userData.ambientSpin as { speedY?: number } | undefined;
      if (spin?.speedY) {
        prop.rotation.y += spin.speedY * step;
      }

      const pulse = prop.userData.ambientPulse as {
        material?: THREE.Material;
        light?: THREE.Light;
        base?: number;
        amount?: number;
        speed?: number;
        phase?: number;
      } | undefined;
      if (!pulse) continue;

      const wave = Math.sin(t * (pulse.speed ?? 1.2) + (pulse.phase ?? 0));
      const value = (pulse.base ?? 1) + wave * (pulse.amount ?? 0.15);
      if (pulse.light) pulse.light.intensity = Math.max(0, value);
      const mat = pulse.material;
      if (mat && 'emissiveIntensity' in mat && typeof mat.emissiveIntensity === 'number') {
        mat.emissiveIntensity = Math.max(0, value);
      }
      if (mat && mat.transparent) {
        mat.opacity = Math.max(0.08, Math.min(1, value));
      }
    }
  }

  private unlockLobbyAudioOnce = (): void => {
    if (!this.audioUnlocked) {
      this.audio.unlock();
      this.audioUnlocked = true;
    }
    this.audio.playLobbyMusic();
  };

  private processLobbyDailyLogin(): void {
    const claimed = FpsBattlePass.processDailyLogin();
    if (claimed) this.refreshLobbyBattlePassUi();
  }

  private copyInviteLink(btn?: HTMLButtonElement): void {
    const url = new URL(window.location.href);
    url.searchParams.set('invite', '1');
    const link = url.toString();
    const onCopied = (): void => {
      this.showPickupToast('Invite link copied!', '#7ef9ff');
      if (btn) {
        const prev = btn.textContent;
        btn.textContent = 'COPIED!';
        this.scheduleTimeout(() => {
          if (btn.textContent === 'COPIED!') btn.textContent = prev ?? 'COPY INVITE';
        }, 1800);
      }
    };
    if (navigator.clipboard?.writeText) {
      void navigator.clipboard.writeText(link).then(onCopied).catch(() => {
        this.showPickupToast('Copy failed — share manually', '#ffb347');
      });
      return;
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = link;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
      onCopied();
    } catch {
      this.showPickupToast('Copy failed — share manually', '#ffb347');
    }
  }

  private refreshLobbyBattlePassUi(): void {
    const xp = FpsBattlePass.loadXp();
    const tier = FpsBattlePass.getTier(xp);
    const progress = FpsBattlePass.getProgressInTier(xp);
    const atMax = tier >= BP_TIER_COUNT && xp >= BP_TIER_COUNT * BP_XP_PER_TIER;
    const pct = atMax ? 100 : (progress / BP_XP_PER_TIER) * 100;
    this.lobbyBpTierEl.textContent = atMax ? `Tier ${BP_TIER_COUNT} (MAX)` : `Tier ${tier}`;
    this.lobbyBpFillEl.style.width = `${pct}%`;
    this.lobbyBpPreviewEl.textContent = FpsBattlePass.getNextRewardPreview();
    this.lobbyBpGalleryEl.innerHTML = FpsBattlePass.getClaimedGalleryHtml();

    const career = FpsCareerStats.load();
    const rank = FpsCareerStats.getArenaRank(career.arenaPoints);
    this.lobbyRankBadgeEl.innerHTML = this.arenaRankBadgeHtml(rank, career.arenaPoints);

    const arenaProg = FpsCareerStats.getArenaRankProgress(career.arenaPoints);
    const rankColor = FpsCareerStats.arenaRankColor(arenaProg.rank);
    const nextColor = arenaProg.nextRank ? FpsCareerStats.arenaRankColor(arenaProg.nextRank) : rankColor;
    this.lobbyArenaFillEl.style.width = `${arenaProg.pct}%`;
    this.lobbyArenaFillEl.style.background = `linear-gradient(90deg,${rankColor},${nextColor})`;
    if (arenaProg.nextRank) {
      const nextThreshold =
        arenaProg.rank === 'Bronze' ? 100 : arenaProg.rank === 'Silver' ? 300 : 600;
      this.lobbyArenaTextEl.textContent =
        `${career.arenaPoints} / ${nextThreshold} AP to ${arenaProg.nextRank}`;
    } else {
      this.lobbyArenaTextEl.textContent = `${career.arenaPoints} AP · ${arenaProg.rank} (MAX)`;
    }

    const dailyWrap = this.lobbyOverlay.querySelector('#lobby-daily-wrap');
    if (dailyWrap) {
      dailyWrap.innerHTML =
        FpsDailyChallenges.getLobbySummary() + FpsDailyChallenges.getWeeklyLobbySummary();
    }
    const weeklyWrap = this.lobbyOverlay.querySelector('#lobby-weekly-wrap');
    if (weeklyWrap) weeklyWrap.innerHTML = FpsWeeklyBoard.getLobbySummary();
  }

  private arenaRankBadgeHtml(rank: ArenaRank, arenaPoints: number, earned?: number): string {
    const color = FpsCareerStats.arenaRankColor(rank);
    const earnedLine =
      earned != null && earned > 0
        ? `<div style="margin-top:6px;font:700 11px system-ui;color:#9ec4ff">+${earned} Arena Points</div>`
        : '';
    return (
      `<span style="display:inline-block;padding:5px 12px;border-radius:8px;font:800 11px system-ui;letter-spacing:0.1em;color:#0a0e18;background:${color};box-shadow:0 2px 10px rgba(0,0,0,0.35)">${rank.toUpperCase()} · ${arenaPoints} AP</span>` +
      earnedLine
    );
  }

  private toggleLobbyGameMode(): void {
    const idx = GAME_MODE_CYCLE.indexOf(this.gameMode);
    this.gameMode = GAME_MODE_CYCLE[(idx + 1) % GAME_MODE_CYCLE.length]!;
    this.applyLobbyGameModeUi();
  }

  private loadRankedMode(): boolean {
    try {
      return localStorage.getItem(RANKED_STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  private saveRankedMode(): void {
    try {
      localStorage.setItem(RANKED_STORAGE_KEY, this.rankedMode ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  }

  private toggleRankedMode(): void {
    this.rankedMode = !this.rankedMode;
    this.saveRankedMode();
    this.applyRankedLobbyUi();
    this.applyBotDifficulty();
  }

  private applyRankedLobbyUi(): void {
    if (!this.lobbyRankedToggleEl) return;
    this.lobbyRankedToggleEl.textContent = this.rankedMode ? 'Ranked: ON' : 'Ranked: OFF';
    this.lobbyRankedToggleEl.style.borderColor = this.rankedMode
      ? 'rgba(255,72,72,0.85)'
      : 'rgba(255,96,96,0.45)';
    this.lobbyRankedToggleEl.style.color = this.rankedMode ? '#ff8888' : '#ffb8b8';
    if (!this.lobbyRankedBadgeEl) return;
    if (this.rankedMode) {
      this.lobbyRankedBadgeEl.style.display = 'block';
      this.lobbyRankedBadgeEl.innerHTML =
        '<span style="display:inline-block;padding:5px 12px;border-radius:8px;font:800 11px system-ui;letter-spacing:0.12em;color:#fff;' +
        'background:linear-gradient(180deg,#ff4444 0%,#cc1818 100%);box-shadow:0 2px 12px rgba(200,24,24,0.5)">RANKED</span>';
    } else {
      this.lobbyRankedBadgeEl.style.display = 'none';
      this.lobbyRankedBadgeEl.innerHTML = '';
    }
  }

  private applyLobbyLtmUi(): void {
    this.dailyLtm = getActiveLtm();
    const ltm = this.dailyLtm;
    const override = getLtmOverride();
    const daily = getDailyLtm();
    const toggleLabel = override ? `LTM: ${getLtmById(override).name}` : 'LTM: Auto';
    this.lobbyLtmToggleEl.textContent = toggleLabel;
    const accent = override ? getLtmById(override).color : '#b8d4ff';
    this.lobbyLtmToggleEl.style.borderColor = `${accent}aa`;
    this.lobbyLtmToggleEl.style.color = accent;
    const rgb = ltm.color;
    this.lobbyLtmBannerEl.style.background = `${rgb}33`;
    this.lobbyLtmBannerEl.style.border = `1px solid ${rgb}aa`;
    this.lobbyLtmBannerEl.style.color = '#f4f8ff';
    const autoHint =
      override == null
        ? `<span style="display:block;margin-top:2px;font:600 9px system-ui;color:#8899bb;letter-spacing:0.04em">Daily rotation · today ${daily.name}</span>`
        : '';
    this.lobbyLtmBannerEl.innerHTML =
      `<span style="font:800 12px system-ui;color:${rgb};letter-spacing:0.1em">LTM · ${ltm.name}</span>` +
      `<span style="display:block;margin-top:3px;font:600 10px system-ui;color:#c8d8ee;letter-spacing:0.04em">${ltm.description}</span>` +
      autoHint;
  }

  private toggleLobbyLtm(): void {
    const current: LtmLobbySelection = getLtmOverride() ?? 'auto';
    const idx = LTM_LOBBY_CYCLE.indexOf(current);
    const next = LTM_LOBBY_CYCLE[(idx + 1) % LTM_LOBBY_CYCLE.length]!;
    setLtmOverride(next === 'auto' ? null : next);
    this.applyLobbyLtmUi();
  }

  private applyDailyLtmModifiers(): void {
    this.dailyLtm = getActiveLtm();
    const ltm = this.dailyLtm;
    this.buildEnabled = true;
    this.siphonAmount = 25;
    this.buildTurboCd = this.BUILD_TURBO_CD_BASE;
    FpsGrenades.setExplosiveMultiplier({ radius: 1, damage: 1 });
    this.storm.setShrinkPerSecond(STORM_SHRINK);
    this.storm.setDamageMultiplier(1);
    this.hotbar.setBuildDisabled(false);

    switch (ltm.id) {
      case 'blitz':
        this.storm.setShrinkPerSecond(STORM_SHRINK * 1.8);
        break;
      case 'zero_build':
        this.buildEnabled = false;
        this.hotbar.setBuildDisabled(true);
        this.buildPlacer.setEnabled(false);
        break;
      case 'explosives':
        FpsGrenades.setExplosiveMultiplier({ radius: 1.3, damage: 1.5 });
        break;
      case 'super_siphon':
        this.siphonAmount = 40;
        break;
      case 'creative':
        this.mats = 999;
        this.hudMats.textContent = `Mats ${this.mats}`;
        this.storm.setDamageMultiplier(0);
        this.buildTurboCd = this.BUILD_TURBO_CD_BASE * 0.5;
        this.buildEnabled = true;
        this.hotbar.setBuildDisabled(false);
        this.buildPlacer.setEnabled(true);
        break;
    }

    this.hudLtmChip.textContent = `LTM: ${ltm.name}`;
    this.hudLtmChip.style.display = 'block';
    this.hudLtmChip.style.background = `${ltm.color}cc`;
    this.hudLtmChip.style.borderColor = `${ltm.color}ee`;
  }

  private applyLobbyGameModeUi(): void {
    const cfg = GAME_MODE_LOBBY[this.gameMode];
    this.lobbyModeTitleEl.textContent = cfg.title;
    this.lobbyModeToggleEl.textContent = cfg.toggle;
    const squad = this.gameMode !== 'solo';
    this.lobbyModeToggleEl.style.borderColor = squad
      ? 'rgba(54,227,107,0.65)'
      : 'rgba(120,180,255,0.55)';
    this.lobbyModeToggleEl.style.color = squad ? '#9bff6a' : '#b8d4ff';
    if (this.lobbyReady) {
      this.lobbyCountEl.textContent = `${this.lobbyPlayerCount()} players`;
    }
  }

  private lobbyPlayerCount(): number {
    return GAME_MODE_LOBBY[this.gameMode].players;
  }

  private isSquadMode(): boolean {
    return this.gameMode !== 'solo';
  }

  private clearTeammates(): void {
    for (const tm of this.teammates) tm.dispose();
    this.teammates = [];
    this.reviveTarget = null;
    this.teammateReviveProgress = 0;
    if (this.carriedTeammate) {
      this.carriedTeammate.setCarried(false);
      this.carriedTeammate = null;
    }
    this.teammateCarryProgress = 0;
    this.player?.setCarrying(false);
  }

  private spawnTeammates(): void {
    this.clearTeammates();
    if (!this.isSquadMode()) return;
    const b = this.player.body.position;
    const spawns: { x: number; z: number; name: string }[] =
      this.gameMode === 'duos'
        ? [{ x: b.x + 2.5, z: b.z + 1.5, name: 'SquadMate' }]
        : [
            { x: b.x + 2.5, z: b.z + 1.5, name: 'SquadMate' },
            { x: b.x - 2.5, z: b.z + 1.5, name: 'Echo' },
          ];
    for (const s of spawns) {
      this.teammates.push(
        new FpsTeammate(this.built.sceneRoot, this.bots, s.x, s.z, s.name),
      );
    }
    this.teammateKnockPinged = spawns.map(() => false);
    this.squadCallouts.reset();
  }

  private getNearestKnockedTeammate(): FpsTeammate | null {
    const b = this.player.body.position;
    let nearest: FpsTeammate | null = null;
    let bestDist = Infinity;
    for (const tm of this.teammates) {
      if (!tm.isKnocked()) continue;
      if (!tm.isWithinReviveRange(b.x, b.z, REVIVE_RADIUS)) continue;
      const pos = tm.getPosition(new THREE.Vector3());
      const d2 = (pos.x - b.x) ** 2 + (pos.z - b.z) ** 2;
      if (d2 < bestDist) {
        bestDist = d2;
        nearest = tm;
      }
    }
    return nearest;
  }

  private getTeammateNeedingReboot(): FpsTeammate | null {
    return this.teammates.find((tm) => tm.needsReboot()) ?? null;
  }

  private updateLobbyMatchmaking(dt: number): void {
    this.lobbyMatchT += dt;
    const t = this.lobbyMatchT;
    const dotCount = 1 + (Math.floor(t * 2.4) % 3);
    this.lobbyDotsEl.textContent = '.'.repeat(dotCount);
    this.lobbyDotsEl.style.opacity = String(0.55 + Math.sin(t * 5.2) * 0.35);

    if (t < 1.15) {
      if (this.lobbyStatusEl.firstChild?.textContent !== 'Searching') {
        this.lobbyStatusEl.textContent = 'Searching';
        this.lobbyStatusEl.appendChild(this.lobbyDotsEl);
      }
      this.lobbyDotsEl.style.display = 'inline';
      return;
    }

    if (t < 1.35) {
      this.lobbyStatusEl.textContent = 'Match Found!';
      this.lobbyDotsEl.style.display = 'none';
      return;
    }

    this.lobbyDotsEl.style.display = 'none';
    const countT = Math.min(1, (t - 1.35) / 2);
    const players = Math.round(100 - countT * 89);
    this.lobbyCountEl.textContent = `${players} players`;

    if (countT >= 1) this.markLobbyReady();
  }

  private markLobbyReady(): void {
    if (!this.lobbyActive) return;
    this.lobbyReady = true;
    this.lobbyStartBtn.disabled = false;
    this.applyLobbyStartButtonVisuals();
    this.lobbyCountEl.textContent = `${this.lobbyPlayerCount()} players`;
    this.lobbyStatusEl.innerHTML =
      '<span style="display:inline-flex;align-items:center;gap:8px;color:#f8fbff;text-shadow:0 0 16px rgba(126,249,255,0.45)">' +
      '<span style="padding:3px 9px;border-radius:999px;background:linear-gradient(90deg,rgba(126,249,255,0.22),rgba(255,213,74,0.24));' +
      'border:1px solid rgba(255,255,255,0.34);box-shadow:0 0 18px rgba(126,249,255,0.28);font:900 10px system-ui;letter-spacing:0.16em;color:#ffe66d">DROP READY</span>' +
      '<span>Ready to drop!</span>' +
      '</span>';
  }

  private applyLobbyStartButtonVisuals(): void {
    // Phase 34X-21: lobby start hover lift polish
    if (!this.lobbyStartBtn) return;
    const active = this.lobbyReady && !this.lobbyStartBtn.disabled;
    this.lobbyStartBtn.style.opacity = active ? '1' : '0.58';
    this.lobbyStartBtn.style.cursor = active ? 'pointer' : 'not-allowed';
    this.lobbyStartBtn.style.transition = 'transform 0.16s ease, box-shadow 0.16s ease, filter 0.16s ease';
    this.lobbyStartBtn.style.filter = active && this.lobbyStartBtnHovered ? 'brightness(1.12) saturate(1.12)' : '';
    if (active && this.lobbyStartBtnPressed) {
      this.lobbyStartBtn.style.transform = 'translateY(3px) scale(0.985)';
    } else if (active && this.lobbyStartBtnHovered) {
      this.lobbyStartBtn.style.transform = 'translateY(-3px) scale(1.03)';
    } else {
      this.lobbyStartBtn.style.transform = '';
    }
    this.lobbyStartBtn.style.boxShadow = active
      ? this.lobbyStartBtnHovered
        ? '0 8px 0 #c97800,0 18px 52px rgba(255,210,70,0.78),0 0 32px rgba(126,249,255,0.48),inset 0 0 18px rgba(255,255,255,0.28)'
        : '0 6px 0 #c97800,0 16px 48px rgba(255,190,28,0.58),0 0 22px rgba(126,249,255,0.28),inset 0 0 14px rgba(255,255,255,0.22)'
      : '0 4px 0 #9b5c00,0 12px 34px rgba(255,180,0,0.28)';
  }

  private spawnGliderTrailParticle(): void {
    this.player.getGliderTrailPosition(this.fxScratch);
    const vel = new THREE.Vector3(
      (Math.random() - 0.5) * 0.6,
      -0.4 - Math.random() * 0.5,
      (Math.random() - 0.5) * 0.6,
    );
    const maxLife = 0.55 + Math.random() * 0.25;

    if (FpsBattlePass.isTrailUnlocked()) {
      const col = new THREE.Color().setHSL(this.selectedOutfitHue, 0.72, 0.62);
      const mat = new THREE.MeshBasicMaterial({
        color: col,
        transparent: true,
        opacity: 0.78,
        depthWrite: false,
        side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(this.fxGlidePlaneGeo, mat);
      mesh.position.copy(this.fxScratch);
      mesh.position.x += (Math.random() - 0.5) * 0.25;
      mesh.position.z += (Math.random() - 0.5) * 0.25;
      mesh.lookAt(this.camera.position);
      this.scene.add(mesh);
      this.worldFx.push({ mesh, vel, life: 0, maxLife, billboard: true, baseOpacity: 0.78 });
      return;
    }

    const mesh = new THREE.Mesh(this.fxGeo, this.fxGlideMat);
    mesh.position.copy(this.fxScratch);
    mesh.position.x += (Math.random() - 0.5) * 0.25;
    mesh.position.z += (Math.random() - 0.5) * 0.25;
    this.scene.add(mesh);
    this.worldFx.push({ mesh, vel, life: 0, maxLife });
  }

  private spawnSkydiveWindStreaks(): void {
    const body = this.player.body;
    const yaw = this.player.yawObject.rotation.y;
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);
    const backX = Math.sin(yaw);
    const backZ = Math.cos(yaw);
    const pos = new THREE.Vector3(
      body.position.x + backX * (0.5 + Math.random() * 0.6) + rightX * (Math.random() - 0.5) * 1.6,
      body.position.y + 0.1 + Math.random() * 1.2,
      body.position.z + backZ * (0.5 + Math.random() * 0.6) + rightZ * (Math.random() - 0.5) * 1.6,
    );
    this.particles.emit({
      count: 3,
      position: pos,
      velocityRange: {
        min: new THREE.Vector3(-0.45, 6.5, -0.45),
        max: new THREE.Vector3(0.45, 10.5, 0.45),
      },
      colorA: new THREE.Color(0.74, 0.96, 1),
      colorB: new THREE.Color(0.24, 0.62, 1),
      lifetime: 0.24,
      gravity: 0.6,
      sizeStart: 0.035,
      sizeEnd: 0.012,
    });
  }

  private updateDropCue(gliding: boolean): void {
    const body = this.player.body;
    const altitudeM = Math.max(0, Math.round(body.position.y - 0.9));
    const falling = body.velocity.y < -3.5;
    const airborneDrop = !this.player.isGrounded() && altitudeM > 4 && (falling || gliding);
    if (this.matchEnd || this.introActive || !airborneDrop) {
      this.hudDropCue.style.display = 'none';
      return;
    }

    const deployReady = !gliding && body.position.y > 4.2;
    this.hudDropCue.style.display = 'block';
    this.hudDropCue.innerHTML = deployReady
      ? `<div style="font-size:10px;color:#9fdcff;letter-spacing:0.14em">ALT ${altitudeM}M</div><div><span style="color:#9fffd8">SPACE</span> DEPLOY GLIDER</div>`
      : `<div style="font-size:10px;color:#9fdcff;letter-spacing:0.14em">ALT ${altitudeM}M</div><div>${gliding ? 'GLIDING TO LANDING' : 'DROPPING'}</div>`;
  }

  private showEarlyObjectiveHint(): void {
    if (this.earlyObjectiveHintShown || this.matchEnd || this.introActive) return;
    this.earlyObjectiveHintShown = true;
    this.pushElimFeed('Objective: Find a chest or weapon', 'loot');
    this.showPickupCard('Find a chest or weapon', 'common', 'First objective', 'Loot');
  }

  private showMantleHint(): void {
    if (this.mantleHintCooldown > 0) return;
    this.mantleHintCooldown = 1.1;
    this.mantleHintTimer = 0.55;
    this.hudMantleHint.style.opacity = '1';
    this.hudMantleHint.style.transform = 'translate(-50%,-50%) scale(1)';
    this.pushElimFeed('Mantle', 'loot');
  }

  private grantBattlePassXp(amount: number): void {
    const { tier, prevTier } = FpsBattlePass.addXp(amount);
    if (tier <= prevTier) return;
    const rewards = FpsBattlePass.claimPendingRewards(tier);
    for (const reward of rewards) {
      this.pushElimFeed(`BP Reward: ${reward.label}`, 'loot');
    }
  }

  private syncGoldHud(): void {
    this.hudGold.textContent = `Gold ${FpsGoldBars.load()}`;
  }

  private getBountyTargetPos(): { x: number; z: number } | null {
    const target = this.vendors.getActiveBountyTarget();
    if (!target) return null;
    for (const bot of this.bots.getLivingBots()) {
      if (bot.name === target && !bot.knocked) {
        return { x: bot.x, z: bot.z };
      }
    }
    return null;
  }

  private handleVendorResult(result: VendorInteractResult): void {
    if (result.mats < 0) {
      this.mats += Math.abs(result.mats);
      this.hudMats.textContent = `Mats ${this.mats}`;
    } else if (result.mats > 0) {
      this.hudMats.textContent = `Mats ${this.mats}`;
    }

    if (result.goldSpent > 0) {
      this.syncGoldHud();
      this.applyGoldVendPurchase(result.vendItem);
    }

    switch (result.kind) {
      case 'upgrade': {
        const slot = this.hotbar.getSelectedIndex();
        const next = upgradeRarity(this.weapon.getRarity());
        if (next) {
          this.weapon.setRarity(next);
          if (slot >= 0 && slot <= 2) this.hotbar.setSlotRarity(slot, next);
          this.hotbar.applyWeaponRarityBorders();
          this.syncViewModelWeapon();
          this.pushElimFeed(`Weapon → ${RARITY_LABELS[next]}`, 'loot');
        }
        break;
      }
      case 'hire':
        this.pushElimFeed('Escort bot hired (follows you)', 'loot');
        break;
      case 'bounty':
        if (result.bountyTarget) {
          this.pushElimFeed(`Bounty accepted: ${result.bountyTarget}`, 'loot');
          const pos = this.getBountyTargetPos();
          if (pos) {
            this.pingStack.pushPing(pos.x, pos.z, 5200);
            this.worldPingMarkers.spawnAt(new THREE.Vector3(pos.x, 1.4, pos.z), {
              type: 'danger',
              label: 'BOUNTY',
            });
          }
        }
        break;
      case 'info':
      case 'emote':
      case 'gold_vend':
        break;
      default: {
        const _exhaustive: never = result.kind;
        void _exhaustive;
        break;
      }
    }
  }

  private applyGoldVendPurchase(item: VendGoldItem | null): void {
    if (!item) return;
    if (item === 'med') {
      if (this.health >= 100) {
        this.showHealFeedback('Health already full', '#ffe08a');
        return;
      }
      this.health = Math.min(100, this.health + 25);
      this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
      this.audio.playChest();
      this.showHealFeedback('Health recovered', '#7ef9a8', true);
      this.pushElimFeed('Gold vend: Med (+25 HP)', 'loot');
      return;
    }
    if (item === 'shield') {
      if (this.shieldHud.getShield() >= 100) {
        this.showHealFeedback('Shield already full', '#ffe08a');
        return;
      }
      this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 45));
      this.audio.playChest();
      this.showHealFeedback('Shield recovered', '#93c5fd', true);
      this.pushElimFeed('Gold vend: Shield (+45)', 'loot');
      return;
    }
    const kind = VEND_WEAPON_KINDS[Math.floor(Math.random() * VEND_WEAPON_KINDS.length)]!;
    const rarity = rollSupplyDropWeaponRarity();
    const b = this.player.body.position;
    if (this.hotbar.areAllWeaponSlotsFilled()) {
      this.weaponPickups.spawnDrop(b.x, b.z, kind, rarity);
      this.pushElimFeed(`Gold vend: ${RARITY_LABELS[rarity]} ${WEAPON_SHORT_LABELS[kind]} dropped`, 'loot');
    } else {
      this.equipWeaponToSlot(kind, rarity, this.hotbar.getBestSlotForWeapon(kind));
      this.pushElimFeed(`Gold vend: ${RARITY_LABELS[rarity]} ${WEAPON_SHORT_LABELS[kind]}`, 'loot');
    }
    this.audio.playVendWeapon();
  }

  private checkBountyElim(victimName: string): void {
    if (!this.vendors.checkBountyElim(victimName)) return;
    this.mats += 200;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.pushElimFeed('Bounty complete — +200 mats', 'loot');
    this.showPickupToast('Bounty claimed!', '#ffd54a');
  }

  private triggerMovementCue(
    key: string,
    label: string,
    color: THREE.Color,
    cooldownSeconds: number,
    position?: THREE.Vector3,
  ): void {
    const now = performance.now() * 0.001;
    const last = this.movementCueLastAt.get(key) ?? -Infinity;
    if (now - last < cooldownSeconds) return;
    this.movementCueLastAt.set(key, now);

    this.pushElimFeed(label, 'loot');
    if (!position) return;

    const reduced = this.prefersReducedMotion();
    this.particles.emit({
      count: reduced ? 3 : 7,
      position,
      velocityRange: {
        min: new THREE.Vector3(-0.55, 0.25, -0.55),
        max: new THREE.Vector3(0.55, 1.05, 0.55),
      },
      colorA: color,
      colorB: color,
      lifetime: reduced ? 0.22 : 0.34,
      gravity: 1.8,
      sizeStart: reduced ? 0.045 : 0.06,
      sizeEnd: 0.01,
    });
  }

  private spawnLandingDust(x: number, y: number, z: number, hard = false): void {
    const reduced = this.prefersReducedMotion();
    this.particles.emit({
      count: reduced ? (hard ? 8 : 5) : hard ? 22 : 14,
      position: new THREE.Vector3(x, y + 0.08, z),
      velocityRange: {
        min: new THREE.Vector3(hard ? -2.5 : -1.8, 0.2, hard ? -2.5 : -1.8),
        max: new THREE.Vector3(hard ? 2.5 : 1.8, hard ? 2.15 : 1.7, hard ? 2.5 : 1.8),
      },
      colorA: new THREE.Color(0.72, 0.52, 0.32),
      colorB: new THREE.Color(0.38, 0.25, 0.14),
      lifetime: reduced ? 0.28 : hard ? 0.52 : 0.42,
      gravity: hard ? 4.8 : 3.8,
      sizeStart: hard ? 0.09 : 0.07,
      sizeEnd: 0.01,
      critical: hard,
    });

    const dustPuffs = reduced ? (hard ? 4 : 2) : hard ? 15 : 10;
    for (let i = 0; i < dustPuffs; i++) {
      const mesh = new THREE.Mesh(this.fxGeo, this.fxDustMat);
      const spread = hard ? 0.58 : 0.35;
      mesh.position.set(x + (Math.random() - 0.5) * spread, y + 0.05, z + (Math.random() - 0.5) * spread);
      mesh.scale.setScalar((hard ? 1.05 : 0.8) + Math.random() * (hard ? 1.35 : 1.1));
      this.scene.add(mesh);
      const angle = Math.random() * Math.PI * 2;
      const speed = (hard ? 1.25 : 0.8) + Math.random() * (hard ? 2.35 : 1.6);
      this.worldFx.push({
        mesh,
        vel: new THREE.Vector3(
          Math.cos(angle) * speed,
          (hard ? 0.45 : 0.6) + Math.random() * (hard ? 1.9 : 1.4),
          Math.sin(angle) * speed,
        ),
        life: 0,
        maxLife: (hard ? 0.42 : 0.35) + Math.random() * (hard ? 0.32 : 0.25),
      });
    }
  }

  private applyMantleAssist(dt: number): void {
    this.mantleAssistCooldown = Math.max(0, this.mantleAssistCooldown - dt);
    this.mantleHintCooldown = Math.max(0, this.mantleHintCooldown - dt);
    if (
      this.mantleAssistCooldown > 0 ||
      this.player.isVaulting() ||
      this.player.isGliderActive() ||
      this.player.isSwimming() ||
      this.player.isKnocked() ||
      this.matchEnd !== null
    ) {
      return;
    }

    const body = this.player.body;
    if (body.velocity.y < -2.5) return;

    const yaw = this.player.yawObject.rotation.y;
    const forward = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
    const horizSpeed = Math.hypot(body.velocity.x, body.velocity.z);
    const forwardSpeed = body.velocity.x * forward.x + body.velocity.z * forward.z;
    if (horizSpeed < 3.2 || forwardSpeed < 2.4 || !this.player.isKeyHeld('KeyW')) return;

    const feetY = body.position.y - 0.9;
    const targets: THREE.Object3D[] = [this.built.sceneRoot];
    if (this.buildPlacer) {
      for (const build of this.buildPlacer.getPlacedBuilds()) targets.push(build.mesh);
    }

    const lowOrigin = new THREE.Vector3(body.position.x, feetY + 0.52, body.position.z);
    const lowRay = new THREE.Raycaster(lowOrigin, forward, 0.05, 0.72);
    const lowHits = lowRay.intersectObjects(targets, true);
    if (lowHits.length === 0) return;

    const probe = lowOrigin.clone().addScaledVector(forward, Math.min(0.74, lowHits[0]!.distance + 0.18));
    const downRay = new THREE.Raycaster(
      new THREE.Vector3(probe.x, feetY + MANTLE_ASSIST_MAX_RISE + 0.35, probe.z),
      new THREE.Vector3(0, -1, 0),
      0,
      MANTLE_ASSIST_MAX_RISE + 0.7,
    );
    const topHit = downRay.intersectObjects(targets, true)[0];
    if (!topHit) return;

    const rise = topHit.point.y - feetY;
    if (rise < MANTLE_ASSIST_MIN_RISE || rise > MANTLE_ASSIST_MAX_RISE) return;

    const headOrigin = new THREE.Vector3(body.position.x, feetY + 1.42, body.position.z);
    const headRay = new THREE.Raycaster(headOrigin, forward, 0.05, 0.7);
    if (headRay.intersectObjects(targets, true).length > 0) return;

    const upStep = Math.min(0.08, rise * 0.09, 4.5 * dt);
    const fwdStep = Math.min(0.055, Math.max(0, forwardSpeed) * dt * 0.22);
    body.position.x += forward.x * fwdStep;
    body.position.y += upStep;
    body.position.z += forward.z * fwdStep;
    body.velocity.y = Math.max(body.velocity.y, 1.8 + rise * 0.8);
    this.mantleAssistCooldown = 0.08;
    this.showMantleHint();
  }

  private updateWorldFx(dt: number): void {
    for (let i = this.worldFx.length - 1; i >= 0; i--) {
      const p = this.worldFx[i]!;
      p.life += dt;
      p.mesh.position.addScaledVector(p.vel, dt);
      p.vel.y -= dt * 2.4;
      if (p.billboard) p.mesh.lookAt(this.camera.position);
      const t = p.life / p.maxLife;
      const mat = p.mesh.material as THREE.MeshBasicMaterial;
      const baseOp =
        p.baseOpacity ?? (mat === this.fxGlideMat ? 0.72 : mat === this.fxDustMat ? 0.55 : 0.92);
      mat.opacity = (1 - t) * baseOp;
      p.mesh.scale.setScalar(1 + t * 0.8);
      if (p.life >= p.maxLife) {
        this.scene.remove(p.mesh);
        if (p.mesh.material !== this.fxGlideMat && p.mesh.material !== this.fxDustMat) {
          (p.mesh.material as THREE.Material).dispose();
        }
        this.worldFx.splice(i, 1);
      }
    }
  }

  private updateNextZoneRing(): void {
    const nr = this.storm.getNextSafeZoneRadius();
    const nc = this.storm.getNextCenterXZ();
    if (nr <= 0.5 || this.storm.isFullyShrunk()) {
      this.nextZoneRing.visible = false;
      return;
    }
    this.nextZoneRing.visible = true;
    this.nextZoneRing.position.set(nc.x, 0.06, nc.z);
    const inner = Math.max(0.05, nr - 0.35);
    const outer = nr + 0.35;
    const key = Math.round(inner * 10) * 10000 + Math.round(outer * 10);
    if (key !== this.nextZoneRingKey) {
      this.nextZoneRingKey = key;
      this.nextZoneRing.geometry.dispose();
      this.nextZoneRing.geometry = new THREE.RingGeometry(inner, outer, 96);
    }
  }

  private updateCompass(yaw: number): void {
    const markerW = 56;
    const pxPerDeg = markerW / 45;
    const yawDeg = (THREE.MathUtils.radToDeg(yaw) % 360 + 360) % 360;
    const centerPx = 160;
    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    const offset = centerPx - (yawDeg / 45) * markerW - markerW / 2;
    this.compassTrack.style.transform = `translateX(${offset}px)`;

    for (let i = 0; i < this.compassMarkerEls.length; i++) {
      const el = this.compassMarkerEls[i]!;
      const markerX = offset + i * markerW + markerW / 2;
      const dist = Math.abs(markerX - centerPx);
      const isCardinal =
        el.textContent === 'N' || el.textContent === 'E' || el.textContent === 'S' || el.textContent === 'W';
      const centered = dist < 10;
      el.style.color = centered ? '#fff' : isCardinal ? 'rgba(232,244,255,0.88)' : 'rgba(232,244,255,0.46)';
      el.style.fontSize = centered ? '13px' : isCardinal ? '11px' : '10px';
      el.style.fontWeight = centered ? '900' : '700';
      el.style.transform = centered ? 'scale(1.12)' : 'scale(1)';
      el.style.textShadow = centered
        ? '0 0 10px rgba(46,230,255,0.95),0 1px 2px rgba(0,0,0,0.6)'
        : isCardinal
          ? '0 0 4px rgba(46,230,255,0.25)'
          : '';
    }

    if (this.supplyDrop && !this.supplyDrop.getOpened()) {
      const drop = this.supplyDrop.getXZ();
      const dropBearing =
        (THREE.MathUtils.radToDeg(Math.atan2(drop.x - px, drop.z - pz)) + 360) % 360;
      let rel = dropBearing - yawDeg;
      if (rel > 180) rel -= 360;
      if (rel < -180) rel += 360;
      const x = centerPx + rel * pxPerDeg;
      if (Math.abs(rel) <= 135) {
        // Phase 33X-18: supply compass marker pulse when near bearing
        this.compassSupplyDropEl.style.display = 'block';
        this.compassSupplyDropEl.style.left = `${x}px`;
        const nearCenter = Math.abs(rel) < 8;
        const pulse = nearCenter ? 0.75 + Math.sin(performance.now() * 0.012) * 0.25 : 1;
        this.compassSupplyDropEl.style.fontSize = nearCenter ? `${15 + pulse * 2}px` : '13px';
        this.compassSupplyDropEl.style.opacity = String(nearCenter ? pulse : 0.88);
        this.compassSupplyDropEl.style.color = nearCenter ? '#ffe566' : '#ffd54a';
        this.compassSupplyDropEl.style.textShadow = nearCenter
          ? '0 0 10px rgba(255,220,80,0.85)'
          : '';
      } else {
        this.compassSupplyDropEl.style.display = 'none';
      }
    } else {
      this.compassSupplyDropEl.style.display = 'none';
    }

    if (this.shouldShowDropMarker() && this.dropMarker) {
      const marker = this.dropMarker;
      const markerBearing =
        (THREE.MathUtils.radToDeg(Math.atan2(marker.x - px, marker.z - pz)) + 360) % 360;
      let rel = markerBearing - yawDeg;
      if (rel > 180) rel -= 360;
      if (rel < -180) rel += 360;
      const x = centerPx + rel * pxPerDeg;
      if (Math.abs(rel) <= 135) {
        this.compassDropMarkerEl.style.display = 'block';
        this.compassDropMarkerEl.style.left = `${x}px`;
        const nearCenter = Math.abs(rel) < 8;
        this.compassDropMarkerEl.style.fontSize = nearCenter ? '15px' : '13px';
        this.compassDropMarkerEl.style.opacity = nearCenter ? '1' : '0.9';
      } else {
        this.compassDropMarkerEl.style.display = 'none';
      }
    } else {
      this.compassDropMarkerEl.style.display = 'none';
    }

    if (this.storm) {
      const safe = this.storm.getNextCenterXZ();
      const dx = safe.x - px;
      const dz = safe.z - pz;
      const dist = Math.hypot(dx, dz);
      if (dist > 2) {
        const safeBearing = (THREE.MathUtils.radToDeg(Math.atan2(dx, dz)) + 360) % 360;
        let rel = safeBearing - yawDeg;
        if (rel > 180) rel -= 360;
        if (rel < -180) rel += 360;
        const x = centerPx + rel * pxPerDeg;
        if (Math.abs(rel) <= 135) {
          this.compassSafeZoneEl.style.display = 'block';
          this.compassSafeZoneEl.style.left = `${x}px`;
          const nearCenter = Math.abs(rel) < 8;
          const inStorm = this.storm.isOutside(px, pz);
          const stormPhase = this.storm.getStormPhase();
          const finalCircle = this.storm.isFinalCircle();
          const guidancePulse = Math.max(0, Math.min(1, (this.safeZoneGuidancePulseUntil - performance.now()) / 1600));
          const safePulse = Math.max(
            this.stormWarnFlash,
            inStorm ? 1 : 0,
            finalCircle ? 0.74 : stormPhase >= 5 ? 0.58 : 0,
            guidancePulse,
          );
          const pulseWave = safePulse > 0.02 ? 0.5 + Math.sin(performance.now() * 0.014) * 0.5 : 0;
          const pulseScale = 1 + Math.min(0.34, safePulse * (0.1 + pulseWave * 0.22));
          this.compassSafeZoneEl.style.opacity = String(Math.min(1, (nearCenter ? 1 : 0.82) + safePulse * 0.2));
          this.compassSafeZoneEl.style.transform = `translateX(-50%) scale(${(nearCenter ? 1.08 : 1) * pulseScale})`;
          this.compassSafeZoneEl.style.boxShadow = safePulse > 0.02
            ? `0 0 ${8 + safePulse * (10 + pulseWave * 12)}px rgba(82,245,255,${0.28 + safePulse * 0.44})`
            : 'none';
          this.compassSafeZoneEl.style.background = safePulse > 0.02
            ? 'rgba(20,38,48,0.86)'
            : 'rgba(12,18,28,0.58)';
          this.compassSafeZoneEl.style.borderColor = safePulse > 0.02
            ? 'rgba(200,255,255,0.94)'
            : 'rgba(132,238,255,0.42)';
        } else {
          this.compassSafeZoneEl.style.display = 'none';
        }
      } else {
        this.compassSafeZoneEl.style.display = 'none';
      }
    } else {
      this.compassSafeZoneEl.style.display = 'none';
    }

    let poiLabel: string | null = null;
    let poiBestDist = Infinity;
    for (const poi of POI_LOOT_ZONES) {
      const dx = poi.x - px;
      const dz = poi.z - pz;
      const dist = Math.hypot(dx, dz);
      if (dist >= 80) continue;
      const bearing = (THREE.MathUtils.radToDeg(Math.atan2(dx, dz)) + 360) % 360;
      let rel = bearing - yawDeg;
      if (rel > 180) rel -= 360;
      if (rel < -180) rel += 360;
      if (Math.abs(rel) > 35) continue;
      if (dist < poiBestDist) {
        poiBestDist = dist;
        poiLabel = poi.name;
      }
    }
    if (poiLabel) {
      this.compassPoiLabelEl.textContent = poiLabel;
      this.compassPoiLabelEl.style.opacity = '1';
    } else {
      this.compassPoiLabelEl.style.opacity = '0';
    }
  }

  private updateAutoPerformanceScaler(rawDt: number): void {
    if (!this.isMatchLive() || this.settings.isOpen()) return;

    this.fpsSampleAcc += rawDt;
    this.fpsSampleFrames += 1;
    this.fpsSampleTimer += rawDt;
    if (this.fpsSampleTimer < 3) return;

    const avgFps = this.fpsSampleFrames / this.fpsSampleAcc;
    this.fpsSampleTimer = 0;
    this.fpsSampleAcc = 0;
    this.fpsSampleFrames = 0;

    if (avgFps < 42 && !this.graphicsState.performanceMode) {
      this.fpsLowStreak += 1;
      if (this.fpsLowStreak >= 2) {
        this.perfAutoEnabled = true;
        this.applyGraphics({ performanceMode: true });
        this.pushElimFeed('Performance mode auto-enabled');
        this.fpsLowStreak = 0;
      }
    } else {
      this.fpsLowStreak = 0;
    }
  }

  private updateStormShrinkWarning(dt: number): void {
    const phase = this.storm.getStormPhase();
    const eta = this.stormGrace > 0 ? null : this.storm.getShrinkEtaSeconds();

    if (phase !== this.stormSirenCyclePhase) {
      this.stormSirenCyclePhase = phase;
      this.stormSirenPlayed = false;
    }

    if (eta === null || eta <= 0.05) {
      this.stormSirenPlayed = false;
      this.stormWarnFlash = Math.max(0, this.stormWarnFlash - dt * 2.5);
      this.stormWarnBorder.style.opacity = String(this.stormWarnFlash);
      return;
    }

    const urgent = this.isMatchLive() && eta <= 12;
    if (urgent && !this.stormSirenPlayed) {
      this.stormSirenPlayed = true;
      this.audio.playStormSiren();
      this.audioMix.triggerStormSirenDuck();
      this.stormWarnFlash = 1;
      const lateStorm = phase >= 5 || this.storm.isFinalCircle();
      this.triggerSafeZoneGuidancePulse(lateStorm ? 3600 : 2600);
      if (lateStorm && this.stormLateUrgencyPhase !== phase) {
        this.stormLateUrgencyPhase = phase;
        const finalCircle = this.storm.isFinalCircle();
        this.showStormWarningBanner(finalCircle ? 'FINAL CIRCLE CLOSING' : 'LATE ZONE CLOSING');
        this.pushElimFeed(finalCircle ? 'Final circle closing — stay in safe' : 'Late zone closing — rotate now', 'elim');
      }
    }

    if (urgent) {
      const lateStorm = phase >= 5 || this.storm.isFinalCircle();
      this.stormWarnFlash = (lateStorm ? 0.62 : 0.54) + Math.sin(performance.now() * 0.009) * (lateStorm ? 0.22 : 0.18);
    } else {
      this.stormWarnFlash = Math.max(0, this.stormWarnFlash - dt * 3);
    }
    this.stormWarnBorder.style.opacity = String(this.stormWarnFlash);
  }

  private updateStormCountdownHud(dt: number): void {
    const graceTotal = 14;
    const formatMmSs = (secs: number): string => {
      const total = Math.max(0, Math.ceil(secs));
      const m = Math.floor(total / 60);
      const s = total % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    if (this.stormGrace > 0) {
      // Phase 33X-17: stronger grace ring pulse in last 5s
      this.stormGraceFade = 0;
      const secs = Math.ceil(this.stormGrace);
      this.hudStormGrace.textContent = `Storm closes in ${secs}s`;
      const urgent = this.stormGrace <= 5;
      const pulse = urgent ? 0.75 + Math.sin(performance.now() * 0.014) * 0.25 : 1;
      this.hudStormGrace.style.opacity = String(pulse);
      this.hudStormGrace.style.color = urgent ? '#ffb0ff' : '';
      this.hudStormGraceRing.style.opacity = String((urgent ? 0.95 : 0.85) * pulse);
      const ratio = this.stormGrace / graceTotal;
      const deg = Math.max(0, Math.min(360, ratio * 360));
      const ringCol = urgent ? 'rgba(255,120,255,0.75)' : 'rgba(180,100,255,0.55)';
      this.hudStormGraceRing.style.background = `conic-gradient(${ringCol} ${deg}deg, transparent ${deg}deg)`;
      return;
    }

    const shrinkEta = this.storm.getSecondsUntilNextShrink();
    if (shrinkEta !== null) {
      this.stormGraceFade = 0;
      const lateStorm = this.storm.isFinalCircle() || this.storm.getStormPhase() >= 5;
      this.hudStormGrace.textContent = `${lateStorm ? 'Final zone' : 'Zone'} closes in ${formatMmSs(shrinkEta)}`;
      this.hudStormGrace.style.opacity = '1';
      this.hudStormGraceRing.style.opacity = '0.72';
      const totalShrink = (STORM_INIT_R - STORM_MIN_R) / STORM_SHRINK;
      const ratio = Math.max(0, Math.min(1, shrinkEta / totalShrink));
      const deg = Math.max(0, Math.min(360, ratio * 360));
      this.hudStormGraceRing.style.background = `conic-gradient(rgba(255,90,120,0.55) ${deg}deg, transparent ${deg}deg)`;
      return;
    }

    if (this.stormGraceFade < 1.2) {
      if (this.stormGraceFade === 0) this.stormGraceFade = 0.001;
      this.stormGraceFade += dt;
      const fade = Math.max(0, 1 - this.stormGraceFade / 1.2);
      this.hudStormGrace.style.opacity = String(fade);
      this.hudStormGraceRing.style.opacity = String(fade * 0.85);
    } else {
      this.hudStormGrace.style.opacity = '0';
      this.hudStormGraceRing.style.opacity = '0';
    }
  }

  /** Match-rule ledger of placed fort pieces (unit-testable spend + record path). */
  private readonly placedBuildLedger: PlacedBuildRecord[] = [];

  private tryPlaceBuild(turbo = false): boolean {
    if (!this.buildEnabled) {
      this.buildPlacer.updateBlockedReason(this.mats, false);
      this.pushBuildBlockedHint();
      return false;
    }
    const kind = this.buildPlacer.getPieceKind();
    const prepaid = this.craftPrepaidPiece === kind;
    const cost = prepaid ? 0 : this.buildPlacer.getMatCost();
    if (this.mats < cost) {
      this.buildPlacer.updateBlockedReason(this.mats, this.buildEnabled, prepaid);
      this.pushBuildBlockedHint();
      return false;
    }
    if (this.buildPlacer.updateBlockedReason(this.mats, this.buildEnabled, prepaid)) {
      this.pushBuildBlockedHint();
      return false;
    }
    // Pure material spend / piece record (grid-snapped preview for ledger)
    const gx = Math.round(this.buildPlacer.previewPos.x);
    const gy = Math.round(this.buildPlacer.previewPos.y);
    const gz = Math.round(this.buildPlacer.previewPos.z);
    if (!prepaid) {
      const rule = tryPlaceBuildPiece(this.mats, kind, gx, gy, gz);
      if (!rule.ok) {
        this.buildPlacer.updateBlockedReason(this.mats, this.buildEnabled, prepaid);
        this.pushBuildBlockedHint();
        return false;
      }
    }
    if (!this.buildPlacer.tryPlace({ turbo })) {
      this.pushBuildBlockedHint();
      return false;
    }
    if (prepaid) {
      this.craftPrepaidPiece = null;
    } else {
      const rule = tryPlaceBuildPiece(this.mats, kind, gx, gy, gz);
      if (rule.ok) {
        this.mats = rule.materialsLeft;
        this.placedBuildLedger.push(rule.piece);
      } else {
        this.mats -= cost;
      }
      this.hudMats.textContent = `Mats ${this.mats}`;
    }
    this.audio.playBuildConfirm(this.buildPlacer.getBuildMat());

    // Spawn construction dust VFX
    const p = this.buildPlacer.previewPos;
    this.buildDustVfx.burst(p.x, p.y, p.z, kind);

    // turbo-build: decrease cooldown with each successive placement
    this.buildTurboCd = Math.max(0.05, this.buildTurboCd * 0.92);
    if (turbo) {
      const p = this.buildPlacer.previewPos;
      this.buildTurboVfx.burst(p.x, p.y, p.z);
    }
    return true;
  }

  private pushBuildBlockedHint(): void {
    const msg = this.buildPlacer.getBlockedReasonText();
    if (!msg) return;
    const now = performance.now();
    if (now - this.buildBlockedFeedAt < 850) return;
    this.buildBlockedFeedAt = now;
    this.pushElimFeed(msg);
  }

  private pushEnemyBuildLowHpHint(object: THREE.Object3D): void {
    const hp = this.bots.getBotBuildHp(object);
    if (!hp || hp.hp <= 0 || hp.hp / hp.maxHp > 0.34) return;
    const now = performance.now();
    if (now - this.enemyBuildLowHpFeedAt < 1400) return;
    this.enemyBuildLowHpFeedAt = now;
    this.pushElimFeed('Enemy build weak');
  }

  private tryRepairTargetedBuild(): boolean {
    const repair = this.buildPlacer.repairTargetedBuild(this.mats);
    if (!repair.repaired) return false;
    this.mats -= repair.cost;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.audio.playHit();
    this.pushElimFeed(`Repaired build +${repair.hpGain} HP — ${repair.cost} mats`);
    return true;
  }

  private updateVehicleHud(): void {
    const speedKmh = (this.vehicles.getCurrentSpeed() * 3.6).toFixed(0);
    const hp = this.vehicles.getCurrentHealth();
    if (!hp) {
      this.hudVehicle.textContent = `${speedKmh} km/h`;
      return;
    }
    const pct = Math.max(0, Math.min(100, (hp.health / hp.maxHealth) * 100));
    const barColor = pct > 45 ? '#2ee6ff' : pct > 20 ? '#ffb347' : '#ff6060';
    this.hudVehicle.innerHTML =
      `<div>${speedKmh} km/h</div>` +
      `<div style="margin-top:6px;width:120px;height:7px;border-radius:2px;background:rgba(0,0,0,0.45);border:1px solid rgba(46,230,255,0.35);overflow:hidden">` +
      `<div style="width:${pct.toFixed(1)}%;height:100%;background:linear-gradient(90deg,${barColor},#7ef9ff)"></div></div>`;
  }

  private applyBotDifficulty(): void {
    if (!this.bots || !this.botAI2) return;
    this.botAI2.setDifficultyTier(this.graphicsState.botDifficulty);
    const rankedBonus = this.rankedMode ? 0.06 : 0;
    this.bots.setReactionTime(this.botAI2.getReactionTime());
    this.bots.setHitChanceBonus(this.botAI2.getHitBonus() + rankedBonus);
  }

  private applyGraphics(patch: Partial<FpsGraphicsState>): void {
    if (patch.bloom !== undefined) this.graphicsState.bloom = patch.bloom;
    if (patch.shadowsHigh !== undefined) this.graphicsState.shadowsHigh = patch.shadowsHigh;
    if (patch.performanceMode !== undefined) this.graphicsState.performanceMode = patch.performanceMode;
    if (patch.fov !== undefined) this.graphicsState.fov = patch.fov;
    if (patch.lookSensitivity !== undefined) this.graphicsState.lookSensitivity = patch.lookSensitivity;
    if (patch.adsSensitivity !== undefined) this.graphicsState.adsSensitivity = patch.adsSensitivity;
    if (patch.colorblindStorm !== undefined) this.graphicsState.colorblindStorm = patch.colorblindStorm;
    if (patch.touchAimAssist !== undefined) this.graphicsState.touchAimAssist = patch.touchAimAssist;
    if (patch.reducedMotion !== undefined) this.graphicsState.reducedMotion = patch.reducedMotion;
    if (patch.hitFeedbackIntensity !== undefined) this.graphicsState.hitFeedbackIntensity = patch.hitFeedbackIntensity;
    if (patch.hudScale !== undefined) this.graphicsState.hudScale = patch.hudScale;
    if (patch.botDifficulty !== undefined) this.graphicsState.botDifficulty = patch.botDifficulty;
    if (patch.masterVolume !== undefined) {
      this.graphicsState.masterVolume = patch.masterVolume;
      this.audio.setMasterVolume(patch.masterVolume);
    }
    document.body.dataset.fpsReducedMotion = this.graphicsState.reducedMotion ? 'true' : 'false';
    document.body.dataset.fpsColorblind = this.graphicsState.colorblindStorm ? 'true' : 'false';
    document.documentElement.style.setProperty('--fps-hud-scale', String(this.graphicsState.hudScale));
    if (this.hudRoot) {
      this.hudRoot.style.transform = `scale(${this.graphicsState.hudScale})`;
    }
    this.hitFeedback?.setReducedMotion(this.graphicsState.reducedMotion);
    this.hitFeedback?.setIntensity(this.graphicsState.hitFeedbackIntensity);
    this.player?.setLookSensitivity(this.graphicsState.lookSensitivity);
    this.player?.setAdsSensitivity(this.graphicsState.adsSensitivity);
    this.player?.setReducedMotion(this.graphicsState.reducedMotion);
    this.hotbar?.setColorblindMode(this.graphicsState.colorblindStorm);
    this.storm?.setColorblindMode(this.graphicsState.colorblindStorm);
    if (patch.botDifficulty !== undefined) this.applyBotDifficulty();
    if (this.postFx) {
      this.postFx.bloomPass.enabled = this.graphicsState.bloom;
      this.postFx.bloomPass.strength = this.graphicsState.performanceMode ? BLOOM_STRENGTH_PERF : BLOOM_STRENGTH_NORMAL;
    }
    this.renderer.setPixelRatio(this.getRenderPixelRatio());
    const sz = this.graphicsState.shadowsHigh ? 4096 : 2048;
    this.sunLight.shadow.mapSize.set(sz, sz);
  }

  private getRenderPixelRatio(width = this.mount.clientWidth || window.innerWidth, height = this.mount.clientHeight || window.innerHeight): number {
    if (this.graphicsState.performanceMode) return 1;
    const dpr = window.devicePixelRatio || 1;
    const pixels = width * height;
    const cap = pixels >= 1920 * 1080 ? 1.25 : 1.5;
    return Math.max(1, Math.min(dpr, cap));
  }

  private applyCrosshair(patch: Partial<FpsCrosshairSettings>): void {
    if (patch.style !== undefined) this.crosshairSettings.style = patch.style;
    if (patch.color !== undefined) this.crosshairSettings.color = patch.color;
    if (patch.size !== undefined) {
      this.crosshairSettings.size = Math.max(0.8, Math.min(1.4, patch.size));
    }
  }

  private startMatchFromLobby(): void {
    if (!this.lobbyActive || !this.lobbyReady) return;
    this.elimCounts.clear();
    this.killLeaderName = null;
    this.finalCircleAnnounced = false;
    this.stormMovingAnnounced = false;
    this.stormLatePhaseAnnounced = false;
    this.stormOutsideAnnounceAt = -Infinity;
    this.wasInStorm = false;
    this.safeZoneGuidancePulseUntil = 0;
    this.stormSirenPlayed = false;
    this.stormSirenCyclePhase = 0;
    this.stormLateUrgencyPhase = 0;
    this.finalDuelShown = false;
    this.lastOpponentCueShown = false;
    this.placementTop10Shown = false;
    this.placementTop5Shown = false;
    this.placementTop3Shown = false;
    this.endgameFovPulseT = 0;
    this.playerEliminations = 0;
    this.elimStreak = 0;
    this.bestElimStreak = 0;
    this.rebootCardCount = 0;
    this.botAirstrikeCd = 28;
    this.botSupplyContestT = 0;
    this.supplyDropLandedAnnounced = false;
    this.bossObjectiveAnnounced = false;
    this.vaultObjectiveAnnounced = false;
    this.objectiveAnnouncementCooldowns.clear();
    this.npcQuest.resetForMatch();
    this.npcQuestHarvest.resetForMatch();
    this.npcQuestVisit.resetForMatch();
    this.stormSurge.resetForMatch();
    this.matchDirector = new FpsMatchDirector();
    this.matchDirectorPressureTimer = 0;
    this.bots.setPressureMultiplier(1);
    this.bots.setEndgameMode(false);
    this.botAI2.setEndgameMode(false);
    this.teammateKnockPinged = [];
    this.skydivers.length = 0;
    this.lastAttackerName = null;
    this.lastElimSource = 'combat';
    this.deathRecapVisible = false;
    this.hideDeathRecap();
    this.killLeaderHud.style.display = 'none';
    this.playerCrownHud.style.display = 'none';
    this.applyBotDifficulty();
    this.bots.setKillLeaderBotName(null);
    this.hudRankedChip.style.display = this.rankedMode ? 'block' : 'none';
    this.hotbar.resetWeaponSlots(LOADOUT_SLOT0[this.loadoutKind]);
    this.applyLoadoutPreference(this.loadoutKind, false);
    this.weapon.setWeaponKind(LOADOUT_SLOT0[this.loadoutKind]);
    this.syncViewModelWeapon();
    this.applyDailyLtmModifiers();
    this.slurpBarrelUsed = false;
    this.shieldKegUsed = false;
    this.bots.setSupplyDropTarget(null, null);
    this.bots.setLlamaTarget(null, null);
    const now = new Date();
    const startOfYear = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now.getTime() - startOfYear.getTime()) / 86400000);
    this.storm.setCenter(Math.sin(dayOfYear) * 15, Math.cos(dayOfYear) * 12);
    this.nextZoneRingKey = -1;
    this.lobbyActive = false;
    this.replaySystem?.clearRecording();
    this.replaySystem?.startRecording();
    void this.ensureReplaySystem().then(() => {
      this.replaySystem?.clearRecording();
      this.replaySystem?.startRecording();
    });
    this.playerCharacter.visible = false;
    this.lobbyPedestal.visible = false;
    this.lobbyPedestal.scale.setScalar(1);
    this.lobbyShowcaseRimLight.visible = false;
    this.lobbyOverlay.style.display = 'none';
    this.audio.stopLobbyMusic();
    this.tryShowTutorial();
    this.introActive = true;
    this.earlyObjectiveHintShown = false;
    this.dropMarkerVisibleUntilLand = false;
    this.busJumpCount = 99;
    this.busJumpChip.style.display = 'block';
    this.busJumpChip.textContent = 'Jumping: 99 players';
    this.minimap.setMapClickEnabled(true);
    if (!this.audioUnlocked) {
      this.audio.unlock();
      this.audioUnlocked = true;
    }
    this.battleBus.start(() => {
      this.finishIntro();
    });
    this.pushElimFeed('99 players jumping...');
    this.spawnSkydivers();
  }

  /** Drop marker world position for glider hints / tests (null if unset). */
  getDropMarker(): { x: number; z: number } | null {
    return this.dropMarker ? { ...this.dropMarker } : null;
  }

  private setDropMarker(x: number, z: number): void {
    if (!this.introActive) return;
    this.dropMarker = { x, z };
    this.pushElimFeed('Drop marker set', 'loot');
  }

  private shouldShowDropMarker(): boolean {
    return this.dropMarker != null && (this.introActive || this.dropMarkerVisibleUntilLand);
  }

  private finishIntro(): void {
    if (!this.introActive) return;
    this.introActive = false;
    this.busJumpChip.style.display = 'none';
    this.skydivers.length = 0;
    this.minimap.setMapClickEnabled(false);
    if (this.dropMarker) {
      this.dropMarkerVisibleUntilLand = true;
    }
    this.battleBus.skip();
    this.scene.remove(this.camera);
    this.player.pitchObject.add(this.camera);
    this.camera.position.set(0, 0, 0);
    this.camera.rotation.set(0, 0, 0);
    this.player.setIntroFrozen(false);
    this.ui.innerHTML =
      '<div><span style="opacity:0.95">1-3 guns · RMB aim · 4 build · 5 med · 6 mini · 7 bandage · C crouch · F vehicle · Y 3rd-person · R edit · Tab/X pickaxe · E chest/drop · Q cycle vend · G ping · Space glide · Esc</span></div>';
    this.miniShieldCount = 2;
    this.bandageInventoryCount = 3;
    this.medKitCount = 2;
    this.fishing.reset();
    this.hotbar.setConsumableCounts(this.miniShieldCount, this.bandageInventoryCount);
    this.updateTacticalHud();
    if (this.isSquadMode()) {
      this.spawnTeammates();
    }
  }

  private syncConsumableHud(): void {
    this.hotbar.setConsumableCounts(this.miniShieldCount, this.bandageInventoryCount);
    this.updateTacticalHud();
  }

  private updateTacticalHud(): void {
    if (!this.tacticalCountEls) return;
    const set = (el: HTMLSpanElement, n: number): void => {
      el.textContent = String(Math.max(0, n));
      el.style.opacity = n > 0 ? '1' : '0.35';
    };
    set(this.tacticalCountEls.grenades, this.grenades?.getCount() ?? 0);
    set(this.tacticalCountEls.stink, this.grenades?.getStinkCount() ?? 0);
    set(this.tacticalCountEls.mini, this.miniShieldCount);
    set(this.tacticalCountEls.bandage, this.bandageInventoryCount);
    set(this.tacticalCountEls.med, this.medKitCount);
    set(this.tacticalCountEls.airstrike, this.airstrike?.getCount() ?? 0);
    set(this.tacticalCountEls.chug, this.chugSplash?.getCount() ?? 0);
    set(this.tacticalCountEls.reboot, this.rebootCardCount);
    set(this.tacticalCountEls.shock, this.shockwave?.getCount() ?? 0);
  }

  private tryShowTutorial(): void {
    if (this.tutorialDismissedSession || !this.tutorial.shouldShow() || this.tutorial.isVisible()) {
      return;
    }
    this.tutorial.show();
  }

  private tryShowTutorialOnPointerLock(): void {
    if (this.tutorialDismissedSession || !this.tutorial.shouldShow() || this.tutorial.isVisible()) {
      return;
    }
    this.tutorial.show();
  }

  private readonly onPointerLockChange = (): void => {
    if (this.player.isLocked()) this.tryShowTutorialOnPointerLock();
  };

  private showStormPhaseBanner(phase: number): void {
    this.stormPhaseBanner.textContent = `⚠ STORM PHASE ${phase}`;
    this.stormPhaseBanner.style.display = 'block';
    this.stormPhaseBanner.style.animation = 'none';
    void this.stormPhaseBanner.offsetWidth;
    this.stormPhaseBanner.style.animation = 'stormPhaseBannerIn 2.5s ease-out forwards';
    this.stormPhaseBannerTimer = 2.5;
  }

  private showStormWarningBanner(text: string): void {
    this.stormPhaseBanner.textContent = text;
    this.stormPhaseBanner.style.display = 'block';
    this.stormPhaseBanner.style.animation = 'none';
    void this.stormPhaseBanner.offsetWidth;
    this.stormPhaseBanner.style.animation = 'stormPhaseBannerIn 2.2s ease-out forwards';
    this.stormPhaseBannerTimer = 2.2;
  }

  private triggerSafeZoneGuidancePulse(durationMs = 2200): void {
    this.safeZoneGuidancePulseUntil = Math.max(this.safeZoneGuidancePulseUntil, performance.now() + durationMs);
  }

  private triggerEndgamePulse(): void {
    this.endgameFovPulseT = ENDGAME_FOV_PULSE_DURATION;
    this.triggerCameraShake(0.006);
  }

  private checkFinalCircle(): void {
    if (this.finalCircleAnnounced || !this.isMatchLive() || this.matchEnd || this.stormGrace > 0) return;
    if (!this.storm.isFinalCircle()) return;
    this.finalCircleAnnounced = true;
    this.showStormWarningBanner('FINAL CIRCLE - HOLD SAFE');
    this.pushElimFeed('Final circle — hold the safe edge', 'elim');
    this.triggerSafeZoneGuidancePulse(4200);
    this.triggerEndgamePulse();
    this.audio.playFinalCircle();
  }

  private syncBotEndgameMode(): void {
    const stormPhase = this.storm.getStormPhase();
    const alivePlayers = this.countMatchAlive();
    const endgameActive = this.storm.isFinalCircle() || stormPhase >= 5 || alivePlayers <= 5;
    this.bots.setEndgameMode(endgameActive);
    this.botAI2.setEndgameMode(endgameActive);
    this.enforceFinalCircleBotCap();
  }

  private enforceFinalCircleBotCap(): void {
    const cap = this.storm.getActiveBotCap();
    if (cap == null || !this.isMatchLive() || this.matchEnd) return;

    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    const trimmed = this.bots.enforceFinalCircleBotCap(cap, px, pz);
    if (trimmed > 0 && !this.finalCircleBotCapApplied) {
      this.finalCircleBotCapApplied = true;
      this.pushElimFeed(`Final circle — capped to ${FINAL_CIRCLE_BOT_CAP} fighters`, 'elim');
    }
  }

  private showPlacementBanner(text: string): void {
    this.placementBanner.textContent = text;
    this.placementBanner.style.display = 'block';
    this.placementBanner.style.animation = 'none';
    void this.placementBanner.offsetWidth;
    this.placementBanner.style.animation = 'stormPhaseBannerIn 2.5s ease-out forwards';
    this.placementBannerTimer = 2.5;
    this.audio.playPlacementBanner();
  }

  private countMatchAlive(): number {
    let alive = this.bots.aliveCount();
    if (this.isMatchLive() && !this.spectateActive && this.matchEnd !== 'lost') {
      alive += 1;
    }
    for (const tm of this.teammates) {
      if (tm.isAlive()) alive += 1;
    }
    return alive;
  }

  private checkPlacementThresholds(alive: number): void {
    if (!this.isMatchLive() || this.matchEnd) return;
    if (alive <= 10 && !this.placementTop10Shown) {
      this.placementTop10Shown = true;
      this.showPlacementBanner('TOP 10!');
    }
    if (alive <= 5 && !this.placementTop5Shown) {
      this.placementTop5Shown = true;
      this.showPlacementBanner('TOP 5!');
    }
    if (alive <= 3 && !this.placementTop3Shown) {
      this.placementTop3Shown = true;
      this.showPlacementBanner('TOP 3!');
      this.pushElimFeed('Top 3 — hold your ground', 'elim');
      this.triggerEndgamePulse();
    }
    if (alive <= 2 && !this.finalDuelShown) {
      this.finalDuelShown = true;
      this.showPlacementBanner('FINAL DUEL!');
      this.pushElimFeed('Final Duel — one fight left', 'elim');
      this.triggerEndgamePulse();
    }
    if (this.bots.aliveCount() === 1 && !this.lastOpponentCueShown) {
      this.lastOpponentCueShown = true;
      this.pushElimFeed('Last opponent standing', 'elim');
      this.audio.playPlacementBanner();
      this.triggerEndgamePulse();
    }
  }

  private tryUseMiniShield(): boolean {
    if (this.consumableUseCd > 0) {
      this.showHealFeedback('Consumable not ready', '#ffe08a');
      return false;
    }
    if (this.miniShieldCount <= 0) {
      this.showHealFeedback('No mini shields', '#ffc8c8');
      return false;
    }
    if (this.shieldHud.getShield() >= 100) {
      this.showHealFeedback('Shield already full', '#ffe08a');
      return false;
    }
    this.miniShieldCount -= 1;
    this.applyChestConsumable('mini');
    this.consumableUseCd = 1.2;
    this.audio.playJump();
    this.showHealFeedback('Shield recovered', '#93c5fd', true);
    this.pushElimFeed('Mini shield (+25)', 'loot');
    this.syncConsumableHud();
    return true;
  }

  private tryUseQuickBandage(): boolean {
    if (this.consumableUseCd > 0) {
      this.showHealFeedback('Consumable not ready', '#ffe08a');
      return false;
    }
    if (this.bandageInventoryCount <= 0) {
      this.showHealFeedback('No bandages', '#ffc8c8');
      return false;
    }
    if (this.health >= 100) {
      this.showHealFeedback('Health already full', '#ffe08a');
      return false;
    }
    this.bandageInventoryCount -= 1;
    this.health = Math.min(100, this.health + 25);
    this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
    this.consumableUseCd = 1.2;
    this.audio.playJump();
    this.showHealFeedback('Health recovered', '#7ef9a8', true);
    this.pushElimFeed('Bandage (+25 HP)', 'loot');
    this.syncConsumableHud();
    return true;
  }

  private isNearVending(): boolean {
    const b = this.player.body.position;
    const dx = b.x - VEND_X;
    const dz = b.z - VEND_Z;
    return dx * dx + dz * dz < 18;
  }

  private isNearCraftBench(): boolean {
    const b = this.player.body.position;
    return this.craftBench.isNearBench(b.x, b.z, CRAFT_BENCH_X, CRAFT_BENCH_Z);
  }

  private isNearSlurpBarrel(): boolean {
    const b = this.player.body.position;
    const dx = b.x - SLURP_BARREL_X;
    const dz = b.z - SLURP_BARREL_Z;
    return dx * dx + dz * dz < SLURP_BARREL_R * SLURP_BARREL_R;
  }

  private isNearShieldKeg(): boolean {
    const b = this.player.body.position;
    const dx = b.x - SHIELD_KEG_X;
    const dz = b.z - SHIELD_KEG_Z;
    return dx * dx + dz * dz < SHIELD_KEG_R * SHIELD_KEG_R;
  }

  private trySlurpBarrelInteract(): boolean {
    if (!this.isNearSlurpBarrel() || this.matchEnd || this.knocked || !this.isMatchLive()) return false;
    if (this.slurpBarrelUsed) return false;
    if (this.shieldHud.getShield() >= 100) {
      this.showHealFeedback('Shield already full', '#ffe08a');
      return false;
    }
    this.slurpBarrelUsed = true;
    this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 25));
    this.audio.playChest();
    this.showHealFeedback('Shield recovered', '#93c5fd', true);
    this.pushElimFeed('Slurp barrel (+25 shield)', 'loot');
    return true;
  }

  private tryShieldKegInteract(): boolean {
    if (!this.isNearShieldKeg() || this.matchEnd || this.knocked || !this.isMatchLive()) return false;
    if (this.shieldKegUsed) return false;
    if (this.shieldHud.getShield() >= 100) {
      this.showHealFeedback('Shield already full', '#ffe08a');
      return false;
    }
    this.shieldKegUsed = true;
    this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 40));
    this.audio.playChest();
    this.showHealFeedback('Shield recovered', '#93c5fd', true);
    this.pushElimFeed('Shield keg (+40 shield)', 'loot');
    return true;
  }

  private tryCraftRecipe(recipeId: string): boolean {
    if (this.matchEnd || this.knocked) return false;
    const costs: Record<string, { cost: number; label: string }> = {
      bandage: { cost: 15, label: 'Bandage' },
      mini: { cost: 25, label: 'Mini Shield' },
      trap: { cost: 30, label: 'Spike Trap' },
      launchpad: { cost: 40, label: 'Launch Pad' },
      chug: { cost: 35, label: 'Chug Splash' },
      shock: { cost: 45, label: 'Shockwave' },
      ar_to_smg:       { cost: 30, label: 'AR → SMG' },
      smg_to_ar:       { cost: 30, label: 'SMG → AR' },
      shotgun_upgrade: { cost: 50, label: 'Shotgun Upgrade' },
      sniper_upgrade:  { cost: 50, label: 'Sniper Upgrade' },
    };
    const recipe = costs[recipeId];
    if (!recipe || this.mats < recipe.cost) return false;

    // Check weapon-part requirements via craftBench
    if (!this.craftBench.canAffordSidegrade(recipeId)) return false;

    this.mats -= recipe.cost;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.audio.playCraft();

    // Consume any weapon parts required
    this.craftBench.consumePartsForRecipe(recipeId);

    if (recipeId === 'bandage') {
      this.bandageInventoryCount += 1;
    } else if (recipeId === 'mini') {
      this.miniShieldCount += 1;
    } else if (recipeId === 'trap') {
      this.craftPrepaidPiece = 'trap';
      this.hotbar.setSelected(3);
      this.buildPlacer.setPiece('trap');
    } else if (recipeId === 'launchpad') {
      this.craftPrepaidPiece = 'launchpad';
      this.hotbar.setSelected(3);
      this.buildPlacer.setPiece('launchpad');
    } else if (recipeId === 'chug') {
      const gained = this.chugSplash.addCount(1);
      if (gained <= 0) return false;
    } else if (recipeId === 'shock') {
      const gained = this.shockwave.addCount(1);
      if (gained <= 0) return false;
    } else if (recipeId === 'ar_to_smg') {
      const rarity = this.hotbar.getSlotRarity(0);
      this.equipWeaponToSlot('smg', rarity, 0);
      this.pushElimFeed('Sidegrade: AR → SMG', 'loot');
    } else if (recipeId === 'smg_to_ar') {
      const rarity = this.hotbar.getSlotRarity(0);
      this.equipWeaponToSlot('ar', rarity, 0);
      this.pushElimFeed('Sidegrade: SMG → AR', 'loot');
    } else if (recipeId === 'shotgun_upgrade') {
      const next = upgradeRarity(this.hotbar.getSlotRarity(1));
      if (!next) return false;
      this.hotbar.setSlotRarity(1, next);
      this.hotbar.markSlotFilled(1);
      if (this.hotbar.getSelectedIndex() === 1) this.syncHotbarFromSlot();
      this.pushElimFeed(`Shotgun → ${RARITY_LABELS[next]}`, 'loot');
    } else if (recipeId === 'sniper_upgrade') {
      const next = upgradeRarity(this.hotbar.getSlotRarity(2));
      if (!next) return false;
      this.hotbar.setSlotRarity(2, next);
      this.hotbar.markSlotFilled(2);
      if (this.hotbar.getSelectedIndex() === 2) this.syncHotbarFromSlot();
      this.pushElimFeed(`Sniper → ${RARITY_LABELS[next]}`, 'loot');
    } else {
      this.pushElimFeed(`Crafted ${recipe.label}`, 'loot');
    }

    this.syncConsumableHud();
    this.updateTacticalHud();
    return true;
  }

  private getVendOption(): VendOption {
    return VEND_OPTIONS[this.vendOptionIndex % VEND_OPTIONS.length]!;
  }

  private cycleVendOption(): void {
    this.vendOptionIndex = (this.vendOptionIndex + 1) % VEND_OPTIONS.length;
    this.audio.playHit();
  }

  private getVendHudText(): string {
    const opt = this.getVendOption();
    const afford = this.mats >= opt.cost;
    const buyKey = afford ? '[E]' : 'need';
    return `[Q] Next · ${buyKey} ${opt.label} — ${opt.cost} mats → ${opt.hint}`;
  }

  private tryVendingPurchase(): boolean {
    if (!this.isNearVending() || this.matchEnd || this.knocked) return false;
    const opt = this.getVendOption();
    if (this.mats < opt.cost) return false;

    if (this.vendOptionIndex % VEND_OPTIONS.length === 0) {
      if (this.shieldHud.getShield() >= 100) {
        this.showHealFeedback('Shield already full', '#ffe08a');
        return false;
      }
      this.mats -= opt.cost;
      this.hudMats.textContent = `Mats ${this.mats}`;
      this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 45));
      this.audio.playChest();
      this.showHealFeedback('Shield recovered', '#93c5fd', true);
      this.pushElimFeed('Vending: Shield (+45)', 'loot');
      return true;
    }
    if (this.vendOptionIndex % VEND_OPTIONS.length === 1) {
      const slot0 = this.hotbar.getSlot0Weapon();
      if (!this.weapon.refillMagForKind(slot0)) return false;
      this.mats -= opt.cost;
      this.hudMats.textContent = `Mats ${this.mats}`;
      this.audio.playChest();
      this.pushElimFeed(`Vending: ${slot0.toUpperCase()} ammo refilled`, 'loot');
      return true;
    }
    if (this.health >= 100) {
      this.showHealFeedback('Health already full', '#ffe08a');
      return false;
    }
    this.mats -= opt.cost;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.health = Math.min(100, this.health + 25);
    this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
    this.audio.playChest();
    this.showHealFeedback('Health recovered', '#7ef9a8', true);
    this.pushElimFeed('Vending: Med (+25 HP)', 'loot');
    return true;
  }

  private syncHotbarFromSlot(): void {
    const slot = this.hotbar.getSelectedIndex();
    const w = this.hotbar.weaponKindForSlot(slot);
    if (w) {
      const prev = this.weapon.getWeaponKind();
      const changed = this.weapon.setWeaponKind(w);
      this.weapon.setRarity(this.hotbar.getSlotRarity(slot));
      if (changed) this.beginWeaponSwap(prev);
      else this.syncViewModelWeapon();
      this.buildPlacer.setEnabled(false);
    } else if (slot === 3) {
      this.buildPlacer.setEnabled(this.buildEnabled);
    } else {
      this.buildPlacer.setEnabled(false);
    }
    if (!w) this.syncViewModelWeapon();
    this.syncModHud();
    if (slot < 0 || slot > 2) this.weaponInspectOpen = false;
    this.updateWeaponInspectOverlay();
  }

  private syncModHud(): void {
    const slot = this.hotbar.getSelectedIndex();
    if (slot < 0 || slot > 2) {
      this.hotbar.setModChip(null);
      return;
    }
    const letters = getModHudLetters(this.weapon.getCurrentModSet());
    this.hotbar.setModChip(letters || null);
  }

  private updateWeaponInspectOverlay(): void {
    const slot = this.hotbar.getSelectedIndex();
    const show =
      this.weaponInspectOpen &&
      !this.knocked &&
      slot >= 0 &&
      slot <= 2 &&
      !this.matchEnd &&
      !this.settings.isOpen() &&
      !this.craftBench.isOpen();
    if (!show) {
      this.weaponInspectOverlay.style.display = 'none';
      return;
    }
    const stats = this.weapon.getInspectStats();
    const rarityColor = getRarityCssColor(stats.rarity);
    const rarityLabel = RARITY_LABELS[stats.rarity];
    const modLines =
      stats.mods.length > 0
        ? stats.mods
            .map(
              (m) =>
                `<div style="margin-top:4px;color:#d7aefb;font:700 12px system-ui">· ${getModDisplayName(m.kind)}</div>`,
            )
            .join('')
        : '<div style="margin-top:6px;color:#8899aa;font:600 11px system-ui">No attachments</div>';
    this.weaponInspectOverlay.innerHTML =
      `<div style="font:800 11px system-ui;letter-spacing:0.14em;color:${rarityColor};text-transform:uppercase;text-shadow:0 0 12px ${rarityColor}88">${rarityLabel}</div>` +
      `<div style="margin-top:6px;font:800 17px system-ui;color:#f4f8ff">${stats.displayName}</div>` +
      `<div style="margin-top:10px;display:grid;grid-template-columns:1fr 1fr;gap:8px">` +
      `<div style="padding:8px;border-radius:8px;background:rgba(0,0,0,0.28)"><div style="font:700 10px system-ui;color:#8899aa;letter-spacing:0.08em">DAMAGE</div><div style="margin-top:2px;font:800 16px ui-monospace,monospace;color:#ffb86c">${stats.damage}</div></div>` +
      `<div style="padding:8px;border-radius:8px;background:rgba(0,0,0,0.28)"><div style="font:700 10px system-ui;color:#8899aa;letter-spacing:0.08em">FIRE RATE</div><div style="margin-top:2px;font:800 16px ui-monospace,monospace;color:#7ee8ff">${stats.fireRate}/s</div></div>` +
      `</div>` +
      `<div style="margin-top:12px;font:700 10px system-ui;color:#8899aa;letter-spacing:0.1em">ATTACHMENTS</div>` +
      modLines +
      `<div style="margin-top:10px;font:700 10px system-ui;color:#667788;letter-spacing:0.06em">[I] Inspect</div>`;
    this.weaponInspectOverlay.style.display = 'block';
    this.weaponInspectOverlay.style.borderColor = `${rarityColor}cc`;
    this.weaponInspectOverlay.style.boxShadow = `0 8px 32px ${rarityColor}44, inset 0 1px 0 rgba(255,255,255,0.08)`;
  }

  private tryApplyWeaponMod(mod: WeaponMod, dropX?: number, dropZ?: number): boolean {
    if (this.weapon.canApplyMod(mod)) {
      if (this.weapon.applyMod(mod)) {
        this.syncModHud();
        this.updateWeaponInspectOverlay();
        this.pushElimFeed(getModAttachFeedLine(mod.kind), 'loot');
        this.showPickupToast(getModAttachFeedLine(mod.kind), '#d7aefb');
        return true;
      }
    }
    if (dropX != null && dropZ != null) {
      this.weaponPickups.spawnModDrop(dropX, dropZ, mod);
      this.pushElimFeed(`${getModDisplayName(mod.kind)} dropped — mod slots full`, 'loot');
    }
    return false;
  }

  private beginWeaponSwap(from: WeaponKind): void {
    this.weaponSwapT = WeaponSystem.SWAP_DURATION;
    this.weaponSwapFromKind = from;
    this.weaponSwapEquipCuePlayed = false;
    this.syncViewModelWeapon();
    this.audio.playWeaponSwap();
    this.viewmodelRig.triggerSwap();
  }

  private getViewModelDisplayKind(): WeaponKind {
    if (this.weaponSwapT > 0 && this.weaponSwapFromKind) {
      const half = WeaponSystem.SWAP_DURATION * 0.5;
      const elapsed = WeaponSystem.SWAP_DURATION - this.weaponSwapT;
      if (elapsed < half) return this.weaponSwapFromKind;
    }
    return this.weapon.getWeaponKind();
  }

  private syncViewModelWeapon(): void {
    if (this.pickaxeMode) {
      for (const mesh of Object.values(this.viewModelVariants)) {
        if (mesh) mesh.visible = false;
      }
      this.viewModelPickaxe.visible = true;
      return;
    }
    this.viewModelPickaxe.visible = false;
    const kind = this.getViewModelDisplayKind();
    for (const [k, mesh] of Object.entries(this.viewModelVariants) as [WeaponKind, THREE.Group][]) {
      mesh.visible = k === kind;
    }
  }

  private updatePickaxeMode(): void {
    this.pickaxeMode = this.pickaxeModeSticky || this.pickaxeKeyHeld;
    this.syncViewModelWeapon();
  }

  private getShootRecoilAmount(): number {
    const kind = this.weapon.getWeaponKind();
    if (kind === 'sniper') return 0.038;
    if (kind === 'ar' || kind === 'shotgun') return 0.0276;
    return 0.024;
  }

  private triggerCrosshairPulse(amount: number): void {
    // Phase 34X-24: slightly longer hit expand on crosshair
    const modestAmount = this.prefersReducedMotion() ? amount * 0.45 : amount * 1.12;
    this.crosshairPulse = Math.min(0.22, Math.max(this.crosshairPulse, modestAmount));
  }

  private getCasingEjectOptions(kind: WeaponKind): { count?: number; velocityMul?: number; scale?: number; lifeMul?: number } {
    if (kind === 'shotgun') return { count: 2, velocityMul: 0.86, scale: 1.34, lifeMul: 1.08 };
    if (kind === 'sniper') return { velocityMul: 1.28, scale: 1.18, lifeMul: 1.15 };
    if (kind === 'smg') return { velocityMul: 1.18, scale: 0.82, lifeMul: 0.82 };
    if (kind === 'pistol') return { velocityMul: 0.95, scale: 0.78, lifeMul: 0.9 };
    return { velocityMul: 1.04, scale: 0.94 };
  }

  private onGunFired(): void {
    this.matchStats.recordShot();
    const kind = this.weapon.getWeaponKind();
    this.player.fireWeaponRecoil(kind);
    this.spawnShotMuzzleFlash(kind);
    if (kind === 'shotgun') {
      this.triggerCameraShake(0.014);
    }
    this.audio.triggerCombatFocus(kind === 'shotgun' || kind === 'sniper' ? 1 : 0.82);
    this.audio.playShoot(kind);
    FpsHaptics.vibrateLight();
    this.viewmodelRig.triggerRecoil(0.6);
    this.triggerCrosshairPulse(kind === 'shotgun' ? 0.085 : kind === 'sniper' ? 0.07 : 0.055);

    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    this.bulletCasingVfx.spawn(this.camera.position, dir, this.getCasingEjectOptions(kind));
  }

  private spawnShotMuzzleFlash(kind: WeaponKind): void {
    const peak =
      kind === 'shotgun' ? 14 : kind === 'sniper' ? 16 : kind === 'ar' ? 11 : kind === 'smg' ? 9.5 : 8.5;
    const color = kind === 'sniper' ? 0xaaccff : 0xffcc88;

    this.camera.getWorldPosition(this.muzzleFlashScratch);
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    this.muzzleFlashScratch.addScaledVector(dir, 0.55);
    this.muzzleFlashScratch.y -= 0.04;

    let slot = this.shotMuzzleLights.find((flash) => flash.life <= 0);
    if (!slot) {
      const light = new THREE.PointLight(color, 0, 12);
      light.castShadow = false;
      this.scene.add(light);
      slot = { light, life: 0, peak: 0 };
      this.shotMuzzleLights.push(slot);
    }
    slot.light.color.setHex(color);
    slot.peak = peak;
    slot.light.intensity = peak;
    slot.light.position.copy(this.muzzleFlashScratch);
    slot.life = FPSGame.MUZZLE_FLASH_DECAY_S;
  }

  private updateShotMuzzleFlashes(dt: number): void {
    for (const flash of this.shotMuzzleLights) {
      if (flash.life <= 0) {
        flash.light.intensity = 0;
        continue;
      }
      flash.life = Math.max(0, flash.life - dt);
      const t = flash.life / FPSGame.MUZZLE_FLASH_DECAY_S;
      flash.light.intensity = flash.peak * t * t;
    }
  }

  private applyPlayerShieldHit(shieldDamage: number, brokeShield: boolean): void {
    if (shieldDamage <= 0) return;
    // Phase 32X-2: shield-break juice from pure intensity rule
    const breakI = brokeShield ? shieldBreakIntensity(shieldDamage + 0.01, 0) : 0;
    const intensity = brokeShield
      ? Math.max(0.85, breakI)
      : Math.min(0.85, 0.18 + shieldDamage / 52);
    const pos = new THREE.Vector3(
      this.player.body.position.x,
      this.player.body.position.y,
      this.player.body.position.z,
    );
    this.shieldCrackVfx.spawn(pos, intensity);
    this.shieldCrackFlashOpacity = Math.max(
      this.shieldCrackFlashOpacity,
      brokeShield ? Math.min(1, 0.92 + breakI * 0.08) : intensity * 0.42,
    );
    if (brokeShield) {
      this.audio.playShieldBreak();
      this.triggerCameraShake(0.035 + breakI * 0.02);
      this.damageFlashOpacity = Math.min(0.9, this.damageFlashOpacity + 0.28);
      this.pushElimFeed('Shield cracked!', 'knock');
    } else if (shieldDamage >= 10) {
      this.audio.playHitTier('shield');
    }
  }

  private performPickaxeSwing(): void {
    const r = this.harvest.swing();
    const swingDur = this.player.getPickaxeSwingDuration(r.comboHit);
    this.pickaxeSwingDur = swingDur;
    this.pickaxeSwingT = swingDur;
    this.player.applyShootRecoil(r.isFinisher ? 0.055 : r.comboHit === 2 ? 0.048 : 0.042);
    this.viewmodelRig.triggerSwing();
    if (r.isFinisher && r.hit) {
      const lungeDir = new THREE.Vector3();
      this.camera.getWorldDirection(lungeDir);
      this.player.applyPickaxeFinisherLunge(lungeDir);
      this.triggerCameraShake(0.018);
    }
    if (r.hit) {
      this.triggerHitStop(r.isFinisher ? 0.022 : 0.012);
      this.triggerHarvestImpactFeedback(r.hitPoint);
      this.harvestStreakIdle = 0;
      this.harvestStreak += 1;
      if (this.harvestStreak >= this.HARVEST_STREAK_HITS) {
        this.harvestStreak = 0;
        this.mats += this.HARVEST_STREAK_BONUS;
        this.hudMats.textContent = `Mats ${this.mats}`;
        this.pushBotFeed(`Harvest streak +${this.HARVEST_STREAK_BONUS} mats`);
      }
      this.audio.playHit();
      if (r.mats > 0) {
        this.mats = addMaterials(this.mats, r.mats);
        this.hudMats.textContent = `Mats ${this.mats}`;
        this.npcQuestHarvest.recordHarvest(r.mats);
        this.pushBotFeed(`harvested +${r.mats} mats`);
        if (this.xpBar.addXp(18)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
      }
      if (r.propBreak) {
        this.triggerPropBreakFeedback(r.propBreak.kind, r.propBreak.point);
      }
    }
    if (r.botHit) {
      this.triggerHitStop(0.018);
      this.triggerHarvestImpactFeedback(r.botHit.pos, true);
      this.matchStats.recordDamage(r.botHit.damage);
      this.audio.playHit();
      this.hitMarkerOpacity = 0.9;
      this.hitFeedback.showHit('body');
      this.hitFeedback.showDamage(r.botHit.damage, 'body');
      this.applyHitConfirmImpulse(r.botHit.killed ? 'elimination' : 'body');
      this.damageNumbers.spawn(
        r.botHit.pos.clone().add(new THREE.Vector3(0, 1.4, 0)),
        r.botHit.damage,
        this.camera,
        false,
      );
      if (r.botHit.killed) {
        this.onPlayerElimination({ victim: r.botHit.name });
        if (this.xpBar.addXp(45)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
        this.reportPlayerElim(r.botHit.name, r.botHit.pos, ' · PICKAXE', 'Pickaxe');
        this.tryTriggerKillCam(r.botHit.pos, {
          name: r.botHit.name,
          weapon: 'Pickaxe',
          headshot: false,
        });
        this.trySpawnElimWeaponDrop(r.botHit.pos);
        this.mats += 30;
        this.hudMats.textContent = `Mats ${this.mats}`;
      } else {
        this.pushElimFeed(`Pickaxe ${r.botHit.name} (-${Math.round(r.botHit.damage)})`, 'knock');
      }
    }
  }

  private triggerPropBreakFeedback(kind: 'barrel' | 'fence' | 'crate', point: THREE.Vector3): void {
    const isBarrel = kind === 'barrel';
    this.particles.emit({
      count: isBarrel ? 22 : 14,
      position: point,
      velocityRange: {
        min: new THREE.Vector3(-2.6, 0.8, -2.6),
        max: new THREE.Vector3(2.6, 4.4, 2.6),
      },
      colorA: new THREE.Color(isBarrel ? 0.95 : 0.72, isBarrel ? 0.42 : 0.52, isBarrel ? 0.18 : 0.28),
      colorB: new THREE.Color(isBarrel ? 0.45 : 0.32, isBarrel ? 0.12 : 0.2, isBarrel ? 0.08 : 0.1),
      lifetime: isBarrel ? 0.42 : 0.34,
      gravity: 6.2,
      sizeStart: isBarrel ? 0.08 : 0.06,
      sizeEnd: 0.01,
      critical: true,
    });
    this.audio.playHit();
    this.triggerCameraShake(isBarrel ? 0.008 : 0.005);
  }

  private triggerHarvestImpactFeedback(hitPoint: THREE.Vector3 | undefined, botHit = false): void {
    if (this.harvestImpactCd > 0) return;
    this.harvestImpactCd = 0.09;

    const pos = hitPoint?.clone() ?? this.camera.getWorldPosition(new THREE.Vector3()).add(
      this.camera.getWorldDirection(new THREE.Vector3()).multiplyScalar(2.2),
    );
    const normal = this.camera.getWorldDirection(new THREE.Vector3()).negate().normalize();
    this.impactSparks.spawn(pos.x, pos.y, pos.z, normal);
    this.particles.emit({
      count: botHit ? 5 : 7,
      position: pos,
      velocityRange: {
        min: new THREE.Vector3(-1.8, 0.6, -1.8),
        max: new THREE.Vector3(1.8, 3.2, 1.8),
      },
      colorA: new THREE.Color(botHit ? 1 : 0.95, botHit ? 0.28 : 0.72, botHit ? 0.2 : 0.34),
      colorB: new THREE.Color(botHit ? 0.55 : 0.32, botHit ? 0.04 : 0.2, botHit ? 0.04 : 0.1),
      lifetime: 0.24,
      gravity: 4.5,
      sizeStart: botHit ? 0.045 : 0.055,
      sizeEnd: 0,
      critical: true,
    });
    this.triggerCameraShake(botHit ? 0.006 : 0.0045);
    this.triggerCrosshairPulse(botHit ? 0.08 : 0.065);
  }

  private resetViewModelMags(): void {
    for (const mag of Object.values(this.viewModelMags)) {
      if (!mag) continue;
      mag.position.set(0, 0, 0);
      mag.rotation.set(0, 0, 0);
    }
  }

  private updateViewModelRarityPulse(): void {
    if (this.pickaxeMode) return;
    const body = this.viewModelBodyMat;
    const accent = this.viewModelAccentMat;
    if (!body || !accent) return;
    const rarity = this.weapon.getRarity();
    const pulseRarity = rarity === 'epic' || rarity === 'legendary';
    const t = performance.now() * 0.001;
    if (pulseRarity) {
      const pulse = 0.12 + Math.sin(t * 3.6) * 0.1;
      const emissive = rarity === 'legendary' ? 0xff8822 : 0x9944ff;
      body.emissive.setHex(emissive);
      body.emissiveIntensity = pulse + 0.12;
      accent.emissive.setHex(emissive);
      accent.emissiveIntensity = pulse * 1.35 + 0.18;
    } else {
      body.emissive.setHex(0x3311aa);
      body.emissiveIntensity = 0.14;
      accent.emissive.setHex(0xffeaa7);
      accent.emissiveIntensity = 0.25;
    }
  }

  private applyReloadViewModel(reloadRatio: number): void {
    const smooth = (u: number) => u * u * (3 - 2 * u);
    const r = reloadRatio;

    if (r > 0.02 && this.prevReloadRatio <= 0.02) {
      this.audio.playReload();
      this.reloadMidpointCuePlayed = false;
    }
    if (!this.reloadMidpointCuePlayed && this.prevReloadRatio < 0.5 && r >= 0.5) {
      this.reloadMidpointCuePlayed = true;
      this.viewmodelRig.triggerReloadTick();
      this.triggerCrosshairPulse(0.035);
    }
    this.prevReloadRatio = r;

    const reloadArc = Math.sin(r * Math.PI);
    const timedBob = reloadArc * Math.sin(r * Math.PI * 5.5);
    const timedTilt = reloadArc * Math.sin(r * Math.PI * 3.0 + 0.45);
    const tilt = reloadArc * 0.34 + timedTilt * 0.055;
    const pullY = reloadArc * 0.045 - timedBob * 0.014;
    const pullZ = reloadArc * 0.055 + Math.abs(timedBob) * 0.018;

    this.viewModelGun.rotation.x = this.viewModelGunRest.rotX + tilt;
    this.viewModelGun.position.x = this.viewModelGunRest.x - reloadArc * 0.035 + timedTilt * 0.012;
    this.viewModelGun.position.y = this.viewModelGunRest.y - pullY;
    this.viewModelGun.position.z = this.viewModelGunRest.z + pullZ;

    const jitterT = Math.sin(r * Math.PI);
    const t = performance.now() * 0.001;
    this.viewModelGun.rotation.y =
      this.viewModelGunBaseRotY + Math.sin(t * 18.5) * 0.016 * jitterT - timedTilt * 0.035;
    this.viewModelGun.rotation.z = Math.cos(t * 20.8) * 0.01 * jitterT + timedBob * 0.018;

    const kind = this.weapon.getWeaponKind();
    const mag = this.viewModelMags[kind];
    if (!mag) return;

    let drop = 0;
    let back = 0;
    if (r < 0.26) {
      const u = smooth(r / 0.26);
      drop = u;
      back = u * 0.05;
    } else if (r < 0.82) {
      drop = 1;
      back = 0.05;
    } else {
      const u = smooth((r - 0.82) / 0.18);
      drop = 1 - u;
      back = 0.05 * (1 - u);
    }
    mag.position.set(0, -drop * 0.12, back);
    mag.rotation.x = drop * 0.24;
  }

  private tryUseBandage(): boolean {
    if (this.bandageCd > 0) {
      this.showHealFeedback('Consumable not ready', '#ffe08a');
      return false;
    }
    if (this.medKitCount <= 0) {
      this.showHealFeedback('No medkits', '#ffc8c8');
      return false;
    }
    if (this.health < 100) {
      this.health = Math.min(100, this.health + 38);
      this.showHealFeedback('Health recovered', '#7ef9a8', true);
    } else if (this.shieldHud.getShield() < 100) {
      this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 32));
      this.showHealFeedback('Shield recovered', '#93c5fd', true);
    } else {
      this.showHealFeedback('Health and shield full', '#ffe08a');
      return false;
    }
    this.medKitCount -= 1;
    this.bandageCd = 2.8;
    this.audio.playJump();
    this.pushElimFeed('Slurp / bandage applied');
    this.updateTacticalHud();
    return true;
  }

  private pushBotFeed(action: string): void {
    this.pushElimFeed(`${randomElimName()} ${randomFeedVerb()} ${action}`);
  }

  private elimDistanceM(elimPos: THREE.Vector3): number {
    const p = this.player.body.position;
    return Math.round(Math.hypot(p.x - elimPos.x, p.z - elimPos.z));
  }

  private syncHealthHud(): void {
    this.shieldHud.setHealth(this.health);
  }

  private killFeedWeaponAccent(weapon: string): string {
    const w = weapon.toLowerCase();
    if (w.includes('legendary') || w.includes('mythic') || w.includes('gold') || w.includes('drum') || w.includes('infinity')) {
      return '#ff9933';
    }
    if (w.includes('epic') || w.includes('purple') || w.includes('scoped') || w.includes('bolt')) {
      return '#b455ff';
    }
    if (w.includes('sniper') || w.includes('rifle') || w.includes('ar')) {
      return '#3399ff';
    }
    if (w.includes('shotgun') || w.includes('pump') || w.includes('smg')) {
      return '#30c030';
    }
    if (w.includes('storm') || w.includes('pickaxe') || w.includes('fall')) {
      return '#b0b0b0';
    }
    return '#3399ff';
  }

  private pushKillFeedEntry(
    eliminator: string,
    eliminated: string,
    weapon: string,
    elimPos: { x: number; z: number },
    headshot?: boolean,
    weaponKindLabel?: string,
  ): void {
    const p = this.player.body.position;
    const distanceM = Math.round(Math.hypot(p.x - elimPos.x, p.z - elimPos.z));
    this.victoryRoyale.addKillFeedEntry({
      eliminator,
      eliminated,
      weapon,
      weaponKindLabel,
      headshot,
      distanceM,
      timestamp: Date.now(),
    });
  }

  private reportPlayerElim(
    name: string,
    elimPos: THREE.Vector3,
    bannerSuffix?: string,
    weapon?: string,
    weaponKind?: WeaponKind,
  ): void {
    const distM = this.elimDistanceM(elimPos);
    const kind = weaponKind ?? this.weapon.getWeaponKind();
    const kindLabel = weaponKindShortLabel(kind);
    this.pushElimFeed(`You eliminated ${name} · ${distM}m`, 'elim');
    const headshot = !!(bannerSuffix && bannerSuffix.includes('HEADSHOT'));
    this.pushKillFeedEntry(
      'You',
      name,
      weapon ?? this.weapon.getDisplayName(),
      elimPos,
      headshot,
      kindLabel,
    );
    let banner = `ELIMINATED ${name.toUpperCase()}`;
    if (bannerSuffix) banner += bannerSuffix;
    this.showKillBanner(banner, `${kindLabel} · ${distM}m`);
  }

  private showKillBanner(text: string, subtitle?: string): void {
    if (subtitle) {
      this.killBanner.innerHTML =
        `${text}<div style="font:700 11px system-ui;margin-top:4px;opacity:0.85;letter-spacing:0.12em">${subtitle}</div>`;
    } else {
      this.killBanner.textContent = text;
    }
    this.killBanner.style.display = 'block';
    this.killBanner.classList.remove('fps-kill-banner-pop');
    void this.killBanner.offsetWidth;
    this.killBanner.classList.add('fps-kill-banner-pop');
    this.killBannerTimer = 1.6;
  }

  private tryTriggerKillCam(
    elimPos: THREE.Vector3,
    info?: { name: string; weapon?: string; headshot?: boolean },
  ): void {
    if (this.killCamActive || this.matchEnd || this.spectateActive) return;
    this.startKillCam(elimPos, info);
  }

  private startKillCam(
    elimPos: THREE.Vector3,
    info?: { name: string; weapon?: string; headshot?: boolean },
  ): void {
    this.killCamActive = true;
    this.killCamTimer = KILL_CAM_DURATION;
    this.killCamTarget.copy(elimPos);
    this.killCamElimName = info?.name ?? 'Unknown';
    this.killCamWeapon = info?.weapon ?? this.weapon.getDisplayName();
    this.killCamHeadshot = info?.headshot ?? false;
    if (!this.cameraDetached) {
      this.player.pitchObject.remove(this.camera);
      this.scene.add(this.camera);
      this.cameraDetached = true;
    }
    this.viewModelGun.visible = false;
    this.player.setMovementLocked(true);
    this.player.setInputLocked(true);
    this.killCamOverlay.style.display = 'flex';
    this.renderKillCamOverlay();
    const camPos = new THREE.Vector3();
    this.camera.getWorldPosition(camPos);
    this.killTracer.spawn(this.scene, camPos, elimPos);
    this.spawnElimBurstParticles(elimPos.x, elimPos.y, elimPos.z);
    this.updateKillCamCamera();
    this.victoryRoyale.startDeathCam(elimPos, KILL_CAM_DURATION, this.killCamElimName);
  }

  private renderKillCamOverlay(): void {
    const headshotBadge = this.killCamHeadshot
      ? '<div style="margin-top:10px;font:800 13px system-ui;letter-spacing:0.28em;color:#ffd700;' +
        'padding:6px 14px;border-radius:4px;border:2px solid rgba(255,215,0,0.65);' +
        'background:rgba(40,32,0,0.55);text-shadow:0 0 12px rgba(255,215,0,0.55)">HEADSHOT</div>'
      : '';
    this.killCamOverlay.innerHTML =
      '<div style="text-align:center">' +
      '<div style="font:900 clamp(28px,6vw,44px) system-ui;letter-spacing:0.22em;color:#fff;' +
      'text-shadow:0 0 24px rgba(199,125,255,0.85),0 2px 8px rgba(0,0,0,0.8);' +
      'border-bottom:3px solid #c77dff;padding-bottom:8px">ELIMINATION</div>' +
      `<div style="margin-top:14px;font:800 clamp(18px,3.5vw,26px) system-ui;color:#e8d4ff;` +
      `letter-spacing:0.08em;text-shadow:0 2px 10px rgba(0,0,0,0.75)">${this.killCamElimName.toUpperCase()}</div>` +
      `<div style="margin-top:6px;font:600 14px system-ui;color:#9edcff;letter-spacing:0.06em">${this.killCamWeapon}</div>` +
      headshotBadge +
      '</div>';
  }

  private spawnElimBurstParticles(x: number, y: number, z: number): void {
    const colors = [0xffd700, 0xffe566, 0xffc040, 0xfff4a8];
    for (let i = 0; i < 22; i++) {
      const mat = this.fxGoldMat.clone();
      mat.color.setHex(colors[Math.floor(Math.random() * colors.length)]!);
      const mesh = new THREE.Mesh(this.fxGeo, mat);
      mesh.position.set(x + (Math.random() - 0.5) * 0.5, y + 0.8 + Math.random() * 0.6, z + (Math.random() - 0.5) * 0.5);
      mesh.scale.setScalar(0.6 + Math.random() * 0.8);
      this.scene.add(mesh);
      const angle = Math.random() * Math.PI * 2;
      const speed = 2.2 + Math.random() * 4.5;
      this.worldFx.push({
        mesh,
        vel: new THREE.Vector3(
          Math.cos(angle) * speed,
          3.5 + Math.random() * 5.5,
          Math.sin(angle) * speed,
        ),
        life: 0,
        maxLife: 0.32 + Math.random() * 0.28,
      });
    }
  }

  private endKillCam(): void {
    this.killCamActive = false;
    this.killTracer.dispose();
    this.killCamOverlay.style.display = 'none';
    this.victoryRoyale.stopDeathCam();
    this.player.setInputLocked(false);
    const driving = this.vehicles.isPlayerDriving();
    this.player.setMovementLocked(driving);
    this.viewModelGun.visible = !this.thirdPerson && !driving;
    const needDetached = driving || this.thirdPerson;
    if (!needDetached && this.cameraDetached) {
      this.scene.remove(this.camera);
      this.camera.position.set(0, 0, 0);
      this.camera.rotation.set(0, 0, 0);
      this.player.pitchObject.add(this.camera);
      this.cameraDetached = false;
    } else if (needDetached && !this.cameraDetached) {
      this.player.pitchObject.remove(this.camera);
      this.scene.add(this.camera);
      this.cameraDetached = true;
    }
  }

  private updateKillCamCamera(): void {
    const target = this.killCamTarget;
    const playerPos = this.player.body.position;
    const dx = target.x - playerPos.x;
    const dz = target.z - playerPos.z;
    const len = Math.hypot(dx, dz) || 1;
    const sideX = -dz / len;
    const sideZ = dx / len;
    const dist = 5.8;
    const height = 2.35;
    this.camera.position.set(target.x + sideX * dist, target.y + height, target.z + sideZ * dist);
    this.camera.lookAt(target.x, target.y + 1.25, target.z);
  }

  private openEmoteWheel(): void {
    if (
      this.emoteWheelOpen ||
      this.chatWheelOpen ||
      this.pingWheelOpen ||
      this.killCamActive ||
      this.matchEnd ||
      this.spectateActive ||
      this.knocked ||
      this.vehicles.isPlayerDriving()
    )
      return;
    this.emoteWheelOpen = true;
    this.emoteWheelPick = -1;
    this.emoteMouseMoved = false;
    this.emoteVDownT = performance.now();
    this.emoteVDownMouse.x = this.lastMouse.x;
    this.emoteVDownMouse.y = this.lastMouse.y;
    this.emoteWheel.style.display = 'block';
    this.updateEmoteWheelHighlight(this.pickEmoteFromMouse());
  }

  private closeEmoteWheel(): void {
    this.emoteWheelOpen = false;
    this.emoteWheel.style.display = 'none';
    this.emoteWheelPick = -1;
    const offsets = [
      'translate(-50%, 0)',
      'translate(-100%, -50%)',
      'translate(-50%, -100%)',
      'translate(0, -50%)',
    ];
    for (let i = 0; i < this.emoteWheelSegments.length; i++) {
      const seg = this.emoteWheelSegments[i]!;
      seg.style.transform = offsets[i] ?? '';
      seg.style.background = 'rgba(0,0,0,0.45)';
      seg.style.borderColor = 'rgba(224,170,255,0.35)';
    }
  }

  private pickEmoteFromMouse(): number {
    const rect = this.emoteWheel.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(this.lastMouse.y - cy, this.lastMouse.x - cx);
    let a = ang + Math.PI / 2;
    if (a < 0) a += Math.PI * 2;
    return Math.floor(a / (Math.PI / 2)) % 4;
  }

  private updateEmoteWheelHighlight(index: number): void {
    this.emoteWheelPick = index;
    const offsets = [
      'translate(-50%, 0)',
      'translate(-100%, -50%)',
      'translate(-50%, -100%)',
      'translate(0, -50%)',
    ];
    for (let i = 0; i < this.emoteWheelSegments.length; i++) {
      const seg = this.emoteWheelSegments[i]!;
      const on = i === index;
      seg.style.transform = `${offsets[i]}${on ? ' scale(1.12)' : ''}`;
      seg.style.background = on ? 'rgba(157,78,221,0.82)' : 'rgba(0,0,0,0.45)';
      seg.style.borderColor = on ? '#f3e8ff' : 'rgba(224,170,255,0.35)';
    }
  }

  private confirmEmoteWheel(): void {
    if (!this.emoteWheelOpen) return;
    let pick = this.emoteWheelPick;
    const heldMs = performance.now() - this.emoteVDownT;
    if (pick < 0) {
      if (!this.emoteMouseMoved && heldMs < 260) {
        pick = Math.floor(Math.random() * EMOTE_OPTIONS.length);
      } else {
        pick = this.pickEmoteFromMouse();
      }
    }
    this.closeEmoteWheel();
    this.playEmote(pick);
  }

  private playEmote(index: number): void {
    const em = EMOTE_OPTIONS[Math.max(0, Math.min(EMOTE_OPTIONS.length - 1, index))]!;
    this.emoteToast.textContent = em.toast;
    this.emoteToast.style.display = 'block';
    this.emoteToastTimer = 2.5;
    this.audio.playChest();
  }

  private openChatWheel(): void {
    if (
      this.chatWheelOpen ||
      this.emoteWheelOpen ||
      this.pingWheelOpen ||
      this.killCamActive ||
      this.matchEnd ||
      this.spectateActive ||
      this.knocked ||
      this.vehicles.isPlayerDriving()
    )
      return;
    this.chatWheelOpen = true;
    this.chatWheelPick = -1;
    this.chatMouseMoved = false;
    this.chatHDownT = performance.now();
    this.chatHDownMouse.x = this.lastMouse.x;
    this.chatHDownMouse.y = this.lastMouse.y;
    this.chatWheel.style.display = 'block';
    this.updateChatWheelHighlight(this.pickChatFromMouse());
  }

  private closeChatWheel(): void {
    this.chatWheelOpen = false;
    this.chatWheel.style.display = 'none';
    this.chatWheelPick = -1;
    const offsets = [
      'translate(-50%, 0)',
      'translate(-100%, -50%)',
      'translate(-50%, -100%)',
      'translate(0, -50%)',
    ];
    for (let i = 0; i < this.chatWheelSegments.length; i++) {
      const seg = this.chatWheelSegments[i]!;
      seg.style.transform = offsets[i] ?? '';
      seg.style.background = 'rgba(0,0,0,0.45)';
      seg.style.borderColor = 'rgba(125,211,252,0.35)';
    }
  }

  private pickChatFromMouse(): number {
    const rect = this.chatWheel.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(this.lastMouse.y - cy, this.lastMouse.x - cx);
    let a = ang + Math.PI / 2;
    if (a < 0) a += Math.PI * 2;
    return Math.floor(a / (Math.PI / 2)) % 4;
  }

  private updateChatWheelHighlight(index: number): void {
    this.chatWheelPick = index;
    const offsets = [
      'translate(-50%, 0)',
      'translate(-100%, -50%)',
      'translate(-50%, -100%)',
      'translate(0, -50%)',
    ];
    for (let i = 0; i < this.chatWheelSegments.length; i++) {
      const seg = this.chatWheelSegments[i]!;
      const on = i === index;
      seg.style.transform = `${offsets[i]}${on ? ' scale(1.12)' : ''}`;
      seg.style.background = on ? 'rgba(2,132,199,0.82)' : 'rgba(0,0,0,0.45)';
      seg.style.borderColor = on ? '#e0f2fe' : 'rgba(125,211,252,0.35)';
    }
  }

  private confirmChatWheel(): void {
    if (!this.chatWheelOpen) return;
    let pick = this.chatWheelPick;
    const heldMs = performance.now() - this.chatHDownT;
    if (pick < 0) {
      if (!this.chatMouseMoved && heldMs < 260) {
        pick = Math.floor(Math.random() * CHAT_WHEEL_OPTIONS.length);
      } else {
        pick = this.pickChatFromMouse();
      }
    }
    this.closeChatWheel();
    const line = CHAT_WHEEL_OPTIONS[Math.max(0, Math.min(CHAT_WHEEL_OPTIONS.length - 1, pick))]!;
    this.pushElimFeed(`[You] ${line}`);
  }

  private openPingWheel(): void {
    if (
      this.pingWheelOpen ||
      this.emoteWheelOpen ||
      this.chatWheelOpen ||
      this.killCamActive ||
      this.replaySystem?.isDeathReplayActive() ||
      this.matchEnd ||
      this.spectateActive ||
      this.knocked ||
      this.vehicles.isPlayerDriving()
    )
      return;
    this.pingWheelOpen = true;
    this.pingWheelPick = -1;
    this.pingMouseMoved = false;
    this.pingGDownT = performance.now();
    this.pingGDownMouse.x = this.lastMouse.x;
    this.pingGDownMouse.y = this.lastMouse.y;
    this.pingWheel.style.display = 'block';
    this.updatePingWheelHighlight(this.pickPingFromMouse());
  }

  private closePingWheel(): void {
    this.pingWheelOpen = false;
    this.pingWheel.style.display = 'none';
    this.pingWheelPick = -1;
    const offsets = [
      'translate(-50%, 0)',
      'translate(-100%, -50%)',
      'translate(-50%, -100%)',
      'translate(0, -50%)',
    ];
    for (let i = 0; i < this.pingWheelSegments.length; i++) {
      const seg = this.pingWheelSegments[i]!;
      seg.style.transform = offsets[i] ?? '';
      seg.style.background = 'rgba(0,0,0,0.45)';
      seg.style.borderColor = 'rgba(253,164,175,0.35)';
    }
  }

  private pickPingFromMouse(): number {
    const rect = this.pingWheel.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ang = Math.atan2(this.lastMouse.y - cy, this.lastMouse.x - cx);
    let a = ang + Math.PI / 2;
    if (a < 0) a += Math.PI * 2;
    return Math.floor(a / (Math.PI / 2)) % 4;
  }

  private updatePingWheelHighlight(index: number): void {
    this.pingWheelPick = index;
    const offsets = [
      'translate(-50%, 0)',
      'translate(-100%, -50%)',
      'translate(-50%, -100%)',
      'translate(0, -50%)',
    ];
    for (let i = 0; i < this.pingWheelSegments.length; i++) {
      const seg = this.pingWheelSegments[i]!;
      const on = i === index;
      seg.style.transform = `${offsets[i]}${on ? ' scale(1.12)' : ''}`;
      seg.style.background = on ? 'rgba(190,24,93,0.82)' : 'rgba(0,0,0,0.45)';
      seg.style.borderColor = on ? '#ffe4e6' : 'rgba(253,164,175,0.35)';
    }
  }

  private confirmPingWheel(): void {
    if (!this.pingWheelOpen) return;
    let pick = this.pingWheelPick;
    const heldMs = performance.now() - this.pingGDownT;
    if (pick < 0) {
      if (!this.pingMouseMoved && heldMs < 260) {
        pick = 2;
      } else {
        pick = this.pickPingFromMouse();
      }
    }
    this.closePingWheel();
    const opt = PING_WHEEL_OPTIONS[Math.max(0, Math.min(PING_WHEEL_OPTIONS.length - 1, pick))]!;
    const target = this.worldPingMarkers.spawnFromLook(this.camera, this.built.sceneRoot, {
      type: opt.type,
      label: opt.label.toUpperCase(),
    });
    this.pingStack.pushPing(target.x, target.z, 4200);
    this.pushElimFeed(`Ping: ${opt.label}`, opt.type === 'loot' ? 'loot' : 'elim');
    this.audio.playShoot('ar');
  }

  private onWindowMouseMove = (e: MouseEvent): void => {
    this.lastMouse.x = e.clientX;
    this.lastMouse.y = e.clientY;
    if (this.emoteWheelOpen) {
      const dx = e.clientX - this.emoteVDownMouse.x;
      const dy = e.clientY - this.emoteVDownMouse.y;
      if (dx * dx + dy * dy > 36) this.emoteMouseMoved = true;
      this.updateEmoteWheelHighlight(this.pickEmoteFromMouse());
    }
    if (this.chatWheelOpen) {
      const dx = e.clientX - this.chatHDownMouse.x;
      const dy = e.clientY - this.chatHDownMouse.y;
      if (dx * dx + dy * dy > 36) this.chatMouseMoved = true;
      this.updateChatWheelHighlight(this.pickChatFromMouse());
    }
    if (this.pingWheelOpen) {
      const dx = e.clientX - this.pingGDownMouse.x;
      const dy = e.clientY - this.pingGDownMouse.y;
      if (dx * dx + dy * dy > 36) this.pingMouseMoved = true;
      this.updatePingWheelHighlight(this.pickPingFromMouse());
    }
    this.emoteWheel12.onMouseMove(e.clientX, e.clientY);
  };

  private damageWorldBuilding(mesh: THREE.Mesh, damage: number, hitPoint: THREE.Vector3): void {
    if (!mesh.userData['destructible']) return;
    mesh.userData['currentHp'] = (mesh.userData['currentHp'] as number) - damage;
    const hpRatio = (mesh.userData['currentHp'] as number) / (mesh.userData['maxHp'] as number);

    // Stage 2: below 66% — add crack overlay mesh
    if (hpRatio < 0.66 && !mesh.userData['crackMesh1']) {
      const crackGeo = mesh.geometry.clone();
      const crackMat = new THREE.MeshStandardMaterial({
        color: 0x111111,
        transparent: true,
        opacity: 0.35,
        depthWrite: false,
      });
      const crackMesh = new THREE.Mesh(crackGeo, crackMat);
      crackMesh.scale.setScalar(1.015);
      crackMesh.position.copy(mesh.position);
      crackMesh.rotation.copy(mesh.rotation);
      mesh.parent?.add(crackMesh);
      mesh.userData['crackMesh1'] = crackMesh;
    }

    // Stage 3: below 33% — stronger cracks + flickering
    if (hpRatio < 0.33 && !mesh.userData['crackMesh2']) {
      const crackGeo2 = mesh.geometry.clone();
      const crackMat2 = new THREE.MeshStandardMaterial({
        color: 0x330000,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
        emissive: new THREE.Color(0.3, 0, 0),
        emissiveIntensity: 0.4,
      });
      const crackMesh2 = new THREE.Mesh(crackGeo2, crackMat2);
      crackMesh2.scale.setScalar(1.025);
      crackMesh2.position.copy(mesh.position);
      crackMesh2.rotation.copy(mesh.rotation);
      mesh.parent?.add(crackMesh2);
      mesh.userData['crackMesh2'] = crackMesh2;
      mesh.userData['flickering'] = true;
    }

    // Destruction: hp <= 0
    if ((mesh.userData['currentHp'] as number) <= 0) {
      mesh.visible = false;
      const c1 = mesh.userData['crackMesh1'] as THREE.Mesh | undefined;
      const c2 = mesh.userData['crackMesh2'] as THREE.Mesh | undefined;
      if (c1) { c1.parent?.remove(c1); c1.geometry.dispose(); }
      if (c2) { c2.parent?.remove(c2); c2.geometry.dispose(); }
      mesh.userData['crackMesh1'] = undefined;
      mesh.userData['crackMesh2'] = undefined;

      this.spawnDebrisChunks(hitPoint, 6);

      // Tree fall animation
      if (mesh.userData['isTree']) {
        mesh.visible = true;
        const fallAngle = -1.35;
        const fallDuration = 0.6;
        let fallTimer = 0;
        let done = false;
        const tickFall = (dt: number) => {
          if (done) return;
          fallTimer += dt;
          const t = Math.min(fallTimer / fallDuration, 1);
          mesh.rotation.x = fallAngle * t;
          if (fallTimer >= fallDuration + 0.6) {
            mesh.visible = false;
            done = true;
          }
        };
        this.treeFallTickers.push(tickFall);
      }
    }
  }

  private spawnDebrisChunks(pos: THREE.Vector3, count: number): void {
    for (let i = 0; i < count; i++) {
      const size = 0.2 + Math.random() * 0.35;
      const geo = new THREE.BoxGeometry(size, size, size);
      const mat = new THREE.MeshStandardMaterial({ color: 0x8B6914, roughness: 0.9 });
      const chunk = new THREE.Mesh(geo, mat);
      chunk.position.copy(pos);
      this.scene.add(chunk);
      const vx = (Math.random() - 0.5) * 10;
      const vy = 3 + Math.random() * 5;
      const vz = (Math.random() - 0.5) * 10;
      const vel = new THREE.Vector3(vx, vy, vz);
      this.debrisChunks.push({ mesh: chunk, vel, life: 1.8 });
    }
  }

  private showHitMarker(headshot: boolean, opacity: number): void {
    FpsHaptics.vibrateMedium();
    this.triggerCrosshairPulse(headshot ? 0.12 : 0.09);
    this.hitMarkerHeadshot = headshot;
    this.hitMarkerOpacity = opacity;
    if (headshot) {
      this.hitMarker.style.width = '48px';
      this.hitMarker.style.height = '48px';
      this.hitMarker.style.background =
        'linear-gradient(#ffd060,#ffd060) center/3px 16px no-repeat,' +
        'linear-gradient(#ffd060,#ffd060) center/16px 3px no-repeat';
      this.hitMarker.style.filter = 'drop-shadow(0 0 8px #ffd060)';
    } else {
      this.hitMarker.style.width = '38px';
      this.hitMarker.style.height = '38px';
      this.hitMarker.style.background =
        'linear-gradient(#fff,#fff) center/2px 12px no-repeat,' +
        'linear-gradient(#fff,#fff) center/12px 2px no-repeat';
      this.hitMarker.style.filter = 'drop-shadow(0 0 4px #f33)';
    }
  }

  private updateCrosshairVisual(horizSpeed: number, gunSlot: boolean, visible: boolean, pickaxe = false): void {
    if (!visible) {
      this.crosshair.style.opacity = '0';
      return;
    }
    const reticlePulse = this.crosshairPulse;
    const pulseScale = 1 + reticlePulse;
    this.crosshair.style.opacity = String(Math.min(1, 0.92 + reticlePulse * 0.8));
    const { style, color, size } = this.crosshairSettings;
    const colorCss = crosshairColorCss(color);
    const glowCss = crosshairGlowCss(color);
    const sizeMul = size;

    if (pickaxe) {
      this.crosshairRing.style.display = 'none';
      for (const line of this.crosshairLines) {
        line.style.display = 'none';
      }
      const pickSize = 10 * sizeMul;
      const half = pickSize / 2;
      this.crosshairDot.style.cssText =
        `position:absolute;left:50%;top:50%;width:${pickSize}px;height:${pickSize}px;margin:${-half}px 0 0 ${-half}px;border-radius:50%;background:${colorCss};box-shadow:0 0 6px ${glowCss},0 0 2px rgba(0,0,0,0.5);`;
      this.crosshair.style.transform = `translate(-50%,-50%) scale(${pulseScale})`;
      return;
    }

    const dotSize = 4 * sizeMul;
    const dotHalf = dotSize / 2;
    this.crosshairDot.style.cssText =
      `position:absolute;left:50%;top:50%;width:${dotSize}px;height:${dotSize}px;margin:${-dotHalf}px 0 0 ${-dotHalf}px;border-radius:50%;` +
      `background:${colorCss};box-shadow:0 0 5px ${glowCss},0 0 1px rgba(0,0,0,0.55);`;

    if (style === 'dot') {
      this.crosshairRing.style.display = 'none';
      this.crosshairDot.style.display = '';
      for (const line of this.crosshairLines) {
        line.style.display = 'none';
      }
      this.crosshair.style.transform = `translate(-50%,-50%) scale(${pulseScale})`;
      return;
    }

    if (style === 'circle') {
      for (const line of this.crosshairLines) {
        line.style.display = 'none';
      }
      this.crosshairDot.style.display = 'none';
      const ringSize = 16 * sizeMul;
      const ringHalf = ringSize / 2;
      this.crosshairRing.style.display = '';
      this.crosshairRing.style.cssText =
        `position:absolute;left:50%;top:50%;width:${ringSize}px;height:${ringSize}px;margin:${-ringHalf}px 0 0 ${-ringHalf}px;border-radius:50%;` +
        `border:1.5px solid ${colorCss};box-shadow:0 0 5px ${glowCss},0 0 1px rgba(0,0,0,0.55);background:transparent;`;
      this.crosshair.style.transform = `translate(-50%,-50%) scale(${pulseScale})`;
      return;
    }

    this.crosshairRing.style.display = 'none';
    this.crosshairDot.style.display = '';
    for (const line of this.crosshairLines) {
      line.style.display = '';
    }
    const isJumping = !this.player.isGrounded();
    const spreadScale = gunSlot ? this.weapon.getCrosshairSpreadScale(horizSpeed, isJumping) : 1;
    const recoilKick = gunSlot ? this.weapon.getCrosshairRecoilKick() : 0;
    const gapMul = gunSlot ? this.weapon.getCrosshairGapMultiplier() : 1;
    const combined = spreadScale + recoilKick * 2.4;
    const gap = (5 + combined * 4.5) * gapMul * sizeMul;
    const len = (8 + combined * 3.2) * sizeMul;
    const thick = 1.1 * sizeMul;
    const shadow = '0 0 2px rgba(0,0,0,0.5)';
    for (const line of this.crosshairLines) {
      const dir = line.dataset.dir;
      if (dir === 'top') {
        line.style.cssText =
          `position:absolute;left:50%;top:50%;width:${thick}px;height:${len}px;margin-left:${-thick / 2}px;transform:translateY(${-gap - len}px);background:${colorCss};box-shadow:${shadow};border-radius:0.5px;`;
      } else if (dir === 'bottom') {
        line.style.cssText =
          `position:absolute;left:50%;top:50%;width:${thick}px;height:${len}px;margin-left:${-thick / 2}px;transform:translateY(${gap}px);background:${colorCss};box-shadow:${shadow};border-radius:0.5px;`;
      } else if (dir === 'left') {
        line.style.cssText =
          `position:absolute;left:50%;top:50%;width:${len}px;height:${thick}px;margin-top:${-thick / 2}px;transform:translateX(${-gap - len}px);background:${colorCss};box-shadow:${shadow};border-radius:0.5px;`;
      } else {
        line.style.cssText =
          `position:absolute;left:50%;top:50%;width:${len}px;height:${thick}px;margin-top:${-thick / 2}px;transform:translateX(${gap}px);background:${colorCss};box-shadow:${shadow};border-radius:0.5px;`;
      }
    }
    this.crosshair.style.transform = `translate(-50%,-50%) scale(${combined * pulseScale})`;
  }

  private updateScopeReticlePulse(active: boolean): void {
    if (!this.sniperScopeReticle) return;
    if (!active) {
      this.sniperScopeReticle.style.opacity = '1';
      this.sniperScopeReticle.style.transform = 'translate(-50%,-50%) scale(1)';
      return;
    }
    const reticlePulse = this.crosshairPulse;
    this.sniperScopeReticle.style.opacity = String(Math.min(1, 0.9 + reticlePulse));
    this.sniperScopeReticle.style.transform = `translate(-50%,-50%) scale(${1 + reticlePulse * 0.7})`;
  }

  private showLootToast(loot: ChestLootPayload): void {
    const parts: string[] = [];
    parts.push(`<span style="color:#f1c40f">+${loot.mats} Mats</span>`);
    parts.push(`<span style="color:#5dade2">+${loot.shield} Shield</span>`);
    if (loot.weapon) {
      const names: Record<WeaponKind, string> = {
        ar: 'Assault Rifle',
        shotgun: 'Pump Shotgun',
        sniper: 'Bolt Sniper',
        smg: 'SMG',
        pistol: 'Pistol',
      };
      const displayName = formatRarityWeaponName(
        loot.weapon.rarity,
        names[loot.weapon.kind],
      );
      parts.push(
        `<span style="color:${getRarityCssColor(loot.weapon.rarity)}">${displayName}</span>`,
      );
    }
    if (loot.consumable) {
      const cLabels: Record<ChestConsumable, string> = {
        bandage: 'Bandage',
        mini: 'Mini Shield',
        chug: 'Chug Jug',
      };
      const cColors: Record<ChestConsumable, string> = {
        bandage: '#58d68d',
        mini: '#85c1e9',
        chug: '#af7ac5',
      };
      parts.push(`<span style="color:${cColors[loot.consumable]}">${cLabels[loot.consumable]}</span>`);
    }
    if (loot.mods?.length) {
      for (const mod of loot.mods) {
        parts.push(`<span style="color:#d7aefb">${getModDisplayName(mod.kind)}</span>`);
      }
    }
    const tierTag =
      loot.tier === 'rare'
        ? '<div style="margin-top:6px;font:700 11px system-ui;color:#d7aefb;letter-spacing:0.12em">RARE CHEST</div>'
        : '';
    this.lootToast.style.setProperty('--fps-toast-rail', loot.tier === 'rare' ? '#d7aefb' : '#ffd54a');
    this.lootToast.style.setProperty(
      '--fps-toast-glow',
      loot.tier === 'rare' ? 'rgba(215,174,251,0.26)' : 'rgba(255,213,74,0.24)',
    );
    this.lootToast.innerHTML = parts.join('<span style="color:#667;margin:0 6px">·</span>') + tierTag;
    this.lootToast.style.display = 'block';
    this.lootToastTimer = 2.4;
  }

  private showPickupToast(text: string, color = '#ffdd33'): void {
    this.lootToast.style.setProperty('--fps-toast-rail', color);
    this.lootToast.style.setProperty('--fps-toast-glow', `${color}40`);
    this.lootToast.innerHTML = `<span style="color:${color}">${text}</span>`;
    this.lootToast.style.display = 'block';
    this.lootToastTimer = 1.8;
  }

  private showPickupCard(name: string, rarity: WeaponRarity, meta?: string, action?: string, delayMs = 0): void {
    this.clearScheduledTimeout(this.pickupCardDelayTimer);
    if (delayMs > 0) {
      this.pickupCard.hide();
      this.pickupCardTimer = 0;
      this.pickupCardDelayTimer = this.scheduleTimeout(() => {
        this.pickupCard.show({ name, rarity, meta, action });
        this.pickupCardTimer = 2.1;
      }, delayMs);
      return;
    }
    this.pickupCard.show({ name, rarity, meta, action });
    this.pickupCardTimer = 2.1;
  }

  private showRarityMoment(name: string, rarity: WeaponRarity | 'mythic', action?: string): boolean {
    return this.rarityStinger.show({ name, rarity, action });
  }

  private rarityRank(rarity: WeaponRarity): number {
    return ['common', 'uncommon', 'rare', 'epic', 'legendary'].indexOf(rarity);
  }

  private getWeaponPickupComparison(kind: WeaponKind, rarity: WeaponRarity): { action: string; detail: string } {
    const slot = this.getLoadoutPreferredSlot(kind);
    const offered = `${RARITY_LABELS[rarity]} ${this.weaponDisplayName(kind)}`;
    if (!this.hotbar.isSlotFilled(slot)) {
      return { action: 'Pickup', detail: `Offered: ${offered}` };
    }

    const currentKind = this.hotbar.weaponKindForSlot(slot);
    const currentRarity = this.hotbar.getSlotRarity(slot);
    const current = currentKind
      ? `${RARITY_LABELS[currentRarity]} ${this.weaponDisplayName(currentKind)}`
      : `${RARITY_LABELS[currentRarity]} weapon`;
    const offeredRank = this.rarityRank(rarity);
    const currentRank = this.rarityRank(currentRarity);
    const action = offeredRank > currentRank
      ? 'Upgrade'
      : currentKind && currentKind !== kind && offeredRank >= currentRank
        ? 'Sidegrade'
        : 'Swap';
    return { action, detail: `Current: ${current} -> Offered: ${offered}` };
  }

  private weaponDisplayName(kind: WeaponKind): string {
    const names: Record<WeaponKind, string> = {
      ar: 'Assault Rifle',
      shotgun: 'Pump Shotgun',
      sniper: 'Bolt Sniper',
      smg: 'SMG',
      pistol: 'Pistol',
    };
    return names[kind];
  }

  private mythicDisplayName(kind: MythicKind): string {
    const names: Record<MythicKind, string> = {
      drum_gun: 'Drum Gun',
      infinity_blade: 'Infinity Blade',
      boom_bow: 'Boom Bow',
      grappler: 'Grappler',
      jetpack: 'Jetpack',
    };
    return names[kind];
  }

  private weaponDisplayColor(kind: WeaponKind): string {
    const colors: Record<WeaponKind, string> = {
      ar: '#f1c40f',
      shotgun: '#bb8fce',
      sniper: '#5dade2',
      smg: '#58d68d',
      pistol: '#bdc3c7',
    };
    return colors[kind];
  }

  private trySpawnElimWeaponDrop(pos: THREE.Vector3): void {
    if (Math.random() >= 0.3) return;
    const kinds: WeaponKind[] = ['ar', 'shotgun', 'sniper', 'smg', 'pistol'];
    const kind = kinds[Math.floor(Math.random() * kinds.length)]!;
    this.weaponPickups.spawnDrop(pos.x, pos.z, kind);
  }

  private applyWeaponPickup(pickup: WeaponPickupResult): void {
    const comparison = this.getWeaponPickupComparison(pickup.kind, pickup.rarity);
    this.applyChestWeaponDrop(pickup.kind, pickup.rarity);
    const name = formatRarityWeaponName(pickup.rarity, this.weaponDisplayName(pickup.kind));
    this.showPickupToast(`Picked up ${name}`, getRarityCssColor(pickup.rarity));
    const statsLine = `${comparison.detail}${pickup.mod ? ` · Mod: ${pickup.mod}` : ''}`;
    const stingerShown = this.showRarityMoment(name, pickup.rarity, comparison.action);
    this.showPickupCard(name, pickup.rarity, statsLine, comparison.action, stingerShown ? 520 : 0);
    this.lootRarity.showTooltip(name, pickup.rarity, statsLine);
    if (pickup.mod) {
      this.tryApplyWeaponMod(pickup.mod);
    }
    this.audio.playChest();
  }

  private showPoiZone(name: string): void {
    // Phase 31X-13: enter-toast banner + elim-feed cue
    const colors = getPoiZoneColors(name);
    this.poiLabelEl.textContent = name;
    this.poiLabelEl.style.display = 'block';
    this.poiLabelEl.style.background = colors.bg;
    this.poiLabelEl.style.border = `3px solid ${colors.border}`;
    this.poiLabelEl.style.textShadow = `0 4px 22px rgba(0,0,0,0.92), 0 0 34px ${colors.glow}`;
    this.poiLabelEl.style.animation = 'none';
    void this.poiLabelEl.offsetWidth;
    this.poiLabelEl.style.animation = 'poiZoneBannerIn 3s ease-out forwards';
    this.poiLabelAge = 0;
    this.pushElimFeed(`Entered ${name}`, 'loot');
  }

  private updatePoiZones(px: number, pz: number, dt: number): void {
    if (this.poiLabelAge >= 0) {
      this.poiLabelAge += dt;
      if (this.poiLabelAge > 3.05) {
        this.poiLabelAge = -1;
        this.poiLabelEl.style.display = 'none';
        this.poiLabelEl.style.animation = 'none';
      }
    }

    if (this.matchEnd || this.introActive) return;

    const found = findPoiZoneAt(px, pz);

    if (found) {
      if (found.name !== this.poiCurrentZone) {
        this.poiCurrentZone = found.name;
        const until = this.poiZoneCooldowns.get(found.name) ?? 0;
        if (this.matchElapsed >= until) {
          this.showPoiZone(found.name);
          this.poiZoneCooldowns.set(found.name, this.matchElapsed + 20);
          this.npcQuestVisit.recordPoiVisit(found.name);
        }
      }
    } else {
      this.poiCurrentZone = null;
    }
  }

  private applyChestConsumable(kind: ChestConsumable): void {
    if (kind === 'bandage') {
      if (this.health < 100) this.health = Math.min(100, this.health + 38);
      else this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 32));
    } else if (kind === 'mini') {
      this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 25));
    } else {
      this.shieldHud.setShield(100);
    }
    this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
  }

  private equipWeaponToSlot(kind: WeaponKind, rarity: WeaponRarity, preferSlot?: number): void {
    this.weapon.clearModForKind(kind);
    const slot = preferSlot ?? this.getLoadoutPreferredSlot(kind);
    if (kind === 'ar' || kind === 'smg' || kind === 'pistol') {
      this.hotbar.setSlot0Weapon(kind);
      this.hotbar.setSlotRarity(0, rarity);
      this.hotbar.markSlotFilled(0);
    } else if (kind === 'shotgun') {
      this.hotbar.setSlotRarity(1, rarity);
      this.hotbar.markSlotFilled(1);
    } else if (kind === 'sniper') {
      this.hotbar.setSlotRarity(2, rarity);
      this.hotbar.markSlotFilled(2);
    }
    this.hotbar.setSelected(slot);
    this.syncHotbarFromSlot();
  }

  private getLoadoutPreferredSlot(kind: WeaponKind): number {
    const slot0Accepts = LOADOUT_SLOT0_ACCEPTS[this.loadoutKind];
    const slot1Pref = LOADOUT_SLOT1_WEAPON[this.loadoutKind];

    if (kind === 'ar' || kind === 'smg' || kind === 'pistol') {
      if (!this.hotbar.isSlotFilled(0) && slot0Accepts.includes(kind)) return 0;
      return 0;
    }
    if (kind === 'shotgun') {
      if (!this.hotbar.isSlotFilled(1) && slot1Pref === 'shotgun') return 1;
      return 1;
    }
    if (kind === 'sniper') {
      if (!this.hotbar.isSlotFilled(2) && slot1Pref === 'sniper') return 2;
      return 2;
    }
    return this.hotbar.getBestSlotForWeapon(kind);
  }

  private applyChestWeaponDrop(weapon: WeaponKind, rarity: WeaponRarity = 'common'): void {
    this.equipWeaponToSlot(weapon, rarity, this.getLoadoutPreferredSlot(weapon));
  }

  private applySupplyDropWeaponLoot(dropX: number, dropZ: number): void {
    const kind = VEND_WEAPON_KINDS[Math.floor(Math.random() * VEND_WEAPON_KINDS.length)]!;
    const rarity = rollSupplyDropWeaponRarity();

    if (this.hotbar.areAllWeaponSlotsFilled()) {
      this.weaponPickups.spawnDrop(dropX, dropZ, kind, rarity);
      this.pushElimFeed(
        `${RARITY_LABELS[rarity]} ${WEAPON_SHORT_LABELS[kind]} on the ground`,
        'loot',
      );
      return;
    }

    this.equipWeaponToSlot(kind, rarity, this.hotbar.getBestSlotForWeapon(kind));
    this.pushElimFeed(
      `Supply drop — ${RARITY_LABELS[rarity]} ${WEAPON_SHORT_LABELS[kind]}`,
      'loot',
    );
  }

  private announceObjective(
    key: string,
    line: string,
    opts?: { x?: number; z?: number; worldLabel?: string; cooldownS?: number },
  ): void {
    const now = this.matchElapsed;
    const cooldown = opts?.cooldownS ?? 18;
    const last = this.objectiveAnnouncementCooldowns.get(key) ?? -Infinity;
    if (now - last < cooldown) return;

    this.objectiveAnnouncementCooldowns.set(key, now);
    this.pushElimFeed(line, 'loot');
    if (opts?.x != null && opts.z != null) {
      this.worldPingMarkers.spawnAt(new THREE.Vector3(opts.x, 1.2, opts.z), {
        label: opts.worldLabel ?? 'OBJ',
      });
    }
  }

  private applyLlamaLoot(): void {
    const pos = this.llama?.getXZ();
    this.bots.setLlamaTarget(null, null);
    const mats = 120 + Math.floor(Math.random() * 61);
    this.mats += mats;
    this.hudMats.textContent = `Mats ${this.mats}`;
    const types: ChestConsumable[] = ['bandage', 'mini', 'chug'];
    const count = 1 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      const kind = types[Math.floor(Math.random() * types.length)]!;
      if (kind === 'mini') {
        this.miniShieldCount += 1;
      } else if (kind === 'bandage') {
        this.bandageInventoryCount += 1;
      } else {
        this.applyChestConsumable('chug');
      }
    }
    this.syncConsumableHud();
    this.audio.playChest();
    const legKind = (['ar', 'smg', 'shotgun', 'sniper'] as const)[Math.floor(Math.random() * 4)]!;
    const legRarity = rollSupplyDropWeaponRarity();
    this.applyChestWeaponDrop(legKind, legRarity);
    this.pushElimFeed(`Llama dropped ${legRarity.toUpperCase()} ${legKind.toUpperCase()}!`, 'loot');
    this.announceObjective('llama-loot', 'LLAMA LOOT!', {
      x: pos?.x,
      z: pos?.z,
      worldLabel: 'LLAMA',
      cooldownS: 30,
    });
    if (this.xpBar.addXp(45)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
  }

  private spawnLlamaAtRandomPoi(): void {
    const poi = FPS_POI_ZONES[Math.floor(Math.random() * FPS_POI_ZONES.length)]!;
    const angle = Math.random() * Math.PI * 2;
    const dist = Math.random() * poi.radius * 0.55;
    const x = poi.x + Math.cos(angle) * dist;
    const z = poi.z + Math.sin(angle) * dist;
    this.llama = new FpsLlama(this.built.sceneRoot, x, z);
    this.bots.setLlamaTarget(x, z);
    this.announceObjective('llama-spotted', `Llama spotted at ${poi.name}!`, {
      x,
      z,
      worldLabel: 'LLAMA',
      cooldownS: 30,
    });
  }

  private applyChestLoot(loot: ChestLootPayload, chestX?: number, chestZ?: number): void {
    const burstX = chestX ?? this.player.yawObject.position.x;
    const burstZ = chestZ ?? this.player.yawObject.position.z;
    this.chestBurst.spawn(burstX, 0.55, burstZ, loot.tier);
    this.trackLootEffect(burstChest(
      this.scene,
      new THREE.Vector3(burstX, 0.55, burstZ),
      loot.weapon?.rarity ?? (loot.tier === 'rare' ? 'epic' : 'uncommon'),
      this.particles,
    ));
    this.mats += loot.mats;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.trackDomEffect(spawnPickupFlyChip(document.body, `BUILD MATS +${loot.mats}`, '#f1c40f'));
    this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + loot.shield));
    if (loot.consumable) {
      if (loot.consumable === 'mini') {
        this.miniShieldCount += 1;
        this.syncConsumableHud();
      } else if (loot.consumable === 'bandage') {
        this.bandageInventoryCount += 1;
        this.syncConsumableHud();
      } else {
        this.applyChestConsumable(loot.consumable);
      }
    }
    const weaponPickupComparison = loot.weapon ? this.getWeaponPickupComparison(loot.weapon.kind, loot.weapon.rarity) : undefined;
    if (loot.weapon) {
      if (loot.tier === 'rare' && chestX != null && chestZ != null) {
        this.weaponPickups.spawnDrop(chestX, chestZ, loot.weapon.kind, loot.weapon.rarity);
        this.lootRarity.spawnBeam(chestX, 0, chestZ, loot.weapon.rarity);
      } else {
        this.applyChestWeaponDrop(loot.weapon.kind, loot.weapon.rarity);
      }
      if (this.rarityRank(loot.weapon.rarity) >= this.rarityRank('rare')) {
        this.trackLootEffect(spawnRareLootArrivalPulse(this.scene, new THREE.Vector3(burstX, 0.42, burstZ), loot.weapon.rarity));
      }
    } else if (loot.tier === 'rare') {
      this.trackLootEffect(spawnRareLootArrivalPulse(this.scene, new THREE.Vector3(burstX, 0.42, burstZ), 'epic'));
    }
    if (loot.mods?.length) {
      for (const mod of loot.mods) {
        this.tryApplyWeaponMod(mod, chestX, chestZ);
      }
    }
    if (loot.airstrike) {
      const gained = this.airstrike.addCount(1);
      if (gained > 0) {
        this.pushElimFeed('Air Strike +1', 'loot');
        this.updateTacticalHud();
      }
    }
    if (loot.rebootCard) {
      this.rebootCardCount = Math.min(2, this.rebootCardCount + 1);
      this.pushElimFeed('Reboot Card +1', 'loot');
      this.updateTacticalHud();
    }
    if (loot.chugSplash) {
      const gained = this.chugSplash.addCount(1);
      if (gained > 0) {
        this.pushElimFeed('Chug Splash +1', 'loot');
        this.updateTacticalHud();
      }
    }
    if (loot.shockwave) {
      const gained = this.shockwave.addCount(1);
      if (gained > 0) {
        this.pushElimFeed('Shockwave +1', 'loot');
        this.updateTacticalHud();
      }
    }
    this.player.applyChestOpenNudge(loot.tier);
    this.audio.playChest(loot.tier);
    this.statChests += 1;
    this.dailyChallenges.recordChestOpen();
    this.pushBotFeed(`opened a ${loot.tier} chest (+${loot.mats} mats)`);
    this.revealChestLootUi(loot, weaponPickupComparison);
  }

  private flashLootRarity(rarity: WeaponRarity | undefined, tier: ChestLootPayload['tier']): void {
    const hex = chestLootRarityFlashHex(rarity, tier);
    const css = `#${hex.toString(16).padStart(6, '0')}`;
    this.lootRarityFlashEl.style.background =
      `radial-gradient(ellipse at center, ${css}66 0%, transparent 68%)`;
    this.lootRarityFlashTimer = this.prefersReducedMotion() ? 0.14 : 0.32;
    this.lootRarityFlashEl.style.opacity = this.prefersReducedMotion() ? '0.38' : '0.88';
  }

  private updateLootRarityFlash(dt: number): void {
    if (this.lootRarityFlashTimer <= 0) {
      this.lootRarityFlashEl.style.opacity = '0';
      return;
    }
    this.lootRarityFlashTimer = Math.max(0, this.lootRarityFlashTimer - dt);
    const peak = this.prefersReducedMotion() ? 0.38 : 0.88;
    const fadeDur = this.prefersReducedMotion() ? 0.14 : 0.32;
    this.lootRarityFlashEl.style.opacity = String(peak * (this.lootRarityFlashTimer / fadeDur));
  }

  private revealChestLootUi(
    loot: ChestLootPayload,
    weaponPickupComparison?: { action: string; detail: string },
  ): void {
    let step = 0;
    const staggerMs = CHEST_LOOT_STAGGER_SEC * 1000;
    const scheduleStep = (fn: () => void): void => {
      this.scheduleTimeout(fn, step * staggerMs);
      step += 1;
    };

    scheduleStep(() => {
      this.pushElimFeed(
        loot.tier === 'rare' ? `Rare chest · +${loot.shield} shield` : `Chest loot · +${loot.shield} shield`,
        'loot',
      );
    });

    scheduleStep(() => {
      this.flashLootRarity(loot.weapon?.rarity, loot.tier);
      this.showLootToast(loot);
    });

    if (loot.weapon) {
      const weapon = loot.weapon;
      const name = formatRarityWeaponName(weapon.rarity, this.weaponDisplayName(weapon.kind));
      scheduleStep(() => {
        this.flashLootRarity(weapon.rarity, loot.tier);
        const stingerShown = this.showRarityMoment(name, weapon.rarity, weaponPickupComparison?.action);
        this.showPickupCard(
          name,
          weapon.rarity,
          `${weaponPickupComparison?.detail ?? 'Chest drop'} · +${loot.mats} mats · +${loot.shield} shield`,
          weaponPickupComparison?.action,
          stingerShown ? 520 : 0,
        );
      });
    } else {
      scheduleStep(() => {
        const fallbackRarity = loot.tier === 'rare' ? 'epic' : 'uncommon';
        const fallbackName = loot.tier === 'rare' ? 'Rare Chest Loot' : 'Chest Loot';
        this.flashLootRarity(fallbackRarity, loot.tier);
        const stingerShown = this.showRarityMoment(fallbackName, fallbackRarity, 'Pickup');
        this.showPickupCard(
          fallbackName,
          fallbackRarity,
          `+${loot.mats} mats · +${loot.shield} shield`,
          'Pickup',
          stingerShown ? 520 : 0,
        );
      });
    }

    scheduleStep(() => {
      if (this.xpBar.addXp(loot.tier === 'rare' ? 38 : 28)) {
        this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
      }
    });
  }

  private onPlayerElimination(opts?: { headshot?: boolean; victim?: string }): void {
    FpsHaptics.vibrateHeavy();
    this.hitFeedback.showHit('elimination');
    this.applyHitConfirmImpulse('elimination');
    this.playerEliminations += 1;
    this.elimStreak += 1;
    this.bestElimStreak = Math.max(this.bestElimStreak, this.elimStreak);
    this.npcQuest.recordPlayerElimination();
    this.dailyChallenges.recordElimination();
    this.updateKillLeader();

    FpsGoldBars.add(GOLD_PER_ELIM);
    this.syncGoldHud();

    const bpElimXp = FpsBattlePass.xpForElim(!!opts?.headshot);
    this.grantBattlePassXp(bpElimXp);
    FpsBattlePass.showElimXpBurst(bpElimXp);

    if (opts?.victim) this.checkBountyElim(opts.victim);

    if (this.elimStreak >= 2) {
      const bounty = 15 + (this.elimStreak - 2) * 10;
      this.mats += bounty;
      this.hudMats.textContent = `Mats ${this.mats}`;
      this.pushElimFeed(`Kill streak x${this.elimStreak} — +${bounty} mats`, 'loot');
    }

    if (!this.knocked && this.health > 0 && this.isMatchLive()) {
      this.health = Math.min(100, this.health + this.siphonAmount);
      this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + this.siphonAmount));
      this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
      this.pushElimFeed(`Siphon +${this.siphonAmount} HP/shield`, 'loot');
    }

    if (opts?.headshot) {
      this.audio.playHeadshotElim();
    }
  }

  private recordElim(killer: string, _victim: string): void {
    if (killer === 'You') return;
    this.elimCounts.set(killer, (this.elimCounts.get(killer) ?? 0) + 1);
    this.updateKillLeader();
  }

  private updateKillLeader(): void {
    const counts = new Map(this.elimCounts);
    if (this.playerEliminations > 0) {
      counts.set('You', this.playerEliminations);
    }

    let bestName: string | null = null;
    let bestCount = 0;
    for (const [name, count] of counts) {
      if (count > bestCount) {
        bestCount = count;
        bestName = name;
      }
    }

    if (!bestName || bestCount <= 0) {
      this.killLeaderName = null;
      this.killLeaderHud.style.display = 'none';
      this.playerCrownHud.style.display = 'none';
      this.bots?.setKillLeaderBotName(null);
      return;
    }

    const changed = bestName !== this.killLeaderName;
    this.killLeaderName = bestName;
    this.killLeaderHud.style.display = 'block';
    this.killLeaderHud.textContent = `👑 KILL LEADER: ${bestName.toUpperCase()}`;
    this.playerCrownHud.style.display = bestName === 'You' ? 'block' : 'none';
    this.bots?.setKillLeaderBotName(bestName);

    if (changed) {
      this.killLeaderHud.style.animation = 'none';
      void this.killLeaderHud.offsetWidth;
      this.killLeaderHud.style.animation = 'stormPhaseBannerIn 0.85s ease-out forwards';
    }
  }

  private lastAliveCountHud = -1;

  private updateAliveCounter(): void {
    const alive = this.countMatchAlive();
    // Phase 30X-3: punch scale when players remaining drops
    if (this.lastAliveCountHud >= 0 && alive < this.lastAliveCountHud) {
      this.aliveCounter.style.transition = 'transform 0.18s cubic-bezier(0.22,1,0.36,1)';
      this.aliveCounter.style.transform = 'scale(1.18)';
      window.setTimeout(() => {
        this.aliveCounter.style.transform = 'scale(1)';
      }, 180);
    }
    this.lastAliveCountHud = alive;
    this.aliveCounter.textContent = `Players · ${alive}`;
    this.checkPlacementThresholds(alive);
  }

  private buildSquadCardHtml(stats: ReturnType<FpsTeammate['getSquadStats']>): string {
    let hpWidth: number;
    let hpBg = 'linear-gradient(90deg,#36e36b,#9bff6a)';
    let shieldWidth = Math.max(0, stats.shield);
    let statusHtml = '';

    if (stats.dead) {
      hpWidth = 0;
      shieldWidth = 0;
      statusHtml = `<div style="margin-top:5px;font:700 9px system-ui;color:#ff7070;letter-spacing:0.05em">ELIMINATED · ${Math.max(0, Math.ceil(stats.respawnIn))}s</div>`;
    } else if (stats.knocked) {
      hpWidth = Math.max(0, (stats.bleedout / KNOCK_BLEEDOUT) * 100);
      hpBg = 'linear-gradient(90deg,#ff9a3c,#ffb86a)';
      shieldWidth = 0;
      statusHtml = `<div style="margin-top:5px;font:700 9px system-ui;color:#ff9a3c;letter-spacing:0.05em">KNOCKED · ${Math.max(0, Math.ceil(stats.bleedout))}s</div>`;
    } else {
      hpWidth = Math.max(0, stats.hp);
    }

    return (
      `<div style="min-width:168px;padding:8px 10px;border-radius:10px;background:rgba(8,14,22,0.9);border:1px solid rgba(54,227,107,0.45)">` +
      `<div style="font:800 11px system-ui;color:#9bff6a;letter-spacing:0.06em;margin-bottom:6px">${stats.name.toUpperCase()}</div>` +
      `<div style="height:6px;border-radius:3px;background:rgba(0,0,0,0.55);border:1px solid rgba(54,227,107,0.35);overflow:hidden;margin-bottom:4px">` +
      `<div style="height:100%;width:${hpWidth}%;background:${hpBg};transition:width 0.08s linear"></div></div>` +
      `<div style="height:5px;border-radius:3px;background:rgba(0,0,0,0.55);border:1px solid rgba(54,227,107,0.28);overflow:hidden">` +
      `<div style="height:100%;width:${shieldWidth}%;background:linear-gradient(90deg,#2ecc71,#7ef9a8);transition:width 0.08s linear"></div></div>` +
      statusHtml +
      '</div>'
    );
  }

  private updateSquadHud(): void {
    if (
      !this.isSquadMode() ||
      this.teammates.length === 0 ||
      !this.isMatchLive() ||
      this.matchEnd
    ) {
      this.hudSquad.style.display = 'none';
      this.hudSquad.innerHTML = '';
      return;
    }
    this.hudSquad.style.display = 'flex';
    this.hudSquad.innerHTML = this.teammates
      .map((tm) => this.buildSquadCardHtml(tm.getSquadStats()))
      .join('');
    for (let i = 0; i < this.teammates.length; i++) {
      const tm = this.teammates[i]!;
      if (tm.isKnocked() && !this.teammateKnockPinged[i]) {
        this.teammateKnockPinged[i] = true;
        tm.getPosition(this.fxScratch);
        this.pingStack.pushPing(this.fxScratch.x, this.fxScratch.z, 5200);
        this.pushElimFeed(`${tm.name} knocked — squad ping`, 'elim');
      } else if (!tm.isKnocked() && !tm.getSquadStats().dead) {
        this.teammateKnockPinged[i] = false;
      }
    }
  }

  private getSquadTeammates(): FpsTeammate[] {
    return this.teammates;
  }

  private getCarryTarget(): FpsTeammate | null {
    if (this.carriedTeammate || this.knocked || this.matchEnd || !this.isMatchLive()) return null;
    const b = this.player.body.position;
    for (const tm of this.getSquadTeammates()) {
      if (tm.isCarryable() && tm.isWithinReviveRange(b.x, b.z, CARRY_RADIUS)) return tm;
    }
    return null;
  }

  private syncCarriedTeammatePosition(): void {
    if (!this.carriedTeammate) return;
    const b = this.player.body.position;
    const carrierPos = new THREE.Vector3(b.x, b.y, b.z);
    this.carriedTeammate.setCarried(true, carrierPos, this.player.yawObject.rotation.y);
  }

  private pickUpCarriedTeammate(tm: FpsTeammate): void {
    this.carriedTeammate = tm;
    this.teammateCarryProgress = 0;
    this.player.setCarrying(true);
    this.syncCarriedTeammatePosition();
    this.audio.playCarryPickup();
    this.hudCarry.style.display = 'none';
    const fillEl = this.hudCarry.querySelector('#carry-progress-fill') as HTMLDivElement;
    fillEl.style.width = '0%';
    this.pushElimFeed(`Carrying ${tm.name}`, 'loot');
  }

  private dropCarriedTeammate(): void {
    if (!this.carriedTeammate) return;
    const b = this.player.body.position;
    const dropPos = new THREE.Vector3(b.x, b.y, b.z);
    const tm = this.carriedTeammate;
    tm.setCarried(false, dropPos);
    this.carriedTeammate = null;
    this.teammateCarryProgress = 0;
    this.player.setCarrying(false);
    this.hudCarry.style.display = 'none';
    const fillEl = this.hudCarry.querySelector('#carry-progress-fill') as HTMLDivElement;
    fillEl.style.width = '0%';
    this.pushElimFeed(`Dropped ${tm.name}`, 'loot');
  }

  private updateTeammateCarry(dt: number): void {
    const hintEl = this.hudCarry.querySelector('#carry-hint') as HTMLDivElement;
    const fillEl = this.hudCarry.querySelector('#carry-progress-fill') as HTMLDivElement;
    const progressBar = fillEl.parentElement as HTMLDivElement;

    if (this.carriedTeammate) {
      this.syncCarriedTeammatePosition();
      if (!this.knocked && this.isMatchLive() && !this.matchEnd) {
        this.hudCarry.style.display = 'block';
        hintEl.textContent = 'F — Drop teammate';
        progressBar.style.display = 'none';
      } else {
        this.hudCarry.style.display = 'none';
      }
      this.teammateCarryProgress = 0;
      fillEl.style.width = '0%';
      return;
    }

    progressBar.style.display = 'block';
    const target = this.getCarryTarget();
    if (!target) {
      this.teammateCarryProgress = 0;
      this.hudCarry.style.display = 'none';
      fillEl.style.width = '0%';
      return;
    }

    this.hudCarry.style.display = 'block';
    hintEl.textContent = `HOLD F — Carry ${target.name}`;

    if (this.player.isKeyHeld('KeyF')) {
      this.teammateCarryProgress += dt;
      if (this.teammateCarryProgress >= TEAMMATE_CARRY_HOLD) {
        this.pickUpCarriedTeammate(target);
        return;
      }
    } else {
      this.teammateCarryProgress = 0;
    }

    fillEl.style.width = `${Math.min(100, (this.teammateCarryProgress / TEAMMATE_CARRY_HOLD) * 100)}%`;
  }

  private updateTeammateRevive(dt: number): void {
    const hintEl = this.hudRevive.querySelector('#revive-hint') as HTMLDivElement;
    const fillEl = this.hudRevive.querySelector('#revive-progress-fill') as HTMLDivElement;
    const target = this.getNearestKnockedTeammate();

    if (!target || !this.isMatchLive() || this.matchEnd || this.knocked) {
      this.teammateReviveProgress = 0;
      this.reviveTarget = null;
      this.hudRevive.style.display = 'none';
      fillEl.style.width = '0%';
      return;
    }

    if (this.reviveTarget !== target) {
      this.reviveTarget = target;
      this.teammateReviveProgress = 0;
    }

    this.hudRevive.style.display = 'block';
    // Phase 33X-19: revive HUD urgency by teammate bleedout remaining
    const stats = target.getSquadStats?.() as { bleedout?: number } | undefined;
    const bleed = stats?.bleedout ?? 30;
    const urgent = bleed < 12;
    this.hudRevive.style.borderColor = urgent
      ? 'rgba(255,80,80,0.75)'
      : 'rgba(46,230,255,0.45)';
    this.hudRevive.style.boxShadow = urgent
      ? '0 0 18px rgba(255,60,60,0.35)'
      : '';
    hintEl.textContent = urgent
      ? `HOLD E — Revive ${target.name} · ${Math.ceil(bleed)}s!`
      : `HOLD E — Revive ${target.name}`;
    hintEl.style.color = urgent ? '#ffb0b0' : '';

    if (this.player.isKeyHeld('KeyE')) {
      this.teammateReviveProgress += dt;
      if (this.teammateReviveProgress >= TEAMMATE_REVIVE_HOLD) {
        target.revive();
        this.teammateReviveProgress = 0;
        this.reviveTarget = null;
        this.hudRevive.style.display = 'none';
        fillEl.style.width = '0%';
        this.audio.playJump();
        this.pushElimFeed(`Revived ${target.name}`, 'loot');
        return;
      }
    } else {
      this.teammateReviveProgress = 0;
    }

    fillEl.style.width = `${Math.min(100, (this.teammateReviveProgress / TEAMMATE_REVIVE_HOLD) * 100)}%`;
    fillEl.style.background = urgent
      ? 'linear-gradient(90deg,#ff5050,#ff9090)'
      : 'linear-gradient(90deg,#2ee6ff,#8af)';
  }

  private canReviveTeammateNearby(): boolean {
    if (this.knocked || this.matchEnd) return false;
    return this.getNearestKnockedTeammate() != null;
  }

  private canFishNearby(): boolean {
    if (this.knocked || this.matchEnd || !this.isMatchLive()) return false;
    if (this.canReviveTeammateNearby()) return false;
    if (this.vehicles.isPlayerDriving() || this.ziplines.isRiding()) return false;
    const p = this.player.body.position;
    return this.fishing.canFish(p.x, p.z);
  }

  private updateFishing(dt: number): void {
    const hintEl = this.hudFish.querySelector('#fish-hint') as HTMLDivElement;
    const fillEl = this.hudFish.querySelector('#fish-progress-fill') as HTMLDivElement;

    if (!this.canFishNearby()) {
      this.hudFish.style.display = 'none';
      fillEl.style.width = '0%';
      return;
    }

    const remaining = 3 - this.fishing.getCatches();
    this.hudFish.style.display = 'block';
    hintEl.textContent =
      remaining > 0 ? `[Hold E] Fish · ${remaining} left` : '[Hold E] Fish';

    const p = this.player.body.position;
    const result = this.fishing.update(dt, p.x, p.z, this.player.isKeyHeld('KeyE'));
    fillEl.style.width = `${this.fishing.getProgressPct()}%`;

    if (result === 'caught') {
      this.health = Math.min(100, this.health + FISH_HP_HEAL);
      this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
      this.audio.playFishCatch();
      this.pushElimFeed(`Caught Flopper +${FISH_HP_HEAL} HP`, 'loot');
      this.hudFish.style.display = this.canFishNearby() ? 'block' : 'none';
      fillEl.style.width = '0%';
    }
  }

  private pushElimFeed(line: string, kind: 'normal' | 'knock' | 'loot' | 'elim' = 'normal'): void {
    const urgentFeedActive = this.priorityFeedQuietTimer > 0;
    const lowPriority = kind === 'normal';
    if (urgentFeedActive && lowPriority) return;
    const text = urgentFeedActive && lowPriority && line.length > 38 ? `${line.slice(0, 35).trim()}...` : line;
    if (kind === 'elim' || kind === 'knock') {
      this.priorityFeedQuietTimer = Math.max(this.priorityFeedQuietTimer, 3.2);
      this.priorityHudQuietTimer = Math.max(this.priorityHudQuietTimer, 1.8);
    } else if (kind === 'loot') {
      this.priorityHudQuietTimer = Math.max(this.priorityHudQuietTimer, 1.2);
    }
    const palette = {
      normal: { border: '#2ee6ff', glow: 'rgba(46,230,255,0.22)', bg: 'rgba(0,0,0,0.52)', color: '#e8f4ff' },
      knock: { border: '#ff9f43', glow: 'rgba(255,159,67,0.28)', bg: 'rgba(28,12,4,0.58)', color: '#ffe8d4' },
      loot: { border: '#38bdf8', glow: 'rgba(56,189,248,0.34)', bg: 'rgba(4,18,32,0.62)', color: '#dbeafe' },
      elim: { border: '#ffd54a', glow: 'rgba(255,213,74,0.38)', bg: 'rgba(24,18,4,0.64)', color: '#fff8e1' },
    }[kind];
    const el = document.createElement('div');
    el.className = `fps-elim-feed-entry fps-elim-feed-entry-${kind}`;
    el.dataset.kind = kind;
    el.textContent = text;
    el.style.cssText = [
      'padding:8px 11px 8px 12px',
      'border-radius:10px',
      `background:linear-gradient(180deg,rgba(255,255,255,0.07),transparent 38%),${palette.bg}`,
      `border:1px solid rgba(255,255,255,0.13)`,
      `border-left:4px solid ${palette.border}`,
      `box-shadow:0 6px 22px rgba(0,0,0,0.25),0 0 18px ${palette.glow}, inset 0 1px 0 rgba(255,255,255,0.09)`,
      'font:700 clamp(11px,1.35vw,12px) system-ui,sans-serif',
      `color:${palette.color}`,
      kind === 'elim' ? 'letter-spacing:0.03em;font-weight:700' : '',
    ]
      .filter(Boolean)
      .join(';');
    this.elimFeed.prepend(el);
    const feedCap = urgentFeedActive ? 4 : 5;
    while (this.elimFeed.childElementCount > feedCap) {
      this.elimFeed.lastElementChild?.remove();
    }
    this.scheduleTimeout(() => el.remove(), urgentFeedActive && lowPriority ? 3200 : 4600);
  }

  private pushSquadCallout(speaker: string, message: string): void {
    const urgentFeedActive = this.priorityFeedQuietTimer > 0;
    const el = document.createElement('div');
    el.className = 'fps-elim-feed-entry fps-elim-feed-entry-squad';
    el.dataset.kind = 'squad';
    el.innerHTML = `<span style="color:#36e36b;font-weight:700">[${speaker}]</span> ${message}`;
    el.style.cssText =
      'padding:8px 11px 8px 12px;border-radius:10px;background:linear-gradient(180deg,rgba(255,255,255,0.07),transparent 38%),rgba(0,24,12,0.64);border:1px solid rgba(255,255,255,0.13);border-left:4px solid #36e36b;' +
      'box-shadow:0 6px 22px rgba(0,0,0,0.25),0 0 18px rgba(54,227,107,0.28), inset 0 1px 0 rgba(255,255,255,0.09);' +
      'font:700 clamp(11px,1.35vw,12px) system-ui,sans-serif;color:#e8f4ff;';
    this.elimFeed.prepend(el);
    while (this.elimFeed.childElementCount > (urgentFeedActive ? 4 : 6)) {
      this.elimFeed.lastElementChild?.remove();
    }
    this.scheduleTimeout(() => el.remove(), urgentFeedActive ? 3400 : 5200);
  }

  private enterKnocked(source: 'combat' | 'storm' | 'explosion' | 'stink'): void {
    if (this.knocked || this.matchEnd) return;
    if (this.carriedTeammate) this.dropCarriedTeammate();
    if (source === 'storm') {
      this.lastElimSource = 'storm';
      this.lastAttackerName = null;
    } else if (source === 'explosion' || source === 'stink') {
      this.lastElimSource = 'self';
      this.lastAttackerName = null;
    } else {
      this.lastElimSource = 'combat';
    }
    this.knocked = true;
    this.weaponInspectOpen = false;
    this.bleedoutTimer = BLEEDOUT_TIME;
    this.health = 0;
    this.player.setKnocked(true);
    this.hudKnocked.style.display = 'flex';
    if (this.isSquadMode()) {
      this.hotbar.setDbnoBleedout(true, 1, BLEEDOUT_TIME);
    }
    this.deathOverlay.style.opacity = '0';
    const msg =
      source === 'storm'
        ? 'Knocked by storm damage'
        : source === 'explosion'
          ? 'Knocked by explosion'
          : 'Knocked by enemy fire';
    this.pushElimFeed(msg, 'knock');
    if (source === 'combat') this.showDeathRecap(false);
  }

  private isNearRebootVan(): boolean {
    const b = this.player.body.position;
    const dx = b.x - REBOOT_X;
    const dz = b.z - REBOOT_Z;
    return dx * dx + dz * dz <= REBOOT_RADIUS * REBOOT_RADIUS;
  }

  private tryRebootFromVan(): boolean {
    if (!this.knocked || this.redeployLeft <= 0 || this.matchEnd) return false;
    const nearVan = this.isNearRebootVan();
    const useCard = !nearVan && this.rebootCardCount > 0;
    if (!nearVan && !useCard) return false;

    if (useCard) {
      this.rebootCardCount -= 1;
      this.updateTacticalHud();
    }
    this.redeployLeft -= 1;
    this.knocked = false;
    this.bleedoutTimer = 0;
    this.player.setKnocked(false);
    this.hudKnocked.style.display = 'none';
    this.hotbar.setDbnoBleedout(false, 0, 0);
    this.deathOverlay.style.opacity = '0';
    this.hideDeathRecap();
    this.health = 40;
    this.shieldHud.setShield(35);
    if (nearVan) {
      this.player.body.position.set(REBOOT_X, 1.82, REBOOT_Z);
    }
    this.player.body.velocity.set(0, 6, 0);
    this.audio.playRebootVan();
    this.pushElimFeed(useCard ? 'Reboot Card — back in the fight!' : 'Reboot Van — back in the fight!');
    return true;
  }

  private completeTeammateRebootFromVan(): boolean {
    if (
      !this.isSquadMode() ||
      this.knocked ||
      this.matchEnd ||
      !this.isNearRebootVan()
    ) {
      return false;
    }
    const tm = this.teammateRebootTarget ?? this.getTeammateNeedingReboot();
    if (!tm || !tm.rebootFromVan(REBOOT_X, REBOOT_Z)) return false;
    tm.setRebootQueueHud(false);
    this.teammateRebootTarget = null;
    this.teammateRebootProgress = 0;
    this.audio.playRebootVan();
    this.pushElimFeed(`Rebooted ${tm.name} — squad back up!`, 'loot');
    return true;
  }

  private updateTeammateRebootVan(dt: number): void {
    const target = this.getTeammateNeedingReboot();
    const nearVan = this.isNearRebootVan();
    const canQueue =
      this.isSquadMode() &&
      !this.knocked &&
      !this.matchEnd &&
      nearVan &&
      target != null;

    if (!canQueue) {
      if (this.teammateRebootTarget) {
        this.teammateRebootTarget.setRebootQueueHud(false);
      }
      this.teammateRebootTarget = null;
      this.teammateRebootProgress = 0;
      return;
    }

    if (this.teammateRebootTarget !== target) {
      if (this.teammateRebootTarget) this.teammateRebootTarget.setRebootQueueHud(false);
      this.teammateRebootTarget = target;
      this.teammateRebootProgress = 0;
    }

    if (this.player.isKeyHeld('KeyR')) {
      this.teammateRebootProgress += dt;
      const ratio = this.teammateRebootProgress / REBOOT_VAN_QUEUE_HOLD;
      const left = REBOOT_VAN_QUEUE_HOLD - this.teammateRebootProgress;
      target.setRebootQueueHud(true, ratio, left);
      if (this.teammateRebootProgress >= REBOOT_VAN_QUEUE_HOLD) {
        this.completeTeammateRebootFromVan();
      }
    } else {
      this.teammateRebootProgress = 0;
      target.setRebootQueueHud(false);
    }
  }

  private applyChugSplashHeal(heal: { x: number; y: number; z: number; radius: number; heal: number }): void {
    if (!this.isMatchLive() || this.matchEnd) return;
    const px = this.player.body.position.x;
    const py = this.player.body.position.y;
    const pz = this.player.body.position.z;
    if (Math.hypot(px - heal.x, py - heal.y, pz - heal.z) > heal.radius) return;
    if (this.knocked) return;
    const before = this.health;
    this.health = Math.min(100, this.health + heal.heal);
    this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
    if (this.health > before) {
      this.pushElimFeed(`Chug Splash +${heal.heal} HP`, 'loot');
      this.audio.playJump();
    }
  }

  private applyShockwaveBlast(blast: { x: number; y: number; z: number; radius: number; force: number; lift: number }): void {
    if (!this.isMatchLive() || this.matchEnd) return;
    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    const dx = px - blast.x;
    const dz = pz - blast.z;
    const d = Math.hypot(dx, dz);
    if (d < blast.radius && d > 0.01 && !this.knocked) {
      const scale = (1 - d / blast.radius) * blast.force;
      this.player.body.velocity.x += (dx / d) * scale;
      this.player.body.velocity.z += (dz / d) * scale;
      this.player.body.velocity.y += blast.lift * (1 - d / blast.radius);
    }
    this.bots.applyShockwaveKnockback(blast.x, blast.z, blast.radius, blast.force);
    this.buildPlacer.destroyInRadius(blast.x, blast.y, blast.z, blast.radius);
    this.audio.playJump();
  }

  private applyExplosionAt(
    cx: number,
    cy: number,
    cz: number,
    opts?: { flatDamage?: number; radius?: number },
  ): void {
    const radius = opts?.radius ?? FpsGrenades.getBlastRadius();
    const flatDamage = opts?.flatDamage;
    const px = this.player.body.position.x;
    const py = this.player.body.position.y;
    const pz = this.player.body.position.z;

    let playerDmg = 0;
    if (flatDamage != null) {
      if (Math.hypot(px - cx, py - cy, pz - cz) < radius) playerDmg = flatDamage;
    } else {
      playerDmg = FpsGrenades.damageAtPoint(cx, cy, cz, px, py, pz);
    }

    if (playerDmg > 0 && !this.knocked) {
      this.notePlayerDamage();
      const sh0 = this.shieldHud.getShield();
      let remain = playerDmg;
      const fromShield = Math.min(sh0, remain);
      this.shieldHud.setShield(sh0 - fromShield);
      remain -= fromShield;
      if (remain > 0) this.health -= remain;
      if (fromShield > 0) {
        this.applyPlayerShieldHit(fromShield, sh0 > 0 && fromShield >= sh0);
      }
      this.damageFlashOpacity = Math.min(0.78, this.damageFlashOpacity + 0.45);
      this.showIncomingDamageFromWorld(cx, cz, Math.min(1.25, playerDmg / 45));
      this.audio.triggerCombatFocus(Math.min(1.25, 0.75 + playerDmg / 120));
      this.audio.playPlayerHurt();
      if (this.health <= 0) this.enterKnocked('explosion');
    } else if (playerDmg > 0 && this.knocked) {
      this.notePlayerDamage();
      this.bleedoutTimer = Math.max(0, this.bleedoutTimer - 4);
    }

    const playerDist = Math.hypot(px - cx, py - cy, pz - cz);
    if (playerDist < radius * 1.6) {
      this.audio.triggerCombatFocus(Math.max(0.45, 1 - playerDist / (radius * 1.6)));
    }
    if (playerDist < radius) {
      const proximity = 1 - playerDist / radius;
      const damageScale = flatDamage != null ? Math.min(1, flatDamage / 100) : 1;
      this.triggerCameraShake(0.006 + proximity * 0.022 * damageScale);
    }

    const botHits =
      flatDamage != null
        ? this.bots.applyFlatRadiusDamage(cx, cy, cz, radius, flatDamage, 'Air Strike')
        : this.bots.damageBotsInRadius(cx, cy, cz, radius, FpsGrenades.getMaxDamage(), 'You');
    for (const hit of botHits) {
      this.damageNumbers.spawn(hit.pos, hit.damage, this.camera, false);
      this.hitFeedback.showHit('body');
      this.hitFeedback.showDamage(hit.damage, 'body');
      this.applyHitConfirmImpulse(hit.killed ? 'elimination' : 'body');
      if (hit.killed) {
        this.onPlayerElimination({ victim: hit.name });
        if (this.xpBar.addXp(55)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
        this.reportPlayerElim(hit.name, hit.pos);
      } else if (hit.knocked) {
        this.pushElimFeed(`Knocked ${hit.name}`, 'knock');
      }
    }

    const buildsDestroyed = this.buildPlacer.destroyInRadius(cx, cy, cz, radius);
    if (buildsDestroyed > 0) {
      this.pushElimFeed(`${buildsDestroyed} build piece${buildsDestroyed > 1 ? 's' : ''} destroyed`);
    }

    this.audio.playExplosion();

    // Particle VFX: orange debris + smoke puffs
    const hitPos = new THREE.Vector3(cx, cy, cz);
    this.particles.emit({
      count: 55,
      position: hitPos,
      velocityRange: {
        min: new THREE.Vector3(-8, -2, -8),
        max: new THREE.Vector3(8, 14, 8),
      },
      colorA: new THREE.Color(1.0, 0.55, 0.05),
      colorB: new THREE.Color(0.2, 0.1, 0.02),
      lifetime: 1.4,
      gravity: 12,
      sizeStart: 0.14,
      sizeEnd: 0.01,
      critical: true,
    });
    this.particles.emit({
      count: 18,
      position: hitPos,
      velocityRange: {
        min: new THREE.Vector3(-3, 1, -3),
        max: new THREE.Vector3(3, 5, 3),
      },
      colorA: new THREE.Color(0.5, 0.5, 0.5),
      colorB: new THREE.Color(0.85, 0.85, 0.85),
      lifetime: 2.2,
      gravity: -1,
      sizeStart: 0.3,
      sizeEnd: 0.6,
    });
  }

  private applyStinkTick(cx: number, cy: number, cz: number, radius: number, damage: number): void {
    if (!this.isMatchLive() || damage <= 0) return;

    const px = this.player.body.position.x;
    const py = this.player.body.position.y + 0.55;
    const pz = this.player.body.position.z;
    if (Math.hypot(px - cx, py - cy, pz - cz) < radius) {
      if (!this.knocked) {
        this.notePlayerDamage();
        const sh0 = this.shieldHud.getShield();
        let remain = damage;
        const fromShield = Math.min(sh0, remain);
        this.shieldHud.setShield(sh0 - fromShield);
        remain -= fromShield;
        if (remain > 0) this.health -= remain;
        if (fromShield > 0) {
          this.applyPlayerShieldHit(fromShield, sh0 > 0 && fromShield >= sh0);
        }
        this.damageFlashOpacity = Math.min(0.78, this.damageFlashOpacity + 0.12);
        this.showIncomingDamageFromWorld(cx, cz, Math.min(1.1, damage / 25));
        this.audio.triggerCombatFocus(0.7);
        this.audio.playPlayerHurt();
        if (this.health <= 0) this.enterKnocked('stink');
      } else {
        this.notePlayerDamage();
        this.bleedoutTimer = Math.max(0, this.bleedoutTimer - damage * 0.35);
      }
    }

    const botHits = this.bots.applyFlatRadiusDamage(cx, cy, cz, radius, damage, 'Stink');
    for (const hit of botHits) {
      this.damageNumbers.spawn(hit.pos, hit.damage, this.camera, false);
      this.hitFeedback.showHit('body');
      this.hitFeedback.showDamage(hit.damage, 'body');
      this.applyHitConfirmImpulse(hit.killed ? 'elimination' : 'body');
      if (hit.killed) {
        this.onPlayerElimination({ victim: hit.name });
        if (this.xpBar.addXp(55)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
        this.reportPlayerElim(hit.name, hit.pos);
        this.showHitMarker(false, 1);
        this.tryTriggerKillCam(hit.pos, {
          name: hit.name,
          weapon: 'Stink Cloud',
          headshot: false,
        });
      }
    }
  }

  private isMatchLive(): boolean {
    return !this.matchEnd && !this.spectateActive;
  }

  private hasPriorityHudActive(): boolean {
    return (
      this.priorityHudQuietTimer > 0 ||
      this.killBannerTimer > 0 ||
      this.stormPhaseBannerTimer > 0 ||
      this.placementBannerTimer > 0 ||
      this.lootToastTimer > 0 ||
      this.pickupCardTimer > 0 ||
      this.stormWarnFlash > 0.18
    );
  }

  private updateLowPriorityHudClutter(hudPlay: boolean): void {
    const activeMatch = !this.lobbyActive && !this.introActive && this.isMatchLive();
    this.dailyChallenges.setVisible(!this.lobbyActive && !this.introActive);
    const muted = activeMatch && hudPlay && this.hasPriorityHudActive();
    this.dailyChallenges.setCompact(activeMatch && (muted || this.matchElapsed > 18));
    this.hudQuestChip.classList.toggle('fps-hud-priority-muted', muted && this.hudQuestChip.style.display !== 'none');
    this.hudLtmChip.classList.toggle('fps-hud-priority-muted', muted && this.hudLtmChip.style.display !== 'none');
  }

  private updateHintStripVisibility(hudPlay: boolean): void {
    const activeMatch = !this.lobbyActive && !this.introActive && this.isMatchLive();
    const earlyTutorial = activeMatch && this.matchElapsed < 14;
    const supportOverlay = this.settings.isOpen() || this.graphicsState.performanceMode;
    const visible = !activeMatch || earlyTutorial || supportOverlay;
    const muted = activeMatch && supportOverlay && !earlyTutorial;

    this.hudHintStrip.style.opacity = visible ? (muted ? '0.34' : '0.82') : '0';
    this.hudHintStrip.style.filter = muted || !hudPlay ? 'saturate(0.72)' : 'none';
  }

  private updateMatchDirector(dt: number): void {
    if (this.matchDirectorPressureTimer > 0) {
      this.matchDirectorPressureTimer = Math.max(0, this.matchDirectorPressureTimer - dt);
      if (this.matchDirectorPressureTimer === 0) {
        this.bots.setPressureMultiplier(1);
      }
    }

    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    const nearest = this.bots.getNearestAliveBot(px, pz);
    const nearestBotDist = nearest ? Math.hypot(nearest.x - px, nearest.z - pz) : Infinity;
    const suggestion = this.matchDirector.update(dt, {
      elapsed: this.matchElapsed,
      health: this.health,
      shield: this.shieldHud.getShield(),
      mats: this.mats,
      aliveBots: this.bots.aliveCount(),
      nearestBotDist,
      stormPhase: this.storm.getStormPhase(),
      inLobby: this.lobbyActive || this.introActive,
      isFinalCircle: this.storm.isFinalCircle(),
      pois: POI_LOOT_ZONES,
    });
    this.applyMatchDirectorSuggestion(suggestion);
  }

  private applyMatchDirectorSuggestion(suggestion: MatchDirectorSuggestion): void {
    if (suggestion === 'none') return;

    if (suggestion === 'ease_pressure') {
      this.bots.setPressureMultiplier(0.92);
      this.matchDirectorPressureTimer = 16;
      this.pushElimFeed('Director: reset, heal, and box up', 'loot');
    } else if (suggestion === 'increase_pressure') {
      this.bots.setPressureMultiplier(1.06);
      this.matchDirectorPressureTimer = 20;
      this.pushElimFeed('Director: footsteps closing in');
    } else if (suggestion === 'encourage_loot') {
      this.bots.setPressureMultiplier(0.98);
      this.matchDirectorPressureTimer = 10;
      this.pushElimFeed('Director: farm mats and crack a chest', 'loot');
    } else if (suggestion === 'rotate_storm') {
      this.bots.setPressureMultiplier(1);
      this.pushElimFeed('Director: rotate toward safe zone');
    } else if (suggestion === 'spawn_reinforcement_wave') {
      const px = this.player.body.position.x;
      const pz = this.player.body.position.z;
      const spawned = this.bots.spawnReinforcementWave(px, pz);
      if (spawned > 0) {
        this.pushElimFeed(`Reinforcements inbound — ${spawned} hostiles`, 'elim');
        this.audio.playPlacementBanner();
      }
    } else if (suggestion === 'activate_hot_zone') {
      const zone = this.matchDirector.getActiveHotZone();
      if (zone && !this.matchDirectorHotZoneAnnounced) {
        this.matchDirectorHotZoneAnnounced = true;
        this.loot.setHotZone(zone.x, zone.z, zone.radius, zone.lootMultiplier);
        this.loot.markHotZoneChests();
        this.pushElimFeed(`HOT ZONE: ${zone.name} — bonus loot x${zone.lootMultiplier.toFixed(2)}`, 'loot');
        this.triggerSafeZoneGuidancePulse(3200);
      }
    }
  }

  private notePlayerDamage(): void {
    this.elimStreak = 0;
    this.lastDamageTime = this.matchElapsed;
    this.shieldHud.setRegenActive(false);
  }

  private prefersReducedMotion(): boolean {
    return this.graphicsState.reducedMotion || (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
  }

  private ensureReplaySystem(): Promise<void> {
    if (this.replaySystem) return Promise.resolve();
    if (!this.replaySystemLoad) {
      this.replaySystemLoad = import('./FpsReplaySystem.js').then(({ FpsReplaySystem }) => {
        this.replaySystem = new FpsReplaySystem(document.body);
      });
    }
    return this.replaySystemLoad;
  }

  private triggerCameraShake(intensity: number): void {
    if (intensity <= 0 || this.prefersReducedMotion()) return;
    this.player.applyCameraShake(Math.min(0.04, intensity));
  }

  private resolveHitFeedbackKind(res: {
    headshot: boolean;
    shieldHit?: boolean;
    damage: number;
    killed?: boolean;
  }): FpsHitFeedbackKind {
    if (res.killed) return 'elimination';
    if (res.headshot) return 'headshot';
    if (res.shieldHit) return 'shield';
    if (res.damage >= 38) return 'crit';
    return 'body';
  }

  private applyHitConfirmImpulse(kind: FpsHitFeedbackKind): void {
    const intensity =
      kind === 'elimination' ? 0.011 :
      kind === 'headshot' ? 0.008 :
      kind === 'crit' ? 0.006 :
      kind === 'shield' ? 0.0045 :
      0.004;
    this.triggerCameraShake(intensity);
  }

  private showIncomingDamageFromWorld(x: number, z: number, intensity: number): void {
    this.priorityHudQuietTimer = Math.max(this.priorityHudQuietTimer, 1.1 + intensity * 0.55);
    this.priorityFeedQuietTimer = Math.max(this.priorityFeedQuietTimer, 1.2);
    const b = this.player.body.position;
    const yaw = this.player.yawObject.rotation.y;
    const attackerAngle = Math.atan2(x - b.x, z - b.z);
    this.hitFeedback.showIncoming(attackerAngle - yaw, intensity);
  }

  private showIncomingDamageFallback(intensity: number): void {
    this.priorityHudQuietTimer = Math.max(this.priorityHudQuietTimer, 1.1 + intensity * 0.55);
    this.priorityFeedQuietTimer = Math.max(this.priorityFeedQuietTimer, 1.2);
    this.hitFeedback.showIncoming(0, intensity);
  }

  private maybeShowBotSuppressionCue(shot: BotShotEvent, tracerEnd: THREE.Vector3): void {
    if (shot.damage > 0 || this.knocked || this.suppressionCueCd > 0) return;
    const playerPos = this.player.body.position;
    const playerHead = new THREE.Vector3(playerPos.x, playerPos.y + EYE_HEIGHT * 0.72, playerPos.z);
    const missDistance = this.distancePointToSegment(playerHead, shot.origin, tracerEnd);
    const shooterDistance = Math.hypot(shot.botPos.x - playerPos.x, shot.botPos.z - playerPos.z);
    const nearMiss = missDistance < 1.15 && shooterDistance < 42;
    const closeMiss = shooterDistance < 9.5;
    if (!nearMiss && !closeMiss) return;

    const yaw = this.player.yawObject.rotation.y;
    const attackerAngle = Math.atan2(shot.botPos.x - playerPos.x, shot.botPos.z - playerPos.z);
    const nearMissIntensity = nearMiss ? 1 - missDistance / 1.15 : 0;
    const closeMissIntensity = closeMiss ? 1 - shooterDistance / 9.5 : 0;
    const loadoutBoost = shot.loadout === 'sniper' ? 0.12 : shot.loadout === 'shotgun' ? 0.08 : 0;
    const intensity = Math.min(0.72, 0.28 + Math.max(nearMissIntensity, closeMissIntensity) * 0.36 + loadoutBoost);
    this.priorityHudQuietTimer = Math.max(this.priorityHudQuietTimer, 0.95 + intensity * 0.5);
    this.priorityFeedQuietTimer = Math.max(this.priorityFeedQuietTimer, 1.0);
    this.hitFeedback.showSuppression(attackerAngle - yaw, intensity);
    this.audio.triggerCombatFocus(Math.min(0.62, 0.32 + intensity * 0.34));
    this.suppressionCueCd = shot.loadout === 'smg' ? 0.42 : 0.58;
  }

  private distancePointToSegment(point: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3): number {
    const sx = end.x - start.x;
    const sy = end.y - start.y;
    const sz = end.z - start.z;
    const lenSq = sx * sx + sy * sy + sz * sz;
    if (lenSq <= 0.0001) return point.distanceTo(start);
    const t = THREE.MathUtils.clamp(
      ((point.x - start.x) * sx + (point.y - start.y) * sy + (point.z - start.z) * sz) / lenSq,
      0,
      1,
    );
    const dx = start.x + sx * t - point.x;
    const dy = start.y + sy * t - point.y;
    const dz = start.z + sz * t - point.z;
    return Math.hypot(dx, dy, dz);
  }

  private showHealFeedback(text: string, color: string, force = false): void {
    if (!force && this.healFeedbackCd > 0) return;
    this.healItems.showFeedback(text, color);
    this.healFeedbackCd = force ? 0.2 : 0.7;
  }

  public triggerHitStop(seconds: number): void {
    this.hitStopTimer = Math.max(this.hitStopTimer, seconds);
  }

  private updateShieldRegen(dt: number, takingStormDamage: boolean): void {
    if (!this.isMatchLive() || this.knocked) {
      this.shieldHud.setRegenActive(false);
      return;
    }
    const shield = this.shieldHud.getShield();
    if (shield >= 100) {
      this.shieldHud.setRegenActive(false);
      return;
    }
    const ooc = this.matchElapsed - this.lastDamageTime >= SHIELD_REGEN_DELAY;
    const canRegen = ooc && !takingStormDamage;
    this.shieldHud.setRegenActive(canRegen);
    if (canRegen) {
      const nextShield = Math.min(100, shield + SHIELD_REGEN_RATE * dt);
      this.shieldHud.setShield(nextShield);
      if (shield < 100 && nextShield >= 100) {
        this.showHealFeedback('Shield recovered', '#93c5fd', true);
      }
    }
  }

  private startVictoryConfetti(): void {
    this.confettiTimer = MATCH_END_CONFETTI_S;
    this.confettiParticles.length = 0;
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.confettiCanvas.width = w;
    this.confettiCanvas.height = h;
    this.confettiCanvas.style.display = 'block';
    const colors = ['#ffd700', '#ff6b6b', '#44e8ff', '#b44dff', '#7dff8a', '#ff9f43', '#fff4a8'];
    for (let i = 0; i < 140; i++) {
      this.confettiParticles.push({
        x: w * (0.2 + Math.random() * 0.6),
        y: -20 - Math.random() * h * 0.35,
        vx: (Math.random() - 0.5) * 280,
        vy: 120 + Math.random() * 320,
        color: colors[Math.floor(Math.random() * colors.length)]!,
        size: 5 + Math.random() * 7,
        rot: Math.random() * Math.PI * 2,
        rotV: (Math.random() - 0.5) * 12,
        life: 2.2 + Math.random() * 1.4,
      });
    }
  }

  private updateVictoryConfetti(dt: number): void {
    if (this.confettiTimer <= 0) return;
    this.confettiTimer -= dt;
    const ctx = this.confettiCtx;
    if (!ctx) return;
    const w = this.confettiCanvas.width;
    const h = this.confettiCanvas.height;
    ctx.clearRect(0, 0, w, h);
    for (let i = this.confettiParticles.length - 1; i >= 0; i--) {
      const p = this.confettiParticles[i]!;
      p.life -= dt;
      if (p.life <= 0) {
        this.confettiParticles.splice(i, 1);
        continue;
      }
      p.vy += 420 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.rotV * dt;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.globalAlpha = Math.min(1, p.life);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size * 0.5, -p.size * 0.25, p.size, p.size * 0.5);
      ctx.restore();
    }
    if (this.confettiTimer <= 0 && this.confettiParticles.length === 0) {
      this.confettiCanvas.style.display = 'none';
      ctx.clearRect(0, 0, w, h);
    }
  }

  private startDefeatVignette(): void {
    this.defeatVignetteActive = true;
    this.defeatVignetteEl.style.display = 'block';
  }

  private updateDefeatVignette(): void {
    if (!this.defeatVignetteActive) return;
    const pulse = 0.22 + Math.sin(performance.now() * 0.0032) * 0.14;
    this.defeatVignetteEl.style.opacity = String(pulse);
  }

  private requestMatchEnd(kind: 'won' | 'lost'): void {
    if (this.matchEnd || this.spectateActive) return;
    if (kind === 'won') {
      this.triggerVictoryEmoteToast();
      this.finalizeMatchEnd('won');
      return;
    }
    this.beginDeathReplayOrSpectate();
  }

  private beginDeathReplayOrSpectate(): void {
    void this.ensureReplaySystem().then(() => {
      if (!this.replaySystem) {
        this.startDeathSpectate();
        return;
      }
      if (this.replaySystem.playDeathSnippet(DEATH_REPLAY_DURATION, () => this.startDeathSpectate())) {
        this.replaySystem.stopRecording();
        if (this.pingWheelOpen) this.closePingWheel();
        if (this.emoteWheelOpen) this.closeEmoteWheel();
        if (this.chatWheelOpen) this.closeChatWheel();
        this.player.setInputLocked(true);
        this.player.setMovementLocked(true);
        document.exitPointerLock();
        if (!this.cameraDetached) {
          this.player.pitchObject.remove(this.camera);
          this.scene.add(this.camera);
          this.cameraDetached = true;
        }
        this.viewModelGun.visible = false;
        return;
      }
      this.startDeathSpectate();
    });
  }

  private triggerVictoryEmoteToast(): void {
    this.emoteToast.textContent = 'Victory Flex!';
    this.emoteToast.style.display = 'block';
    this.emoteToastTimer = 2;
    this.audio.playChest();
  }

  private startDeathSpectate(): void {
    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    const nearest = this.bots.getNearestAliveBot(px, pz);
    if (!nearest) {
      this.finalizeMatchEnd('lost');
      return;
    }
    if (this.killCamActive) this.endKillCam();
    if (this.emoteWheelOpen) this.closeEmoteWheel();
    if (this.chatWheelOpen) this.closeChatWheel();
    if (this.vehicles.isPlayerDriving()) {
      const out = this.vehicles.exit();
      if (out) {
        this.player.body.position.set(out.x, out.y, out.z);
        this.player.body.velocity.set(out.vx, 0, out.vz);
      }
      this.player.setMovementLocked(false);
    }
    this.thirdPerson = false;
    this.playerCharacter.visible = false;
    this.knocked = false;
    this.bleedoutTimer = 0;
    this.player.setKnocked(false);
    this.knockBubble.setActive(false);
    this.hudKnocked.style.display = 'none';
    this.hotbar.setDbnoBleedout(false, 0, 0);
    this.deathOverlay.style.opacity = '0';
    this.pendingMatchEnd = 'lost';
    this.spectateActive = true;
    this.spectateTimer = DEATH_SPECTATE_DURATION;
    this.spectateBotId = nearest.id;
    this.player.setInputLocked(true);
    this.player.setMovementLocked(true);
    document.exitPointerLock();
    if (!this.cameraDetached) {
      this.player.pitchObject.remove(this.camera);
      this.scene.add(this.camera);
      this.cameraDetached = true;
    }
    this.viewModelGun.visible = false;
    this.showDeathRecap(true);
    this.updateSpectateLabel(nearest.name);
    this.spectateOverlay.style.display = 'flex';
    this.updateSpectateCamera();
  }

  private deathRecapElimLine(): string {
    if (this.lastAttackerName) return `Eliminated by ${this.lastAttackerName}`;
    if (this.lastElimSource === 'storm') return 'Storm';
    return 'Self';
  }

  private showDeathRecap(spectating: boolean): void {
    if (this.deathRecapVisible) {
      const hint = this.spectateOverlay.querySelector('#spectate-hint') as HTMLDivElement | null;
      const label = this.spectateOverlay.querySelector('#spectate-label') as HTMLDivElement | null;
      if (hint) hint.style.display = spectating ? 'block' : 'none';
      if (label) label.style.display = spectating ? 'block' : 'none';
      return;
    }
    this.deathRecapVisible = true;
    const placement = 1 + this.bots.aliveCount();
    const damage = Math.round(this.matchStats.damageDealt);
    const recap = this.spectateOverlay.querySelector('#spectate-recap') as HTMLDivElement | null;
    const elimEl = this.spectateOverlay.querySelector('#spectate-recap-elim') as HTMLDivElement | null;
    const placeEl = this.spectateOverlay.querySelector('#spectate-recap-placement') as HTMLDivElement | null;
    const dmgEl = this.spectateOverlay.querySelector('#spectate-recap-damage') as HTMLDivElement | null;
    const hint = this.spectateOverlay.querySelector('#spectate-hint') as HTMLDivElement | null;
    const label = this.spectateOverlay.querySelector('#spectate-label') as HTMLDivElement | null;
    if (elimEl) elimEl.textContent = this.deathRecapElimLine();
    if (placeEl) placeEl.textContent = `#${placement}`;
    if (dmgEl) dmgEl.textContent = `${damage} damage dealt`;
    if (recap) recap.style.display = 'block';
    if (hint) hint.style.display = spectating ? 'block' : 'none';
    if (label) label.style.display = spectating ? 'block' : 'none';
    this.spectateOverlay.style.display = 'flex';
    this.audio.playDeathRecap();
  }

  private hideDeathRecap(): void {
    this.deathRecapVisible = false;
    const recap = this.spectateOverlay.querySelector('#spectate-recap') as HTMLDivElement | null;
    const hint = this.spectateOverlay.querySelector('#spectate-hint') as HTMLDivElement | null;
    const label = this.spectateOverlay.querySelector('#spectate-label') as HTMLDivElement | null;
    if (recap) recap.style.display = 'none';
    if (hint) hint.style.display = 'none';
    if (label) label.style.display = 'none';
    if (!this.spectateActive) this.spectateOverlay.style.display = 'none';
  }

  private cycleSpectateTarget(dir: 1 | -1): void {
    if (!this.spectateActive) return;
    const ids = this.bots.getAliveBotIds();
    if (ids.length <= 1) return;
    const cur = this.spectateBotId;
    let idx = cur != null ? ids.indexOf(cur) : -1;
    if (idx < 0) idx = 0;
    else idx = (idx + dir + ids.length) % ids.length;
    this.spectateBotId = ids[idx]!;
    const pose = this.bots.getAliveBotPose(this.spectateBotId);
    if (pose) this.updateSpectateLabel(pose.name);
    this.updateSpectateCamera();
  }

  private updateSpectateLabel(botName: string): void {
    const label = this.spectateOverlay.querySelector('#spectate-label') as HTMLDivElement | null;
    if (label) label.textContent = `SPECTATING · ${botName}`;
  }

  private refreshSpectateTarget(): boolean {
    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    let pose = this.spectateBotId != null ? this.bots.getAliveBotPose(this.spectateBotId) : null;
    if (!pose) {
      const nearest = this.bots.getNearestAliveBot(px, pz);
      if (!nearest) return false;
      this.spectateBotId = nearest.id;
      pose = nearest;
    }
    this.updateSpectateLabel(pose.name);
    return true;
  }

  private updateSpectateCamera(): void {
    if (this.spectateBotId == null) return;
    const pose = this.bots.getAliveBotPose(this.spectateBotId);
    if (!pose) return;
    const dist = 4.5;
    const height = 1.85;
    const forwardX = -Math.sin(pose.yaw);
    const forwardZ = -Math.cos(pose.yaw);
    this.camera.position.set(
      pose.x - forwardX * dist,
      pose.y + height,
      pose.z - forwardZ * dist,
    );
    this.camera.lookAt(pose.x + forwardX * 1.6, pose.y + 1.35, pose.z + forwardZ * 1.6);
  }

  private tickDeathSpectate(dt: number): void {
    if (!this.refreshSpectateTarget()) {
      this.endDeathSpectate();
      return;
    }
    this.updateSpectateCamera();
    this.spectateTimer -= dt;
    if (this.spectateTimer <= 0) this.endDeathSpectate();
  }

  private endDeathSpectate(): void {
    if (!this.spectateActive) return;
    this.spectateActive = false;
    this.spectateOverlay.style.display = 'none';
    this.spectateBotId = null;
    const kind = this.pendingMatchEnd ?? 'lost';
    this.pendingMatchEnd = null;
    this.finalizeMatchEnd(kind);
  }

  private finalizeMatchEnd(kind: 'won' | 'lost'): void {
    if (this.matchEnd) return;
    if (this.killCamActive) this.endKillCam();
    this.spectateActive = false;
    this.spectateOverlay.style.display = 'none';
    this.shieldHud.setRegenActive(false);
    if (this.emoteWheelOpen) this.closeEmoteWheel();
    if (this.chatWheelOpen) this.closeChatWheel();
    if (this.vehicles.isPlayerDriving()) {
      const out = this.vehicles.exit();
      if (out) {
        this.player.body.position.set(out.x, out.y, out.z);
        this.player.body.velocity.set(out.vx, 0, out.vz);
      }
      this.player.setMovementLocked(false);
    }
    this.thirdPerson = false;
    this.player.setInputLocked(false);
    if (this.cameraDetached) {
      this.scene.remove(this.camera);
      this.camera.position.set(0, 0, 0);
      this.camera.rotation.set(0, 0, 0);
      this.player.pitchObject.add(this.camera);
      this.cameraDetached = false;
    }
    this.viewModelGun.visible = true;
    this.knocked = false;
    this.bleedoutTimer = 0;
    this.player.setKnocked(false);
    this.hudKnocked.style.display = 'none';
    this.hotbar.setDbnoBleedout(false, 0, 0);
    this.matchEnd = kind;
    this.deathOverlay.style.opacity = '0';
    this.audio.stopStormAmbient();
    this.stormAmbientOn = false;
    document.exitPointerLock();
    this.endScreen.style.display = 'flex';
    const totalPlayers = 1 + this.initialBotCount;
    const elapsed = Math.floor(this.matchElapsed);
    const traveled = Math.round(this.statMeters);
    const placement = kind === 'won' ? 1 : 1 + this.bots.aliveCount();
    const arena = FpsCareerStats.recordArenaMatch({
      won: kind === 'won',
      kills: this.playerEliminations,
      placement,
      ranked: this.rankedMode,
    });
    const rankedPlacement = FpsCareerStats.recordRankedPlacement({
      won: kind === 'won',
      placement,
      totalPlayers,
      ranked: this.rankedMode,
    });
    const rankedPlacementLine = this.rankedMode
      ? `<p style="margin-top:8px;font:800 14px system-ui;color:${FpsCareerStats.placementPointsColor(rankedPlacement.delta)}">${FpsCareerStats.formatPlacementPointsDelta(rankedPlacement.delta)}` +
        `${rankedPlacement.total > 0 ? ` · ${rankedPlacement.total} total` : ''}</p>`
      : '';
    const weekly = FpsWeeklyBoard.recordMatch({
      won: kind === 'won',
      kills: this.playerEliminations,
    });
    const career = FpsCareerStats.load();
    const bpGain = FpsBattlePass.xpForMatch(this.playerEliminations, kind === 'won');
    this.grantBattlePassXp(bpGain);
    if (kind === 'won') this.dailyChallenges.recordMatchWin();
    this.dailyChallenges.recordOutlast(totalPlayers - placement);
    const rankBadge = this.arenaRankBadgeHtml(arena.rank, arena.arenaPoints, arena.pointsEarned);
    const questsDone =
      (this.npcQuest.getState() === 'completed' ? 1 : 0) +
      (this.npcQuestHarvest.getState() === 'completed' ? 1 : 0) +
      (this.npcQuestVisit.getState() === 'completed' ? 1 : 0);
    const matchStatsGrid = this.matchStats.buildGridHtml(this.statChests, traveled);
    const endStatCell = (value: string, label: string, delay: string) =>
      `<div style="padding:12px 10px;border-radius:10px;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.12)">` +
      `<div class="fps-stat-value" style="animation-delay:${delay};font:800 22px system-ui;color:#fff">${value}</div>` +
      `<div style="font:700 10px system-ui;color:#9ab;letter-spacing:0.08em;margin-top:4px">${label}</div></div>`;
    const statsGrid =
      '<div class="fps-end-stats-panel" style="margin-top:24px;width:min(92vw,420px);padding:18px 16px 16px;border-radius:14px;' +
      'background:rgba(8,14,28,0.45);backdrop-filter:blur(14px);-webkit-backdrop-filter:blur(14px);' +
      'border:1px solid rgba(255,255,255,0.1);box-shadow:0 8px 32px rgba(0,0,0,0.38)">' +
      '<div style="display:grid;grid-template-columns:repeat(3,minmax(88px,1fr));gap:12px">' +
      endStatCell(String(this.playerEliminations), 'ELIMS', '0.18s') +
      endStatCell(String(this.bestElimStreak), 'STREAK', '0.32s') +
      endStatCell(`${traveled}m`, 'DISTANCE', '0.46s') +
      '</div></div>' +
      `<p style="margin-top:16px;font:600 12px system-ui;color:#8899aa;line-height:1.6">${elapsed}s · Chests ${this.statChests} · Quests ${questsDone}/3 · Storm P${this.statMaxPhase}</p>` +
      matchStatsGrid +
      '<div style="margin-top:18px;padding:14px 16px;border-radius:10px;background:rgba(0,0,0,0.32);border:1px solid rgba(255,255,255,0.1);width:min(92vw,420px)">' +
      '<div style="font:700 10px system-ui;color:#8899aa;letter-spacing:0.12em;margin-bottom:10px">CAREER TOTALS</div>' +
      '<div style="display:grid;grid-template-columns:repeat(4,minmax(64px,1fr));gap:10px">' +
      `<div><div style="font:800 18px system-ui;color:#e8f0ff">${career.matches}</div><div style="font:700 9px system-ui;color:#778899;margin-top:3px">MATCHES</div></div>` +
      `<div><div style="font:800 18px system-ui;color:#ffe066">${career.wins}</div><div style="font:700 9px system-ui;color:#778899;margin-top:3px">WINS</div></div>` +
      `<div><div style="font:800 18px system-ui;color:#ff8a8a">${career.kills}</div><div style="font:700 9px system-ui;color:#778899;margin-top:3px">KILLS</div></div>` +
      `<div><div style="font:800 18px system-ui;color:#7ec8ff">${career.top3}</div><div style="font:700 9px system-ui;color:#778899;margin-top:3px">TOP 3</div></div>` +
      '</div></div>' +
      `<div style="margin-top:14px">${rankBadge}</div>` +
      rankedPlacementLine +
      `<p style="margin-top:10px;font:600 11px system-ui;color:#8899aa">Battle Pass +${bpGain} XP</p>`;
    const playAgainBtn =
      '<button id="play-again-btn" type="button" style="margin-top:28px;cursor:pointer;border:none;border-radius:10px;padding:16px 48px;' +
      'font:900 18px system-ui;letter-spacing:0.12em;color:#1a1200;' +
      'background:linear-gradient(180deg,#ffe566 0%,#ffc800 50%,#ff9a00 100%);' +
      'box-shadow:0 4px 0 #c97800,0 8px 28px rgba(255,180,0,0.35)">PLAY AGAIN</button>';
    if (kind === 'won') {
      this.endScreen.innerHTML =
        '<div style="font-size:clamp(36px,8vw,56px);font-weight:900;letter-spacing:0.06em;' +
        'background:linear-gradient(180deg,#fff4a8 0%,#ffd700 35%,#ffaa00 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;' +
        'filter:drop-shadow(0 2px 12px rgba(255,200,0,0.55))">VICTORY ROYALE</div>' +
        '<p style="margin-top:14px;font:700 15px system-ui;color:#e8f0ff;opacity:0.92">#1 of ' +
        totalPlayers +
        ' · You held the final circle.</p>' +
        statsGrid +
        playAgainBtn;
      this.audio.playVictoryStinger();
      this.victoryRoyale.showVictory(
        'Player 1',
        this.playerEliminations,
        1,
        new THREE.Vector3(
          this.player.body.position.x,
          this.player.body.position.y,
          this.player.body.position.z,
        ),
      );
      this.pushElimFeed('+1 Weekly Win', 'loot');
      if (weekly.milestoneHit) {
        this.startVictoryConfetti();
        this.pushElimFeed(`Weekly Cup milestone — ${WEEKLY_WIN_MILESTONE} wins!`, 'loot');
      }
      if (this.xpBar.addXp(55)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
    } else {
      this.pushElimFeed('Eliminated', 'elim');
      this.audio.playDefeatStinger();
      this.victoryRoyale.showDefeat(
        placement,
        this.playerEliminations,
        this.matchStats.getTotalDamage(),
        this.mats,
        this.lastAttackerName ?? 'Storm',
      );
      this.endScreen.innerHTML =
        '<div style="font-size:clamp(32px,7vw,48px);font-weight:900;letter-spacing:0.08em;color:#ff5a5a;' +
        'text-shadow:0 0 24px rgba(255,60,60,0.45)">ELIMINATED</div>' +
        `<p style="margin-top:14px;font:800 20px system-ui;color:#ffb8b8">#${placement} of ${totalPlayers}</p>` +
        '<p style="margin-top:8px;font:600 14px system-ui;color:#99aabb;opacity:0.88">Better luck in the next drop.</p>' +
        statsGrid +
        playAgainBtn;
    }
    this.endScreen.querySelector('#play-again-btn')?.addEventListener('click', () => {
      window.location.reload();
    });
  }

  private completeNpcQuest(): void {
    this.mats += QUEST_MAT_REWARD;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + QUEST_SHIELD_REWARD));
    this.pushElimFeed(`Quest complete — +${QUEST_MAT_REWARD} mats · +${QUEST_SHIELD_REWARD} shield`, 'loot');
  }

  private completeNpcQuestHarvest(): void {
    this.mats += HARVEST_QUEST_MAT_REWARD;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.health = Math.min(100, this.health + HARVEST_QUEST_HP_REWARD);
    this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
    this.pushElimFeed(
      `Quest complete — +${HARVEST_QUEST_MAT_REWARD} mats · +${HARVEST_QUEST_HP_REWARD} HP`,
      'loot',
    );
  }

  private completeNpcQuestVisit(): void {
    this.mats += VISIT_QUEST_MAT_REWARD;
    this.hudMats.textContent = `Mats ${this.mats}`;
    this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + VISIT_QUEST_SHIELD_REWARD));
    const gained = this.shockwave.addCount(1);
    if (gained > 0) this.updateTacticalHud();
    this.pushElimFeed(
      `Quest complete — +${VISIT_QUEST_MAT_REWARD} mats · +${VISIT_QUEST_SHIELD_REWARD} shield · Shockwave`,
      'loot',
    );
  }

  private updateBotSupplyContest(dt: number, dropX: number, dropZ: number): void {
    if (!this.supplyDrop || this.supplyDrop.getOpened() || !this.isMatchLive()) return;
    const px = this.player.body.position.x;
    const pz = this.player.body.position.z;
    const playerDist = Math.hypot(px - dropX, pz - dropZ);

    let nearest: { name: string; dist: number } | null = null;
    for (const bot of this.bots.getLivingBots()) {
      if (bot.knocked) continue;
      const d = Math.hypot(bot.x - dropX, bot.z - dropZ);
      if (!nearest || d < nearest.dist) nearest = { name: bot.name, dist: d };
    }

    if (nearest && nearest.dist < 3.6 && playerDist > 7) {
      this.botSupplyContestT += dt;
      this.supplyContestHud.style.display = this.botSupplyContestT > 0.4 ? 'block' : 'none';
      if (this.botSupplyContestT >= 2.8) {
        this.supplyDrop.forceOpenByBot();
        this.bots.setSupplyDropTarget(null, null);
        this.botSupplyContestT = 0;
        this.supplyContestHud.style.display = 'none';
        this.pushElimFeed(`${nearest.name} looted the supply drop!`, 'elim');
      }
    } else {
      this.botSupplyContestT = Math.max(0, this.botSupplyContestT - dt * 0.5);
      this.supplyContestHud.style.display = 'none';
    }
  }

  private spawnSkydivers(): void {
    this.skydivers.length = 0;
    const start = this.battleBus.getPathStart();
    for (let i = 0; i < 10; i++) {
      const poi = POI_LOOT_ZONES[Math.floor(Math.random() * POI_LOOT_ZONES.length)]!;
      this.skydivers.push({
        x: start.x + (Math.random() - 0.5) * 24,
        z: start.z + (Math.random() - 0.5) * 24,
        targetX: poi.x + (Math.random() - 0.5) * 10,
        targetZ: poi.z + (Math.random() - 0.5) * 10,
        speed: 16 + Math.random() * 14,
      });
    }
  }

  private updateSkydivers(dt: number): void {
    for (const d of this.skydivers) {
      const dx = d.targetX - d.x;
      const dz = d.targetZ - d.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.4) continue;
      const step = d.speed * dt;
      d.x += (dx / dist) * step;
      d.z += (dz / dist) * step;
    }
  }

  private updateBusJumpChip(): void {
    const progress = this.battleBus.getBusProgress01();
    let landed = 0;
    for (const d of this.skydivers) {
      if (Math.hypot(d.targetX - d.x, d.targetZ - d.z) < 0.4) landed += 1;
    }
    const next = Math.max(16, 99 - Math.floor(progress * 74) - landed * 2);
    if (next !== this.busJumpCount) {
      this.busJumpCount = next;
      this.busJumpChip.textContent = `Jumping: ${next} players`;
      this.busJumpChip.style.animation = 'none';
      void this.busJumpChip.offsetWidth;
      this.busJumpChip.style.animation = 'fps-stat-pop 0.35s ease-out';
    }
  }

  private getSkydiverMinimapMarkers(): FpsMinimapMarker[] {
    return this.skydivers.map((d) => ({
      x: d.x,
      z: d.z,
      color: '#88ccff',
      dot: true,
    }));
  }

  private isNearNpcQuest(): boolean {
    const b = this.player.body.position;
    return this.npcQuest.isNear(b.x, b.z);
  }

  private isNearNpcQuestHarvest(): boolean {
    const b = this.player.body.position;
    return this.npcQuestHarvest.isNear(b.x, b.z);
  }

  private isNearNpcQuestVisit(): boolean {
    const b = this.player.body.position;
    return this.npcQuestVisit.isNear(b.x, b.z);
  }

  private onGlobalKeyDown = (ev: KeyboardEvent): void => {
    if (this.craftBench.isOpen()) {
      if (ev.code === 'Escape') {
        this.craftBench.close();
        ev.preventDefault();
      }
      return;
    }
    if (this.npcQuest.isOpen()) {
      if (ev.code === 'Escape') {
        this.npcQuest.close();
        ev.preventDefault();
      }
      return;
    }
    if (this.npcQuestHarvest.isOpen()) {
      if (ev.code === 'Escape') {
        this.npcQuestHarvest.close();
        ev.preventDefault();
      }
      return;
    }
    if (this.npcQuestVisit.isOpen()) {
      if (ev.code === 'Escape') {
        this.npcQuestVisit.close();
        ev.preventDefault();
      }
      return;
    }
    if (this.settings.isOpen()) {
      if (ev.code === 'Escape') {
        this.settings.close();
        ev.preventDefault();
      }
      return;
    }
    if (ev.code === 'Escape') {
      this.settings.open();
      document.exitPointerLock();
      ev.preventDefault();
      return;
    }
    if (this.lobbyActive) return;
    if (this.introActive) {
      if (ev.code === 'Enter' || ev.code === 'Space') {
        this.battleBus.skip();
        ev.preventDefault();
      }
      return;
    }
    if (this.matchEnd) return;
    if (this.spectateActive) {
      if (ev.code === 'Space' || ev.code === 'ArrowRight') {
        this.cycleSpectateTarget(1);
        ev.preventDefault();
      } else if (ev.code === 'ArrowLeft') {
        this.cycleSpectateTarget(-1);
        ev.preventDefault();
      }
      return;
    }
    if (this.killCamActive) return;

    const d = ['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5'].indexOf(ev.code);
    if (d >= 0) {
      if (this.emoteWheelOpen && d < 4) {
        this.updateEmoteWheelHighlight(d);
        ev.preventDefault();
        return;
      }
      if (this.chatWheelOpen && d < 4) {
        this.updateChatWheelHighlight(d);
        ev.preventDefault();
        return;
      }
      if (this.pingWheelOpen && d < 4) {
        this.updatePingWheelHighlight(d);
        ev.preventDefault();
        return;
      }
      this.hotbar.setSelected(d);
      this.syncHotbarFromSlot();
    }
    if (ev.code === 'Digit6') {
      if (this.tryUseMiniShield()) ev.preventDefault();
    }
    if (ev.code === 'Digit7') {
      if (this.tryUseQuickBandage()) ev.preventDefault();
    }
    if (ev.code === 'KeyB') {
      this.hotbar.setSelected(3);
      this.syncHotbarFromSlot();
      this.emoteWheel12.onKeyDown('KeyB');
    }
    if (ev.code === 'KeyE') {
      if (this.canReviveTeammateNearby()) {
        ev.preventDefault();
        return;
      }
      if (this.canFishNearby()) {
        ev.preventDefault();
        return;
      }
      const pVendor = new THREE.Vector3(
        this.player.body.position.x,
        this.player.body.position.y,
        this.player.body.position.z,
      );
      if (this.vendors.tryInteract(pVendor)) {
        document.exitPointerLock();
        ev.preventDefault();
        return;
      }
      if (this.isNearCraftBench() && !this.craftBench.isOpen()) {
        this.craftBench.open();
        document.exitPointerLock();
        ev.preventDefault();
        return;
      }
      const pQuest = new THREE.Vector3(
        this.player.body.position.x,
        this.player.body.position.y + 0.55,
        this.player.body.position.z,
      );
      const dirQuest = new THREE.Vector3();
      this.camera.getWorldDirection(dirQuest);
      if (
        this.npcQuest.getState() !== 'completed' &&
        this.npcQuest.tryOpenFromInteract(pQuest, dirQuest)
      ) {
        document.exitPointerLock();
        ev.preventDefault();
        return;
      }
      if (
        this.npcQuestHarvest.getState() !== 'completed' &&
        this.npcQuestHarvest.tryOpenFromInteract(pQuest, dirQuest)
      ) {
        document.exitPointerLock();
        ev.preventDefault();
        return;
      }
      if (
        this.npcQuestVisit.getState() !== 'completed' &&
        this.npcQuestVisit.tryOpenFromInteract(pQuest, dirQuest)
      ) {
        document.exitPointerLock();
        ev.preventDefault();
        return;
      }
      if (this.tryVendingPurchase()) {
        ev.preventDefault();
        return;
      }
      if (this.trySlurpBarrelInteract()) {
        ev.preventDefault();
        return;
      }
      if (this.tryShieldKegInteract()) {
        ev.preventDefault();
        return;
      }
      if (
        !this.vehicles.isPlayerDriving() &&
        !this.ziplines.isRiding() &&
        this.ziplines.tryAttach(this.player.body.position.x, this.player.body.position.z)
      ) {
        this.player.setMovementLocked(true);
        this.mobilityProps.triggerZiplineMountNudge();
        this.prevZiplineRiding = true;
        this.audio.playGliderDeploy();
        ev.preventDefault();
        return;
      }
      const p = new THREE.Vector3(
        this.player.body.position.x,
        this.player.body.position.y + 0.55,
        this.player.body.position.z,
      );
      const dir = new THREE.Vector3();
      this.camera.getWorldDirection(dir);
      const openedVaultId = this.vaults.tryUnlockWithResult(p, this.heldKeycards);
      if (openedVaultId) {
        const vault = this.vaults.getObjectiveStates().find((v) => v.id === openedVaultId);
        const label = openedVaultId.toUpperCase();
        this.announceObjective(`vault-opened-${openedVaultId}`, `Vault ${label} opened!`, {
          x: vault?.x,
          z: vault?.z,
          worldLabel: 'VAULT',
          cooldownS: 45,
        });
        ev.preventDefault();
        return;
      }
      if (this.llama && !this.llama.getOpened() && this.llama.tryBreak(p, dir)) {
        this.applyLlamaLoot();
      } else if (this.supplyDrop && !this.supplyDrop.getOpened() && this.supplyDrop.tryOpen(p, dir)) {
        const dm = 40 + Math.floor(Math.random() * 28);
        const ds = 22 + Math.floor(Math.random() * 28);
        const dropPos = this.supplyDrop.getXZ();
        this.mats += dm;
        this.hudMats.textContent = `Mats ${this.mats}`;
        this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + ds));
        this.weapon.shrinkCooldown(0.85);
        this.audio.playChest();
        this.statSupply += 1;
        this.applySupplyDropWeaponLoot(dropPos.x, dropPos.z);
        this.pushElimFeed(`Supply drop — +${dm} mats · +${ds} shield`, 'loot');
        if (this.xpBar.addXp(35)) this.pushElimFeed(`Level ${this.xpBar.getLevel()}!`);
      } else {
        const chestLoot = this.loot.tryInteract(p, dir);
        if (chestLoot) {
          this.applyChestLoot(chestLoot.loot, chestLoot.x, chestLoot.z);
        } else if (
          this.isMatchLive() &&
          !this.knocked &&
          !this.matchEnd &&
          this.hotbar.getSelectedIndex() !== 3 &&
          this.buildPlacer.hasRemovableBuildInLook(this.camera, 4.2) &&
          this.buildEditMode.activate(this.camera)
        ) {
          this.pushElimFeed('Edit grid — aim cell · [R] toggle/confirm', 'loot');
          ev.preventDefault();
        }
      }
    }
    if (ev.code === 'KeyR') {
      if (this.buildEditMode.isActive()) {
        this.buildEditMode.confirmEdit(this.camera);
        ev.preventDefault();
        return;
      }
      if (this.tryRebootFromVan()) {
        ev.preventDefault();
        return;
      }
      if (
        this.isSquadMode() &&
        !this.knocked &&
        this.isNearRebootVan() &&
        this.getTeammateNeedingReboot() != null
      ) {
        ev.preventDefault();
        return;
      }
      if (this.knocked) return;

      const slot = this.hotbar.getSelectedIndex();
      const refund = this.buildPlacer.tryRemoveFromLook(this.camera, 4.2);
      if (refund > 0) {
        this.mats += refund;
        this.hudMats.textContent = `Mats ${this.mats}`;
        this.audio.playHit();
        this.pushElimFeed(`Piece edited — +${refund} mats`);
        ev.preventDefault();
        return;
      }

      if (slot >= 0 && slot <= 2) {
        if (this.weapon.tryReload()) ev.preventDefault();
        return;
      }

      if (slot === 3) {
        if (this.tryRepairTargetedBuild()) {
          ev.preventDefault();
          return;
        }
        this.buildPlacer.rotatePreview();
        ev.preventDefault();
      }
    }
    if (ev.code === 'KeyQ') {
      if (this.vendors.cycleOpenGoldVend()) {
        ev.preventDefault();
        return;
      }
      if (this.isNearVending()) {
        this.cycleVendOption();
        ev.preventDefault();
      }
    }
    if (ev.code === 'KeyM') {
      if (this.hotbar.getSelectedIndex() === 3) {
        this.buildPlacer.cycleBuildMat();
        const mat = this.buildPlacer.getBuildMat();
        this.pushElimFeed(`Build material: ${mat.charAt(0).toUpperCase() + mat.slice(1)}`);
        ev.preventDefault();
      }
    }
    if (ev.code === 'Tab') {
      this.pickaxeModeSticky = !this.pickaxeModeSticky;
      this.updatePickaxeMode();
      ev.preventDefault();
    }
    if (ev.code === 'KeyX') {
      this.pickaxeKeyHeld = true;
      this.updatePickaxeMode();
    }
    if (ev.code === 'KeyG' && !ev.repeat) {
      this.openPingWheel();
      ev.preventDefault();
    }
    if (ev.code === 'KeyT' && !ev.repeat) {
      if (this.knocked || this.vehicles.isPlayerDriving()) return;
      if (this.grenades.beginCook('frag')) {
        this.grenadeKeyHeld = true;
      }
    }
    if (ev.code === 'Digit8') {
      if (this.knocked || this.vehicles.isPlayerDriving()) return;
      const origin = new THREE.Vector3();
      this.camera.getWorldPosition(origin);
      const dir = new THREE.Vector3();
      this.camera.getWorldDirection(dir);
      if (this.grenades.tryThrowStink(origin, dir)) {
        this.audio.playShoot('shotgun');
        this.updateTacticalHud();
      }
    }
    if (ev.code === 'Digit9') {
      if (this.knocked || this.vehicles.isPlayerDriving() || !this.isMatchLive()) return;
      if (this.airstrike.tryMarkTarget(this.camera)) {
        this.audio.playAirstrikeIncoming();
        this.pushElimFeed('Air strike inbound!', 'loot');
        this.updateTacticalHud();
      }
    }
    if (ev.code === 'Digit0') {
      if (this.knocked || this.vehicles.isPlayerDriving() || !this.isMatchLive()) return;
      const origin = new THREE.Vector3();
      this.camera.getWorldPosition(origin);
      const dir = new THREE.Vector3();
      this.camera.getWorldDirection(dir);
      if (this.chugSplash.tryThrow(origin, dir)) {
        this.audio.playJump();
        this.updateTacticalHud();
      }
    }
    if (ev.code === 'Minus' || ev.code === 'DigitMinus') {
      if (this.knocked || this.vehicles.isPlayerDriving() || !this.isMatchLive()) return;
      const origin = new THREE.Vector3();
      this.camera.getWorldPosition(origin);
      const dir = new THREE.Vector3();
      this.camera.getWorldDirection(dir);
      if (this.shockwave.tryThrow(origin, dir)) {
        this.audio.playJump();
        this.updateTacticalHud();
      }
    }
    if (ev.code === 'KeyV' && !ev.repeat) {
      this.openEmoteWheel();
      ev.preventDefault();
    }
    if (ev.code === 'KeyH' && !ev.repeat) {
      if (!this.player.isKeyHeld('KeyC')) {
        this.openChatWheel();
        if (this.chatWheelOpen) {
          ev.preventDefault();
        } else if (this.vehicles.isPlayerDriving() && this.vehicles.tryHonk()) {
          this.audio.playShoot('ar');
        }
      } else if (this.vehicles.isPlayerDriving() && this.vehicles.tryHonk()) {
        this.audio.playShoot('ar');
      }
    }
    if (ev.code === 'KeyF') {
      if (this.carriedTeammate && !this.knocked && this.isMatchLive() && !this.matchEnd) {
        this.dropCarriedTeammate();
        ev.preventDefault();
        return;
      }
      if (this.getCarryTarget()) {
        ev.preventDefault();
        return;
      }
      if (this.player.isCarryingSquadmate()) {
        ev.preventDefault();
        return;
      }
      if (this.vehicles.isPlayerDriving()) {
        const out = this.vehicles.exit();
        if (out) {
          this.player.body.position.set(out.x, out.y, out.z);
          this.player.body.velocity.set(out.vx, 0, out.vz);
        }
        this.player.setMovementLocked(false);
      } else {
        const enter = this.vehicles.getEnterableNear(
          this.player.body.position.x,
          this.player.body.position.z,
        );
        if (enter) {
          this.vehicles.enter(enter.id);
          this.player.setMovementLocked(true);
          this.audio.playChest();
          this.pushElimFeed(`Entered ${enter.kind === 'quad' ? 'Quad Bike' : 'Pickup Truck'}`);
        }
      }
    }
    if (ev.code === 'KeyI' && !ev.repeat) {
      const slot = this.hotbar.getSelectedIndex();
      if (!this.knocked && slot >= 0 && slot <= 2) {
        this.weaponInspectOpen = !this.weaponInspectOpen;
        this.updateWeaponInspectOverlay();
        ev.preventDefault();
      }
    }
    if (ev.code === 'KeyY') {
      this.thirdPerson = !this.thirdPerson;
      this.pushElimFeed(this.thirdPerson ? 'Third-person view' : 'First-person view');
    }
    if (ev.code === 'KeyZ') {
      const el = document.documentElement;
      if (!document.fullscreenElement) {
        void el.requestFullscreen?.().catch(() => {});
      } else {
        void document.exitFullscreen?.().catch(() => {});
      }
    }
  };

  private onGlobalKeyUp = (ev: KeyboardEvent): void => {
    if (ev.code === 'KeyB') {
      this.emoteWheel12.onKeyUp('KeyB');
    }
    if (ev.code === 'KeyV') {
      this.confirmEmoteWheel();
      ev.preventDefault();
    }
    if (ev.code === 'KeyH') {
      this.confirmChatWheel();
      ev.preventDefault();
    }
    if (ev.code === 'KeyG') {
      this.confirmPingWheel();
      ev.preventDefault();
    }
    if (ev.code === 'KeyX') {
      this.pickaxeKeyHeld = false;
      this.updatePickaxeMode();
    }
    if (ev.code === 'KeyT') {
      this.grenadeKeyHeld = false;
      if (this.grenades.isCooking()) {
        const origin = new THREE.Vector3();
        this.camera.getWorldPosition(origin);
        const dir = new THREE.Vector3();
        this.camera.getWorldDirection(dir);
        if (this.grenades.releaseCook(origin, dir)) {
          this.audio.playShoot('shotgun');
          this.updateTacticalHud();
        } else {
          this.grenades.cancelCook();
        }
      }
    }
  };

  private loadOutfitHue(): number {
    const id = localStorage.getItem('fps_skin_id') ?? 'default';
    const SKINS = [
      { id: 'default',  hue: 0 },
      { id: 'azure',    hue: 0.6 },
      { id: 'jade',     hue: 0.33 },
      { id: 'sunset',   hue: 0.08 },
      { id: 'void',     hue: 0.75 },
      { id: 'prism',    hue: 0.85 },
    ];
    const match = SKINS.find(s => s.id === id);
    if (match) return match.hue;

    let hue = OUTFIT_SWATCH_HUES[0] ?? 0.02;
    try {
      const raw = localStorage.getItem(OUTFIT_HUE_KEY);
      if (raw != null) {
        const n = Number.parseFloat(raw);
        if (Number.isFinite(n)) hue = ((n % 1) + 1) % 1;
      }
    } catch {
      /* ignore storage errors */
    }
    const idx = OUTFIT_SWATCH_HUES.findIndex((h) => Math.abs(h - hue) < 0.001);
    if (idx >= 0 && FpsBattlePass.isSwatchUnlocked(idx)) return hue;
    for (let i = 0; i < OUTFIT_SWATCH_HUES.length; i++) {
      if (FpsBattlePass.isSwatchUnlocked(i)) return OUTFIT_SWATCH_HUES[i]!;
    }
    return OUTFIT_SWATCH_HUES[0] ?? 0.02;
  }

  private saveOutfitHue(hue: number): void {
    this.selectedOutfitHue = hue;
    try {
      localStorage.setItem(OUTFIT_HUE_KEY, String(hue));
    } catch {
      /* ignore storage errors */
    }
  }

  private outfitColorsFromHue(hue: number): {
    body: THREE.Color;
    shirt: THREE.Color;
    legs: THREE.Color;
  } {
    const body = new THREE.Color().setHSL(hue, 0.58, 0.46);
    const shirt = new THREE.Color().setHSL((hue + 0.42) % 1, 0.72, 0.58);
    const legs = new THREE.Color().setHSL(hue, 0.52, 0.32);
    return { body, shirt, legs };
  }

  private applyOutfitHueToCharacter(hue: number): void {
    if (!this.playerOutfitMats) return;
    const { body, shirt, legs } = this.outfitColorsFromHue(hue);
    this.playerOutfitMats.body.color.copy(body);
    this.playerOutfitMats.body.emissive.copy(body).multiplyScalar(0.18);
    this.playerOutfitMats.shirt.color.copy(shirt);
    this.playerOutfitMats.shirt.emissive.copy(shirt).multiplyScalar(0.42);
    this.playerOutfitMats.legs.color.copy(legs);
  }

  private buildLobbyOutfitSwatches(): void {
    const row = this.lobbyOverlay.querySelector('#outfit-swatches') as HTMLDivElement | null;
    if (!row) return;
    row.innerHTML = '';
    for (const hue of OUTFIT_SWATCH_HUES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const { body, shirt } = this.outfitColorsFromHue(hue);
      const bodyHex = `#${body.getHexString()}`;
      const shirtHex = `#${shirt.getHexString()}`;
      const selected = Math.abs(hue - this.selectedOutfitHue) < 0.001;
      btn.style.cssText =
        `width:34px;height:34px;border-radius:50%;cursor:pointer;padding:0;border:3px solid ${selected ? '#ffe566' : 'rgba(255,255,255,0.28)'};` +
        `background:linear-gradient(135deg,${shirtHex} 0 45%,${bodyHex} 45% 100%);box-shadow:0 2px 10px rgba(0,0,0,0.35);transition:transform 0.12s,border-color 0.12s`;
      btn.title = 'Select outfit color';
      btn.addEventListener('click', () => {
        this.saveOutfitHue(hue);
        this.applyOutfitHueToCharacter(hue);
        this.buildLobbyOutfitSwatches();
      });
      btn.addEventListener('mouseenter', () => {
        btn.style.transform = 'scale(1.08)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
      row.appendChild(btn);
    }
  }

  private loadLoadoutKind(): LoadoutKind {
    try {
      const raw = localStorage.getItem(LOADOUT_STORAGE_KEY);
      if (raw === 'balanced' || raw === 'aggro' || raw === 'sniper') return raw;
    } catch {
      /* ignore */
    }
    return 'balanced';
  }

  private applyLoadoutPreference(kind: LoadoutKind, persist = true): void {
    this.loadoutKind = kind;
    if (persist) {
      try {
        localStorage.setItem(LOADOUT_STORAGE_KEY, kind);
      } catch {
        /* ignore */
      }
    }
    this.hotbar.setSlot0Weapon(LOADOUT_SLOT0[kind]);
    this.buildLobbyLoadoutPicker();
  }

  private buildLobbyLoadoutPicker(): void {
    const row = this.lobbyOverlay.querySelector('#loadout-buttons') as HTMLDivElement | null;
    if (!row) return;
    row.innerHTML = '';
    const labels: Record<LoadoutKind, string> = {
      balanced: 'Balanced',
      aggro: 'Aggro',
      sniper: 'Sniper',
    };
    const hints: Record<LoadoutKind, string> = {
      balanced: 'AR + SG',
      aggro: 'SMG + PI',
      sniper: 'SR focus',
    };
    for (const kind of LOADOUT_CYCLE) {
      const btn = document.createElement('button');
      btn.type = 'button';
      const selected = kind === this.loadoutKind;
      btn.style.cssText =
        `padding:5px 12px;border-radius:8px;cursor:pointer;font:700 10px system-ui;letter-spacing:0.06em;` +
        `background:rgba(12,22,38,${selected ? '0.95' : '0.72'});` +
        `border:2px solid ${selected ? '#ffe566' : 'rgba(120,180,255,0.45)'};` +
        `color:${selected ? '#ffe566' : '#b8d4ff'};transition:transform 0.12s,border-color 0.12s,background 0.12s`;
      btn.textContent = labels[kind];
      btn.title = hints[kind];
      btn.addEventListener('click', () => this.applyLoadoutPreference(kind));
      btn.addEventListener('mouseenter', () => {
        btn.style.transform = 'scale(1.05)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.transform = '';
      });
      row.appendChild(btn);
    }
  }

  private buildPlayerCharacter(): THREE.Group {
    const g = new THREE.Group();
    const matBody = new THREE.MeshStandardMaterial({
      color: 0x2a72d0,
      roughness: 0.45,
      metalness: 0.25,
      emissive: 0x0a2548,
      emissiveIntensity: 0.18,
    });
    const matAccent = new THREE.MeshStandardMaterial({
      color: 0xffc04a,
      roughness: 0.4,
      emissive: 0xff9020,
      emissiveIntensity: 0.4,
    });
    const matSkin = new THREE.MeshStandardMaterial({
      color: 0xffd2a8,
      roughness: 0.6,
      emissive: 0x442211,
      emissiveIntensity: 0.04,
    });
    const matHelmet = new THREE.MeshStandardMaterial({
      color: 0x222a32,
      roughness: 0.4,
      metalness: 0.35,
    });
    this.playerOutfitMats = {
      body: matBody,
      shirt: matAccent,
      legs: new THREE.MeshStandardMaterial({
        color: 0x1d4d8e,
        roughness: 0.5,
      }),
    };
    this.playerTorsoMat = matBody;
    this.playerBaseTorsoEmissive.copy(matBody.emissive);
    const torso = new THREE.Mesh(new THREE.BoxGeometry(0.78, 0.95, 0.42), matBody);
    torso.position.y = 1.25;
    torso.castShadow = true;
    torso.name = 'torso';
    g.add(torso);
    const belt = new THREE.Mesh(new THREE.BoxGeometry(0.82, 0.12, 0.46), matAccent);
    belt.position.y = 0.84;
    belt.castShadow = true;
    g.add(belt);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.24, 14, 12), matSkin);
    head.position.y = 1.92;
    head.castShadow = true;
    g.add(head);
    const helmet = new THREE.Mesh(
      new THREE.SphereGeometry(0.27, 14, 10, 0, Math.PI * 2, 0, 1.2),
      matHelmet,
    );
    helmet.position.y = 1.96;
    helmet.castShadow = true;
    g.add(helmet);
    const visor = new THREE.Mesh(
      new THREE.BoxGeometry(0.4, 0.07, 0.05),
      new THREE.MeshStandardMaterial({
        color: 0x66ffe0,
        emissive: 0x55ffe0,
        emissiveIntensity: 1.6,
      }),
    );
    visor.position.set(0, 1.94, 0.22);
    g.add(visor);
    const armGeo = new THREE.CapsuleGeometry(0.1, 0.55, 4, 8);
    const leftArm = new THREE.Mesh(armGeo, matBody);
    leftArm.position.set(-0.46, 1.42, 0);
    leftArm.castShadow = true;
    leftArm.name = 'leftArm';
    g.add(leftArm);
    const rightArm = new THREE.Mesh(armGeo, matBody);
    rightArm.position.set(0.46, 1.42, 0);
    rightArm.castShadow = true;
    rightArm.name = 'rightArm';
    g.add(rightArm);
    const legGeo = new THREE.CapsuleGeometry(0.13, 0.6, 4, 8);
    const legMat = new THREE.MeshStandardMaterial({
      color: 0x1d4d8e,
      roughness: 0.5,
    });
    const leftLeg = new THREE.Mesh(legGeo, legMat);
    leftLeg.position.set(-0.2, 0.5, 0);
    leftLeg.castShadow = true;
    leftLeg.name = 'leftLeg';
    g.add(leftLeg);
    const rightLeg = new THREE.Mesh(legGeo, legMat);
    rightLeg.position.set(0.2, 0.5, 0);
    rightLeg.castShadow = true;
    rightLeg.name = 'rightLeg';
    g.add(rightLeg);
    return g;
  }

  private buildViewModel(): void {
    const gun = new THREE.Group();
    gun.layers.set(1);
    gun.position.set(this.viewModelGunRest.x, this.viewModelGunRest.y, this.viewModelGunRest.z);
    gun.rotation.order = 'YXZ';
    gun.rotation.y = this.viewModelGunBaseRotY;
    gun.rotation.x = this.viewModelGunRest.rotX;

    const bodyMat = new THREE.MeshStandardMaterial({
      color: 0x8b7cff,
      roughness: 0.32,
      metalness: 0.52,
      emissive: 0x3311aa,
      emissiveIntensity: 0.14,
      envMapIntensity: 1.35,
    });
    this.viewModelBodyMat = bodyMat;
    const barrelMat = new THREE.MeshStandardMaterial({
      color: 0x95a5a6,
      metalness: 0.75,
      roughness: 0.28,
      envMapIntensity: 1.3,
    });
    const accentMat = new THREE.MeshStandardMaterial({
      color: 0xffeaa7,
      emissive: 0xffeaa7,
      emissiveIntensity: 0.25,
    });
    const darkMat = new THREE.MeshStandardMaterial({
      color: 0x3a3548,
      roughness: 0.38,
      metalness: 0.48,
    });

    const addPart = (
      group: THREE.Group,
      geo: THREE.BufferGeometry,
      mat: THREE.Material,
      pos: [number, number, number],
      rot?: [number, number, number],
    ): THREE.Mesh => {
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = true;
      mesh.position.set(...pos);
      if (rot) mesh.rotation.set(...rot);
      group.add(mesh);
      return mesh;
    };

    const addMag = (
      variant: THREE.Group,
      kind: WeaponKind,
      geo: THREE.BufferGeometry,
      mat: THREE.Material,
      pos: [number, number, number],
    ): void => {
      const mag = new THREE.Group();
      mag.name = 'mag';
      mag.position.set(...pos);
      const mesh = new THREE.Mesh(geo, mat);
      mesh.castShadow = true;
      mag.add(mesh);
      variant.add(mag);
      this.viewModelMags[kind] = mag;
    };

    const kinds: WeaponKind[] = ['ar', 'shotgun', 'sniper', 'smg', 'pistol'];
    for (const kind of kinds) {
      const variant = new THREE.Group();
      variant.visible = kind === 'ar';

      if (kind === 'ar') {
        addPart(variant, new THREE.BoxGeometry(0.12, 0.14, 0.62), bodyMat, [0, 0, -0.04]);
        addPart(
          variant,
          new THREE.CylinderGeometry(0.035, 0.04, 0.48, 10),
          barrelMat,
          [0, 0.02, -0.48],
          [Math.PI / 2, 0, 0],
        );
        addPart(variant, new THREE.BoxGeometry(0.04, 0.08, 0.12), accentMat, [0.04, 0, -0.1]);
        addMag(variant, kind, new THREE.BoxGeometry(0.08, 0.1, 0.16), darkMat, [0, -0.1, 0.08]);
      } else if (kind === 'shotgun') {
        addPart(variant, new THREE.BoxGeometry(0.16, 0.15, 0.38), bodyMat, [0, 0, 0.02]);
        addPart(variant, new THREE.BoxGeometry(0.22, 0.12, 0.14), barrelMat, [0, 0.04, -0.28]);
        addPart(
          variant,
          new THREE.CylinderGeometry(0.028, 0.03, 0.22, 8),
          barrelMat,
          [-0.07, 0.04, -0.36],
          [Math.PI / 2, 0, 0],
        );
        addPart(
          variant,
          new THREE.CylinderGeometry(0.028, 0.03, 0.22, 8),
          barrelMat,
          [0.07, 0.04, -0.36],
          [Math.PI / 2, 0, 0],
        );
        addPart(variant, new THREE.BoxGeometry(0.06, 0.07, 0.1), accentMat, [0.06, 0, -0.02]);
        addMag(variant, kind, new THREE.BoxGeometry(0.07, 0.09, 0.12), darkMat, [0, -0.08, 0.1]);
      } else if (kind === 'sniper') {
        addPart(variant, new THREE.BoxGeometry(0.11, 0.13, 0.78), bodyMat, [0, 0, -0.12]);
        addPart(
          variant,
          new THREE.CylinderGeometry(0.032, 0.038, 0.58, 10),
          barrelMat,
          [0, 0.02, -0.58],
          [Math.PI / 2, 0, 0],
        );
        addPart(variant, new THREE.CylinderGeometry(0.055, 0.055, 0.18, 10), darkMat, [0, 0.12, -0.18]);
        addPart(variant, new THREE.BoxGeometry(0.07, 0.06, 0.14), accentMat, [0.05, 0, -0.04]);
        addMag(variant, kind, new THREE.BoxGeometry(0.1, 0.08, 0.2), darkMat, [0, -0.09, 0.06]);
      } else if (kind === 'smg') {
        addPart(variant, new THREE.BoxGeometry(0.1, 0.12, 0.34), bodyMat, [0, 0, 0.04]);
        addPart(
          variant,
          new THREE.CylinderGeometry(0.03, 0.034, 0.28, 8),
          barrelMat,
          [0, 0.02, -0.2],
          [Math.PI / 2, 0, 0],
        );
        addPart(variant, new THREE.BoxGeometry(0.03, 0.06, 0.08), accentMat, [0.04, 0, 0]);
        addMag(variant, kind, new THREE.BoxGeometry(0.05, 0.14, 0.08), darkMat, [0, -0.08, 0.1]);
      } else {
        addPart(variant, new THREE.BoxGeometry(0.08, 0.1, 0.18), bodyMat, [0, 0, 0.02]);
        addPart(
          variant,
          new THREE.CylinderGeometry(0.026, 0.03, 0.16, 8),
          barrelMat,
          [0, 0.02, -0.12],
          [Math.PI / 2, 0, 0],
        );
        addMag(variant, kind, new THREE.BoxGeometry(0.04, 0.07, 0.06), darkMat, [0, -0.06, 0.06]);
      }

      gun.add(variant);
      this.viewModelVariants[kind] = variant;
    }

    const pickaxe = new THREE.Group();
    pickaxe.visible = false;
    const pickHandleMat = new THREE.MeshStandardMaterial({
      color: 0x5c4033,
      roughness: 0.55,
      metalness: 0.15,
    });
    const pickHeadMat = new THREE.MeshStandardMaterial({
      color: 0x66ccff,
      emissive: 0x2288cc,
      emissiveIntensity: 0.35,
      roughness: 0.25,
      metalness: 0.72,
    });
    addPart(pickaxe, new THREE.CylinderGeometry(0.028, 0.032, 0.52, 8), pickHandleMat, [0.06, -0.06, 0.02], [
      0.35,
      0,
      -0.15,
    ]);
    addPart(pickaxe, new THREE.BoxGeometry(0.22, 0.06, 0.14), pickHeadMat, [0.1, 0.08, -0.18], [0.2, 0.4, 0]);
    addPart(pickaxe, new THREE.BoxGeometry(0.18, 0.05, 0.1), pickHeadMat, [-0.02, 0.1, -0.2], [-0.55, -0.2, 0]);
    gun.add(pickaxe);
    this.viewModelPickaxe = pickaxe;

    this.player.camera.add(gun);
    this.viewModelGun = gun;
  }

  private onWindowMouseUp = (e: MouseEvent): void => {
    if (e.button === 2) this.adsHeld = false;
  };

  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) {
      this.buildMouseHeld = false;
      this.gunMouseHeld = false;
      this.buildTurboCd = this.BUILD_TURBO_CD_BASE; // reset turbo ramp on release
    }
    if (e.button === 2) this.adsHeld = false;
  };

  private onWheel = (e: WheelEvent): void => {
    if (this.introActive || this.settings.isOpen() || this.matchEnd) return;
    if (this.hotbar.getSelectedIndex() !== 3) return;
    this.buildPlacer.cyclePiece(e.deltaY > 0 ? 1 : -1);
  };

  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 2) {
      e.preventDefault();
      if (!this.introActive && !this.settings.isOpen() && !this.matchEnd && !this.killCamActive && this.player.isLocked()) {
        this.adsHeld = true;
      }
      return;
    }
    if (e.button !== 0) return;
    if (!this.audioUnlocked) {
      this.audio.unlock();
      this.audioUnlocked = true;
    }
    if (this.lobbyActive) return;
    if (this.introActive) {
      this.battleBus.skip();
      return;
    }
    if (this.settings.isOpen() || this.matchEnd || this.killCamActive || this.emoteWheelOpen || this.chatWheelOpen) return;
    if (this.player.isSwimming() || this.ziplines.isRiding()) return;
    if (!this.player.isLocked()) {
      this.player.requestPointerLock(this.renderer.domElement);
      return;
    }
    if (this.hotbar.getSelectedIndex() === 3) {
      if (this.knocked || this.player.isSwimming() || this.ziplines.isRiding()) return;
      this.buildMouseHeld = true;
      if (this.buildPlaceCd <= 0) {
        if (this.tryPlaceBuild()) this.buildPlaceCd = this.BUILD_PLACE_CD;
      }
      return;
    }
    if (this.hotbar.getSelectedIndex() === 4) {
      if (this.tryUseBandage()) {
        this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
      }
      return;
    }
    if (this.pickaxeMode) {
      this.performPickaxeSwing();
      return;
    }
    this.gunMouseHeld = true;
    this.syncWeaponCombatInputs();
    if (this.weapon.fire()) {
      this.onGunFired();
    }
  };

  private updateTouchAimAssist(): void {
    if (!this.touchControls?.isTouchDevice() || !this.graphicsState.touchAimAssist) return;
    if (!this.touchControls.isFireHeld()) return;
    if (this.vehicles.isPlayerDriving()) return;

    const hbAds = this.hotbar.getSelectedIndex();
    const canAds =
      hbAds <= 2 &&
      this.adsHeld &&
      !this.isPistolDualWieldActive() &&
      this.player.isLocked() &&
      !this.matchEnd &&
      !this.settings.isOpen();
    if (canAds) return;

    const b = this.player.body;
    const eyeY = b.position.y + EYE_HEIGHT - this.player.getCrouchEyeDrop();
    const target = FpsTouchControls.findAimAssistTarget(
      this.camera,
      b.position.x,
      eyeY,
      b.position.z,
      this.bots.getAliveBotAimTargets(),
    );
    if (!target) return;

    const nudge = FpsTouchControls.applyAimNudge(
      this.camera,
      b.position.x,
      eyeY,
      b.position.z,
      target,
    );
    this.player.applyAimNudge(nudge.yawDelta, nudge.pitchDelta);
  }

  private isPistolDualWieldActive(): boolean {
    const slot = this.hotbar.getSelectedIndex();
    return slot <= 2 && this.weapon.getWeaponKind() === 'pistol' && this.adsHeld;
  }

  private syncWeaponCombatInputs(sprinting = false): void {
    this.weapon.setAltFire(this.isPistolDualWieldActive());
    this.weapon.setCombatState({
      ads:
        this.hotbar.getSelectedIndex() <= 2 &&
        this.adsHeld &&
        !this.isPistolDualWieldActive(),
      sprinting,
    });
  }

  private tryGunFireHeld(): void {
    const fireHeld = this.gunMouseHeld || (this.touchControls?.isFireHeld() ?? false);
    if (!fireHeld) return;
    if (this.settings.isOpen() || this.matchEnd || this.killCamActive || this.emoteWheelOpen || this.chatWheelOpen) return;
    if (this.player.isSwimming() || this.ziplines.isRiding()) return;
    if (this.knocked) return;
    const slot = this.hotbar.getSelectedIndex();
    if (slot === 3 || slot === 4) return;
    if (this.pickaxeMode) return;
    if (!this.player.isLocked()) return;

    this.syncWeaponCombatInputs();
    this.updateTouchAimAssist();
    if (this.weapon.fire()) {
      this.onGunFired();
    }
  }

  private tryTouchFireHeld(): void {
    this.tryGunFireHeld();
  }

  private updateWeaponHeatHud(): void {
    const slot = this.hotbar.getSelectedIndex();
    for (let i = 0; i < 3; i++) {
      if (i !== slot) {
        this.hotbar.setWeaponHeat(i, 0);
        continue;
      }
      const mythicActive = this.mythics.getActiveKind() === 'drum_gun';
      const ratio = mythicActive ? this.mythics.getHeatRatio() : this.weapon.getHeatRatio();
      const overheated = mythicActive ? this.mythics.isOverheated() : this.weapon.isOverheated();
      this.hotbar.setWeaponHeat(i, ratio, overheated);
    }
  }

  private onResize = (): void => {
    const w = this.mount.clientWidth || window.innerWidth;
    const h = this.mount.clientHeight || window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(this.getRenderPixelRatio(w, h));
    this.renderer.setSize(w, h);
    this.postFx?.composer.setSize(w, h);
  };

  /** JSON snapshot for automated testing / render_game_to_text. */
  getTestState(): Record<string, unknown> {
    const b = this.player.body.position;
    return {
      mode: this.matchEnd ?? (this.lobbyActive ? 'lobby' : this.introActive ? 'drop' : 'play'),
      lobby: this.lobbyActive,
      intro: this.introActive,
      matchEnd: this.matchEnd,
      player: { x: b.x, y: b.y, z: b.z },
      health: Math.round(this.health),
      shield: Math.round(this.shieldHud.getShield()),
      knocked: this.knocked,
      bleedout: Math.round(this.bleedoutTimer * 10) / 10,
      grenades: this.grenades?.getCount() ?? 0,
      miniShield: this.miniShieldCount,
      bandages: this.bandageInventoryCount,
      mats: this.mats,
      elims: this.playerEliminations,
      botsAlive: this.bots?.aliveCount() ?? 0,
      stormRadius: Math.round(this.storm.radius * 10) / 10,
      stormGrace: Math.round(this.stormGrace * 10) / 10,
      weapon: this.weapon.getWeaponKind(),
      ammo: this.weapon.getAmmoHudText(),
      inVehicle: this.vehicles?.isPlayerDriving() ?? false,
      matchElapsed: Math.round(this.matchElapsed),
      coords: 'x=east, z=south, y=up',
      dropMarker: this.getDropMarker(),
    };
  }

  /** Deterministic frame advance for Playwright / test harness. */
  advanceTime(ms: number): void {
    const step = FIXED;
    const steps = Math.max(1, Math.round(ms / (step * 1000)));
    for (let i = 0; i < steps; i++) {
      this.forcedDt = step;
      this.tick();
    }
  }

  /** Skip lobby + battle bus for automated gameplay tests. */
  skipToGameplay(): void {
    // Automation / autoplay path: dismiss tutorial and jump into live match
    this.tutorialDismissedSession = true;
    if (this.tutorial?.isVisible()) this.tutorial.hide();
    if (this.lobbyActive) {
      this.lobbyReady = true;
      this.startMatchFromLobby();
    }
    if (this.introActive) this.finishIntro();
    // Outdoor readable vantage: south of plaza, facing map center/POIs (not sky/fog wash)
    try {
      const groundY = this.getTerrainHeight(8, 28);
      this.player.body.position.set(8, groundY + 1.05, 28);
      this.player.body.velocity.set(0, 0, 0);
      // Face toward center tower / POIs (-Z)
      this.player.yawObject.rotation.y = 0.35;
    } catch {
      /* ignore if world not ready */
    }
    if (!this.player.isLocked()) {
      this.player.requestPointerLock(this.renderer.domElement);
    }
  }

  private updatePlayerCombatRimLight(dt: number): void {
    if (!this.playerTorsoMat) return;
    this.playerCombatRimT = Math.max(0, this.playerCombatRimT - dt);
    const firing =
      this.gunMouseHeld &&
      !this.pickaxeMode &&
      this.hotbar.getSelectedIndex() <= 2 &&
      !this.matchEnd
        ? 0.24
        : 0;
    const hitFlash = this.playerCombatRimT * 0.88;
    // Phase 31X-14: stronger combat rim when low HP
    const lowHp = this.health < 40 ? (40 - this.health) / 40 : 0;
    const intensity = Math.max(firing, hitFlash, lowHp * 0.32);
    if (intensity <= 0.01) {
      this.playerTorsoMat.emissive.copy(this.playerBaseTorsoEmissive);
      this.playerTorsoMat.emissiveIntensity = 0.18;
      return;
    }
    const rimColor = lowHp > 0.4 ? 0xff2244 : 0xff5533;
    this.playerTorsoMat.emissive
      .copy(this.playerBaseTorsoEmissive)
      .lerp(new THREE.Color(rimColor), intensity * (0.48 + lowHp * 0.22));
    this.playerTorsoMat.emissiveIntensity = 0.18 + intensity * (0.68 + lowHp * 0.35);
  }

  private updateDayNightCycle(dt: number): void {
    if (!this.running || this.introActive) return;

    windUniform.value = performance.now() * 0.001;

    // A full day-night cycle takes 180 seconds (3 minutes)
    const cycleDuration = 180;
    this.dayNightTime = (this.dayNightTime ?? 0.25) + dt / cycleDuration;
    if (this.dayNightTime > 1) this.dayNightTime -= 1;

    const angle = this.dayNightTime * Math.PI * 2;

    // Sun position
    const sunX = Math.cos(angle) * 150;
    const sunY = Math.sin(angle) * 120;
    const sunZ = Math.sin(angle) * 50;

    // Moon position (opposite of Sun)
    const moonX = -sunX;
    const moonY = -sunY;
    const moonZ = -sunZ;

    const u = this.sky.material.uniforms;

    // Sky Preetham defaults
    u['turbidity'].value = 2.45;
    u['mieCoefficient'].value = 0.0032;
    u['mieDirectionalG'].value = 0.84;

    const fog = this.scene.fog as THREE.Fog;
    const sunHeight = THREE.MathUtils.clamp(sunY / 120, -1, 1);
    const daylight = Math.max(0, sunHeight);
    const moonHeight = Math.max(0, moonY / 120);
    const lowSunAmount = sunY > 0 ? 1 - THREE.MathUtils.smoothstep(daylight, 0.1, 0.38) : 0;
    const isRising = Math.cos(angle) > 0;
    const sunriseAmount = isRising ? lowSunAmount : 0;
    const sunsetAmount = isRising ? 0 : lowSunAmount;
    const nightAmount = sunY <= 0 ? THREE.MathUtils.smoothstep(moonHeight, 0, 0.85) : 0;
    const phaseAmount = (this.storm.getStormPhase() - 1) / 5;
    const safe = this.storm.getSafeZoneRadius();
    const center = this.storm.getCenterXZ();
    const playerPos = this.player.body.position;
    const distToStormCenter = Math.hypot(playerPos.x - center.x, playerPos.z - center.z);
    const edgeProximity = 1 - THREE.MathUtils.clamp(Math.abs(distToStormCenter - safe) / 24, 0, 1);
    const outsideStorm = distToStormCenter > safe;

    this.lightingNightAmount = nightAmount;
    this.lightingSunsetAmount = sunsetAmount;
    this.stormMoodAmount = Math.min(0.72, Math.max(outsideStorm ? 0.34 : 0, edgeProximity * 0.28 + phaseAmount * 0.06));
    this.stormMoodPulse = this.stormMoodAmount * (0.72 + Math.sin(performance.now() * 0.004 + phaseAmount * Math.PI) * 0.28);

    if (sunY > 0) {
      this.sunLight.position.set(sunX, sunY, sunZ);
      u['sunPosition'].value.copy(this.sunLight.position).multiplyScalar(3000);
      this.sunLight.color.copy(isRising ? SUNRISE_LIGHT : SUNSET_LIGHT).lerp(SUN_DAY, 1 - lowSunAmount);
      this.sunLight.intensity = THREE.MathUtils.lerp(1.64, isRising ? 0.82 : 0.72, lowSunAmount);
      u['rayleigh'].value = 2.35 + sunriseAmount * 3.5 + sunsetAmount * 5.0;
    } else {
      this.sunLight.position.set(moonX, moonY, moonZ);
      u['sunPosition'].value.copy(this.sunLight.position).multiplyScalar(3000);
      this.sunLight.color.setHex(0x9fb3d5);
      this.sunLight.intensity = 0.24 + moonHeight * 0.3;
      u['rayleigh'].value = 0.85;
      u['mieCoefficient'].value = 0.0012;
    }

    if (fog) {
      this.moodFogColor.copy(FOG_DAY);
      this.moodFogColor.lerp(FOG_SUNRISE, sunriseAmount * 0.42);
      this.moodFogColor.lerp(FOG_SUNSET, sunsetAmount * 0.48);
      this.moodFogColor.lerp(FOG_NIGHT, nightAmount * 0.64);
      this.moodFogColor.lerp(FOG_STORM, this.stormMoodPulse * 0.13);
      fog.color.copy(this.moodFogColor);
      fog.near = Math.max(62, 72 - nightAmount * 10 - sunsetAmount * 6 - this.stormMoodPulse * 2.5);
      fog.far = Math.max(188, 214 - nightAmount * 24 - sunsetAmount * 14 - this.stormMoodPulse * 5);
    }

    const gu = this.postFx?.gradePass.uniforms;
    if (gu) {
      if (gu['saturation']) gu['saturation'].value = 1.24 + sunsetAmount * 0.04 - nightAmount * 0.07 - this.stormMoodPulse * 0.02;
      if (gu['contrast']) gu['contrast'].value = 1.08 - nightAmount * 0.025 - this.stormMoodPulse * 0.012;
      if (gu['lift']) gu['lift'].value = 0.02 + nightAmount * 0.035 + this.stormMoodPulse * 0.01;
      if (gu['moodTint']) {
        this.moodGradeColor.setRGB(1, 1, 1);
        this.moodGradeColor.lerp(GRADE_SUNSET, sunsetAmount * 0.22);
        this.moodGradeColor.lerp(GRADE_NIGHT, nightAmount * 0.18);
        this.moodGradeColor.lerp(GRADE_STORM, this.stormMoodPulse * 0.14);
        const poiZone = findPoiZoneAt(playerPos.x, playerPos.z);
        if (poiZone) {
          const poiBlend = getPoiTintBlendAt(playerPos.x, playerPos.z, poiZone);
          if (poiBlend > 0.001) {
            applyPoiAmbientGradeTint(this.moodGradeColor, getPoiAmbientGradeTint(poiZone.name), poiBlend);
          }
        }
        const tint = gu['moodTint'].value as number[];
        tint[0] = this.moodGradeColor.r;
        tint[1] = this.moodGradeColor.g;
        tint[2] = this.moodGradeColor.b;
      }
      if (gu['moodStrength']) {
        let moodStrength = Math.min(0.16, sunsetAmount * 0.1 + nightAmount * 0.08 + this.stormMoodPulse * 0.055);
        const poiZone = findPoiZoneAt(playerPos.x, playerPos.z);
        if (poiZone) {
          moodStrength = Math.min(0.22, moodStrength + getPoiTintBlendAt(playerPos.x, playerPos.z, poiZone) * 0.06);
        }
        // Phase 31X-12: desaturate / storm-tint harder when outside the safe zone
        if (this.storm.isOutside(playerPos.x, playerPos.z)) {
          moodStrength = Math.min(0.34, moodStrength + 0.12);
          if (gu['saturation']) {
            gu['saturation'].value = Math.max(0.92, (gu['saturation'].value as number) - 0.14);
          }
        }
        gu['moodStrength'].value = moodStrength;
      }
    }

    if (this.postFx?.bloomPass && this.graphicsState.bloom) {
      this.postFx.bloomPass.strength = getTimeOfDayBloomStrength(
        daylight,
        nightAmount,
        sunsetAmount,
        sunriseAmount,
        this.graphicsState.performanceMode,
      );
    }

    applyStormSkyWarmth(this.sky, this.storm.getSkyWarmth01());
    applyStormLightningFlash(this.sky, this.storm.getSkyLightningFlash01());

    // Update cloud position and rotation
    const clouds = this.sky.userData.clouds;
    if (clouds) {
      clouds.rotation.y = performance.now() * 0.000015;
      for (const child of clouds.children) {
        if (!(child instanceof THREE.Mesh)) continue;
        child.position.x += Math.sin(performance.now() * 0.00025 + child.id) * 0.002;
        child.position.z += Math.cos(performance.now() * 0.00021 + child.id) * 0.0015;
      }
    }

    updateNightSkyElements(
      this.nightSky,
      nightAmount,
      new THREE.Vector3(moonX, moonY, moonZ),
      performance.now(),
    );
  }

  private updatePlayerShadowBlob(): void {
    if (
      this.introActive ||
      this.matchEnd ||
      this.lobbyActive ||
      this.vehicles.isPlayerDriving() ||
      this.killCamActive ||
      this.spectateActive
    ) {
      this.playerShadowBlob.visible = false;
      return;
    }

    this.player.getLandingFxPosition(this.fxScratch);
    const gliding = this.player.isGliderActive();
    const airborne = gliding || !this.player.isGrounded() || Math.abs(this.player.body.velocity.y) > 0.85;
    const lift = Math.min(1.8, Math.max(0, this.player.body.velocity.y * 0.14 + (gliding ? 0.35 : 0)));
    const scale = airborne ? Math.max(0.48, 0.92 - lift * 0.22) : 1;
    this.playerShadowMat.opacity = airborne ? 0.2 + (1 - lift) * 0.14 : 0.36;
    this.playerShadowBlob.position.set(this.fxScratch.x, this.fxScratch.y + 0.02, this.fxScratch.z);
    this.playerShadowBlob.scale.set(scale, scale, 1);
    this.playerShadowBlob.visible = true;
  }

  private tick = (): void => {
    if (!this.running) return;
    const rawDt = this.forcedDt ?? Math.min(this.clock.getDelta(), 0.05);
    this.forcedDt = null;
    this.audio.updateMix(rawDt);

    if (this.hitStopTimer > 0) {
      this.hitStopTimer = Math.max(0, this.hitStopTimer - rawDt);
      this.renderFrame(rawDt);
      return;
    }

    let dt = this.killCamActive ? rawDt * KILL_CAM_SLOW : rawDt;
    const victoryScale = this.victoryRoyale?.getTimeScale() ?? 1;
    if (this.matchEnd === 'won' && victoryScale < 1) {
      dt = rawDt * victoryScale;
    }

    if (this.lobbyActive) {
      this.updateLobbyMatchmaking(dt);
      if (!this.lobbyDragging) {
        this.lobbyShowcaseYaw += dt * 0.42;
      }
      this.lobbyPedestal.visible = true;
      this.lobbyShowcaseRimLight.visible = true;
      this.lobbyShowcasePulseT += dt;
      const showcasePulse = 0.5 + 0.5 * Math.sin(this.lobbyShowcasePulseT * 2.2);
      this.lobbyPedestalRingMat.emissiveIntensity = 1.45 + showcasePulse * 0.55;
      this.lobbyPedestal.scale.setScalar(1 + showcasePulse * 0.012);
      this.lobbyShowcaseRimLight.intensity = 0.75 + showcasePulse * 0.55;
      this.playerCharacter.visible = true;
      this.playerCharacter.position.set(0, 0.34, 0);
      this.playerCharacter.rotation.y = this.lobbyShowcaseYaw;
      this.lobbyPedestal.rotation.y = this.lobbyShowcaseYaw;
      this.applyOutfitHueToCharacter(this.selectedOutfitHue);
      for (const limb of ['leftLeg', 'rightLeg', 'leftArm', 'rightArm'] as const) {
        const mesh = this.playerCharacter.getObjectByName(limb) as THREE.Mesh | null;
        if (mesh) mesh.rotation.x = 0;
      }
      if (this.skinLocker && this.skinLocker.isVisible()) {
        const lockerCamPos = new THREE.Vector3(0, 1.35, 3.2);
        this.camera.position.lerp(lockerCamPos, Math.min(1, 12 * dt));
        this.camera.lookAt(0, 1.05, 0);
      } else {
        const lt = performance.now() * 0.0003;
        const lobbyCamPos = new THREE.Vector3(Math.sin(lt) * 3.8, 1.5, Math.cos(lt) * 3.8);
        this.camera.position.lerp(lobbyCamPos, Math.min(1, 8 * dt));
        this.camera.lookAt(0, 1.05, 0);
      }
      this.ui.style.opacity = '0';
      this.crosshair.style.opacity = '0';
      updateStylizedSky(this.sky, performance.now());
      this.renderFrame(dt);
      return;
    }

    if (this.introActive) {
      const u = this.battleBus.update(dt);
      this.updateSkydivers(dt);
      this.updateBusJumpChip();
      let camT = this.battleBus.getBusProgress01();
      if (u.phase === 'drop') camT = 1;
      const cf = this.battleBus.getCameraFollow(camT);
      this.camera.position.copy(cf.position);
      this.camera.lookAt(cf.lookAt);
      if (u.phase === 'done') this.finishIntro();
      const busStart = this.battleBus.getPathStart();
      const busEnd = this.battleBus.getPathEnd();
      const busPos = this.battleBus.getPosition();
      this.minimap.update(busPos.x, busPos.z, 0, {
        busRoute: {
          startX: busStart.x,
          startZ: busStart.z,
          endX: busEnd.x,
          endZ: busEnd.z,
          busX: busPos.x,
          busZ: busPos.z,
        },
        heatZones: POI_LOOT_ZONES,
        dropMarker: this.dropMarker ?? undefined,
        markers: this.getSkydiverMinimapMarkers(),
      });
      this.updateCompass(this.player.yawObject.rotation.y);
      this.ui.style.opacity = '1';
      this.crosshair.style.opacity = '0';
      updateStylizedSky(this.sky, performance.now());
      this.renderFrame(dt);
      return;
    }

    this.tickCombat(dt, rawDt);
    this.tickUI(dt, rawDt);
    this.renderFrame(dt);
  };

  /** Simulation, combat, pickups, storm damage, and match systems. */
  private tickCombat(dt: number, rawDt: number): void {
    this.world.step(FIXED, dt, 10);
    const stormGraceBefore = this.stormGrace;
    if (this.stormGrace > 0) {
      this.stormGrace = Math.max(0, this.stormGrace - dt);
      if (stormGraceBefore > 0 && this.stormGrace <= 0 && !this.stormMovingAnnounced && this.isMatchLive()) {
        this.stormMovingAnnounced = true;
        this.showStormWarningBanner('STORM MOVING - FIND SAFE');
        this.pushElimFeed('Storm moving — rotate to the safe edge', 'elim');
        this.audio.playStormClosing();
        this.minimap.triggerStormDamagePulse();
        this.triggerSafeZoneGuidancePulse(3200);
      }
    } else {
      this.storm.update(dt);
      this.stormCircle.update(
        dt,
        this.storm.radius,
        this.storm.getCenterXZ(),
        this.storm.getStormPhase(),
        this.player.body.position.x,
        this.player.body.position.z,
        this.storm.isOutside(this.player.body.position.x, this.player.body.position.z),
        this.storm.getShrinkEtaSeconds(),
      );
      this.checkFinalCircle();
    }
    this.updateStormCountdownHud(dt);
    this.updateStormShrinkWarning(dt);

    for (const { mesh, body } of this.built.syncables) {
      mesh.position.set(body.position.x, body.position.y, body.position.z);
      mesh.quaternion.set(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w);
    }

    if (this.ziplines.isRiding()) {
      const stillRiding = this.ziplines.update(
        dt,
        this.player.body,
        this.player.isKeyHeld('Space'),
      );
      const rideYaw = this.ziplines.getRideYaw();
      if (rideYaw !== null) this.player.yawObject.rotation.y = rideYaw;
      if (!stillRiding) {
        this.player.setMovementLocked(false);
        this.mobilityProps.triggerZiplineDismountNudge();
      }
    }
    this.prevZiplineRiding = this.ziplines.isRiding();

    this.player.setMobilityPitchNudge(this.mobilityProps.updateCameraNudges(dt));
    this.player.setGamepadAimTargets(this.bots.getAliveBotAimTargets());
    this.player.update(dt);
    this.applyMantleAssist(dt);
    this.staminaHud.setStamina(this.player.getStamina());
    this.staminaHud.setSwimSprintActive(this.player.isSwimSprinting());
    this.weapon.update(dt);
    this.updateShotMuzzleFlashes(dt);
    this.crosshairPulse = Math.max(0, this.crosshairPulse - dt * (this.prefersReducedMotion() ? 4.2 : 6.2));

    // Update 3D audio listener position/orientation
    {
      const cameraPos = new THREE.Vector3();
      const cameraForward = new THREE.Vector3();
      const cameraUp = new THREE.Vector3();
      this.camera.getWorldPosition(cameraPos);
      this.camera.getWorldDirection(cameraForward);
      cameraUp.set(0, 1, 0).applyQuaternion(this.camera.quaternion);
      this.audio.updateListener(cameraPos, cameraForward, cameraUp);
    }

    // Low-HP heartbeat
    if (this.health < 30) {
      this.audio.startHeartbeat(62);
    } else {
      this.audio.stopHeartbeat();
    }
    this.tryTouchFireHeld();

    // Vehicle controls (when driving, WASD controls vehicle; Space=boost; Ctrl=brake)
    const driving = this.vehicles.isPlayerDriving();

    // Detach/attach camera based on whether we need world-space cam (driving / 3p / kill cam / spectate)
    if (this.spectateActive) {
      this.tickDeathSpectate(rawDt);
    } else if (this.replaySystem?.isDeathReplayActive()) {
      this.replaySystem.updateDeathSnippet(rawDt, this.camera);
    } else if (this.killCamActive) {
      this.updateKillCamCamera();
      this.killTracer.update();
      this.killCamTimer -= rawDt;
      if (this.killCamTimer <= 0) this.endKillCam();
    } else {
      const needDetached = driving || this.thirdPerson;
      if (needDetached && !this.cameraDetached) {
        this.player.pitchObject.remove(this.camera);
        this.scene.add(this.camera);
        this.cameraDetached = true;
      } else if (!needDetached && this.cameraDetached) {
        this.scene.remove(this.camera);
        this.camera.position.set(0, 0, 0);
        this.camera.rotation.set(0, 0, 0);
        this.player.pitchObject.add(this.camera);
        this.cameraDetached = false;
      }
    }

    if (driving && !this.killCamActive && !this.spectateActive) {
      this.vehicles.update(dt, {
        forward: this.player.isKeyHeld('KeyW'),
        back: this.player.isKeyHeld('KeyS'),
        left: this.player.isKeyHeld('KeyA'),
        right: this.player.isKeyHeld('KeyD'),
        boost: this.player.isKeyHeld('Space'),
        brake: this.player.isKeyHeld('ControlLeft') || this.player.isKeyHeld('ControlRight'),
      });
      const anchor = this.vehicles.getCameraAnchor();
      if (anchor) {
        // Keep player body co-located with vehicle so storm/pickups use vehicle position
        this.player.body.position.set(anchor.pos.x, anchor.pos.y, anchor.pos.z);
        this.player.body.velocity.set(0, 0, 0);
        // Match yaw to vehicle so re-enter alignment feels right
        this.player.yawObject.rotation.y = anchor.yaw;
        // Chase cam: back & up from seat
        const camOffset = 4.2;
        const camHeight = 1.6;
        const cx = anchor.pos.x - anchor.forward.x * camOffset;
        const cz = anchor.pos.z - anchor.forward.z * camOffset;
        this.camera.position.set(cx, anchor.pos.y + camHeight, cz);
        this.camera.lookAt(anchor.pos.x, anchor.pos.y + 0.6, anchor.pos.z);
      }
      this.hudVehicle.style.display = 'block';
      this.updateVehicleHud();
    } else {
      this.vehicles.update(dt, {
        forward: false,
        back: false,
        left: false,
        right: false,
        boost: false,
        brake: false,
      });
      this.hudVehicle.style.display = 'none';
    }

    const hbAds = this.hotbar.getSelectedIndex();
    this.syncWeaponCombatInputs(
      this.player.getTacticalSprintActive() || this.player.getSprintHold() > 0.08,
    );
    const canAds =
      hbAds <= 2 &&
      this.adsHeld &&
      !this.isPistolDualWieldActive() &&
      this.player.isLocked() &&
      !this.matchEnd &&
      !this.settings.isOpen();
    if (canAds && !this.wasAdsSettled) {
      this.viewmodelRig.triggerAdsSettle();
      this.triggerCrosshairPulse(0.045);
    }
    this.wasAdsSettled = canAds;

    // Third-person camera when not driving
    if (this.matchEnd === 'won' && !this.killCamActive) {
      this.victoryOrbitAngle += rawDt * 0.65;
      const px = this.player.body.position.x;
      const py = this.player.body.position.y - 0.92;
      const pz = this.player.body.position.z;
      const radius = 3.8;
      const camHeight = 1.35;
      const cx = px + Math.sin(this.victoryOrbitAngle) * radius;
      const cz = pz + Math.cos(this.victoryOrbitAngle) * radius;
      const cy = py + camHeight;
      this.camera.position.set(cx, cy, cz);
      this.camera.lookAt(px, py + 1.05, pz);
      this.playerCharacter.position.set(px, py, pz);
      this.playerCharacter.rotation.y = this.victoryOrbitAngle + Math.PI;
      this.playerCharacter.rotation.z = 0;
      this.playerCharacter.visible = true;
      this.viewModelGun.visible = false;
      const leftLeg = this.playerCharacter.getObjectByName('leftLeg') as THREE.Mesh | null;
      const rightLeg = this.playerCharacter.getObjectByName('rightLeg') as THREE.Mesh | null;
      const leftArm = this.playerCharacter.getObjectByName('leftArm') as THREE.Mesh | null;
      const rightArm = this.playerCharacter.getObjectByName('rightArm') as THREE.Mesh | null;
      if (leftLeg) leftLeg.rotation.x = 0;
      if (rightLeg) rightLeg.rotation.x = 0;
      if (leftArm) leftArm.rotation.x = 0;
      if (rightArm) rightArm.rotation.x = Math.sin(performance.now() * 0.005) * 0.25;
    } else if (!driving && !this.killCamActive && !this.spectateActive) {
      if (this.thirdPerson && !this.introActive) {
        // Detach camera from pitch object only for positioning purposes — do not reparent.
        // Set camera world position behind player with over-the-shoulder offset.
        const yaw = this.player.yawObject.rotation.y;
        const pitch = (this.player.pitchObject.rotation.x ?? 0);

        // Base target values for over-the-shoulder and ADS
        const motionOffset =
          !this.prefersReducedMotion() && !canAds
            ? this.player.isSliding() ? 1 : this.player.getTacticalSprintActive() ? 0.55 : 0
            : 0;
        const targetDist = canAds ? 1.8 : 3.5 + motionOffset * 0.16;
        const targetShoulder = canAds ? 0.35 : 0.55 + motionOffset * 0.045;

        // Smoothly lerp dist and shoulder offset
        const lerpSpeed = 10 * dt;
        this.cameraThirdPersonDist += (targetDist - this.cameraThirdPersonDist) * Math.min(1, lerpSpeed);
        this.cameraThirdPersonShoulder += (targetShoulder - this.cameraThirdPersonShoulder) * Math.min(1, lerpSpeed);

        // Calculate direction vectors
        const forward = new THREE.Vector3(-Math.sin(yaw), 0, -Math.cos(yaw));
        const right = new THREE.Vector3(Math.cos(yaw), 0, -Math.sin(yaw));

        // Start at player head position (feetY + EYE)
        const px = this.player.body.position.x;
        const py = this.player.body.position.y - 0.9 + 1.62;
        const pz = this.player.body.position.z;
        const headPos = new THREE.Vector3(px, py, pz);

        // Pitch rotation affects look direction
        const lookDir = new THREE.Vector3(
          -Math.sin(yaw) * Math.cos(pitch),
          Math.sin(pitch),
          -Math.cos(yaw) * Math.cos(pitch)
        );

        // Target camera position offset behind and to the right shoulder
        const idealCamPos = headPos.clone()
          .addScaledVector(lookDir, -this.cameraThirdPersonDist)
          .addScaledVector(right, this.cameraThirdPersonShoulder);

        // Camera Collision Raycast to prevent clipping through built items
        const rayDir = new THREE.Vector3().subVectors(idealCamPos, headPos);
        const maxRayDist = rayDir.length();
        rayDir.normalize();

        const raycaster = new THREE.Raycaster(headPos, rayDir, 0.05, maxRayDist);
        const hits = raycaster.intersectObject(this.built.sceneRoot, true);

        let finalCamPos = idealCamPos;
        if (hits.length > 0) {
          const hitDist = hits[0].distance;
          const safeDist = Math.max(0.2, hitDist - 0.15);
          finalCamPos = headPos.clone().addScaledVector(rayDir, safeDist);
        }

        this.camera.position.copy(finalCamPos);

        // Smoothly orient camera look-target ahead of player
        const lookTarget = headPos.clone()
          .addScaledVector(lookDir, 10)
          .addScaledVector(right, this.cameraThirdPersonShoulder);
        this.camera.lookAt(lookTarget);

        this.playerCharacter.position.set(px, this.player.body.position.y - 0.92, pz);
        this.playerCharacter.rotation.y = yaw;
        this.playerCharacter.visible = true;
        // Walk anim via FpsCharacterAnimator
        {
          const horiz = Math.hypot(this.player.body.velocity.x, this.player.body.velocity.z);
          const sprinting = this.player.getTacticalSprintActive() || this.player.getSprintHold() > 0.08 || horiz > 4.5;
          const animState: CharAnimState = {
            velocityXZ: horiz,
            isSprinting: sprinting,
            isAirborne: Math.abs(this.player.body.velocity.y) > 1.5,
            isCrouching: false,
            isShooting: false,
            isDead: this.matchEnd === 'lost',
            deathTime: 0,
          };
          this.playerAnimator.update(dt, animState);
          const lateral = this.player.body.velocity.x * right.x + this.player.body.velocity.z * right.z;
          const leanScale = sprinting ? 0.042 : 0.026;
          const targetLean = THREE.MathUtils.clamp(-lateral * leanScale, -0.12, 0.12);
          this.thirdPersonLeanZ += (targetLean - this.thirdPersonLeanZ) * Math.min(1, dt * 10);
          this.playerCharacter.rotation.z = this.thirdPersonLeanZ;
        }
      } else {
        this.thirdPersonLeanZ += (0 - this.thirdPersonLeanZ) * Math.min(1, dt * 10);
        this.playerCharacter.visible = false;
      }
    } else {
      this.thirdPersonLeanZ += (0 - this.thirdPersonLeanZ) * Math.min(1, dt * 10);
      this.playerCharacter.visible = false;
    }

    this.viewModelGun.visible = !driving && !this.thirdPerson && !this.killCamActive && !this.spectateActive;

    if (this.weaponSwapT > 0) {
      const duration = WeaponSystem.SWAP_DURATION;
      const prevT = this.weaponSwapT;
      this.weaponSwapT = Math.max(0, this.weaponSwapT - dt);
      const progress = 1 - this.weaponSwapT / duration;
      const half = duration * 0.5;
      if (prevT > half && this.weaponSwapT <= half) this.syncViewModelWeapon();

      const holsterY = 0.26;
      const holsterRot = 0.58;
      const holsterZ = 0.1;
      const smooth = (u: number) => u * u * (3 - 2 * u);
      let blend: number;
      if (progress < 0.5) blend = smooth(progress / 0.5);
      else blend = 1 - smooth((progress - 0.5) / 0.5);

      this.viewModelGun.position.y = this.viewModelGunRest.y - holsterY * blend;
      this.viewModelGun.rotation.x = this.viewModelGunRest.rotX + holsterRot * blend;
      this.viewModelGun.position.z = this.viewModelGunRest.z + holsterZ * blend;

      if (this.weaponSwapT <= 0) {
        if (!this.weaponSwapEquipCuePlayed) {
          this.weaponSwapEquipCuePlayed = true;
          this.audio.playWeaponEquip();
        }
        this.weaponSwapFromKind = null;
        this.syncViewModelWeapon();
      }
    } else if (this.pickaxeSwingT > 0) {
      this.pickaxeSwingT = Math.max(0, this.pickaxeSwingT - dt);
      const phase = 1 - this.pickaxeSwingT / Math.max(0.01, this.pickaxeSwingDur);
      const swing = Math.sin(phase * Math.PI);
      this.viewModelGun.rotation.x = this.viewModelGunRest.rotX - swing * 0.95;
      this.viewModelGun.position.z = this.viewModelGunRest.z + swing * 0.14;
      this.viewModelGun.position.y = this.viewModelGunRest.y - swing * 0.06;
    } else {
      const reloadRatio = this.weapon.getReloadRatio();
      if (reloadRatio > 0.02) {
        this.applyReloadViewModel(reloadRatio);
      } else {
        if (this.prevReloadRatio > 0.02) this.resetViewModelMags();
        this.prevReloadRatio = 0;
        this.reloadMidpointCuePlayed = false;

        const body = this.player.body;
        const horizSpeed = Math.hypot(body.velocity.x, body.velocity.z);
        const moveFactor = Math.min(1, horizSpeed / 7);
        if (moveFactor > 0.05 && this.player.isLocked() && !driving) {
          this.viewModelBobPhase += dt * (8 + horizSpeed * 0.85);
        }
        const bobY = Math.sin(this.viewModelBobPhase) * 0.012 * moveFactor;
        const bobZ = Math.cos(this.viewModelBobPhase * 2) * 0.008 * moveFactor;

        const adsTarget = canAds ? 1 : 0;
        this.viewModelAdsBlend += (adsTarget - this.viewModelAdsBlend) * Math.min(1, 9 * dt);
        const ads = this.viewModelAdsBlend;
        const targetX = this.viewModelGunRest.x * (1 - ads * 0.72);
        const targetY = this.viewModelGunRest.y + bobY - ads * 0.028;
        const targetZ = this.viewModelGunRest.z + bobZ + ads * 0.04;
        const targetRotX = this.viewModelGunRest.rotX - ads * 0.018;

        const ease = Math.min(1, 14 * dt);
        this.viewModelGun.rotation.x += (targetRotX - this.viewModelGun.rotation.x) * ease;
        this.viewModelGun.rotation.y +=
          (this.viewModelGunBaseRotY - this.viewModelGun.rotation.y) * ease;
        this.viewModelGun.rotation.z += (0 - this.viewModelGun.rotation.z) * ease;
        this.viewModelGun.position.x += (targetX - this.viewModelGun.position.x) * ease;
        this.viewModelGun.position.z += (targetZ - this.viewModelGun.position.z) * ease;
        this.viewModelGun.position.y += (targetY - this.viewModelGun.position.y) * ease;
        this.updateViewModelRarityPulse();
      }
    }

    // ── FpsViewmodelRig per-frame update ────────────────────────────────────
    {
      const b = this.player.body;
      const pvel = new THREE.Vector3(b.velocity.x, b.velocity.y, b.velocity.z);
      const horizSpeed = Math.sqrt(b.velocity.x * b.velocity.x + b.velocity.z * b.velocity.z);
      const grounded = Math.abs(b.velocity.y) < 1.5;
      const sprinting = this.player.getTacticalSprintActive() || this.player.getSprintHold() > 0.08 || horizSpeed > 4.5;
      this.viewmodelRig.setDualWield(
        this.weapon.isDualWieldActive(),
        this.weapon.getDualWieldShotToggle(),
      );
      this.viewmodelRig.update(
        dt,
        pvel,
        grounded,
        canAds,
        sprinting,
        this.weapon.getReloadRatio(),
        this.prefersReducedMotion(),
      );
    }

    const b = this.player.body;

    const baseFov = this.graphicsState.fov;
    const adsFov = ADS_FOV * (baseFov / BASE_FOV) * this.weapon.getAdsFovMultiplier();
    this.player.setAdsActive(canAds);
    if (this.gliderFovBumpT > 0) {
      this.gliderFovBumpT = Math.max(0, this.gliderFovBumpT - dt);
    }
    if (this.endgameFovPulseT > 0) {
      this.endgameFovPulseT = Math.max(0, this.endgameFovPulseT - dt);
    }
    const gliderFovExtra =
      this.gliderFovBumpT > 0
        ? (this.gliderFovBumpT / GLIDER_FOV_BUMP_DURATION) * GLIDER_FOV_BUMP
        : 0;
    const endgameFovExtra =
      !canAds && this.endgameFovPulseT > 0
        ? (this.endgameFovPulseT / ENDGAME_FOV_PULSE_DURATION) * ENDGAME_FOV_PULSE
        : 0;

    // Freefall FOV push: airborne, falling fast, and NOT gliding
    const isFreefalling = !this.player.isGliderActive() && b.velocity.y < -8 && !this.introActive;
    const fallFovExtra = isFreefalling ? Math.min(b.velocity.y * -1.1, 20) : 0;
    if (isFreefalling && !this.player.isGrounded() && !this.matchEnd) {
      this.skydiveWindAcc += dt;
      const windInterval = b.velocity.y < -16 ? 0.055 : 0.085;
      if (this.skydiveWindAcc >= windInterval) {
        this.skydiveWindAcc = 0;
        this.spawnSkydiveWindStreaks();
      }
    } else {
      this.skydiveWindAcc = 0;
    }
    
    // Phase 30X-7: brief FOV dip during weapon swap
    const swapFovDip =
      this.weaponSwapT > 0
        ? -Math.sin((1 - this.weaponSwapT / WeaponSystem.SWAP_DURATION) * Math.PI) * 4.5
        : 0;
    // Phase 33X-16: slight FOV while on zipline
    const zipFov = this.ziplines?.isRiding() ? 3.5 : 0;

    // Pass the base target FOV to player controller, it handles the dynamic sliding/falling modifiers
    this.player.setBaseFov(
      canAds
        ? adsFov
        : baseFov + gliderFovExtra + fallFovExtra + endgameFovExtra + swapFovDip + zipFov,
    );

    this.weapon.setCombatState({
      ads: canAds,
      sprinting: this.player.getTacticalSprintActive() || this.player.getSprintHold() > 0.08,
    });

    const sliding = this.player.isSliding();
    this.weapon.setSliding(sliding);
    const tactical = this.player.getTacticalSprintActive();
    // Motion blur on sprint/slide
    const isSprinting = this.player.getTacticalSprintActive() || this.player.getSprintHold() > 0.08;
    const blurStr = sliding ? 0.9 : isSprinting ? 0.5 : 0;
    this.postFx?.motionBlurPass.setBlurStrength(blurStr);
    const speedJuice = sliding || tactical;
    const gliding = this.player.isGliderActive();
    this.updateDropCue(gliding);
    const sprintCueActive =
      !sliding &&
      !gliding &&
      this.player.getSprintHold() > 0.12 &&
      Math.hypot(b.velocity.x, b.velocity.z) > 7.5 &&
      this.player.isLocked() &&
      !this.matchEnd;
    if (sprintCueActive && !this.prevMovementCueSprint) {
      this.player.getLandingFxPosition(this.fxScratch);
      this.triggerMovementCue('sprint', 'Sprint burst', new THREE.Color(0xffb04a), 1.4, this.fxScratch.clone());
    }
    if (sliding && !this.prevMovementCueSlide && this.player.isLocked() && !this.matchEnd) {
      this.player.getLandingFxPosition(this.fxScratch);
      this.triggerMovementCue('slide', 'Slide', new THREE.Color(0x9fdcff), 1.1, this.fxScratch.clone());
    }
    const vaulting = this.player.isVaulting();
    if (vaulting && !this.prevMovementCueVault && this.player.isLocked() && !this.matchEnd) {
      this.player.getLandingFxPosition(this.fxScratch);
      this.triggerMovementCue('mantle', 'Mantle', new THREE.Color(0xb7f6ff), 1.25, this.fxScratch.clone());
    }
    this.prevMovementCueSprint = sprintCueActive;
    this.prevMovementCueSlide = sliding;
    this.prevMovementCueVault = vaulting;
    if (speedJuice && this.player.isLocked() && !this.matchEnd) {
      const yaw = this.player.yawObject.rotation.y;
      const rightX = Math.cos(yaw);
      const rightZ = -Math.sin(yaw);
      const lat = b.velocity.x * rightX + b.velocity.z * rightZ;
      const rollMag = sliding ? 0.072 : 0.038;
      this.player.setCameraRoll(Math.sign(lat || 1) * rollMag * Math.min(1, Math.abs(lat) / 10));
      const targetLines = sliding ? 0.58 : 0.34;
      this.speedLinesOpacity += (targetLines - this.speedLinesOpacity) * Math.min(1, 14 * dt);
      this.speedLinesOverlay.style.animationPlayState = 'running';
    } else if (gliding && this.player.isLocked() && !this.matchEnd) {
      this.player.setCameraRoll(0);
      const fallSp = Math.max(0, -b.velocity.y);
      const targetLines = 0.36 + Math.min(0.22, fallSp / 42);
      this.speedLinesOpacity += (targetLines - this.speedLinesOpacity) * Math.min(1, 12 * dt);
      this.speedLinesOverlay.style.animationPlayState = 'running';
    } else {
      this.player.setCameraRoll(0);
      this.speedLinesOpacity += (0 - this.speedLinesOpacity) * Math.min(1, 16 * dt);
      if (this.speedLinesOpacity < 0.02) {
        this.speedLinesOverlay.style.animationPlayState = 'paused';
      }
    }
    this.speedLinesOverlay.style.opacity = String(this.speedLinesOpacity);

    // Glider mesh visibility + wing flutter
    if (this.gliderMesh) {
      this.gliderMesh.visible = gliding && this.playerCharacter.visible;
      if (gliding) {
        const yaw = this.player.yawObject.rotation.y;
        const rightX = Math.cos(yaw);
        const rightZ = -Math.sin(yaw);
        const lateralSpeed = b.velocity.x * rightX + b.velocity.z * rightZ;
        const fallSpeed = Math.max(0, -b.velocity.y);
        const flutter = Math.sin(performance.now() * (0.0055 + fallSpeed * 0.00022));
        const wing = this.gliderMesh.getObjectByName('gliderWing');
        this.gliderMesh.rotation.z += (THREE.MathUtils.clamp(-lateralSpeed * 0.018, -0.16, 0.16) - this.gliderMesh.rotation.z) * Math.min(1, 8 * dt);
        this.gliderMesh.rotation.x += (THREE.MathUtils.clamp(fallSpeed * 0.01, 0, 0.08) - this.gliderMesh.rotation.x) * Math.min(1, 8 * dt);
        if (wing) {
          wing.rotation.x = -Math.PI / 2 + flutter * (0.018 + Math.min(0.018, fallSpeed * 0.002));
          wing.rotation.z = flutter * (0.025 + Math.min(0.035, Math.abs(lateralSpeed) * 0.004));
        }
      } else {
        this.gliderMesh.rotation.set(0, 0, 0);
      }
    }

    this.damageFlashOpacity *= Math.exp(-7.2 * dt);
    this.damageFlash.style.opacity = String(this.damageFlashOpacity);
    this.suppressionCueCd = Math.max(0, this.suppressionCueCd - dt);

    // Bot update (AI patrol/chase/shoot) and apply incoming damage
    if (!this.matchEnd) {
      this.syncBotEndgameMode();
      const playerWorldPos = new THREE.Vector3(b.position.x, b.position.y, b.position.z);
      const stormC = this.storm.getCenterXZ();
      const botResult = this.bots.update(
        dt,
        playerWorldPos,
        this.camera,
        !this.knocked,
        {
          isOutside: (x, z) => this.storm.isOutside(x, z),
          centerX: stormC.x,
          centerZ: stormC.z,
          safeRadius: this.storm.getSafeZoneRadius(),
        },
        this.loot,
        this.buildPlacer,
        this.sniperGlint.isPlayerGlintActive(),
      );
      this.botAI2.update(dt, playerWorldPos, this.matchElapsed, {
        storm: this.storm,
        weaponPickups: this.weaponPickups,
        healItems: this.healItems,
      });
      // Spawn tracers + bot muzzle visuals
      for (const t of botResult.tracerEvents) {
        const tracerLen = t.origin.distanceTo(t.targetPos);
        const end = t.origin.clone().add(t.dir.clone().multiplyScalar(tracerLen));
        this.bots.spawnTracer(this.scene, t.origin, end, 0xffaa55);
        this.maybeShowBotSuppressionCue(t, end);
        if (t.damage > 0 || t.loadout === 'sniper') {
          this.sniperGlint.maybeFromBotShot(t.origin, playerWorldPos, t.loadout === 'sniper');
        }
        // Gunfire visual audio event
        const dist = Math.hypot(t.origin.x - b.position.x, t.origin.z - b.position.z);
        if (dist < 80) {
          const vol = Math.max(0, 1 - dist / 80);
          this.audioVisualizer.addEvent({ x: t.origin.x, z: t.origin.z, type: 'gunfire', intensity: vol }, b.position.x, b.position.z, this.player.yawObject.rotation.y);
          // Positional gunshot audio
          this.audio.playGunshotAt(t.origin, t.loadout);
        }
      }

      // Pass bot footstep events to spatial audio hook
      if (botResult.footstepEvents && botResult.footstepEvents.length > 0) {
        for (const step of botResult.footstepEvents) {
          const dist = Math.hypot(step.x - b.position.x, step.z - b.position.z);
          if (dist < 42) {
             const vol = Math.max(0, 1 - dist / 42);
             // Quadratic falloff feels more natural for footsteps
             this.audio.playBotFootstep(vol * vol);
             // Positional footstep via panner
             this.audio.playFootstepAt(new THREE.Vector3(step.x, 0, step.z), false);
             this.audioVisualizer.addEvent({ x: step.x, z: step.z, type: 'footstep', intensity: vol }, b.position.x, b.position.z, this.player.yawObject.rotation.y);
          }
        }
      }

      // Proximity hiss based on nearest bot distance
      {
        const nearest = this.bots.getNearestAliveBot(b.position.x, b.position.z);
        const nearestBotDist = nearest ? Math.hypot(nearest.x - b.position.x, nearest.z - b.position.z) : 999;
        const hissIntensity = Math.max(0, 1 - nearestBotDist / 15);
        this.audio.setProximityHiss(hissIntensity);
      }

      for (const _botName of botResult.buildCoverEvents) {
        this.pushBotFeed('Bot built cover');
      }
      for (const ev of botResult.rebootEvents) {
        this.pushElimFeed(`${ev.name} rebooted at van!`, 'loot');
      }
      for (const tm of this.teammates) {
        const tmResult = tm.update(dt, playerWorldPos, this.camera);
        for (const t of tmResult.tracerEvents) {
          this.bots.spawnTracer(this.scene, t.origin, t.end, 0x66ffaa);
        }
        for (const elim of tmResult.eliminations) {
          this.pushElimFeed(`${tm.name} eliminated ${elim.botName}!`, 'elim');
          this.pushKillFeedEntry(tm.name, elim.botName, 'Assault Rifle', elim.pos, undefined, 'AR');
        }
      }
      this.updateTeammateRevive(dt);
      this.updateTeammateCarry(dt);
      this.updateTeammateRebootVan(dt);
      this.updateFishing(dt);
      if (this.isSquadMode()) {
        this.squadCallouts.update(dt, {
          teammates: this.teammates,
          playerInStorm: this.storm.isOutside(b.position.x, b.position.z),
          squadMode: true,
        });
      }
      if (botResult.damageToPlayer > 0) {
        const dmg = botResult.damageToPlayer;
        this.audio.triggerCombatFocus(Math.min(1.2, 0.75 + dmg / 120));
        const hp0 = this.health;
        const sh0 = this.shieldHud.getShield();
        const dmgResult = applyCombatDamage(
          { health: this.health, shield: sh0, alive: this.health > 0 && !this.knocked },
          dmg,
        );
        this.health = dmgResult.health;
        this.shieldHud.setShield(dmgResult.shield);
        if (dmgResult.appliedToShield > 0) {
          this.applyPlayerShieldHit(
            dmgResult.appliedToShield,
            sh0 > 0 && dmgResult.appliedToShield >= sh0,
          );
        }
        if (this.health < hp0) {
          this.damageFlashOpacity = Math.min(0.78, this.damageFlashOpacity + 0.4);
          this.audio.playPlayerHurt();
          FpsHaptics.vibratePattern();
          this.postFx?.chromaticAberration.trigger(0.9, 0.4);
        }
        // Record direction of attack for indicator (use nearest shooting bot that dealt damage)
        let nearest: { d: number; ev: typeof botResult.tracerEvents[0] } | null = null;
        for (const ev of botResult.tracerEvents) {
          if (ev.damage <= 0) continue;
          const dx = ev.botPos.x - b.position.x;
          const dz = ev.botPos.z - b.position.z;
          const d2 = dx * dx + dz * dz;
          if (!nearest || d2 < nearest.d) nearest = { d: d2, ev };
        }
        if (nearest) {
          this.lastAttackerName = nearest.ev.botName;
          this.lastElimSource = 'combat';
          this.lastDamageDir = {
            x: nearest.ev.botPos.x - b.position.x,
            z: nearest.ev.botPos.z - b.position.z,
            t: 1.4,
          };
          this.showIncomingDamageFromWorld(nearest.ev.botPos.x, nearest.ev.botPos.z, Math.min(1.25, dmg / 45));
        } else {
          this.showIncomingDamageFallback(Math.min(1, dmg / 45));
        }
        if (this.health <= 0 && !this.knocked) {
          this.enterKnocked('combat');
        } else if (this.knocked && botResult.damageToPlayer > 0) {
          this.bleedoutTimer = Math.max(0, this.bleedoutTimer - 2.5);
        }
      }
    }

    if (this.isMatchLive()) {
      if (this.grenadeKeyHeld && this.grenades.isCooking()) {
        const cookGround = new THREE.Vector3(b.position.x, b.position.y, b.position.z);
        if (this.grenades.updateCook(dt, cookGround)) {
          this.grenadeKeyHeld = false;
          const cookedKind = this.grenades.detonateCookInHand();
          if (cookedKind === 'frag') {
            this.applyExplosionAt(b.position.x, b.position.y + 1.05, b.position.z);
            this.updateTacticalHud();
          }
        }
      }
      this.grenades.update(
        dt,
        (blast) => {
          this.applyExplosionAt(blast.x, blast.y, blast.z);
        },
        (cx, cy, cz, radius, tickDamage) => {
          this.applyStinkTick(cx, cy, cz, radius, tickDamage);
        },
      );
      this.airstrike.update(dt, (blast) => {
        this.applyExplosionAt(blast.x, blast.y, blast.z, {
          flatDamage: blast.damage,
          radius: blast.radius,
        });
      });
      this.chugSplash.update(dt, (heal) => {
        this.applyChugSplashHeal(heal);
      });
      this.shockwave.update(dt, (blast) => {
        this.applyShockwaveBlast(blast);
      });
    }
    // Hit marker fade
    this.hitMarkerOpacity *= Math.exp(-9 * dt);
    this.hitMarker.style.opacity = String(this.hitMarkerOpacity);
    if (this.killBannerTimer > 0) {
      this.killBannerTimer -= dt;
      if (this.killBannerTimer <= 0) this.killBanner.style.display = 'none';
    }
    if (this.placementBannerTimer > 0) {
      this.placementBannerTimer -= dt;
      if (this.placementBannerTimer <= 0) this.placementBanner.style.display = 'none';
    }
    if (this.lootToastTimer > 0) {
      this.lootToastTimer -= dt;
      if (this.lootToastTimer <= 0) this.lootToast.style.display = 'none';
    }
    this.priorityHudQuietTimer = Math.max(0, this.priorityHudQuietTimer - dt);
    this.priorityFeedQuietTimer = Math.max(0, this.priorityFeedQuietTimer - dt);
    if (this.pickupCardTimer > 0) {
      this.pickupCardTimer -= dt;
      if (this.pickupCardTimer <= 0) this.pickupCard.hide();
    }
    if (this.emoteToastTimer > 0) {
      this.emoteToastTimer -= dt;
      if (this.emoteToastTimer <= 0) this.emoteToast.style.display = 'none';
    }
    if (this.mantleHintTimer > 0) {
      this.mantleHintTimer -= dt;
      if (this.mantleHintTimer <= 0) {
        this.hudMantleHint.style.opacity = '0';
        this.hudMantleHint.style.transform = 'translate(-50%,-50%) scale(0.96)';
      }
    }
    if (this.player.consumeMantleFailPrompt()) {
      this.hudMantleHint.textContent = "Can't mantle";
      this.mantleHintTimer = 0.42;
      this.hudMantleHint.style.opacity = '1';
      this.hudMantleHint.style.transform = 'translate(-50%,-50%) scale(1)';
      this.pushElimFeed("Can't mantle — too high or blocked", 'elim');
    }
    if (this.mantleHintTimer <= 0 && this.hudMantleHint.textContent !== 'Mantle') {
      this.hudMantleHint.textContent = 'Mantle';
    }
    this.updateAliveCounter();

    // Damage direction indicator (decay + rotate SVG arrow)
    if (this.lastDamageDir) {
      this.lastDamageDir.t -= dt;
      if (this.lastDamageDir.t <= 0) {
        this.lastDamageDir = null;
        this.damageDirIndicator.style.opacity = '0';
      } else {
        const yaw = this.player.yawObject.rotation.y;
        // Angle from forward to attacker, in screen space (0 = forward/up)
        const attackerAngle = Math.atan2(this.lastDamageDir.x, this.lastDamageDir.z);
        const rel = attackerAngle - yaw;
        const g = this.damageDirIndicator.querySelector('#ddi-g') as SVGGElement | null;
        if (g) g.setAttribute('transform', `rotate(${(rel * 180) / Math.PI})`);
        this.damageDirIndicator.style.opacity = String(Math.min(1, this.lastDamageDir.t));
      }
    }

    // Vehicle enter hint
    const ziplineNear =
      !driving && !this.matchEnd && !this.ziplines.isRiding()
        ? this.ziplines.getNearAttach(b.position.x, b.position.z)
        : null;
    if (this.ziplineHint) {
      if (ziplineNear) {
        this.ziplineHint.style.display = 'block';
        this.ziplineHint.textContent = ziplineNear.label;
      } else {
        this.ziplineHint.style.display = 'none';
      }
    }

    const enterable =
      !driving && !this.matchEnd && !ziplineNear
        ? this.vehicles.getEnterableNear(b.position.x, b.position.z)
        : null;
    if (enterable) {
      this.vehicleEnterHint.style.display = 'block';
      this.vehicleEnterHint.textContent = `[F] Enter ${enterable.kind === 'quad' ? 'Quad Bike' : 'Pickup Truck'}`;
    } else {
      this.vehicleEnterHint.style.display = 'none';
    }
    const phaseEarly = getStormPhaseInfo({
      initialRadius: STORM_INIT_R,
      minRadius: STORM_MIN_R,
      currentRadius: this.storm.radius,
      shrinkPerSecond: this.storm.getShrinkPerSecond(),
    });
    const stormTickEvery = Math.max(0.2, 0.5 - phaseEarly.phase * 0.048);

    if (
      this.isMatchLive() &&
      !this.knocked &&
      !this.matchEnd &&
      phaseEarly.phase >= 5 &&
      this.playerEliminations >= 1
    ) {
      this.botAirstrikeCd -= dt;
      const aliveBots = this.countMatchAlive();
      if (this.botAirstrikeCd <= 0 && aliveBots >= 4 && Math.random() < 0.2) {
        this.botAirstrikeCd = 36 + Math.random() * 32;
        this.airstrike.queueExternalStrike(b.position.x, b.position.z);
        this.audio.playAirstrikeIncoming();
        this.pushElimFeed('Enemy airstrike inbound!', 'knock');
      }
    }

    if (this.jumpPads.tryBoost(b.position, b.velocity, dt)) {
      this.audio.playJump();
    }

    // --- Phase 5: Mobility props (bouncers, launch pads, rifts) ---
    const mobilityHit = this.mobilityProps.update(dt, new THREE.Vector3(b.position.x, b.position.y, b.position.z));
    if (mobilityHit) {
      b.velocity.x += mobilityHit.impulse.x;
      b.velocity.y = mobilityHit.impulse.y;
      b.velocity.z += mobilityHit.impulse.z;
      if (mobilityHit.triggerGlider) {
        this.gliderFovBumpT = GLIDER_FOV_BUMP_DURATION;
      }
      this.audio.playJump();
    }

    // --- Phase 2: Mythic weapons ---
    const mythicPicked = this.mythics.tryPickup(new THREE.Vector3(b.position.x, b.position.y, b.position.z));
    if (mythicPicked) {
      this.mythics.setActive(mythicPicked);
      this.showRarityMoment(this.mythicDisplayName(mythicPicked), 'mythic', 'Pickup');
    }
    const mythicFireHeld =
      this.gunMouseHeld || (this.touchControls?.isFireHeld() ?? false);
    const mythicResult: MythicUpdateResult = this.mythics.update(
      dt,
      new THREE.Vector3(b.position.x, b.position.y, b.position.z),
      new THREE.Vector3(b.velocity.x, b.velocity.y, b.velocity.z),
      this.camera.rotation.y,
      {
        fire: mythicFireHeld && this.mythics.getActiveKind() !== null,
        altFire: this.adsHeld,
        space: this.player.isKeyHeld('Space'),
      },
    );
    if (mythicResult.jetpackLift > 0) {
      b.velocity.y += mythicResult.jetpackLift * dt;
    }
    if (mythicResult.grappleTarget) {
      b.position.set(mythicResult.grappleTarget.x, mythicResult.grappleTarget.y, mythicResult.grappleTarget.z);
    }

    // --- Phase 3: Heal items ---
    this.healItems.update(dt);
    const healPickup = this.healItems.tryPickup(new THREE.Vector3(b.position.x, b.position.y, b.position.z));
    if (healPickup) {
      // pickup added to inventory automatically
    }

    const tSec = performance.now() * 0.001;
    this.floorPickups.update(tSec, dt);
    this.weaponPickups.update(
      tSec,
      new THREE.Vector3(b.position.x, b.position.y, b.position.z),
      this.camera,
      this.renderer.domElement.clientWidth,
      this.renderer.domElement.clientHeight,
    );
    this.updatePoiZones(b.position.x, b.position.z, dt);

    const weaponPickup = this.weaponPickups.tryCollect(
      new THREE.Vector3(b.position.x, b.position.y, b.position.z),
    );
    if (weaponPickup) {
      this.applyWeaponPickup(weaponPickup);
    }

    const modPickup = this.weaponPickups.tryCollectMod(
      new THREE.Vector3(b.position.x, b.position.y, b.position.z),
    );
    if (modPickup) {
      this.tryApplyWeaponMod(modPickup.mod);
      this.audio.playChest();
    }

    const got = this.floorPickups.tryCollect(b.position.x, b.position.y, b.position.z, 1.35);
    if (got) {
      if (got.kind === 'shield') {
        this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + got.amount));
        this.pushBotFeed(`picked up +${got.amount} shield`);
      } else if (got.kind === 'mat') {
        this.mats += got.amount;
        this.hudMats.textContent = `Mats ${this.mats}`;
        this.pushBotFeed(`grabbed +${got.amount} mats`);
        this.trackDomEffect(spawnPickupFlyChip(document.body, `BUILD MATS +${got.amount}`, '#f1c40f'));
        this.showPickupCard('Materials', 'common', `+${got.amount} building mats`, 'Pickup');
      } else if (got.kind === 'forage') {
        this.health = Math.min(100, this.health + got.amount);
        this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
        this.pushBotFeed(`foraged +${got.amount} HP`);
      } else if (got.kind === 'rift') {
        const dir = new THREE.Vector3();
        this.camera.getWorldDirection(dir);
        dir.y = 0;
        if (dir.lengthSq() < 1e-5) dir.set(0, 0, -1);
        dir.normalize();
        b.velocity.x += dir.x * 38;
        b.velocity.z += dir.z * 38;
        b.velocity.y = Math.max(b.velocity.y, 7.2);
        this.audio.playGliderDeploy();
        this.pushElimFeed('Rift shard — launch!', 'loot');
      } else if (got.kind === 'ammo') {
        const added = this.weapon.addAmmo(got.amount);
        if (added > 0) {
          this.showPickupToast(`+${added} AMMO`);
          this.trackDomEffect(spawnPickupFlyChip(document.body, `RESERVE AMMO +${added}`, '#5dade2'));
          this.showPickupCard('Ammo', 'common', `+${added} reserve rounds`, 'Pickup');
        }
      }
      this.audio.playChest();
    }

    const horizSp = driving
      ? this.vehicles.getCurrentSpeed()
      : Math.hypot(b.velocity.x, b.velocity.z);
    if (!this.matchEnd && !this.introActive) {
      this.statMeters += horizSp * dt;
    }

    const g = this.player.isGliderActive();
    const swimming = this.player.isSwimming();
    const waterSurfaceY = 0.08;
    if (this.player.consumeSwimEntryPulse()) {
      this.audio.playSplash();
      this.waterSplash.spawn(b.position.x, waterSurfaceY, b.position.z);
      this.particles.emitWaterSplash(
        new THREE.Vector3(b.position.x, waterSurfaceY, b.position.z),
        Math.min(1.35, 0.85 + horizSp * 0.08),
      );
    }
    if (!swimming && this.prevSwimming) {
      this.waterSplash.spawn(b.position.x, waterSurfaceY, b.position.z);
    }
    this.prevSwimming = swimming;
    if (swimming && horizSp > 2.6) {
      this.waterSplashAcc += dt;
      const splashInterval = horizSp > 3.2 ? 0.14 : 0.22;
      if (this.waterSplashAcc >= splashInterval) {
        this.waterSplashAcc = 0;
        this.waterSplash.spawn(b.position.x, waterSurfaceY, b.position.z);
      }
    } else {
      this.waterSplashAcc = 0;
    }
    if (g && !this.prevGlider) {
      this.audio.playGliderDeploy();
      this.gliderFovBumpT = GLIDER_FOV_BUMP_DURATION;
      this.player.getGliderTrailPosition(this.fxScratch);
      this.triggerMovementCue('glider', 'Glider deployed', new THREE.Color(0x44eeff), 1.4, this.fxScratch.clone());
      this.spawnGliderTrailParticle();
    }
    if (g) {
      this.gliderTrailAcc += dt;
      const gliderTrailInterval = this.prefersReducedMotion() ? 0.13 : 0.075;
      if (this.gliderTrailAcc > gliderTrailInterval) {
        this.gliderTrailAcc = 0;
        this.spawnGliderTrailParticle();
      }
    } else {
      this.gliderTrailAcc = 0;
    }
    if (this.prevGlider && !g) {
      this.player.getLandingFxPosition(this.fxScratch);
      this.player.applyGliderLandingPunch();
      this.spawnLandingDust(this.fxScratch.x, this.fxScratch.y, this.fxScratch.z);
      if (!this.prefersReducedMotion()) {
        this.triggerCameraShake(0.024);
      }
      if (this.dropMarkerVisibleUntilLand) {
        this.dropMarkerVisibleUntilLand = false;
      }
    }
    this.prevGlider = g;

    this.updateWorldFx(dt);
    // Track previous Y velocity for landing shake detection
    const currentVelY = b.velocity.y;
    if (this.player.consumeLandingPulse()) {
      this.player.getLandingFxPosition(this.fxScratch);
      const hardLanding = this.prevPlayerVelY < -8;
      const fallDmg = this.player.computeFallDamage(this.prevPlayerVelY);
      if (fallDmg > 0 && !this.knocked) {
        // Phase 30X-6: fall damage via combat rules + heavier screen flash
        this.notePlayerDamage();
        const sh0 = this.shieldHud.getShield();
        const fallHit = applyCombatDamage(
          { health: this.health, shield: sh0, alive: this.health > 0 },
          fallDmg,
        );
        this.health = fallHit.health;
        this.shieldHud.setShield(fallHit.shield);
        if (fallHit.appliedToShield > 0) {
          this.applyPlayerShieldHit(
            fallHit.appliedToShield,
            sh0 > 0 && fallHit.appliedToShield >= sh0,
          );
        }
        this.damageFlashOpacity = Math.min(0.88, this.damageFlashOpacity + 0.42 + fallDmg * 0.004);
        this.showIncomingDamageFromWorld(this.fxScratch.x, this.fxScratch.z, Math.min(1.2, fallDmg / 40));
        this.audio.playPlayerHurt();
        if (this.health <= 0) this.enterKnocked('combat');
      }
      this.spawnLandingDust(this.fxScratch.x, this.fxScratch.y, this.fxScratch.z, hardLanding);
      if (this.dropMarkerVisibleUntilLand) {
        this.dropMarkerVisibleUntilLand = false;
      }
      const landingPoi = findPoiZoneAt(b.position.x, b.position.z);
      if (landingPoi) {
        this.showPoiZone(landingPoi.name);
        this.poiCurrentZone = landingPoi.name;
        this.poiZoneCooldowns.set(landingPoi.name, this.matchElapsed + 20);
        this.npcQuestVisit.recordPoiVisit(landingPoi.name);
      }
      this.showEarlyObjectiveHint();
      if (hardLanding) {
        this.triggerMovementCue('hard-landing', 'Hard landing', new THREE.Color(0xd2a066), 1.6, this.fxScratch.clone());
      }
      // Hard landing thud shake
      if (hardLanding && !this.prefersReducedMotion()) {
        const shakeIntensity = Math.min(0.032, (-this.prevPlayerVelY - 8) * 0.003 + 0.018);
        this.triggerCameraShake(shakeIntensity);
      }
    }
    this.prevPlayerVelY = currentVelY;
    this.impactSparks.update(dt);
    this.particles.update(dt);
    this.chestBurst.update(dt);
    this.loot.update(dt);
    this.lootRarity.update(dt);
    this.updatePlayerShadowBlob();
    this.footstepDust.update(dt);
    this.buildDustVfx.update(dt);
    this.bulletCasingVfx.update(dt, this.player.body.position.y - 1.55);
    this.shieldCrackVfx.update(dt);

    // Animate debris chunks (world destruction)
    this.debrisChunks = this.debrisChunks.filter(d => {
      d.life -= dt;
      d.vel.y -= 9.8 * dt;
      d.mesh.position.addScaledVector(d.vel, dt);
      if (d.life <= 0) {
        this.scene.remove(d.mesh);
        d.mesh.geometry.dispose();
        return false;
      }
      return true;
    });

    // Animate tree falls
    for (const ticker of this.treeFallTickers) {
      ticker(dt);
    }

    // Animate flickering damaged buildings (stage 3)
    for (const m of this.built.destructibleMeshes) {
      if (m.userData['flickering'] && m.userData['crackMesh2']) {
        const cm2 = m.userData['crackMesh2'] as THREE.Mesh;
        if (cm2.material instanceof THREE.MeshStandardMaterial) {
          cm2.material.opacity = 0.4 + Math.sin(performance.now() * 0.012) * 0.15;
        }
      }
    }

    this.shieldCrackFlashOpacity *= Math.exp(-4.8 * dt);
    this.shieldCrackFlash.style.opacity = String(this.shieldCrackFlashOpacity);
    this.victoryRoyale.update(dt);
    this.waterSplash.update(dt);
    this.player.getLandingFxPosition(this.fxScratch);
    this.slideVfx.update(
      dt,
      sliding,
      this.fxScratch.x,
      this.fxScratch.y,
      this.fxScratch.z,
      horizSp,
      this.camera,
    );
    this.seasonFx.update(dt, this.camera);
    this.ambientLife.update(dt, this.camera);
    this.updateAmbientDecor(dt);
    this.buildTurboVfx.update(dt);

    this.harvest.tickCombo(dt);
    if (this.harvestStreak > 0) {
      this.harvestStreakIdle += dt;
      if (this.harvestStreakIdle >= this.HARVEST_STREAK_RESET) {
        this.harvestStreak = 0;
        this.harvestStreakIdle = 0;
      }
    }

    const feetApprox = b.position.y - 0.92;
    const likelyGrounded = feetApprox < 2.8 && Math.abs(b.velocity.y) < 6;
    if (
      this.dropMarkerVisibleUntilLand &&
      !g &&
      likelyGrounded &&
      feetApprox < 2.2 &&
      Math.abs(b.velocity.y) < 1.5
    ) {
      this.dropMarkerVisibleUntilLand = false;
    }
    if (
      likelyGrounded &&
      horizSp > 0.45 &&
      this.player.isLocked() &&
      !this.matchEnd &&
      !this.settings.isOpen()
    ) {
      this.footstepAcc += dt * horizSp * 0.055;
      if (this.footstepAcc >= 1) {
        this.footstepAcc = 0;
        this.audio.playFootstep(
          this.player.getSprintHold() > 0.35,
          this.player.getFootstepSurface(),
        );
      }

      if (
        !sliding &&
        !g &&
        !swimming &&
        horizSp > 3.8 &&
        !driving
      ) {
        this.footstepDustAcc += dt;
        if (this.footstepDustAcc >= 0.12) {
          this.footstepDustAcc = 0;
          this.player.getLandingFxPosition(this.fxScratch);
          this.footstepDust.spawn(
            this.fxScratch.x,
            this.fxScratch.y,
            this.fxScratch.z,
          );
        }
      } else {
        this.footstepDustAcc = 0;
      }
    } else {
      this.footstepAcc *= 0.92;
      this.footstepDustAcc = 0;
    }

    this.bandageCd = Math.max(0, this.bandageCd - dt);
    this.consumableUseCd = Math.max(0, this.consumableUseCd - dt);
    this.healFeedbackCd = Math.max(0, this.healFeedbackCd - dt);
    this.pingStack.update(dt);
    this.worldPingMarkers.update(dt, this.camera, this.renderer.domElement.clientWidth, this.renderer.domElement.clientHeight);
    this.dailyChallenges.update(dt);
    this.network.update(dt);
    this.culler.update(dt, this.camera.position);

    if (this.isMatchLive()) {
      this.matchElapsed += dt;
      this.harvestImpactCd = Math.max(0, this.harvestImpactCd - dt);
      this.updateMatchDirector(dt);
    }
    if (!this.bossObjectiveAnnounced && this.matchElapsed >= 8) {
      this.bossObjectiveAnnounced = true;
      const boss = this.bossFights.getObjectiveStates().find((bState) => bState.alive);
      this.announceObjective('boss-available', 'Bosses active — defeat one for a keycard', {
        x: boss?.x,
        z: boss?.z,
        worldLabel: 'BOSS',
        cooldownS: 60,
      });
    }
    if (!this.vaultObjectiveAnnounced && this.matchElapsed >= 12) {
      this.vaultObjectiveAnnounced = true;
      const vault = this.vaults.getObjectiveStates().find((vState) => !vState.unlocked);
      this.announceObjective('vault-available', 'Vaults online — keycards required', {
        x: vault?.x,
        z: vault?.z,
        worldLabel: 'VAULT',
        cooldownS: 60,
      });
    }
    if (!this.llamaSpawned && this.matchElapsed >= LLAMA_SPAWN_AFTER_S) {
      this.llamaSpawned = true;
      this.spawnLlamaAtRandomPoi();
    }
    if (this.llama) {
      this.llama.update(dt);
      if (this.llama.getOpened()) {
        this.bots.setLlamaTarget(null, null);
      }
    }
    if (!this.supplySpawned && this.matchElapsed >= SUPPLY_DROP_AFTER_S) {
      this.supplySpawned = true;
      const lx = -32 + Math.random() * 64;
      const lz = -32 + Math.random() * 64;
      this.supplyDrop = new FpsSupplyDrop(this.built.sceneRoot, lx, lz);
      this.supplyDropCraterSpawned = false;
      this.supplyDropSmokeAcc = 0;
      this.audio.playPlaneFlyover();
      this.announceObjective('supply-inbound', 'Supply drop incoming!', {
        x: lx,
        z: lz,
        worldLabel: 'DROP',
        cooldownS: 30,
      });
      this.supplyDropPlane = new FpsSupplyDropPlane(this.built.sceneRoot, lx, lz, () => {
        this.supplyDrop?.releaseDrop();
      }, this.particles);
    }
    if (this.supplyDrop) {
      this.supplyDrop.update(dt);
      if (this.supplyDrop.isFalling()) {
        this.supplyDropSmokeAcc += dt;
        if (this.supplyDropSmokeAcc >= 0.07) {
          this.supplyDropSmokeAcc = 0;
          this.supplyDrop.getWorldPosition(this.fxScratch);
          this.particles.emitSupplyDropSmoke(this.fxScratch);
        }
      } else {
        this.supplyDropSmokeAcc = 0;
      }
      if (this.supplyDrop.isLanded() && !this.supplyDrop.getOpened()) {
        const drop = this.supplyDrop.getXZ();
        if (!this.supplyDropCraterSpawned) {
          this.supplyDropCraterSpawned = true;
          this.particles.spawnSupplyDropCrater(this.built.sceneRoot, drop.x, drop.z);
        }
        if (!this.supplyDropLandedAnnounced) {
          this.supplyDropLandedAnnounced = true;
          this.announceObjective('supply-landed', 'Supply drop landed!', {
            x: drop.x,
            z: drop.z,
            worldLabel: 'DROP',
            cooldownS: 30,
          });
        }
        this.bots.setSupplyDropTarget(drop.x, drop.z);
        this.updateBotSupplyContest(dt, drop.x, drop.z);
      } else if (this.supplyDrop.getOpened()) {
        this.bots.setSupplyDropTarget(null, null);
        this.botSupplyContestT = 0;
      }
    }
    if (this.supplyDropPlane) {
      if (this.supplyDropPlane.update(dt)) {
        this.supplyDropPlane.dispose();
        this.supplyDropPlane = null;
      }
    }
    const sniperScopedAds =
      this.weapon.getWeaponKind() === 'sniper' &&
      this.hotbar.getSelectedIndex() === 2 &&
      this.adsHeld &&
      this.player.isLocked() &&
      !this.matchEnd;
    this.sniperGlint.setPlayerScoped(
      sniperScopedAds,
      dt,
      new THREE.Vector3(b.position.x, b.position.y, b.position.z),
    );
    this.sniperGlint.update(dt);
    this.npcQuest.update(dt);
    this.npcQuestHarvest.update(dt);
    this.npcQuestVisit.update(dt);
    this.pivotPlaza.update(dt);
    this.craftBench.update(dt);
    this.emoteWheel12.update(dt);
    if (
      this.replaySystem?.isRecording() &&
      this.isMatchLive() &&
      !this.lobbyActive &&
      !this.introActive &&
      !this.replaySystem.isDeathReplayActive()
    ) {
      this.player.yawObject.getWorldQuaternion(this.replayQuat);
      this.fxScratch.set(
        this.player.body.position.x,
        this.player.body.position.y,
        this.player.body.position.z,
      );
      this.replaySystem.recordFrame(
        this.fxScratch,
        this.replayQuat,
        this.bots.getReplayBotEntities(),
      );
    }
    // Batch E systems
    this.replaySystem?.update(dt);
    this.lobbyIsland.update(dt);
    this.liveEvent.update(dt);
    this.audioMix.update(dt);

    // --- Phase 6: NPC Vendors ---
    const vendorResult: VendorInteractResult | null = this.vendors.update(
      dt,
      new THREE.Vector3(b.position.x, b.position.y, b.position.z),
      () => this.getBountyTargetPos(),
    );
    if (vendorResult) this.handleVendorResult(vendorResult);

    // --- Phase 7: Boss Fights ---
    const bossHit = this.bossFights.update(dt, new THREE.Vector3(b.position.x, b.position.y, b.position.z), 0);
    if (bossHit && bossHit.damage > 0) {
      this.notePlayerDamage();
      this.health = Math.max(0, this.health - bossHit.damage);
      this.showIncomingDamageFallback(Math.min(1.15, bossHit.damage / 45));
      this.hudHealth.textContent = `HP ${Math.round(this.health)}`;
    }
    const kc = this.bossFights.collectKeycard(new THREE.Vector3(b.position.x, b.position.y, b.position.z));
    if (kc) {
      this.heldKeycards.add(kc.label);
      this.announceObjective(`keycard-${kc.label}`, `Picked up: ${kc.label}`, {
        x: kc.pos.x,
        z: kc.pos.z,
        worldLabel: 'KEYCARD',
        cooldownS: 45,
      });
    }

    // --- Phase 8: Vaults ---
    const vaultResult: VaultUpdateResult = this.vaults.update(dt, new THREE.Vector3(b.position.x, b.position.y, b.position.z), this.heldKeycards);

    if (this.isMatchLive()) {
      const trapNow = performance.now() * 0.001;
      const trapEntities = [
        { key: 'player', x: b.position.x, y: b.position.y, z: b.position.z },
        ...this.bots.getTrapCheckEntities(),
      ];
      for (const hit of this.buildPlacer.checkTraps(trapEntities, trapNow)) {
        if (hit.key === 'player' && !this.knocked) {
          this.notePlayerDamage();
          this.lastElimSource = 'self';
          this.lastAttackerName = null;
          const hp0 = this.health;
          const sh0 = this.shieldHud.getShield();
          let remain = hit.damage;
          const fromShield = Math.min(sh0, remain);
          this.shieldHud.setShield(sh0 - fromShield);
          remain -= fromShield;
          if (remain > 0) this.health -= remain;
          if (fromShield > 0) {
            this.applyPlayerShieldHit(fromShield, sh0 > 0 && fromShield >= sh0);
          }
          if (this.health < hp0) {
            this.damageFlashOpacity = Math.min(0.78, this.damageFlashOpacity + 0.45);
            this.showIncomingDamageFromWorld(hit.pos.x, hit.pos.z, Math.min(1.15, hit.damage / 45));
            this.audio.triggerCombatFocus(Math.min(1.1, 0.7 + hit.damage / 120));
            this.audio.playPlayerHurt();
          }
          this.damageNumbers.spawn(hit.pos, hit.damage, this.camera, false);
          if (this.health <= 0) this.enterKnocked('combat');
        } else if (hit.key.startsWith('bot-')) {
          const botId = Number.parseInt(hit.key.slice(4), 10);
          const res = this.bots.damageBotByTrap(botId, hit.damage, hit.pos);
          if (res) {
            this.botAI2.onBotDamagedByName(res.name);
            this.damageNumbers.spawn(res.pos, res.damage, this.camera, false);
            if (res.killed) {
              this.onPlayerElimination({ victim: res.name });
              this.reportPlayerElim(res.name, res.pos, undefined, 'Spike Trap');
              this.tryTriggerKillCam(res.pos, {
                name: res.name,
                weapon: 'Spike Trap',
                headshot: false,
              });
            } else if (res.knocked) {
              this.pushElimFeed(`Spike trap knocked ${res.name}`, 'knock');
            }
          }
        }
      }

      for (const boost of this.buildPlacer.checkLaunchPads(trapEntities, trapNow)) {
        if (boost.key === 'player' && !this.knocked) {
          b.velocity.y += boost.boostY;
          b.velocity.x += boost.forwardX;
          b.velocity.z += boost.forwardZ;
          this.audio.playJump();
        }
      }
    }

    if (!this.matchEnd) {
      this.storm.syncPlayer(b.position.x, b.position.z);
      const inStorm = this.storm.isOutside(b.position.x, b.position.z);
      this.storm.setOutsideTint(inStorm);
      if (inStorm && !this.stormAmbientOn) {
        this.audio.playStormAmbient();
        this.stormAmbientOn = true;
      } else if (!inStorm && this.stormAmbientOn) {
        this.audio.stopStormAmbient();
        this.stormAmbientOn = false;
      }
      if (this.isMatchLive() && inStorm && (!this.wasInStorm || this.matchElapsed - this.stormOutsideAnnounceAt > 26)) {
        this.stormOutsideAnnounceAt = this.matchElapsed;
        const finalStorm = this.storm.isFinalCircle();
        if (!this.wasInStorm) this.showStormWarningBanner(finalStorm ? 'FINAL CIRCLE - GET SAFE' : 'GET TO SAFE ZONE');
        this.pushElimFeed(finalStorm ? 'Storm damage — cut back into safe' : 'You are in the storm — follow SAFE', 'elim');
        this.minimap.triggerStormDamagePulse();
        this.triggerSafeZoneGuidancePulse(this.wasInStorm ? 2400 : 4200);
      }
      this.wasInStorm = inStorm;

      const liveLightning = this.isMatchLive() && !this.introActive;
      const lightningHit = this.stormLightning.update(
        dt,
        new THREE.Vector3(b.position.x, b.position.y, b.position.z),
        inStorm,
        liveLightning,
      );
      if (lightningHit && liveLightning) {
        const ldmg = lightningHit.damage;
        const strikePos = new THREE.Vector3(lightningHit.x, lightningHit.y + 1.2, lightningHit.z);
        if (!this.knocked) {
          this.notePlayerDamage();
          const sh0 = this.shieldHud.getShield();
          let remain = ldmg;
          const fromShield = Math.min(sh0, remain);
          this.shieldHud.setShield(sh0 - fromShield);
          remain -= fromShield;
          if (remain > 0) this.health -= remain;
          if (fromShield > 0) {
            this.applyPlayerShieldHit(fromShield, sh0 > 0 && fromShield >= sh0);
          }
          this.damageFlashOpacity = Math.min(0.78, this.damageFlashOpacity + 0.38);
          this.showIncomingDamageFromWorld(lightningHit.x, lightningHit.z, Math.min(1.15, ldmg / 45));
          this.audio.triggerStormTickMix();
          this.audio.playPlayerHurt();
          this.damageNumbers.spawn(strikePos, ldmg, this.camera, false);
          if (this.health <= 0) this.enterKnocked('storm');
        } else {
          this.notePlayerDamage();
          this.bleedoutTimer = Math.max(0, this.bleedoutTimer - ldmg * 0.35);
          this.damageNumbers.spawn(strikePos, ldmg, this.camera, false);
        }
      }

      if (this.isMatchLive()) {
      const sdx = b.position.x - SLURP_X;
      const sdz = b.position.z - SLURP_Z;
      if (sdx * sdx + sdz * sdz < SLURP_R * SLURP_R) {
        this.health = Math.min(100, this.health + 9.5 * dt);
        this.shieldHud.setShield(Math.min(100, this.shieldHud.getShield() + 8.5 * dt));
      }

      // Phase 34X-28: late-circle pressure mult from pure rules
      const pressure = lateCirclePressureMult(
        this.storm.getStormPhase(),
        this.countMatchAlive(),
      );
      const stormMult = (this.knocked ? STORM_KNOCKED_MULT : 1) * pressure;
      const dmg = this.storm.getStormDamage(dt) * stormMult;
      if (dmg > 0) {
        this.notePlayerDamage();
        this.showIncomingDamageFallback(Math.min(0.9, dmg / 18));
        this.triggerCameraShake(Math.min(0.007, 0.003 + dmg * 0.001));
        this.minimap.triggerStormDamagePulse();
        const hp0 = this.health;
        const sh0 = this.shieldHud.getShield();
        const stormHit = applyCombatDamage(
          { health: this.health, shield: sh0, alive: this.health > 0 && !this.knocked },
          dmg,
        );
        this.health = stormHit.health;
        this.shieldHud.setShield(stormHit.shield);
        if (stormHit.appliedToShield > 0) {
          this.applyPlayerShieldHit(
            stormHit.appliedToShield,
            sh0 > 0 && stormHit.appliedToShield >= sh0,
          );
        }
        if (this.health < hp0) {
          this.damageFlashOpacity = Math.min(0.58, this.damageFlashOpacity + 0.26);
          this.audio.playPlayerHurt();
        }
        this.stormTickAcc += dt;
        if (this.stormTickAcc > stormTickEvery) {
          this.audio.triggerStormTickMix();
          this.audio.playStormTick();
          this.stormTickAcc = 0;
        }
      }

      const aliveSurge = this.countMatchAlive();
      const surge = this.stormSurge.update(dt, phaseEarly.phase, aliveSurge, inStorm);
      if (surge.showBanner) {
        this.showPlacementBanner('STORM SURGE!');
        this.pushElimFeed('Too many players remain — surge damage active');
        this.audio.triggerStormTickMix();
        this.audio.playStormTick(true);
      }
      if (surge.damage > 0 && !this.knocked) {
        this.notePlayerDamage();
        const hp0s = this.health;
        const sh0s = this.shieldHud.getShield();
        let remainS = surge.damage;
        const fromShieldS = Math.min(sh0s, remainS);
        this.shieldHud.setShield(sh0s - fromShieldS);
        remainS -= fromShieldS;
        if (remainS > 0) this.health -= remainS;
        if (fromShieldS > 0) {
          this.applyPlayerShieldHit(fromShieldS, sh0s > 0 && fromShieldS >= sh0s);
        }
        if (this.health < hp0s) {
          this.damageFlashOpacity = Math.min(0.72, this.damageFlashOpacity + 0.32);
          this.audio.playPlayerHurt();
        }
      }
      if (this.health <= 0 && !this.knocked) {
        this.enterKnocked('storm');
      }
      if (this.knocked) {
        const outsideKnocked = this.storm.isOutside(b.position.x, b.position.z);
        const bleedRate = outsideKnocked ? 2.75 : 1;
        this.bleedoutTimer -= dt * bleedRate;
        const timerEl = this.hudKnocked.querySelector('#knocked-timer') as HTMLDivElement | null;
        if (timerEl) timerEl.textContent = `${Math.max(0, Math.ceil(this.bleedoutTimer))}s`;
        this.deathOverlay.style.opacity = String(
          Math.min(0.35, (1 - this.bleedoutTimer / BLEEDOUT_TIME) * 0.35),
        );
        if (this.bleedoutTimer <= 0) {
          this.requestMatchEnd('lost');
        }
      }
      this.updateShieldRegen(dt, dmg > 0);
      if (this.storm.isFullyShrunk() && !inStorm) {
        this.winTimer += dt;
        if (this.winTimer >= 2.6) this.requestMatchEnd('won');
      } else {
        this.winTimer = 0;
      }
      // Victory if all bots eliminated (use spawn count — corpses are removed from the list)
      if (
        isVictoryRoyale(
          this.health > 0 && !this.matchEnd,
          this.bots.aliveCount(),
          this.initialBotCount,
        )
      ) {
        this.requestMatchEnd('won');
      }
      }
    }

  }
  /** HUD, minimap, crosshair, compass, and presentation updates. */
  private tickUI(dt: number, rawDt: number): void {
    const b = this.player.body;
    const gliding = this.player.isGliderActive();
    const swimming = this.player.isSwimming();
    const phaseEarly = getStormPhaseInfo({
      initialRadius: STORM_INIT_R,
      minRadius: STORM_MIN_R,
      currentRadius: this.storm.radius,
      shrinkPerSecond: this.storm.getShrinkPerSecond(),
    });
    const hbAds = this.hotbar.getSelectedIndex();
    const canAds =
      hbAds <= 2 &&
      this.adsHeld &&
      !this.isPistolDualWieldActive() &&
      this.player.isLocked() &&
      !this.matchEnd &&
      !this.settings.isOpen();
    this.buildPlacer.updateFromCamera();
    this.buildPlacer.updateHpBars(dt, this.camera.position);
    this.buildEditMode.update(dt, this.camera);
    // update materials pickups
    this.fpsMaterials.update(dt, b.position.x, b.position.y, b.position.z);
    const inBuild = this.hotbar.getSelectedIndex() === 3;
    if (inBuild && !this.matchEnd) {
      const buildCost = this.buildPlacer.getMatCost();
      const prepaid = this.craftPrepaidPiece === this.buildPlacer.getPieceKind();
      const blockedReason = this.buildPlacer.updateBlockedReason(this.mats, this.buildEnabled, prepaid);
      const canPlaceBuild = blockedReason === null && this.buildPlacer.hasValidPreview() && (prepaid || this.mats >= buildCost);
      this.buildPlacer.updatePreviewVisual(performance.now(), canPlaceBuild);
    }
    this.buildPlacer.updateTargetHighlight(inBuild || this.player.isKeyHeld('KeyR'));
    const aliveForEdit = this.isMatchLive() && !this.knocked && !this.matchEnd;
    if (aliveForEdit) {
      this.buildPlacer.updateEditHighlight(this.camera);
    }
    const canEditBuild =
      aliveForEdit &&
      !inBuild &&
      this.buildPlacer.hasRemovableBuildInLook(this.camera, 4.2);
    const targetedPrompt = aliveForEdit ? this.buildPlacer.getTargetedBuildPrompt(this.mats) : null;
    this.hudEditHint.style.display =
      canEditBuild || (inBuild && targetedPrompt) || this.buildEditMode.isActive() ? 'block' : 'none';
    if (canEditBuild || (inBuild && targetedPrompt) || this.buildEditMode.isActive()) {
      if (this.buildEditMode.isActive()) {
        this.hudEditHint.textContent = '[R] Toggle cell · [R] empty confirm';
      } else {
        this.hudEditHint.textContent = inBuild && targetedPrompt ? targetedPrompt : '[E] Grid edit · [R] Edit piece';
      }
    }
    const targetedHp = aliveForEdit && !inBuild ? this.buildPlacer.getTargetedBuildHp() : null;
    if (targetedHp) {
      this.hudBuildHp.style.display = 'block';
      const hpPct = Math.max(0, Math.min(100, Math.round((targetedHp.hp / targetedHp.maxHp) * 100)));
      const repairText = targetedHp.hp < targetedHp.maxHp ? ' · Repair R' : '';
      this.hudBuildHp.textContent = `Build ${hpPct}% HP${repairText}`;
      const damageT = 1 - targetedHp.hp / targetedHp.maxHp;
      this.hudBuildHp.style.borderColor =
        damageT > 0.05 ? 'rgba(255,120,40,0.75)' : 'rgba(108,156,255,0.55)';
      this.hudBuildHp.style.color = damageT > 0.05 ? '#ffc898' : '#cfe4ff';
    } else {
      this.hudBuildHp.style.display = 'none';
    }
    if (inBuild && !this.matchEnd) {
      const cost = this.buildPlacer.getMatCost();
      const blockedText = this.buildPlacer.getBlockedReasonText();
      const affordable = this.mats >= cost && !blockedText;
      this.hudBuildCost.style.display = 'block';
      this.hudBuildCost.textContent = blockedText ? `${blockedText} · ${this.buildPlacer.getCostHintText()}` : this.buildPlacer.getCostHintText();
      this.hudBuildCost.style.borderColor = affordable ? 'rgba(108,156,255,0.55)' : 'rgba(255,96,96,0.75)';
      this.hudBuildCost.style.color = affordable ? '#cfe4ff' : '#ffc8c8';
    } else {
      this.hudBuildCost.style.display = 'none';
    }

    if (inBuild && this.buildMouseHeld && this.player.isLocked() && !this.matchEnd && !this.knocked && !swimming && !this.ziplines.isRiding()) {
      this.buildPlaceCd -= dt;
      const turboCd = this.buildTurboCd;
      while (this.buildPlaceCd <= 0) {
        if (!this.tryPlaceBuild(true)) break;
        this.buildPlaceCd += turboCd;
      }
    } else if (this.buildPlaceCd > 0) {
      this.buildPlaceCd -= dt;
    }

    if (inBuild || this.hudBuildCost.style.display !== 'none' || this.hudBuildHp.style.display !== 'none') {
      this.priorityHudQuietTimer = Math.max(this.priorityHudQuietTimer, 0.25);
    }

    const pRay = new THREE.Vector3(b.position.x, b.position.y + 0.55, b.position.z);
    const dirRay = new THREE.Vector3();
    this.camera.getWorldDirection(dirRay);
    this.loot.updateLookRay(pRay, dirRay);
    const lookLoot = this.loot.getLookTarget();
    const lookLlama =
      this.llama && !this.llama.getOpened() && this.llama.shouldShowPrompt(pRay, dirRay);
    const lookSupply =
      this.supplyDrop &&
      !this.supplyDrop.getOpened() &&
      this.supplyDrop.shouldShowPrompt(pRay, dirRay);
    const lookSlurpBarrel =
      this.isNearSlurpBarrel() && !this.slurpBarrelUsed && this.shieldHud.getShield() < 100;
    const lookShieldKeg =
      this.isNearShieldKeg() && !this.shieldKegUsed && this.shieldHud.getShield() < 100;
    const lookNpcQuest =
      this.npcQuest.getState() !== 'completed' &&
      this.npcQuest.shouldShowPrompt(pRay, dirRay);
    const lookNpcHarvest =
      this.npcQuestHarvest.getState() !== 'completed' &&
      this.npcQuestHarvest.shouldShowPrompt(pRay, dirRay);
    const lookNpcVisit =
      this.npcQuestVisit.getState() !== 'completed' &&
      this.npcQuestVisit.shouldShowPrompt(pRay, dirRay);
    const priorityHudActive = this.hasPriorityHudActive();
    const prompt = lookSupply
      ? { text: '[E] Supply drop', high: true }
      : lookLlama
        ? { text: '[E] Break llama', high: true }
        : lookLoot
          ? { text: lookLoot.tier === 'rare' ? '[E] Rare chest' : '[E] Chest', high: true }
          : lookShieldKeg
            ? { text: priorityHudActive ? '[E] Shield' : '[E] Shield keg (+40 shield)', high: false }
            : lookSlurpBarrel
              ? { text: priorityHudActive ? '[E] Slurp' : '[E] Slurp barrel (+25 shield)', high: false }
              : (() => {
                  const vendorHint = this.vendors.getNearHint(
                    new THREE.Vector3(b.position.x, b.position.y, b.position.z),
                  );
                  return vendorHint ? { text: vendorHint, high: false } : null;
                })()
                ?? (lookNpcVisit
                  ? { text: this.npcQuestVisit.getNearHint(), high: false }
                  : lookNpcHarvest
                    ? { text: this.npcQuestHarvest.getNearHint(), high: false }
                    : lookNpcQuest
                      ? { text: this.npcQuest.getNearHint(), high: false }
                      : null);
    const showPrompt = !!prompt && (!priorityHudActive || prompt.high);
    this.interactHint.style.display = showPrompt ? 'block' : 'none';
    if (prompt) this.interactHint.textContent = prompt.text;

    const horiz = Math.hypot(b.velocity.x, b.velocity.z);
    let eyeY = EYE_HEIGHT;
    if (this.player.isLocked() && horiz > 0.42) {
      const sprintBob =
        this.player.getTacticalSprintActive() || this.player.getSprintHold() > 0.35 ? 1.32 : 1;
      eyeY =
        EYE_HEIGHT +
        Math.sin(performance.now() * 0.018) * 0.028 * Math.min(horiz / 9.5, 1) * sprintBob;
    }
    eyeY -= this.player.getViewKickY() + this.player.getCrouchEyeDrop();
    this.player.pitchObject.position.y = eyeY;

    for (const { mesh } of this.built.syncables) {
      const g = mesh.userData.lootGlow as { mat: THREE.MeshToonMaterial; base: number } | undefined;
      if (g?.mat) {
        const ph = mesh.position.x * 0.41 + mesh.position.z * 0.39;
        g.mat.emissiveIntensity = g.base + Math.sin(performance.now() * 0.0026 + ph) * 0.11;
      }
    }

    this.hudHealth.textContent = `HP ${Math.max(0, Math.round(this.health))}`;
    this.updateSquadHud();
    const phase = phaseEarly;
    const eta =
      phase.etaSeconds !== null ? ` · ~${Math.max(0, Math.ceil(phase.etaSeconds))}s` : '';
    this.hudStorm.textContent = `STORM ${this.storm.radius.toFixed(0)}m${eta}`;
    this.hudPhase.textContent = phase.phaseLabel;
    const aliveHud = this.countMatchAlive();
    if (!this.matchEnd && this.storm.shouldShowSurgeWarning(aliveHud)) {
      this.hudStormSurge.style.display = 'block';
      this.hudStormSurge.textContent = this.storm.getSurgeWarningText(aliveHud);
    } else {
      this.hudStormSurge.style.display = 'none';
    }
    if (!this.matchEnd) {
      this.statMaxPhase = Math.max(this.statMaxPhase, phase.phase);
      this.dailyChallenges.recordStormPhase(phase.phase);
      if (this.matchElapsed > 2.5 && phase.phase > this.lastStormPhaseNotified) {
        this.lastStormPhaseNotified = phase.phase;
        this.showStormPhaseBanner(phase.phase);
        if (phase.phase >= 5 && !this.stormLatePhaseAnnounced) {
          this.stormLatePhaseAnnounced = true;
          this.showStormWarningBanner('LATE STORM - ROTATE EARLY');
          this.pushElimFeed('Late storm — rotate early, hold safe', 'elim');
          this.triggerSafeZoneGuidancePulse(3800);
        }
        this.audio.triggerStormTickMix();
        this.audio.playStormTick(true);
        this.audio.playStormClosing();
      }
    }

    const stormC = this.storm.getCenterXZ();
    const nextStormC = this.storm.getNextCenterXZ();
    const playerInStormForGuidance = this.storm.isOutside(b.position.x, b.position.z);
    const timedGuidancePulse = Math.max(0, Math.min(1, (this.safeZoneGuidancePulseUntil - performance.now()) / 1600));
    const finalCircleGuidance = this.storm.isFinalCircle();
    const safeGuidancePulse = Math.max(
      this.stormWarnFlash,
      playerInStormForGuidance ? 1 : 0,
      finalCircleGuidance ? 0.74 : phase.phase >= 5 ? 0.58 : 0,
      timedGuidancePulse,
    );
    const markers: FpsMinimapMarker[] = [
      { x: VEND_X, z: VEND_Z, color: '#e080ff', label: 'V' },
      { x: CRAFT_BENCH_X, z: CRAFT_BENCH_Z, color: '#44ff88', label: 'C' },
      { x: SLURP_X, z: SLURP_Z, color: '#44e8c8', label: 'S' },
      { x: SLURP_BARREL_X, z: SLURP_BARREL_Z, color: '#44ffcc', label: 'B' },
      { x: REBOOT_X, z: REBOOT_Z, color: '#66aaff', label: 'R' },
    ];
    if (this.npcQuest.getState() !== 'completed') {
      markers.push({ x: NPC_QUEST_X, z: NPC_QUEST_Z, color: '#ffd966', label: '!' });
    }
    if (this.npcQuestVisit.getState() !== 'completed') {
      markers.push({ x: NPC_VISIT_X, z: NPC_VISIT_Z, color: '#66bbff', label: '?' });
    }
    for (const boss of this.bossFights.getObjectiveStates()) {
      if (!boss.alive) continue;
      markers.push({ x: boss.x, z: boss.z, color: '#ff5050', label: 'BOSS' });
    }
    for (const vault of this.vaults.getObjectiveStates()) {
      markers.push({
        x: vault.x,
        z: vault.z,
        color: vault.unlocked ? '#44ff88' : '#ffd54a',
        label: vault.unlocked ? 'OPEN' : 'VAULT',
      });
    }
    if (this.llama && !this.llama.getOpened()) {
      const lp = this.llama.getXZ();
      markers.push({ x: lp.x, z: lp.z, color: '#c77dff', label: 'LL' });
    }
    for (const vp of this.vehicles.getVehiclePositions()) {
      markers.push({
        x: vp.x,
        z: vp.z,
        color: vp.driven ? '#2ee6ff' : vp.kind === 'quad' ? '#ffa040' : '#fff060',
      });
    }
    for (const tm of this.teammates) {
      const stats = tm.getSquadStats();
      tm.getPosition(this.fxScratch);
      markers.push({
        x: this.fxScratch.x,
        z: this.fxScratch.z,
        color: stats.dead ? '#6a6a72' : stats.knocked ? '#ff3030' : '#44ff88',
        label: stats.name.slice(0, 3).toUpperCase(),
        knocked: stats.knocked,
        dead: stats.dead,
      });
    }
    for (const bot of this.bots.getLivingBots()) {
      markers.push({
        x: bot.x,
        z: bot.z,
        color: bot.squadColor,
        knocked: bot.knocked,
        dot: true,
      });
    }
    if (this.supplyDrop && !this.supplyDrop.getOpened()) {
      const dropPos = this.supplyDrop.getXZ();
      markers.push({
        x: dropPos.x,
        z: dropPos.z,
        color: '#ffd54a',
        label: 'DROP',
      });
    }

    this.updateNextZoneRing();

    this.minimap.update(b.position.x, b.position.z, this.player.yawObject.rotation.y, {
      storm: {
        stormRadius: this.storm.radius,
        safeRadius: this.storm.getSafeZoneRadius(),
        cx: stormC.x,
        cz: stormC.z,
        colorblind: this.graphicsState.colorblindStorm,
        nextRadius: this.storm.getNextSafeZoneRadius(),
        nextCx: nextStormC.x,
        nextCz: nextStormC.z,
        safePulse: safeGuidancePulse,
      },
      markers,
      dropMarker: this.shouldShowDropMarker() && this.dropMarker ? this.dropMarker : undefined,
    });

    const tac = this.player.getTacticalSprintActive();
    this.hudTacticalSprint.style.opacity = tac ? '1' : '0';

    const nearVend = this.isNearVending();
    if (nearVend && !this.matchEnd) {
      this.hudVend.style.display = 'block';
      this.hudVend.textContent = this.getVendHudText();
    } else {
      this.hudVend.style.display = 'none';
    }
    const nearCraft = this.isNearCraftBench();
    if (nearCraft && !this.matchEnd && !this.knocked && !this.craftBench.isOpen()) {
      this.hudCraftBench.style.display = 'block';
      this.hudCraftBench.textContent = this.craftBench.getNearHint();
    } else {
      this.hudCraftBench.style.display = 'none';
    }
    const nearNpc = this.isNearNpcQuest();
    const nearNpcHarvest = this.isNearNpcQuestHarvest();
    const nearNpcVisit = this.isNearNpcQuestVisit();
    const questHud = this.npcQuest.getHudText();
    const harvestHud = this.npcQuestHarvest.getHudText();
    const visitHud = this.npcQuestVisit.getHudText();
    const activeQuestHud = visitHud || harvestHud || questHud;
    if (
      (nearNpc || nearNpcHarvest || nearNpcVisit) &&
      activeQuestHud &&
      !this.matchEnd &&
      !this.knocked &&
      !this.npcQuest.isOpen() &&
      !this.npcQuestHarvest.isOpen() &&
      !this.npcQuestVisit.isOpen()
    ) {
      this.hudQuestChip.style.display = 'block';
      this.hudQuestChip.textContent = activeQuestHud;
    } else if (
      (this.npcQuest.getState() === 'accepted' && questHud) ||
      (this.npcQuestHarvest.getState() === 'accepted' && harvestHud) ||
      (this.npcQuestVisit.getState() === 'accepted' && visitHud)
    ) {
      if (!this.matchEnd) {
        this.hudQuestChip.style.display = 'block';
        this.hudQuestChip.textContent = activeQuestHud;
      } else {
        this.hudQuestChip.style.display = 'none';
      }
    } else {
      this.hudQuestChip.style.display = 'none';
    }
    const nearReboot = this.isNearRebootVan();
    const showSelfReboot =
      this.knocked && this.redeployLeft > 0 && !this.matchEnd && (nearReboot || this.rebootCardCount > 0);
    const showTeammateReboot =
      this.isSquadMode() &&
      !this.knocked &&
      nearReboot &&
      !this.matchEnd &&
      this.getTeammateNeedingReboot() != null;
    if (showSelfReboot) {
      this.hudReboot.textContent = nearReboot
        ? 'Press R — Reboot Van'
        : `Press R — Reboot Card (${this.rebootCardCount})`;
      this.hudReboot.style.display = 'block';
    } else if (showTeammateReboot) {
      const left = Math.max(0, REBOOT_VAN_QUEUE_HOLD - this.teammateRebootProgress);
      this.hudReboot.textContent =
        this.teammateRebootProgress > 0
          ? `HOLD R — Reboot teammate (${left.toFixed(1)}s)`
          : `HOLD R — Reboot teammate (${REBOOT_VAN_QUEUE_HOLD}s)`;
      this.hudReboot.style.display = 'block';
    } else {
      this.hudReboot.style.display = 'none';
    }
    this.updateTacticalHud();

    const hudPlay =
      !this.matchEnd &&
      !this.settings.isOpen() &&
      !this.craftBench.isOpen() &&
      !this.npcQuest.isOpen() &&
      !this.npcQuestHarvest.isOpen() &&
      !this.npcQuestVisit.isOpen();
    this.updateHintStripVisibility(hudPlay);
    this.updateLowPriorityHudClutter(hudPlay);
    this.ui.style.opacity = this.player.isLocked() && hudPlay ? '0' : '1';
    const hb = this.hotbar.getSelectedIndex();
    const gunSlot = hb >= 0 && hb <= 2;
    const sniperScoped =
      canAds && this.weapon.getWeaponKind() === 'sniper' && hb === 2 && hudPlay;
    this.sniperScope.style.display = sniperScoped ? 'block' : 'none';
    const crosshairShown =
      this.player.isLocked() &&
      hudPlay &&
      (this.pickaxeMode || (hb !== 3 && hb !== 4)) &&
      !sniperScoped;
    if (crosshairShown) {
      this.updateCrosshairVisual(horiz, gunSlot, true, this.pickaxeMode);
    } else {
      this.crosshair.style.opacity = '0.35';
    }
    this.updateScopeReticlePulse(sniperScoped);

    const adsTarget = canAds && gunSlot && hudPlay ? 1 : 0;
    const adsLerp = this.prefersReducedMotion() ? 18 : 9;
    this.adsVignetteAlpha += (adsTarget - this.adsVignetteAlpha) * Math.min(1, adsLerp * dt);
    this.adsVignetteEl.style.opacity = String(this.adsVignetteAlpha * 0.74);

    this.hudAmmo.style.display = gunSlot && hudPlay ? 'block' : 'none';
    if (gunSlot) {
      const line = this.weapon.getAmmoHudText();
      const rr = this.weapon.getReloadProgress();
      const heat = this.mythics.getActiveKind() === 'drum_gun'
        ? this.mythics.getHeatRatio()
        : this.weapon.getHeatRatio();
      const overheated = this.mythics.getActiveKind() === 'drum_gun'
        ? this.mythics.isOverheated()
        : this.weapon.isOverheated();
      // Phase 30X-5: low-ammo pulse when magazine ≤ 20%
      const magMatch = line.match(/^(\d+)\//);
      const magLeft = magMatch ? parseInt(magMatch[1]!, 10) : 99;
      const magCap = (() => {
        const m = line.match(/\/(\d+)/);
        return m ? parseInt(m[1]!, 10) : 30;
      })();
      const lowAmmo = magCap > 0 && magLeft / magCap <= 0.2 && magLeft > 0;
      const emptyAmmo = magLeft === 0;
      if (lowAmmo || emptyAmmo) {
        const pulse = 0.65 + Math.sin(performance.now() * 0.012) * 0.35;
        this.hudAmmo.style.color = emptyAmmo
          ? `rgba(255,80,90,${pulse})`
          : `rgba(255,180,80,${0.75 + pulse * 0.25})`;
        this.hudAmmo.style.transform = `scale(${1 + pulse * 0.04})`;
      } else {
        this.hudAmmo.style.color = '';
        this.hudAmmo.style.transform = '';
      }
      if (rr > 0.02) {
        const pct = Math.round(rr * 100);
        this.hudAmmo.innerHTML = `${line}<div style="margin-top:5px;height:3px;background:rgba(0,0,0,0.5);border-radius:2px;overflow:hidden"><div style="height:100%;width:${pct}%;background:linear-gradient(90deg,#2ee6ff,#8af)"></div></div>`;
      } else if (heat > 0.02 || overheated) {
        const pct = Math.round(heat * 100);
        const barColor = overheated
          ? 'linear-gradient(90deg,#ff3030,#ff7070)'
          : 'linear-gradient(90deg,#ffd34d,#ff4a1a)';
        const tag = overheated ? ' COOLING' : '';
        this.hudAmmo.innerHTML = `${line}${tag}<div style="margin-top:5px;height:3px;background:rgba(0,0,0,0.5);border-radius:2px;overflow:hidden"><div style="height:100%;width:${pct}%;background:${barColor}"></div></div>`;
      } else {
        this.hudAmmo.textContent = line;
      }
      this.hotbar.setReloadProgress(hb, rr, true);
      this.updateWeaponHeatHud();
    } else {
      for (let i = 0; i < 3; i++) {
        this.hotbar.setWeaponHeat(i, 0);
        this.hotbar.setReloadProgress(i, 0, false);
      }
    }
    this.hudGlide.style.display = gliding && hudPlay ? 'block' : 'none';
    this.hudSwim.style.display = swimming && hudPlay && !this.ziplines.isRiding() ? 'block' : 'none';

    // Add chest hum to audio visualizer
    const unopenedChests = this.loot.getUnopenedChestPositions();
    for (const chest of unopenedChests) {
      const dist = Math.hypot(chest.x - b.position.x, chest.z - b.position.z);
      if (dist < 20) {
        // Intermittent pulse instead of constant so it's not overwhelming
        if (Math.random() < 0.05) {
          const vol = Math.max(0, 1 - dist / 20);
          this.audioVisualizer.addEvent({ x: chest.x, z: chest.z, type: 'chest', intensity: vol }, b.position.x, b.position.z, this.player.yawObject.rotation.y);
        }
      }
    }

    // Skydivers / Gliders
    for (const diver of this.skydivers) {
      const dist = Math.hypot(diver.x - b.position.x, diver.z - b.position.z);
      if (dist < 60 && Math.random() < 0.08) {
         const vol = Math.max(0, 1 - dist / 60);
         this.audioVisualizer.addEvent({ x: diver.x, z: diver.z, type: 'glider', intensity: vol }, b.position.x, b.position.z, this.player.yawObject.rotation.y);
      }
    }

    this.audioVisualizer.update(dt, this.player.yawObject.rotation.y);
    this.updateCompass(this.player.yawObject.rotation.y);
    this.updateAutoPerformanceScaler(rawDt);
    const vpW = this.mount.clientWidth || window.innerWidth;
    const vpH = this.mount.clientHeight || window.innerHeight;
    this.damageNumbers.update(dt, this.camera, vpW, vpH);
    this.hitFeedback.update(dt);
    this.updateLootRarityFlash(dt);

    // Phase 31X-15: water foam / caustic pulse on ocean material
    const wm = this.built.waterMaterial;
    if (wm) {
      const t = performance.now() * 0.001;
      const pulse = 0.18 + Math.sin(t * 2.2) * 0.1 + Math.sin(t * 5.1) * 0.04;
      wm.emissiveIntensity = pulse;
      if ('opacity' in wm && typeof wm.opacity === 'number') {
        wm.transparent = true;
        wm.opacity = Math.min(0.92, 0.78 + Math.sin(t * 1.4) * 0.06);
      }
    }

    const gu = this.postFx?.gradePass.uniforms;
    if (gu) {
      if (gu['vignette']) {
        const hp = this.health ?? 100;
        const moodVignette = this.lightingNightAmount * 0.025 + this.stormMoodPulse * 0.07;
        let targetVignette = 0.22 + moodVignette + Math.sin(performance.now() * 0.0007) * 0.05;
        if (hp < 60) targetVignette = 0.22 + moodVignette + (60 - hp) / 60 * 0.16 + Math.sin(performance.now() * 0.0007) * 0.05;
        if (hp < 30) targetVignette = 0.38 + (30 - hp) / 30 * 0.24 + Math.sin(performance.now() * 2.2) * 0.08;
        gu['vignette'].value = Math.min(0.66, targetVignette);
      }
      if (gu['aberration']) {
        gu['aberration'].value = 0.00135 + Math.sin(performance.now() * 0.0004) * 0.00045;
      }
    }
    this.postFx?.chromaticAberration.update(dt);

    this.updateVictoryConfetti(dt);
    this.updateDefeatVignette();

    this.updateDayNightCycle(dt);
    this.updatePlayerCombatRimLight(dt);
  }
  private renderFrame(dt: number): void {
    if (this.postFx) {
      this.postFx.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
    this.perfOverlay.update(dt, this.renderer, this.particles?.getActiveParticleCount());
  }

  private async initPostProcessing(width: number, height: number): Promise<void> {
    if (this.disposed) return;
    try {
      this.postFx = await createPostProcessingPipeline({
        renderer: this.renderer,
        scene: this.scene,
        camera: this.camera,
        width,
        height,
        bloomStrength: this.graphicsState.performanceMode ? BLOOM_STRENGTH_PERF : BLOOM_STRENGTH_NORMAL,
        bloomEnabled: this.graphicsState.bloom,
      });
    } catch (err) {
      console.warn('[FPSGame] Post-processing unavailable, using direct render', err);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.running = false;
    this.renderer.setAnimationLoop(null);
    for (const id of this.scheduledTimeouts) window.clearTimeout(id);
    this.scheduledTimeouts.clear();
    for (const effect of this.domEffectHandles) effect.dispose();
    this.domEffectHandles.clear();
    for (const effect of this.lootEffectHandles) effect.disposeEffect?.();
    this.lootEffectHandles.clear();
    this.audio.stopLobbyMusic();
    this.audio.stopStormAmbient();
    this.lobbyOverlay?.removeEventListener('mousedown', this.unlockLobbyAudioOnce);
    for (const p of this.worldFx) this.scene.remove(p.mesh);
    this.worldFx.length = 0;
    this.fxGeo.dispose();
    this.fxGlidePlaneGeo.dispose();
    this.fxGlideMat.dispose();
    this.fxDustMat.dispose();
    this.fxGoldMat.dispose();
    this.bots?.dispose();
    this.clearTeammates();
    this.killLeaderHud?.remove();
    this.playerCrownHud?.remove();
    this.vehicles?.dispose();
    this.ziplines?.dispose();
    this.hitMarker?.remove();
    this.killBanner?.remove();
    this.aliveCounter?.remove();
    this.hudVehicle?.remove();
    this.hudBuildCost?.remove();
    this.vehicleEnterHint?.remove();
    this.ziplineHint?.remove();
    this.damageNumbers?.dispose();
    this.hitFeedback?.dispose();
    this.touchControls?.dispose();
    this.compassBar?.remove();
    this.compassPoiLabelEl?.remove();
    this.hudStormGrace?.remove();
    this.hudStormGraceRing?.remove();
    if (this.playerCharacter) this.scene.remove(this.playerCharacter);
    if (this.lobbyPedestal) this.scene.remove(this.lobbyPedestal);
    if (this.lobbyShowcaseRimLight) this.scene.remove(this.lobbyShowcaseRimLight);
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('keydown', this.onGlobalKeyDown);
    window.removeEventListener('keydown', this.onPerfKeyDown);
    window.removeEventListener('keyup', this.onGlobalKeyUp);
    window.removeEventListener('mousemove', this.onWindowMouseMove);
    window.removeEventListener('pointerdown', this.onLobbyPointerDown);
    window.removeEventListener('pointermove', this.onLobbyPointerMove);
    window.removeEventListener('pointerup', this.onLobbyPointerEnd);
    window.removeEventListener('pointercancel', this.onLobbyPointerEnd);
    document.removeEventListener('pointerlockchange', this.onPointerLockChange);
    window.removeEventListener('mouseup', this.onWindowMouseUp);
    this.renderer.domElement.removeEventListener('mousedown', this.onMouseDown);
    this.renderer.domElement.removeEventListener('mouseup', this.onMouseUp);
    this.renderer.domElement.removeEventListener('wheel', this.onWheel);
    this.renderer.domElement.removeEventListener('contextmenu', this.onContextMenu);
    this.slideVfx?.dispose();
    this.impactSparks?.dispose();
    this.particles?.dispose();
    this.perfOverlay?.dispose();
    this.chestBurst?.dispose();
    this.footstepDust?.dispose();
    this.buildDustVfx?.dispose();
    this.bulletCasingVfx?.dispose();
    this.shieldCrackVfx?.dispose();
    this.shieldCrackFlash?.remove();
    this.waterSplash?.dispose();
    this.knockBubble?.dispose();
    this.seasonFx?.dispose();
    this.ambientLife?.dispose();
    this.buildTurboVfx?.dispose();
    this.player.dispose();
    this.weapon?.dispose();
    for (const flash of this.shotMuzzleLights) {
      flash.light.removeFromParent();
      flash.light.dispose();
    }
    this.shotMuzzleLights.length = 0;
    this.settings.dispose();
    this.speedLinesOverlay?.remove();
    this.speedLinesStyle?.remove();
    this.storm.dispose();
    this.stormLightning.dispose();
    this.stormCircle.dispose();
    this.nextZoneRing.geometry.dispose();
    (this.nextZoneRing.material as THREE.Material).dispose();
    this.scene.remove(this.nextZoneRing);
    this.battleBus.dispose();
    this.buildPlacer.dispose();
    this.buildEditMode.dispose();
    this.fpsMaterials.dispose();
    this.botAI2.dispose();
    this.minimap.dispose();
    this.hotbar.dispose();
    this.xpBar.dispose();
    this.pingStack.dispose();
    this.worldPingMarkers.dispose();
    this.dailyChallenges.dispose();
    this.shieldHud.dispose();
    this.staminaHud.dispose();
    this.lootRarity.dispose();
    this.floorPickups.dispose();
    this.weaponPickups.dispose();
    this.poiLabelEl.remove();
    this.busJumpChip?.remove();
    this.stormPhaseBanner?.remove();
    this.stormPhaseBannerStyle?.remove();
    this.hudAnimStyle?.remove();
    this.supplyDrop?.dispose();
    this.supplyDropPlane?.dispose();
    this.llama?.dispose();
    this.hudPhase.remove();
    this.hudTactical.remove();
    this.tutorial.dispose();
    this.deathOverlay.remove();
    this.damageFlash.remove();
    this.stormWarnBorder?.remove();
    this.emoteToast.remove();
    this.hudHintStrip.remove();
    this.emoteWheel.remove();
    this.chatWheel.remove();
    this.pingWheel.remove();
    this.replaySystem?.dispose();
    this.killCamOverlay.remove();
    this.spectateOverlay.remove();
    this.lootToast.remove();
    this.clearScheduledTimeout(this.pickupCardDelayTimer);
    this.pickupCard.dispose();
    this.rarityStinger.dispose();
    this.sniperScope.remove();
    this.hudHealth.remove();
    this.hudRankedChip.remove();
    this.hudLtmChip.remove();
    this.hudStorm.remove();
    this.hudStormSurge.remove();
    this.hudMats.remove();
    this.hudGold.remove();
    this.hudReboot.remove();
    this.hudKnocked.remove();
    this.hudRevive.remove();
    this.hudCarry.remove();
    this.hudSquad.remove();
    this.hudAutoRun.remove();
    this.hudVend.remove();
    this.hudCraftBench.remove();
    this.hudQuestChip.remove();
    this.craftBench.dispose();
    this.npcQuest.dispose();
    this.npcQuestHarvest.dispose();
    this.npcQuestVisit.dispose();
    this.chugSplash?.dispose();
    this.shockwave?.dispose();
    this.supplyContestHud?.remove();
    this.sniperGlint?.dispose();
    this.hudEditHint.remove();
    this.hudBuildHp.remove();
    this.hudAmmo.remove();
    this.weaponInspectOverlay.remove();
    this.hudGlide.remove();
    this.hudDropCue.remove();
    this.hudMantleHint.remove();
    this.hudSwim.remove();
    this.interactHint.remove();
    this.elimFeed.remove();
    this.endScreen.remove();
    this.confettiCanvas.remove();
    this.defeatVignetteEl.remove();
    this.lobbyOverlay?.remove();
    this.postFx?.gradePass.dispose();
    this.postFx?.bloomPass.dispose();
    this.postFx?.composer.dispose();
    this.pmrem?.dispose();
    const clouds = this.sky.userData.clouds as THREE.Group | undefined;
    if (clouds) {
      const cloudTex = clouds.userData.cloudTex as THREE.Texture | undefined;
      cloudTex?.dispose();
      clouds.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry.dispose();
          (obj.material as THREE.Material).dispose();
        }
      });
      this.scene.remove(clouds);
    }
    this.sky.geometry.dispose();
    (this.sky.material as THREE.Material).dispose();
    this.renderer.dispose();
    this.mount.removeChild(this.renderer.domElement);
    this.mount.removeChild(this.ui);
    this.mount.removeChild(this.crosshair);
    this.victoryRoyale.dispose();
  }
}
